
import { v4 as uuidv4 } from "uuid";
import { 
  UpsertableBase,
  User,
  ModelBase,
  FloatingFeature,
  FeatureSource,
  FeatureUserRating,
  IStringHashFeature,
  NumberFeature,
  StringFeature,
  StringsFeature,
  OptionalFeatureBase,
  ReusableString,
  FeatureBase,
  PlayerObject,
  Feature,
  ParentedBase,
  CreatureFeature,
} from "@models";
import { 
  Selectable,
  SourceBook,
  Condition,
  SpellList,
  Background,
  Item,
  Campaign,
  Character,
  Creature,
  CreatureGroup,
  EldritchInvocation,
  FightingStyle,
  EquipmentPack,
  PactBoon,
  Feat,
  GameClass,
  Organization,
  OrgInstance,
  MagicItemKeyword,
  WeaponMasteryProperty,
  Race,
  Subrace,
  Gift,
  BastionFacility,
  Resource,
  Spell,
  SpellSlotType,
  Subclass,
  Table,
  Tool,
  TemplateBase,
  StartEquipmentFeature,
  ConditionFeature,
  BasicItem,
  Ammunition,
  Container,
  Armor,
  Weapon,
  DeckItem,
  Ability,
  Advantage,
  ASIBaseFeature,
  BonusSpells,
  SuggestedSpells,
  CreatureAbility,
  DamageMultiplier,
  LanguageFeature,
  MinionAbility,
  Modifier,
  Proficiency,
  Reroll,
  ResourceFeature,
  SenseFeature,
  Spellbook,
  SpellcastingFeature,
  SpellModifier,
  UpgradableNumberFeature,
  CharacterFeatureSource,
  FeatFeature,
  UnarmedStrikesFeature,
  ReplaceAbilityScoreFeature,
  AttackUpgradeFeature,
  BonusActionAttackFeature,
  WeaponKeywordUpgradeFeature,
  CharacteristicFeature,
  RollableTable,
  SpellsFromList,
  MinimumRoll,
  SubclassFeature,
  LightAttackAddDamageMod,
  SummonUpgradeFeature,
  ActionTypeUpgrade,
  Encounter,
  // EncounterLog,
  LootTable,
  ExtraEffectsFeature,
  ExtraEffectTypeFeature,
  ExtraEffectsOfTypeFeature,
  // CreatureSpellAsAbility,
  ExtraSpellLists,
  SkillAsAbility,
  SpecialFeature,
  SFFeature,
  SFMergeFeature,
  Transform,
  TransformUpgradeFeature,
  // Adaptation,
  KnowledgeFeature,
  LearnableFeature,
  CreatureTransform,
  Deity,
  WeaponMasteryFeature,
  EldritchInvocationsFeature,
  SkillAbilityOverrideFeature,
  PactBoonFeature,
  WeaponDamageUpgradeFeature,
  GameCard,
  GameDeck,
  CampaignDeck,
  CharacterLimitation,
  Attack,
  WeaponLimitation,
  PseudoItem,
  ItemLimitation,
  SpellLimitation,
  AbilityLimitation,
  RollPlus,
  SpellDamageOptions,
  SpellbookModifier,
  GemItem,
  CreatureLimitation,
  CreatureDetails,
  AlternateConditionFeature,
  WeaponKeywordUpgradeFeature2,
  DisableActionsFeature,
  IgnoreStealthDisadvantage,
  WeaponMasteryMasterFeature,
  SizeOptions,
  CreatureGift,
  GreaterAdvantage,
  UpgradableNumber,
  StandardFeatures,
  DandDFeatureSource,
  LimitSpoofFeature,
  CharacterItem,
  Attributes,
  AbilityScoresUpgradable,
  AttachedTable,
  TableEntry,
  HitDiceUpgradable,
  SpellcastingEntry,
  AbilityEffect,
  Potence,
  iCreatureSpell,
  SpellcastingTable,
  LegendariesTable,
  Legendaries,
  ARMOR_TYPES,
  SENSES,
  SKILLS,
  WEAPON_KEYWORDS,
  CharacterReceivable,
  BonusSpellsUpgrade,
  CharacterDrawnCard,
  RestAlterationFeature,
  SpellsFromListWithCosts,
  SBSection,
  CharacterOptions,
  CreatureTypeFeature,
  Sense,
  Skill,
  ComplexCreatureInstance,
} from "@dndModels";
import { 
  FEATURE_SOURCE_TYPES,
  ABILITY_SCORES,
  DURATION_NUMBER_MAP,
  CHARACTER_PIECES,
  SIZE_TO_NUM_MAP,
  CREATURE_TYPES,
  DAMAGE_TYPES,
  METALLIC_DRAGONS,
  CHROMATIC_DRAGONS,
  GEM_DRAGONS,
  CREATURE_ALIGNMENTS,
  ABILITY_SCORE_FULL_TO_ABBREV_MAP,
  AREA_TYPES,
  SIZE_OPTIONS,
  SPEED_OPTIONS,
  SPELL_LEVELS_USING_TYPE,
  CASTING_TIME_MAP,
  SIZE_MAP,
  XP_FOR_CR,
} from "@dndModels/Constants";

import { APIClass, processResponseProps } from "@utilities/smart_api_class";
import { AbilityScoreType, AreaType, AttackType, CreatureFBType, CreatureType, DamageType, DataType, isCreatureFBType, isCreatureType, isDamageType, isDataType, isItemType, isPlayerObjectType, isSpeedType, isStatType, ItemType, SourceBookType, SpeedType } from "@dndModels/Types";
import { 
  CONSTANTIZED_DATA_TYPES,
  PLAYER_OBJECT_TYPES, 
  TRUST_JSON_FOR_DATA_TYPES
} from "@dndModels/DataTypeConstants";
import { Ticket, UserTicket } from "@models/Ticket";

import CommonUtilities from "@utilities/common_utilities";
import { AuthContextProps } from "react-oidc-context";

const common_util = CommonUtilities.getInstance();

const delay = (milliseconds: number) => new Promise(res => setTimeout(res, milliseconds));

export class DandDAPIClass extends APIClass {
  system = "D&D";
  topHeight = 0;
    
  getFullObjects = async (data_type: DataType, filter: any = {}) => {
    let objects = await this.getObjectsWithCallbacks({ data_type });
    objects = this.filter(data_type, objects, filter);
    for (const obj of objects) {
      if (obj instanceof ModelBase) {
        // await this.loadObject(obj);
        await this.connectObject(obj);
      }
    }
    return objects;
  };
  get all_floating_features(): FloatingFeature[] {
    return this.smart_hash['floating_feature'] as FloatingFeature[];
  }
  get all_creature_features(): CreatureFeature[] {
    return this.smart_hash['creature_feature'] as CreatureFeature[];
  }
  get all_reusable_strings(): ReusableString[] {
    return this.smart_hash['reusable_string'] as ReusableString[];
  }
  
  get_sub_feature_sources = (char_feature_source: CharacterFeatureSource) => {
    let feature_sources: FeatureSource[] = [];
    if (char_feature_source.source instanceof FeatureSource) {
      feature_sources.push(char_feature_source.source);
    }
    char_feature_source.sub_char_feature_sources.forEach(scfs => {
      feature_sources = [...feature_sources,...this.get_sub_feature_sources(scfs)];
    });
    return feature_sources;
  }

  cleanFormula = (formula: string) => {
    while (formula.includes("{table=")) {
      let id = formula.substring(formula.indexOf("{table=") + 7);
      id = id.substring(0, id.indexOf("}"));
      const table_finder = (this.smart_hash.table as Table[]).filter(o => o.for_id === id);
      if (table_finder.length > 0) {
        const table = table_finder[0];
        formula = formula.replace(`{table=${id}}`, table.name);
      } else {
        break;
      }
    }
    while (formula.includes("{game_class=")) {
      let id = formula.substring(formula.indexOf("{game_class=") + 12);
      id = id.substring(0, id.indexOf("}"));
      const class_finder = (this.smart_hash.game_class as GameClass[]).filter(o => o._id === id);
      if (class_finder.length === 1) {
        const gc = class_finder[0];
        formula = formula.replace(`{game_class=${id}}`, gc.name);
      } else {
        break;
      }
    }
    while (formula.includes("{resource=")) {
      let id = formula.substring(formula.indexOf("{resource=") + 10);
      id = id.substring(0, id.indexOf("}"));

      const resource_finder = (this.smart_hash.resource as Resource[]).filter(o => o._id === id);
      if (resource_finder.length === 1) {
        const resource = resource_finder[0];
        formula = formula.replace(`{resource=${id}}`, resource.name);
      } else {
        break;
      }
    }
    return formula;
  }

  loadFeatureBases = async (fbs: FeatureBase[]) => {
    for (const fb of fbs) {
      await this.loadFeatureBase(fb);
    }
  }

  loadFeatureBase = async (fb: FeatureBase) => {
    await this.loadFeatures(fb.features);
  }

  loadFeatures = async (features: Feature[]) => {
    for (const f of features) {
      await this.loadFF(f);
    }
  }

  loadFF = async (f: Feature) => {
    let ff: FloatingFeature | null = null;
    if (f.feature_type === "Creature Ability") {
      if (!this.smart_hash.creature_feature) {
        await this.getObjects({ data_type: "creature_feature" });
      }
      ff = this.get_creature_feature(f.floating_feature_id);
    } else {
      ff = this.get_floating_feature(f.floating_feature_id);
    }
    if (!ff) { 
      // This should be redundant and never get hit.
      if (!this.smart_hash.creature_feature) {
        await this.getObjects({ data_type: "creature_feature" });
      }
      ff = this.get_creature_feature(f.floating_feature_id);
    }
    if (ff) {
      await this.loadObject(ff);
      if (ff instanceof SummonUpgradeFeature) {
        for (const f2 of ff.features) {
          await this.loadFF(f2);
        }
      }
      if (f.floating_feature) {
        f.floating_feature.copy(ff);
      } else {
        f.floating_feature = ff;
      }
    }
  }
  
  loadObject = async (obj: ModelBase) => {
    // if (obj instanceof SpellcastingFeature) { // && obj.name === "Natural Weapons Upgrade" 
    //   debugger;
    // }
    if (!obj.loaded && obj._id !== "") {
      const sbs = [...obj.source_books];
      const sbs_old = [...obj.source_books_old];
      const from_db = await this.getObjectFromAPI<ModelBase>({ data_type: obj.data_type, id: obj._id });
      if (from_db instanceof SourceBook) {
        // // Get Sections
        // const filter = { parent_type: "Source Book", parent_id: from_db._id };
        // const sections = await this.getObjects<SBSection>({ 
        //   data_type: "sb_section", filter
        // });
        // for (const section of sections) {
        //   await this.loadObject(section);
        // }
        // if (obj.name.includes("5.1")) {
        //   debugger;
        // }
        // // if (sections.length === 0 && from_db.sections.length > 0) {
        // //   // debugger;
        // //   const insertOldSection = async (section: SBSection) => {
        // //     const res = await this.upsertObject("sb_section", section);
            
        // //     if (res) {
        // //       section._id = res.body.id;
        // //       for (let i = 0; i < section.subsections.length; ++i) {
        // //         const subsection = section.subsections[i];
        // //         subsection.parent_id = section._id;
        // //         subsection.parent_type = "SBSection";
        // //         subsection.position = i;
        // //         await insertOldSection(subsection);
        // //       }
        // //     }
        // //   }
        // //   for (let i = 0; i < from_db.sections.length; ++i) {
        // //     const subsection = from_db.sections[i];
        // //     subsection.parent_id = from_db._id;
        // //     subsection.parent_type = "Source Book";
        // //     subsection.position = i;
        // //     await insertOldSection(subsection);
        // //   }
        // // } else {
        // from_db.sections = sections;
        // // }
        // // obj.copy(from_db);
      } else if (from_db instanceof SBSection) {
        debugger;
        // // Get Sections
        // const sections = await this.getObjects<SBSection>({ 
        //   data_type: "sb_section", filter: { parent_type: "SBSection", parent_id: from_db._id 
        // }});
        // for (const section of sections) {
        //   await this.loadObject(section);
        // }
        // from_db.subsections = sections;
        // // obj.copy(from_db);
      } else if (from_db instanceof StandardFeatures ||
        from_db instanceof FeatureSource) {
        // if (from_db instanceof Subclass && from_db.name.includes("Medium")) {
        //   debugger;
        // }
        await this.loadFeatureBases(from_db.feature_bases);
        if (obj instanceof FeatureSource) {
          await this.getTableAsync(obj);
        }
      } else if (from_db instanceof Creature) {
        await this.loadFeatureBases(from_db.fbs);
      } else if (from_db instanceof CreatureGift) {
        await this.loadFeatureBases(from_db.fbs);
      } else if (from_db instanceof OptionalFeatureBase) {
        await this.loadFeatures(from_db.features);
      } else if (from_db instanceof ModelBase) {
        if (from_db instanceof Ability) {
          if (from_db.counts_as_weapon !== "" && from_db.counts_as_weapon !== "Use Properties") {
            if (!this.smart_hash.item) {
              await this.getObjects({ data_type: "item" });
            }
            const weapon = this.smart_hash.item.find(o => o._id === from_db.counts_as_weapon);
            if (weapon) {
              await this.loadObject(weapon);
            }
          }
        }
        if (from_db instanceof Ability 
          || from_db instanceof ExtraEffectsFeature 
          || from_db instanceof ExtraEffectsOfTypeFeature
        ) {
          const summon_effects = from_db.effects.filter(o => o.type === "Summon");
          for (const effect of summon_effects) {
            // if (from_db.name.includes("Carrion Companion")) {
            //   debugger;
            // }
            for (const potence of effect.potences) {
              for (const opt of potence.summon_options) {
                if (opt.custom_stat_block) {
                  const stat_block = opt.custom_stat_block;
                  await this.loadFeatureBases(stat_block.fbs);
                }
              }
            }
          }
        }
      }
      if (from_db) {
        obj.copy(from_db);
      }
      obj.source_books = sbs;
      obj.source_books_old = sbs_old;
      // if (obj instanceof CreatureGift) {
      //   debugger;
      // }
      obj.loaded = true;
    }
  };

  get tables() {
    return this.smart_hash.table as Table[];
  }

  getTableAsync = async (fs: FeatureSource) => { // }, edition_id: string) {
    if (fs instanceof DandDFeatureSource && fs.table.entries.length > 0) {
      return fs.table;
    } else {
      if (!this.tables || this.tables.length === 0) {
        await this.getObjects({ data_type: "table" });
      }
      let table = this.tables.find(o => 
        o.type === fs.data_type && o.for_id === fs._id);
      if (!table) {
        if (!fs.isDominant()) {
          // Look for one from the dominant one
          const dominant = fs.getDominant(); // fs.variant_group.find(o => o.isDominant());
          if (dominant) {
            table = this.tables.find(o => 
              o.type === fs.data_type && o.for_id === dominant._id);
          }
        }
        if (!table) {
          // We still don't have a table. Look for one from the original.
          const original = fs.variant_group.find(o => o._id === o.variant_group_id);
          if (original) {
            table = this.tables.find(o => 
              o.type === fs.data_type && o.for_id === original._id);
          }
        }
      }
      if (table) {
        await this.loadObject(table);
      }
      return table;
    }
  }

  getSpellcastingFeatureForFS(fs: FeatureSource) {
    const fb = fs.feature_bases.find(o => o.features.find(o2 => o2.feature_type === "Spellcasting"));
    if (fb) {
      const f = fb.features.find(o => o.feature_type === "Spellcasting");
      if (f && f.floating_feature instanceof SpellcastingFeature) {
        return { level: fb.level, spellcasting_feature: f.floating_feature };
      }
    }
    return { level: 0, spellcasting_feature: undefined };
  }

  // getTable = (fs: FeatureSource) => { // }, edition_id: string) {
  //   if (fs instanceof DandDFeatureSource && fs.table.entries.length > 0) {
  //     return fs.table;
  //   } else {
  //     if (!this.tables || this.tables.length === 0) {
  //       return undefined;
  //     }
  //     let table = this.tables.find(o => 
  //       o.type === fs.data_type && o.for_id === fs._id);
  //     if (!table) {
  //       if (!fs.is_true_dominant) {
  //         // Look for one from the dominant one
  //         const dominant = fs.variant_group.find(o => o.isDominant());
  //         if (dominant) {
  //           table = this.tables.find(o => 
  //             o.type === fs.data_type && o.for_id === dominant._id);
  //         }
  //       }
  //       if (!table) {
  //         // We still don't have a table. Look for one from the original.
  //         const original = fs.variant_group.find(o => o._id === o.variant_group_id);
  //         if (original) {
  //           table = this.tables.find(o => 
  //             o.type === fs.data_type && o.for_id === original._id);
  //         }
  //       }
  //     }
  //     return table;
  //   }
  // }

  // Connect Functions
  connectObject = async <T extends ModelBase>(obj: ModelBase): Promise<T> => {
    // if (!obj.loaded) {
    //   console.log("I think this is redundant now that I have loadObject.");
    //   if (obj instanceof FeatureSource) {
    //     const feature_source = obj as FeatureSource;
    //     for (const fb of feature_source.feature_bases) {
    //       const f_finder = fb.features.filter(o => o.floating_feature_id !== "");
    //       for (const f of f_finder) {
    //         f.floating_feature = this.get_floating_feature(f.floating_feature_id);
    //         f.floating_feature_id = f.floating_feature ? f.floating_feature._id : f.floating_feature_id;
    //       }
    //     }
    //   } else if (obj instanceof StandardFeatures) {
    //     // debugger;
    //     // if (this.smart_hash.floating_feature && this.smart_hash.floating_feature.length > 0) {
    //     const feature_source = obj;
    //     for (const fb of feature_source.feature_bases) {
    //       const f_finder = fb.features.filter(o => o.floating_feature_id !== "");
    //       for (const f of f_finder) {
    //         f.floating_feature = this.get_floating_feature(f.floating_feature_id);
    //         f.floating_feature_id = f.floating_feature ? f.floating_feature._id : f.floating_feature_id;
    //       }
    //     }
    //     // }
    //   } else if (obj instanceof Creature) {
    //     // if (this.smart_hash.floating_feature && this.smart_hash.floating_feature.length > 0) {
    //     const creature = obj as Creature;
        
    //     const fb_finder = creature.fbs.filter(o => o.features.filter(o2 => o2.floating_feature_id !== "").length > 0);
    //     for (const fb of fb_finder) {
    //       for (const f of fb.features) {
    //         f.floating_feature = this.get_floating_feature(f.floating_feature_id);
    //         f.floating_feature_id = f.floating_feature ? f.floating_feature._id : f.floating_feature_id;
    //       }
    //       if (fb.features.length > 0 && fb.features[0].floating_feature) {
    //         fb.name = fb.features[0].floating_feature.name;
    //         fb.description = fb.features[0].floating_feature.description;
    //       }
    //     }
    //   } else if (obj instanceof CreatureGift) {
    //     // if (this.smart_hash.floating_feature && this.smart_hash.floating_feature.length > 0) {
    //     const creature_gift = obj as CreatureGift;
        
    //     const fb_finder = creature_gift.fbs.filter(o => o.features.filter(o2 => o2.floating_feature_id !== "").length > 0);
    //     for (const fb of fb_finder) {
    //       for (const f of fb.features) {
    //         f.floating_feature = this.get_floating_feature(f.floating_feature_id);
    //         f.floating_feature_id = f.floating_feature ? f.floating_feature._id : f.floating_feature_id;
    //       }
    //       if (fb.features.length > 0 && fb.features[0].floating_feature) {
    //         fb.name = fb.features[0].floating_feature.name;
    //         fb.description = fb.features[0].floating_feature.description;
    //       }
    //     }
    //   } else if (obj instanceof Spell) {
    //     // if (this.smart_hash.floating_feature && this.smart_hash.floating_feature.length > 0) {
    //     const spell = obj as Spell;
    //     // if (spell.name === "Summon Celestial") {
    //     //   console.log(spell);
    //     // }
    //     let effect_finder = spell.effects.filter(o => o.type === "Summon");
    //     for (let j = 0; j < effect_finder.length; ++j) {
    //       const effect = effect_finder[j];
    //       for (let k = 0; k < effect.potences.length; ++k) {
    //         const potence = effect.potences[k];
    //         const summon_finder = potence.summon_options.filter(o => o.summon_type === "Custom");
    //         for (let m = 0; m < summon_finder.length; ++m) {
    //           const creature = summon_finder[m].custom_stat_block;
    //           if (creature) {
    //             let f_finder = creature.actions.filter(o => o.floating_feature_id !== "");
    //             for (let i = 0; i < f_finder.length; ++i) {
    //               const f = f_finder[i];
    //               f.floating_feature = this.get_floating_feature(f.floating_feature_id);
    //               f.floating_feature_id = f.floating_feature ? f.floating_feature._id : f.floating_feature_id;
    //             }
    //             f_finder = creature.legendary_actions.filter(o => o.floating_feature_id !== "");
    //             for (let i = 0; i < f_finder.length; ++i) {
    //               const f = f_finder[i];
    //               f.floating_feature = this.get_floating_feature(f.floating_feature_id);
    //               f.floating_feature_id = f.floating_feature ? f.floating_feature._id : f.floating_feature_id;
    //             }
    //             f_finder = creature.special_abilities.filter(o => o.floating_feature_id !== "");
    //             for (let i = 0; i < f_finder.length; ++i) {
    //               const f = f_finder[i];
    //               f.floating_feature = this.get_floating_feature(f.floating_feature_id);
    //               f.floating_feature_id = f.floating_feature ? f.floating_feature._id : f.floating_feature_id;
    //             }
    //           }
    //         }
    //       }
    //     }
    //     effect_finder = spell.effects.filter(o => o.type === "Self Condition");
    //     if (effect_finder.length > 0) {
    //       if (!this.smart_hash.condition) {
    //         await this.getObjects({ data_type: "condition" });
    //       }
    //     }
    //     for (let j = 0; j < effect_finder.length; ++j) {
    //       const effect = effect_finder[j];
    //       for (let k = 0; k < effect.potences.length; ++k) {
    //         const potence = effect.potences[k];
    //         const conditions = this.smart_hash.condition.filter(o => potence.conditions_applied.includes(o._id)) as Condition[];
    //         potence.the_conditions = conditions;
    //       }
    //     }
    //     // }
    //   } else if (obj instanceof OptionalFeatureBase) {
    //     // if (this.smart_hash.floating_feature && this.smart_hash.floating_feature.length > 0) {
    //     const ofb = obj as OptionalFeatureBase;
    //     const f_finder = ofb.features.filter(o => o.floating_feature_id !== "");
    //     for (let i = 0; i < f_finder.length; ++i) {
    //       const f = f_finder[i];
    //       f.floating_feature = this.get_floating_feature(f.floating_feature_id);
    //       f.floating_feature_id = f.floating_feature ? f.floating_feature._id : f.floating_feature_id;
    //     }
    //     // }
    //   } else {
    //     // console.log("Not implemented");
    //     // console.log(obj);
    //   }
    // }
    await this.loadObject(obj);
    return obj as T;
  };

  createBaseItem = (obj: any): Item | null => {
    let base_item: Item | null = null;
    switch (obj.item_type) {
      case "Armor":
        base_item = new Armor(obj);
      break;
      case "Weapon":
        base_item = new Weapon(obj);
      break;
      case "Ammunition":
        base_item = new Ammunition(obj);
      break;
      case "Bag":
        base_item = new Container(obj);
      break;
      case "Container":
        base_item = new Container(obj);
      break;
      case "Deck":
        base_item = new DeckItem(obj);
      break;
      case "Gem":
        base_item = new GemItem(obj);
      break;
      default:
        base_item = new BasicItem(obj);
      break;
    }
    if (base_item) {
      switch (base_item.item_type) {
        case "Armor":
          const armor = base_item as Armor;
          if (armor.armor_type_name === "") {
            const obj_finder = ARMOR_TYPES.find(o => o._id === armor.armor_type_id);
            if (obj_finder) {
              armor.armor_type = obj_finder;
            }
          }
        break;
        case "Weapon":
        break;
        default:
        break;
      }
    }
    return base_item;
  }

  getMIKsForItem(base_item: Item): MagicItemKeyword[] {
    try {
      const all_miks = (this.smart_hash.magic_item_keyword as MagicItemKeyword[]);
      let miks = all_miks.filter(o => 
        o.uses_specific &&
        o.base_item_ids.includes(base_item._id) && 
        o.min_rarity > -1
      );
      if (base_item.item_type === "Armor" && base_item instanceof Armor) {
        miks = [...miks,...all_miks.filter(o => 
          !o.uses_specific &&
          o.base_item_types.length === 1 &&
          o.base_item_types.includes("Armor") && o.min_rarity > -1 && 
          (o.base_item_details.length === 0 || 
            o.base_item_details.includes(base_item.armor_type_id)) &&
          !o.exclusions.includes(base_item.name))
        ];
      } else if (base_item.item_type === "Weapon" && base_item instanceof Weapon) {
        miks = [...miks,...all_miks.filter(o => 
          !o.uses_specific &&
          o.base_item_types.length === 1 &&
          o.base_item_types.includes("Weapon") && o.min_rarity > -1 && 
          (!o.uses_specific && this.fitWeaponLimit(base_item, o.weapon_limit)) &&
            // (o.base_item_details.length === 0 || 
            // common_util.any_overlaps(o.base_item_details, base_item.weapon_keyword_ids))) &&
          !o.exclusions.includes(base_item.name))
        ];
      } else {
        miks = [...miks,...all_miks.filter(o => 
          !o.uses_specific &&
          o.min_rarity > -1 && 
          o.base_item_types.includes(base_item.item_type))
        ];
      }
      return miks;
    } catch (ex) {
      console.log(ex);
      console.error(base_item);
      debugger;
      return [];
    }
  }

  doesMIKFitItem(mik: MagicItemKeyword, base_item: Item): boolean {
    if (mik.uses_specific) {
      return mik.base_item_ids.includes(base_item._id);
    } else {
      if (base_item.item_type === "Armor" && base_item instanceof Armor) {
        return mik.base_item_types.length === 1 &&
          mik.base_item_types.includes("Armor") && 
          (mik.base_item_details.length === 0 || 
            mik.base_item_details.includes(base_item.armor_type_id)) &&
          !mik.exclusions.includes(base_item.name);
      } else if (base_item.item_type === "Weapon" && base_item instanceof Weapon) {
        // if (base_item.name.includes("Club")) {
        //   debugger;
        // }
        return mik.base_item_types.length === 1 &&
          mik.base_item_types.includes("Weapon") && 
          (!mik.uses_specific && this.fitWeaponLimit(base_item, mik.weapon_limit)) &&
            // (mik.base_item_details.length === 0 || 
            // common_util.any_overlaps(mik.base_item_details, base_item.weapon_keyword_ids))) &&
          !mik.exclusions.includes(base_item.name);
      } else {
        return mik.base_item_types.includes(base_item.item_type);
      }
    }
  }

  getMagicItemLimits(item: Item | null, magic_item_keywords: MagicItemKeyword[]) {
    let limits: CharacterLimitation[] = [];
    if (item && item.attunement) {
      if (item.limitations.length > 0) {
        limits = [...limits,...item.limitations];
      }
    }
    magic_item_keywords.filter(o => o.attunement).forEach(keyword => {
      if (keyword && keyword.limitations.length > 0) {
        limits = [...limits,...keyword.limitations];
      }
    });
    return limits;
  }

  getCharLimitsDesc = async (limits: CharacterLimitation[]) => {
    let desc = "";
    // For now I'm only going to have it support single limits. 
    // I'll put a debugger and console.error for if there are more 
    // and then when I see it happen I'll figure out the ideal way to handle it.
    if (limits.length === 1) {
      const limit = limits[0];
      switch (limit.type) {
        case "alignment":
          desc = `a creature of ${common_util.standardJoin(limit.ids)} alignment`;
        break;
        case "at_least_1_language":
          desc = `a creature that speaks at least 1 Language`;
        break;
        case "at_least_1_weapon":
          desc = `a Creature that is proficient with at least 1 Weapon`;
        break;
        case "class_group":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "class_or_class_group":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "dragonmark":
          if (limit.exclusive) {
            desc = "a Character without a Dragonmark";
          } else {
            desc = "a Character with a Dragonmark";
          }
        break;
        case "eldritch_invocation":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "feat":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "game_class":
          if (true) {
            const all_classes = await this.getObjects<GameClass>({ data_type: "game_class" });
            const filtered = all_classes.filter(o => limit.ids.includes(o._id));
            desc = `a ${common_util.standardJoin(filtered.map(o => o.name).sort((a, b) => a.localeCompare(b)))}`;
          }
        break;
        case "gift":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "level":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = `a level ${limit.ids[0]} Character`;
        break;
        case "min_ability_score":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "pact_boon":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "proficiency":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "race":
          if (true) {
            const all_races = await this.getObjects<Race>({ data_type: "race" });
            const filtered = all_races.filter(o => limit.ids.includes(o._id));
            desc = `a ${common_util.standardJoin(filtered.map(o => o.name).sort((a, b) => a.localeCompare(b)))}`;
          }
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "spell":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "spellcaster":
          desc = "a spellcaster";
        break;
        case "spells_of_schools":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "subclass":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "subrace":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "tags":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        case "wild_talent":
          console.error(`getCharLimitsDesc Not Implemented for ${limit.type}`);
          desc = "";
        break;
        
        case "feature_of_type":
          desc = `a creature with a ${limit.ids.join(", ")} feature`;
        break;
      }
    } else {
      debugger;
      console.error("getCharLimitsDesc Not Implemented for multiple limits");
      // for (const limit of limits) {

      // }
    }
    return desc;
  }

  fitWeaponLimit(weapon: Weapon | Attack, limit: WeaponLimitation, attack_type = "", log_it = false): boolean {
    // log_it = weapon instanceof Attack;
    if (log_it) {
      // if (weapon instanceof Weapon && weapon.name.includes("Club")) {
      //   debugger;
      // }
      console.log(limit);
      console.log(weapon);
    }
    if (limit.mik_ids.length > 0) {
      return false; // Only CharacterWeapons can have this one true
    }
    if (limit.item_ids.length > 0) {
      if (weapon instanceof Weapon && limit.item_ids.includes(weapon._id)) {
        return true;
      } else if (limit.isEmpty(["item_ids"])) {
        return false;
      }
    }
    if (limit.item_ids.length > 0) {
      return weapon instanceof Weapon && limit.item_ids.includes(weapon._id);
    }
    let good = true;
    if (limit.weapon_keyword_ids.length > 0) {
      if (weapon instanceof Weapon) {
        good = common_util.anyOverlaps(limit.weapon_keyword_ids, weapon.weapon_keyword_ids);
      } else {
        good = limit.weapon_keyword_ids.includes("Any") || limit.weapon_keyword_ids.includes("Unarmed Strike");
      }
    }
    if (good && limit.excluded_keyword_ids.length > 0) {
      if (weapon instanceof Weapon) {
        good = !common_util.anyOverlaps(limit.excluded_keyword_ids, weapon.weapon_keyword_ids);
      } else {
        good = !limit.excluded_keyword_ids.includes("Any") && !limit.excluded_keyword_ids.includes("Unarmed Strike");
      }
    }
    if (log_it) {
      console.log(good);
      debugger;
    }
    if (good && (limit.attack_types.length > 0 || limit.damage_types.length > 0)) {
      const the_attacks = weapon instanceof Weapon ? weapon.attacks : { [weapon.attack_type]: { rolls: weapon.attack_rolls, uses_proficiencty_id: ""} };
      const damage_types: string[] = [];
      let damages: RollPlus[] = [];
      if (log_it) {
        console.log(the_attacks);
      }
      for (const key of Object.keys(the_attacks)) {
        const attack = the_attacks[key];
        damages = [...damages, ...attack.rolls];
      }
      for (const damage of damages) {
        damage_types.push(damage.type);
      }
      if (limit.attack_types.length > 0) {
        if (attack_type !== "") {
          good = limit.attack_types.includes(attack_type as AttackType);
        } else {
          const attack_types = weapon instanceof Weapon ? Object.keys(weapon.attacks) : [weapon.attack_type];
          for (const at of attack_types) {
            const new_damages: any = the_attacks[at];
            if (new_damages.rolls) {
              damages = [...new_damages.rolls];
            } else {
              damages = [...new_damages];
            }
          }
          good = common_util.anyOverlaps(limit.attack_types, attack_types);
        }
      }
      if (good) {
        if (limit.damage_types.length > 0) {
          good = common_util.anyOverlaps(limit.damage_types, damage_types);
        }
      }
    }
    return good;
  }

  fitAbilityLimit(ability: Ability | CreatureAbility, limit: AbilityLimitation, log_it = false): boolean {
    let good = true;
    // log_it = ability.name.includes("Blast");
    if (log_it) {
      debugger;
      console.log(limit);
      console.log(ability);
    }
    if (limit.name.length > 0) {
      good = ability.name.includes(limit.name);
    } 
    if (limit.ability_tag.length > 0) {
      good = ability.tags.includes(limit.ability_tag);
    }
    if (log_it) {
      console.log(good);
    }
    if (log_it) {
      console.log(good);
    }
    if (good && limit.attack_types.length > 0) {
      good = ability.effects.find(o => limit.attack_types.includes(o.attack_type)) !== undefined;
    }
    if (good && limit.effect_types.length > 0) {
      good = ability.effects.find(o => limit.effect_types.includes(o.type)) !== undefined;
    }
    if (good && limit.damage_types.length > 0) {
      good = ability.effects.find(o => common_util.anyOverlaps(limit.damage_types, o.damage_types)) !== undefined;
    }
    if (good && (limit.min_duration !== null || limit.max_duration !== null)) {
      const numeric_duration = (DURATION_NUMBER_MAP as any)[ability.duration];
      if (limit.min_duration !== null) {
        if (typeof(numeric_duration) === "number") {
          good = numeric_duration === -1 || numeric_duration > limit.min_duration;
        } else {
          return false;
        }
      }
      if (good && limit.max_duration !== null) {
        if (typeof(numeric_duration) === "number") {
          good = numeric_duration === -1 || numeric_duration < limit.max_duration;
        } else {
          return false;
        }
      }
    }
    if (good && (limit.min_range !== null || limit.max_range !== null || limit.touch !== "Ignore" || limit.self !== "Ignore")) {
      const range = ability.range;
      if (range === "Self") {
        if (limit.self === "Ignore") {
          if (limit.touch === "Ignore") {
            if (limit.min_range !== null) {
              good = limit.min_range <= 0;
            } // else it has a max_range but not a min_range, so allow it
          } else {
            good = limit.touch === "Yes"; // Self works with Touch
          }
        } else {
          good = limit.self === "Yes";
        }
      } else if (range === "Touch") {
        if (limit.touch === "Ignore") {
          if (limit.min_range !== null) {
            good = limit.min_range <= 0;
          } else if (limit.max_range === null) {
            good = limit.self !== "Yes";
          }
        } else {
          good = limit.touch === "Yes";
        }
      } else {
        const range_num = +range.split(" ")[0];
        if (limit.min_range !== null || limit.max_range !== null) {
          if (limit.min_range !== null) {
            good = range_num >= limit.min_range;
          }
          if (good && limit.max_range !== null) {
            good = range_num <= limit.max_range;
          }
        } else {
          const okay = ["Ignore","No"];
          good = okay.includes(limit.self) && okay.includes(limit.touch);
        }
      }
    }
    if (good && limit.casting_times.length > 0) {
      good = limit.casting_times.includes(ability.casting_time);
      if (good && limit.casting_times.includes("Special") && limit.special_casting_time.length > 0) {
        good = limit.special_casting_time === ability.special_casting_time;
      }
    }
    if (good && limit.uses_resource.length > 0) {
      good = limit.uses_resource.includes(ability.consumed_resource.resource_consumed);
    }
    // if (good && limit.ritual !== "Ignore") {
    //   good = (limit.ritual === "Yes") === ability.ritual; 
    // }
    if (good && limit.concentration !== "Ignore") {
      good = (limit.concentration === "Yes") === ability.concentration; 
    }
    // if (good && limit.target_types.length > 0) {
    //   good = common_util.any_overlaps(limit.target_types, ability.target_types);
    // }
    // if (good && limit.area_types.length > 0) {
    //   good = common_util.any_overlaps(limit.area_types, ability.area_types);
    // }
    // if (good && limit.twinnable_1dd_ua7 !== "Ignore") {
    //   good = (limit.twinnable_1dd_ua7 === "Yes") === ability.twinnable_1dd_ua7; 
    // }
    // if (good && limit.disease !== "Ignore") {
    //   good = (limit.disease === "Yes") === ability.disease; 
    // }
    // if (good && limit.charm !== "Ignore") {
    //   good = (limit.charm === "Yes") === ability.charm; 
    // }
    // if (good && limit.contaminated !== "Ignore") {
    //   good = (limit.contaminated === "Yes") === ability.contaminated; 
    // }
    if (good && limit.sight !== "Ignore" && ability instanceof Ability) {
      good = (limit.sight === "Yes") === ability.sight; 
    }
    if (good && limit.verbal !== "Ignore") {
      good = (limit.verbal === "Yes") === ability.components.includes("V"); 
    }
    if (good && limit.somatic !== "Ignore") {
      good = (limit.somatic === "Yes") === ability.components.includes("S"); 
    }
    if (good && limit.material !== "Ignore") {
      good = (limit.material === "Yes") === ability.components.includes("M"); 
    }
    return good;
  }

  fitSpellLimits({ spell, limits, log_it = false, ignore_variance = false }: 
    { spell: Spell, limits: SpellLimitation[], log_it?: boolean, ignore_variance?: boolean }): boolean {
    return limits.some(limit => this.fitSpellLimit({ spell, limit, log_it, ignore_variance }));
  }

  fitSpellList({ 
    spell, 
    spell_list, 
    char_options,
    ignore_variance = false,
    sb_id, 
    log_it = false 
  }: { 
    spell: Spell, 
    spell_list: SpellList, 
    char_options?: CharacterOptions,
    ignore_variance?: boolean,
    sb_id?: string, 
    log_it?: boolean 
  }): boolean {
    if (sb_id) {
      const limit = spell_list.limit_hash[sb_id];
      if (limit) {
        return this.fitSpellLimit({spell, limit, ignore_variance, log_it});
      }
      return false;
    } else if (char_options) {
      // It can check non-major limits, but only one major limit.
      const major_spell_list_sb_id = char_options.default_major_spell_list_sb.id;
      const sb_ids = Object.keys(spell_list.limit_hash);
      for (const sb_id of sb_ids) {
        if (sb_id === major_spell_list_sb_id || !spell_list.major_limits.includes(sb_id)) {
          const limit = spell_list.limit_hash[sb_id];
          if (this.fitSpellLimit({spell, limit, ignore_variance})) {
            return true;
          }
        }
      }
      return false;
    }
    return spell_list.limits.some(limit => this.fitSpellLimit({spell, limit, ignore_variance, log_it}));
  }

  fitSpellLimit({
    spell, 
    limit, 
    log_it = false,
    ignore_variance = false
  }: {
    spell: Spell, 
    limit: SpellLimitation, 
    log_it?: boolean,
    ignore_variance?: boolean
  }): boolean {
    // if (ignore_variance && spell.name.includes("Dancing Lights")) {
    //   console.log(spell);
    // }
    if (log_it) {
      console.log(limit);
      console.log(spell);
      console.log(limit.list_ids);
      console.log(spell.spell_list_ids);
      console.log(common_util.anyOverlaps(limit.list_ids, spell.spell_list_ids));
    }
    if (limit.spell_ids.length > 0) {
      if (limit.spell_ids.includes(spell._id)) {
        if (log_it) {
          console.log("True Found");
          console.log(limit.spell_ids);
          console.log(spell._id);
        }
        return true; 
      } else if (!ignore_variance && common_util.anyOverlaps(limit.spell_ids, spell.variant_ids)) {
        if (log_it) {
          console.log("True Found");
          console.log(limit.spell_ids);
          console.log(spell.variant_ids);
        }
        return true; 
      } else if (limit.isEmpty(["spell_ids"])) {
        return false;
      }
    } 
    if (limit.excluded_spell_ids.includes(spell._id)) {
      return false;
    } else if (common_util.anyOverlaps(limit.excluded_spell_ids, spell.variant_ids)) {
      return false;
    }
    if (log_it) {
      console.log("After Traditional Spell Lists");
      console.log(limit);
      console.log(spell);
    }
    let good = true;
    if (limit.list_ids.length > 0) {
      // if (log_it) {
      //   debugger;
      // }
      good = common_util.anyOverlaps(limit.list_ids, spell.spell_list_ids);
      if (!good) {
        // Check for any limits that are more than just lists of spell ids
        let spell_lists = this.smart_hash.spell_list.filter(o => limit.list_ids.includes(o._id)) as SpellList[];
        spell_lists = spell_lists.filter(o => !o.isEmpty(["spell_ids"]));
        for (const spell_list of spell_lists) {
          if (this.fitSpellList({ spell, spell_list, log_it })) {
            good = true;
            break;
          }
        }
      }
    }
    if (good && limit.schools.length > 0) {
      good = limit.schools.includes(spell.school);
    } 
    if (good && limit.levels.length > 0) {
      if (limit.levels.includes(-1)) {
        // Don't limit anything here.  
        // It gets limited in the char_util version.
      } else {
        good = limit.levels.includes(spell.level);
      }
    }
    if (log_it) {
      console.log(good);
    }
    if (good && limit.attack_types.length > 0) {
      good = spell.effects.find(o => limit.attack_types.includes(o.attack_type)) !== undefined;
    }
    if (good && limit.effect_types.length > 0) {
      good = spell.effects.find(o => limit.effect_types.includes(o.type)) !== undefined;
    }
    if (good && limit.damage_types.length > 0) {
      good = spell.effects.find(o => common_util.anyOverlaps(limit.damage_types, o.damage_types)) !== undefined;
    }
    if (good && (limit.min_duration !== null || limit.max_duration !== null)) {
      const numeric_duration = (DURATION_NUMBER_MAP as any)[spell.duration];
      if (limit.min_duration !== null) {
        if (typeof(numeric_duration) === "number") {
          good = numeric_duration === -1 || numeric_duration > limit.min_duration;
        } else {
          return false;
        }
      }
      if (good && limit.max_duration !== null) {
        if (typeof(numeric_duration) === "number") {
          good = numeric_duration === -1 || numeric_duration < limit.max_duration;
        } else {
          return false;
        }
      }
    }
    if (good && (limit.min_range !== null || limit.max_range !== null || limit.touch !== "Ignore" || limit.self !== "Ignore")) {
      const range = spell.range;
      if (range === "Self") {
        if (limit.self === "Ignore") {
          if (limit.touch === "Ignore") {
            if (limit.min_range !== null) {
              good = limit.min_range <= 0;
            } // else it has a max_range but not a min_range, so allow it
          } else {
            good = limit.touch === "Yes"; // Self works with Touch
          }
        } else {
          good = limit.self === "Yes";
        }
      } else if (range === "Touch") {
        if (limit.touch === "Ignore") {
          if (limit.min_range !== null) {
            good = limit.min_range <= 0;
          } else if (limit.max_range === null) {
            good = limit.self !== "Yes";
          }
        } else {
          good = limit.touch === "Yes";
        }
      } else {
        const range_num = +range.split(" ")[0];
        if (limit.min_range !== null || limit.max_range !== null) {
          if (limit.min_range !== null) {
            good = range_num >= limit.min_range;
          }
          if (good && limit.max_range !== null) {
            good = range_num <= limit.max_range;
          }
        } else {
          const okay = ["Ignore","No"];
          good = okay.includes(limit.self) && okay.includes(limit.touch);
        }
      }
    }
    if (good && limit.casting_times.length > 0) {
      good = limit.casting_times.includes(spell.casting_time);
      if (good && limit.casting_times.includes("Special") && limit.special_casting_time.length > 0) {
        good = limit.special_casting_time === spell.special_casting_time;
      }
    }
    if (good && limit.ritual !== "Ignore") {
      good = (limit.ritual === "Yes") === spell.ritual; 
    }
    if (good && limit.concentration !== "Ignore") {
      good = (limit.concentration === "Yes") === spell.concentration; 
    }
    if (good && limit.target_types.length > 0) {
      good = common_util.anyOverlaps(limit.target_types, spell.target_types);
    }
    if (good && limit.area_types.length > 0) {
      good = common_util.anyOverlaps(limit.area_types, spell.area_types);
    }
    if (good && limit.twinnable_1dd_ua7 !== "Ignore") {
      good = (limit.twinnable_1dd_ua7 === "Yes") === spell.twinnable_1dd_ua7; 
    }
    if (good && limit.disease !== "Ignore") {
      good = (limit.disease === "Yes") === spell.disease; 
    }
    if (good && limit.charm !== "Ignore") {
      good = (limit.charm === "Yes") === spell.charm; 
    }
    if (good && limit.contaminated !== "Ignore") {
      good = (limit.contaminated === "Yes") === spell.contaminated; 
    }
    if (good && limit.sight !== "Ignore") {
      good = (limit.sight === "Yes") === spell.sight; 
    }
    if (good && limit.verbal !== "Ignore") {
      good = (limit.verbal === "Yes") === spell.components.includes("V"); 
    }
    if (good && limit.somatic !== "Ignore") {
      good = (limit.somatic === "Yes") === spell.components.includes("S"); 
    }
    if (good && limit.material !== "Ignore") {
      good = (limit.material === "Yes") === spell.components.includes("M"); 
    }
    if (good && limit.m_cost_listed !== "Ignore") {
      good = (limit.m_cost_listed === "Yes") === spell.material_cost_listed; 
    }
    if (good && limit.m_consumed !== "Ignore") {
      good = (limit.m_consumed === "Yes") === spell.material_consumed; 
    }
    return good;
  }

  getSpellsForLimits({
    limits, 
  }: {
    limits: SpellLimitation[], 
  }): Spell[] {
    return this.spells.filter(spell => this.fitSpellLimits({ spell, limits }));
  }

  getSpellsForLimit({
    limit, 
    ignore_variance = false
  }: {
    limit: SpellLimitation, 
    ignore_variance?: boolean
  }): Spell[] {
    return this.spells.filter(spell => this.fitSpellLimit({ spell, limit, ignore_variance }));
  }

  fitItemLimit(item: PseudoItem | CharacterItem, limit: ItemLimitation, log_it = false): boolean {
    // log_it = item.name.includes("Holding"); // true;
    if (log_it) {
      console.log(limit);
      console.log(item);
    }
    if (limit.item_ids.length > 0) {
      return item.base_item !== null && item.magic_item_keywords.length === 0 && limit.item_ids.includes(item.base_item._id);
    } 
    let good = true;
    if (limit.item_types.length > 0) {
      good = limit.item_types.includes(item.item_type) !== limit.exclude_item_types;
    }
    if (good && !limit.exclude_item_types && limit.item_types.length === 1 && limit.item_types[0] === "Weapon") {
      if (limit.weapon_keyword_ids.length > 0){
        const weapon = item.base_item;
        if (weapon instanceof Weapon) {
          good = common_util.anyOverlaps(limit.weapon_keyword_ids, weapon.weapon_keyword_ids);
        } else {
          good = false; // Should never go here because we already checked the item_type
        }
      }
      if (good && limit.excluded_keyword_ids.length > 0){
        const weapon = item.base_item;
        if (weapon instanceof Weapon) {
          good = !common_util.anyOverlaps(limit.excluded_keyword_ids, weapon.weapon_keyword_ids);
        } else {
          good = false; // Should never go here because we already checked the item_type
        }
      }
    }
    if (good && limit.mik_ids.length > 0) {
      const mik_ids = item.magic_item_keyword_ids;
      good = common_util.anyOverlaps(limit.mik_ids, mik_ids);
    }
    if (good && limit.excluded_mik_ids.length > 0) {
      const mik_ids = item.magic_item_keyword_ids;
      good = common_util.anyOverlaps(limit.excluded_mik_ids, mik_ids);
    }
    if (good && limit.magical) {
      good = item.rarity > 0 || item.magic_item_keywords.length > 0;
    } 
    if (good && limit.min_rarity > 0) {
      good = item.rarity >= limit.min_rarity;
    } 
    if (good && limit.max_rarity < 5) {
      good = item.rarity <= limit.max_rarity;
    } 
    if (good && limit.attack_types.length > 0) {
      good = common_util.anyOverlaps(limit.attack_types, item.the_attack_types);
    }
    if (good && limit.damage_types.length > 0) {
      good = common_util.anyOverlaps(limit.damage_types, item.the_effect_types);
    }
    // if (!good) {
    //   console.log(item.name);
    // }
    return good;
  }

  fitCreatureLimit(creature: CreatureDetails, limit: CreatureLimitation, log_it = false): boolean {
    // log_it = item.name.includes("Holding"); // true;
    if (log_it) {
      console.log(limit);
      console.log(creature);
    }
    if (limit.creature_ids.length > 0) {
      return limit.creature_ids.includes(creature._id);
    } 
    let good = true;
    if (good && limit.creature_types.length > 0) {
      good = limit.creature_types.includes(creature.creature_type);
    } 
    if (good && limit.subtypes.length > 0) {
      good = common_util.anyOverlaps(limit.subtypes, creature.subtypes);
    } 
    if (good && limit.tags.length > 0) {
      good = common_util.anyOverlaps(limit.tags, creature.tags);
    }
    if (good && limit.swarm !== undefined) {
      good = limit.swarm === creature.swarm;
    }
    if (good && limit.age_categories.length > 0) {
      if (creature instanceof Creature && creature.name_table.length > 0) {
        const age_categories = creature.name_table.map(o => o.age_category).filter(o => o !== "");
        if (age_categories.length > 0) {
          good = common_util.anyOverlaps(limit.age_categories, age_categories);
        } else {
          good = limit.age_categories.includes(creature.age_category);
        }
      } else {
        good = limit.age_categories.includes(creature.age_category);
      }
    } 
    if (good && limit.size_nums.length > 0) {
      good = limit.size_nums.includes(creature.size_num);
    } 
    if (good && limit.min_challenge_rating > -1) {
      good = limit.min_challenge_rating <= creature.max_challenge_rating;
    } 
    if (good && limit.max_challenge_rating > -1) {
      good = limit.max_challenge_rating >= creature.min_challenge_rating;
    } 
    if (good && !limit.allow_flight) {
      good = !creature.flying;
    }
    if (good && !limit.allow_swimming) {
      good = !creature.swimming;
    }
    return good;
  }

  handleModelBases = async (all: any[], data_type: string, check_hash = false) => {
    const model_bases: ModelBase[] = [];
    for (let i = 0; i < all.length; ++i) {
      const obj = all[i];
      let model_base: ModelBase | null = null;
      switch(data_type) {
        case "background": 
          if (true) {
            const bg = new Background(obj); 
            bg.feats = (this.smart_hash.feat as Feat[]).filter(o => bg.feat_ids.includes(o._id));
            model_base = bg;
          }
        break;
        case "standard_features": 
          // debugger;
          model_base = new StandardFeatures(obj); 
        break;
        case "condition": model_base = new Condition(obj); break;
        case "eldritch_invocation": model_base = new EldritchInvocation(obj); break;
        case "feat": model_base = new Feat(obj); break;
        case "fighting_style": model_base = new FightingStyle(obj); break;
        case "game_class": model_base = new GameClass(obj); break;
        case "gift": model_base = new Gift(obj); break;
        case "bastion_facility": model_base = new BastionFacility(obj); break;
        case "transform": 
          const transform = obj.creature_potences ? new CreatureTransform(obj) : new Transform(obj); 
          for (const feature of transform.features) {
            feature.floating_feature = this.get_floating_feature(feature.floating_feature_id);
          }
          model_base = transform;
        break;
        // case "adaptation": 
        //   const adaptation = new Adaptation(obj); 
        //   for (const feature of adaptation.features) {
        //     feature.floating_feature = this.get_floating_feature(feature.floating_feature_id);
        //   }
        //   model_base = adaptation;
        // break;
        case "pact_boon": model_base = new PactBoon(obj); break;
        case "race": model_base = new Race(obj); break;
        case "special_feature": model_base = new SpecialFeature(obj); break;
        case "subclass": 
          const subclass = new Subclass(obj); 
          if (this.smart_hash.game_class) {
            subclass.game_class = (this.smart_hash.game_class as GameClass[]).find(o => o._id === subclass.game_class_id);
          }
          model_base = subclass;
        break;
        case "subrace": 
          if (true) {
            const subrace = new Subrace(obj); 
            subrace.races = (this.smart_hash.race as Race[]).filter(o => subrace.race_ids.includes(o._id));
            model_base = subrace;
          }
        break;
        case "base_item": case "item": 
          model_base = this.createBaseItem(obj);
        break;
        case "magic_item_keyword": 
          model_base = new MagicItemKeyword(obj);
          // if (true) {
          //   const mik = new MagicItemKeyword(obj); 
          //   if (mik.base_item_types.length === 0 && mik.base_item_ids.length > 0) {
          //     // debugger;
          //     const item_types = common_util.unique_array((this.smart_hash.item as Item[]).filter(o => 
          //       mik.base_item_ids.includes(o._id)).map(o => o.item_type)) as ItemType[];
          //     mik.base_item_types = item_types;
          //   }
          //   model_base = mik;
          // }
        break;
        case "weapon_mastery_property": model_base = new WeaponMasteryProperty(obj); break;
        // case "org_position": model_base = new OrgPosition(obj); break;
        case "creature": 
          const creature = new Creature(obj); 
          if (creature.spellcasting_table.entries.length > 0 && 
            creature.spellcasting_table.entries.some(o => o.spells.some(o2 => o2.spell_level === -1))) {
            // Fix the spell levels
            const spells = await this.getObjects<Spell>({ data_type: "spell" });
            for (const entry of creature.spellcasting_table.entries) {
              for (const c_spell of entry.spells) {
                if (c_spell.spell_level === -1) {
                  const spell = spells.find(o => o._id === c_spell.spell_id);
                  if (spell) {
                    c_spell.spell_level = spell.level;
                  }
                }
              }
            }
          }
          model_base = creature;
        break;
        case "creature_group": model_base = new CreatureGroup(obj); break;
        case "creature_gift": model_base = new CreatureGift(obj); break;
        case "deity": 
          model_base = new Deity(obj); 
        break;
        case "equipment_pack": model_base = new EquipmentPack(obj); break;
        case "reusable_string": model_base = new ReusableString(obj); break;
        case "loot_table": model_base = new LootTable(obj); break;
        case "organization": model_base = new Organization(obj); break;
        case "resource": model_base = new Resource(obj); break;
        case "rollable_table": model_base = new RollableTable(obj); break;
        case "spell": model_base = new Spell(obj); break;
        case "spell_list": 
          const sl = new SpellList(obj); 
          model_base = sl;
        break;
        case "spell_slot_type": model_base = new SpellSlotType(obj); break;
        case "table": model_base = new Table(obj); break;
        case "template_base": model_base = new TemplateBase(obj); break;
        case "tool": model_base = new Tool(obj); break;
        case "game_card": model_base = new GameCard(obj); break;
        case "game_deck": model_base = new GameDeck(obj); break;
        case "ticket": 
          const ticket = new Ticket(obj); 
          if (ticket.user_tickets.length === 0 && ticket.user_ticket_ids.length > 0) {
            const uts = await this.getPlayerObjects({ data_type: "user_ticket", filter: { "_id*ObjectIDs": ticket.user_ticket_ids } }) as UserTicket[];
            ticket.user_tickets = uts;
            for (const ut of uts) {
              ut.ticket = ticket;
            }
          }
          model_base = ticket;
        break;
        case "template": model_base = new TemplateBase(obj); break;
        case "sb_section": model_base = new SBSection(obj); break;
        default: 
          debugger;
        break;
      }
      if (model_base) {
        // if (model_base.name.includes("Artificer")) {
        //   debugger;
        // }
        // if (this.user && 
        //   this.user.admin && 
        //   !(model_base instanceof FeatureSource || model_base instanceof SourceBook) &&
        //   TRUST_JSON_FOR_DATA_TYPES.includes(model_base.data_type)) {
        //   model_base.loaded = true;
        // }
        model_bases.push(model_base);
      } else {
        console.log(obj);
      }
    }
    for (const variant of model_bases) {
      await this.handleVersion2(variant, model_bases, check_hash);
      // model_bases.push(variant);
    }
    if (data_type === "item") {
      const items = model_bases as Item[]
      for (const item of items) {
        // if (item.name.includes("Shortsword")) {
        //   debugger;
        // }
        const miks = this.getMIKsForItem(item);
        item.possible_mik_ids = miks.map(o => o._id);
        for (const mik of miks) {
          mik.possible_base_item_ids.push(item._id);
        }
      }
      const miks = this.smart_hash.magic_item_keyword as MagicItemKeyword[];
      const miks_that_need_this = miks.filter(o => o.base_item_types.length === 0 && o.base_item_ids.length > 0);
      for (const mik of miks_that_need_this) {
        // debugger;
        const item_types = common_util.unique_array(items.filter(o => 
          mik.base_item_ids.includes(o._id)).map(o => o.item_type));
        mik.base_item_types = item_types;
      }
    }
    for (const mb of model_bases.filter(o => o instanceof DandDFeatureSource)) {
      const fs = mb as DandDFeatureSource;
      for (const fb of fs.feature_bases) {
        for (const f of fb.features) {
          f.floating_feature = this.get_floating_feature(f.floating_feature_id);
        }
      }
      // THIS IS WHERE I CAN FIX LIMITATIONS IF THEY GET BROKEN
      // for (const limit of fs.limitations) {
      //   switch (limit.type) {
      //     // case "alignment": break;
      //     // case "at_least_1_language": break;
      //     // case "at_least_1_weapon": break;
      //     // case "class_group": break;
      //     // case "level": break;
      //     // case "min_ability_score": break;
      //     // case "spellcaster": break;
      //     // case "spells_of_schools": break;
      //     // case "tags": break;
      //     case "class_or_class_group": 
      //       if (true) {
      //         // Make sure that it's variant_group_ids instead of different _ids
      //         if (fs.data_type !== "game_class" && !this.smart_hash.game_class) {
      //           await this.getObjects({ data_type: "game_class" });
      //         }
      //         const objects = fs.data_type !== "game_class" ? this.smart_hash.game_class : all;
      //         const ids = [...limit.ids];
      //         for (const id of ids) {
      //           if (!CLASS_GROUPS.includes(id)) {
      //             let obj = objects.find(o => o.variant_group_id === id);
      //             if (!obj) {
      //               obj = objects.find(o => o._id === id);
      //               if (obj) {
      //                 limit.ids = limit.ids.filter(o => o !== id);
      //                 limit.ids.push(obj.variant_group_id);
      //               } else {
      //                 console.error("Unable to fix limit id.");
      //                 console.error(mb);
      //                 console.error(limit);
      //               }
      //             }
      //           }
      //         }
      //       }
      //     break;
      //     default: 
      //       if (true) {
      //         // Make sure that it's variant_group_ids instead of different _ids
      //         if (limit.type !== fs.data_type && !this.smart_hash[limit.type]) {
      //           await this.getObjects({ data_type: limit.type });
      //         }
      //         const objects = limit.type !== fs.data_type ? this.smart_hash[limit.type] : all;
      //         const ids = [...limit.ids];
      //         for (const id of ids) {
      //           let obj = objects.find(o => o.variant_group_id === id);
      //           if (!obj) {
      //             obj = objects.find(o => o._id === id);
      //             if (obj) {
      //               limit.ids = limit.ids.filter(o => o !== id);
      //               limit.ids.push(obj.variant_group_id);
      //             } else {
      //               console.error("Unable to fix limit id.");
      //               console.error(mb);
      //               console.error(limit);
      //             }
      //           }
      //         }
      //       }
      //     break;
      //     // case "eldritch_invocation": break;
      //     // case "game_class": break;
      //     // case "gift": break;
      //     // case "subclass": break;
      //     // case "subrace": break;
      //     // case "race": break;
      //     // case "pact_boon": break;
      //     // case "proficiency": break;
      //     // case "spell": break;
      //   }
      // }
    }
    for (const mb of model_bases.filter(o => o instanceof StandardFeatures)) {
      // debugger;
      const fs = mb as StandardFeatures;
      for (const fb of fs.feature_bases) {
        for (const f of fb.features) {
          f.floating_feature = this.get_floating_feature(f.floating_feature_id);
        }
      }
    }
    for (const mb of model_bases.filter(o => o instanceof OptionalFeatureBase)) {
      const ofb = mb as OptionalFeatureBase;
      for (const f of ofb.features) {
        f.floating_feature = this.get_floating_feature(f.floating_feature_id);
      }
    }
    for (const mb of model_bases.filter(o => o instanceof Creature)) {
      const c = mb as Creature;
      if (c.name === "Zombies") {
        debugger;
      }
      for (const fb of c.fbs) {
        for (const f of fb.features) {
          f.floating_feature = this.getCForFF(f.floating_feature_id);
        }
      }
    }
    for (const mb of model_bases.filter(o => o instanceof CreatureGift)) {
      const cg = mb as CreatureGift;
      // if (c.name === "Velociraptor") {
      //   debugger;
      // }
      for (const fb of cg.fbs) {
        for (const f of fb.features) {
          f.floating_feature = this.getCForFF(f.floating_feature_id);
        }
      }
    }
    if (data_type === "spell") {
      const spell_lists = (this.smart_hash.spell_list) as SpellList[];
      for (const spell of (model_bases as Spell[])) {
        spell.spell_list_ids = spell_lists.filter(o => this.fitSpellList({ spell, spell_list: o })).map(o => o._id);
      }
    }
    return model_bases;
  }

  newObjectOfType = (data_type: string): ModelBase => {
    switch(data_type) {
      default: return new ModelBase();
    }
  }

  createCharacter = async (obj: Character) => {
    const response = await this.postData({
      system: this.system,
      path: `/createObject`, 
      data: { data_type: "character", obj: obj.toDBObj() }
    });
    const res = await this.processResponse({ response, data_type: "character" });
    if (res.body.id) {
      obj._id = res.body.id;
      const pieces = CHARACTER_PIECES;
      for (let i = 0; i < pieces.length; ++i) {
        const piece = pieces[i];
        const dbObj = obj.toCharacterDBObj(piece);

        await this.postData({
          system: this.system,
          path: `/createCharacterPiece`, 
          data: { piece, obj: dbObj }
        });
      }
    }
    return res;
  };

  updateCharacter = async (obj: Character | ComplexCreatureInstance, pieces: string[] = ["all"], save_to_db = false) => {
    pieces = common_util.unique_array(pieces);
    // if (!save_to_db) {
    //   debugger;
    // }
    // console.log(obj);
    // console.log(pieces);
    const data_type = obj.data_type;
    if (pieces.includes("all") || (obj instanceof Character && !pieces.includes("!old_pieces") && obj.old_pieces)) {
      const db_obj = obj.toDBObj();
      // console.log(db_obj);
      const cookieObj = {
        body: db_obj,
        expiresAt: "never"
      };
      localStorage.setItem(`${data_type}_${obj._id}`, JSON.stringify(cookieObj));
      if (save_to_db) {
        await this.patchData({ path: "/updateObject", system: this.system, data: { data_type, obj: db_obj } });
      }
      pieces = CHARACTER_PIECES;
      for (let i = 0; i < pieces.length; ++i) {
        const piece = pieces[i];
        // if (piece === "race") {
        //   debugger;
        // }
        const dbObj = obj.toCharacterDBObj(piece);
        const cookieObj = {
          body: dbObj,
          expiresAt: "never"
        };
        localStorage.setItem(`${data_type}_${obj._id}_${piece}`, JSON.stringify(cookieObj));
        if (save_to_db) {
          await this.patchData({ path: "/updateCharacterPiece", system: this.system, data: { piece, obj: dbObj } });
        }
      }
    } else {
      for (let i = 0; i < pieces.length; ++i) {
        const piece = pieces[i];
        if (piece === "main") {
          const db_obj = obj.toDBObj();
          console.log(db_obj);
          const cookieObj = {
            body: db_obj,
            expiresAt: "never"
          };
          localStorage.setItem(`${data_type}_${obj._id}`, JSON.stringify(cookieObj));
          if (save_to_db) {
            await this.patchData({ path: "/updateObject", system: this.system, data: { data_type, obj: db_obj } });
          }
        } else if (piece !== "!old_pieces") {
          const dbObj = obj.toCharacterDBObj(piece) as any;
          console.log(dbObj);
          
          const cookieObj = {
            body: dbObj,
            expiresAt: "never"
          };
          localStorage.setItem(`${data_type}_${obj._id}_${piece}`, JSON.stringify(cookieObj));
          if (save_to_db) {
            await this.patchData({ path: "/updateCharacterPiece", system: this.system, data: { piece, obj: dbObj } });
          }
        }
      }
    }
    // } else {
      
    //   const sessionObj = {
    //     body: userObj,
    //     expiresAt: "never"
    //   };
    //   localStorage.setItem("loginUser", JSON.stringify(sessionObj));
    // }
  };
  removeCharacterCookies = (char_id: string) => {
    localStorage.removeItem(`character_${char_id}`);
    const pieces = CHARACTER_PIECES;
    for (let i = 0; i < pieces.length; ++i) {
      const piece = pieces[i];
      localStorage.removeItem(`character_${char_id}_${piece}`);
    }
  };

  upsertObject = async (data_type: DataType, obj: UpsertableBase, fix_type = false) => {
    if (fix_type) {
      switch (data_type) {
        case "source_book": obj = new SourceBook(obj.toDBObj()); break;
        case "standard_features": obj = new StandardFeatures(obj.toDBObj()); break;
        case "floating_feature": obj = new FloatingFeature(obj.toDBObj()); break;
        case "creature_feature": obj = new CreatureFeature(obj.toDBObj()); break;
        case "reusable_string": obj = new ReusableString(obj.toDBObj()); break;
        case "optional_feature_base": obj = new OptionalFeatureBase(obj.toDBObj()); break;
        // Character Origins
        case "race": obj = new Race(obj.toDBObj()); break;
        case "subrace": obj = new Subrace(obj.toDBObj()); break;
        case "background": obj = new Background(obj.toDBObj()); break;
        // Class Related
        case "game_class": obj = new GameClass(obj.toDBObj()); break;
        case "subclass": obj = new Subclass(obj.toDBObj()); break;
        case "special_feature": obj = new SpecialFeature(obj.toDBObj()); break;
        case "pact_boon": obj = new PactBoon(obj.toDBObj()); break;
        case "eldritch_invocation": obj = new EldritchInvocation(obj.toDBObj()); break;
        case "feat": obj = new Feat(obj.toDBObj()); break;
        case "fighting_style": obj = new FightingStyle(obj.toDBObj()); break;
        case "table": obj = new Table(obj.toDBObj()); break;
        // Items
        // case "item": obj = new Item(obj.toDBObj()); break;
        case "magic_item_keyword": obj = new MagicItemKeyword(obj.toDBObj()); break;
        case "tool": obj = new Tool(obj.toDBObj()); break;
        case "weapon_mastery_property": obj = new WeaponMasteryProperty(obj.toDBObj()); break;
        case "game_card": obj = new GameCard(obj.toDBObj()); break;
        case "game_deck": obj = new GameDeck(obj.toDBObj()); break;
        case "equipment_pack": obj = new EquipmentPack(obj.toDBObj()); break;
        // Other Feature Sources
        case "condition": obj = new Condition(obj.toDBObj()); break;
        case "gift": obj = new Gift(obj.toDBObj()); break;
        case "bastion_facility": obj = new BastionFacility(obj.toDBObj()); break;
        // Spells
        case "spell": obj = new Spell(obj.toDBObj()); break;
        case "spell_list": obj = new SpellList(obj.toDBObj()); break;
        case "spell_slot_type": obj = new SpellSlotType(obj.toDBObj()); break;
        // Transforms
        case "transform": obj = new Transform(obj.toDBObj()); break;
        // case "adaptation": obj = new Adaptation(obj.toDBObj()); break;
        // Peripherals
        case "resource": obj = new Resource(obj.toDBObj()); break;
        // case "org_position": obj = new OrgPosition(obj.toDBObj()); break;
        case "organization": obj = new Organization(obj.toDBObj()); break;
        case "rollable_table": obj = new RollableTable(obj.toDBObj()); break;
        // case "template": obj = new Template(obj.toDBObj()); break;
        // Creatures
        case "creature": obj = new Creature(obj.toDBObj()); break;
        case "creature_group": obj = new CreatureGroup(obj.toDBObj()); break;
        case "creature_gift": obj = new CreatureGift(obj.toDBObj()); break;
        // Other
        case "ticket": obj = new Ticket(obj.toDBObj()); break;
        case "sb_section": obj = new SBSection(obj.toDBObj()); break;
        default:
          obj = obj.clone(false);
        break;
      }
    }
    if (obj._id && obj._id !== "") {
      const res = await this.updateObject(data_type, obj);
      return res;
    } else {
      const res = await this.createObject(data_type, obj);
      return res;
    }
  };

  cleanUpCreatureFeatures = async (fbs: FeatureBase[]) => {
    for (const fb of fbs) {
      const cleanUs = fb.features.filter(o => o.floating_feature_id === "");
      for (const cleanMe of cleanUs) {
        const ff = cleanMe.floating_feature;
        if (ff) {
          try {
            const response = await this.upsertObject(ff.data_type, ff);
            if (response) {
              ff._id = response.body.id;
              cleanMe.floating_feature_id = response.body.id;
            }
          } catch (ex) {
            console.error("cleanUpCreatureFeatures error");
            console.error(ff);
            console.error(ex);
            debugger;
          }
        }
      }
    }
  }

  upsertCreature = async (obj: Creature) => {
    const data_type = "creature";
    // If it's newly parsed then there can be features that their floating_features aren't saved 
    // Need to go through them, save them, update the features with the ids of the new ffs
    // And then we can save the creature
    await this.cleanUpCreatureFeatures(obj.fbs);
    if (obj._id && obj._id !== "") {
      const res = await this.updateObject(data_type, obj);
      return res;
    } else {
      const res = await this.createObject(data_type, obj);
      return res;
    }
  };

  upsertCreatureGift = async (obj: CreatureGift) => {
    const data_type = "creature_gift";
    // If it's newly parsed then there can be features that their floating_features aren't saved 
    // Need to go through them, save them, update the features with the ids of the new ffs
    // And then we can save the creature
    await this.cleanUpCreatureFeatures(obj.action_fbs);
    await this.cleanUpCreatureFeatures(obj.legendary_action_fbs);
    await this.cleanUpCreatureFeatures(obj.traits);
    await this.cleanUpCreatureFeatures(obj.lair_action_fbs);
    if (obj._id && obj._id !== "") {
      const res = await this.updateObject(data_type, obj);
      return res;
    } else {
      const res = await this.createObject(data_type, obj);
      return res;
    }
  };

  upsertObjects = async (data_type: DataType, objects: UpsertableBase[]) => {
    const return_me: any[] = [];
    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i];
      return_me.push(await this.upsertObject(data_type, obj));
    }
    return return_me;
  };

  processResponse = async <T extends UpsertableBase>({
    response, 
    data_type, 
    retry = null, 
    sessionName = null, 
    noExpiry = false,
    log_it = false,
    check_hash = false
  }: processResponseProps): Promise<{ body: any, res: T | null }> => {
    // log_it = data_type === "equipment_pack";
    const body = response; // await response.json();
    if (log_it) {
      console.log(response);
      console.log(body);
    }
    let my_response: UpsertableBase | null = null;
    // if (data_type === "floating_feature") {
    //   debugger;
    // }
    if (body.objects) {
      debugger;
      // // This is from a get
      // if (log_it) {
      //   debugger;
      // }
      // if ([...FEATURE_SOURCE_TYPES,...OTHER_OBJECT_TYPES,"standard_features"].includes(data_type)) {
      //   if (["item","magic_item_keyword"].includes(data_type)) { // "base_item","magic_item",
      //     if (!this.smart_hash.weapon_mastery_property) {
      //       await this.getObjectsOld("weapon_mastery_property");
      //     }
      //   }
      //   if (data_type === "item") {
      //     if (!this.smart_hash.magic_item_keyword) {
      //       await this.getObjectsOld("magic_item_keyword");
      //     }
      //     // if (!this.smart_hash.tool) {
      //     //   await this.getObjectsOld("tool");
      //     // }
      //   }
      // }
      // if (data_type === "user") {
      //   const users: User[] = [];
      //   for (const o of body.objects) {
      //     users.push(new User(o));
      //   }
      //   my_response = users;
      // } else if (PLAYER_OBJECT_TYPES.includes(data_type)) {
      //   if (!body.objects) {
      //     debugger;
      //   }
      //   switch(data_type) {
      //     case "campaign": 
      //       const campaigns: Campaign[] = [];
      //       for (const o of body.objects) {
      //         campaigns.push(new Campaign(o));
      //       }
      //       my_response = campaigns;
      //     break;
      //     case "character": 
      //       const characters: Character[] = [];
      //       for (const o of body.objects) {
      //         characters.push(new Character(o));
      //       }
      //       my_response = characters;
      //     break;
      //     case "org_instance": 
      //       const org_instances: OrgInstance[] = [];
      //       for (const o of body.objects) {
      //         org_instances.push(new OrgInstance(o));
      //       }
      //       my_response = org_instances;
      //     break;
      //     case "encounter":
      //       const encounters: Encounter[] = [];
      //       for (const o of body.objects) {
      //         encounters.push(new Encounter(o));
      //       }
      //       my_response = encounters;
      //     break;
      //     // case "encounter_log":
      //     //   const encounter_logs: EncounterLog[] = [];
      //     //   body.objects.forEach((o: any) => {
      //     //     encounter_logs.push(new EncounterLog(o));
      //     //   });
      //     //   my_response = encounter_logs;
      //     // break;
      //     case "campaign_deck": 
      //       const decks_of_many_things: CampaignDeck[] = [];
      //       for (const o of body.objects) {
      //         decks_of_many_things.push(new CampaignDeck(o));
      //       }
      //       my_response = decks_of_many_things;
      //     break;
      //     case "character_receivable": 
      //       const receivables: CharacterReceivable[] = [];
      //       for (const o of body.objects) {
      //         receivables.push(new CharacterReceivable(o));
      //       }
      //       my_response = receivables;
      //     break;
      //     case "character_drawn_card": 
      //       const drawn_cards: CharacterDrawnCard[] = [];
      //       for (const o of body.objects) {
      //         drawn_cards.push(new CharacterDrawnCard(o));
      //       }
      //       my_response = drawn_cards;
      //     break;
      //     case "user_ticket": 
      //       const tickets: UserTicket[] = [];
      //       for (const o of body.objects) {
      //         const ut = new UserTicket(o);
      //         tickets.push(ut);
      //       }
      //       my_response = tickets;
      //     break;
      //   }
      // } else if (data_type === "floating_feature") {
      //   this.smart_hash.floating_feature = [];
      //   for (const o of body.objects) {
      //     const new_feature = this.new_feature(o);
      //     this.add_floating_feature(new_feature);
      //   }
      //   await this.connect_floating_features();
      //   my_response = [...this.smart_hash.floating_feature];
      // } else if (data_type === "optional_feature_base") {
      //   const optional_feature_bases: OptionalFeatureBase[] = [];
      //   body.objects.forEach((o: any) => {
      //     const ofb = new OptionalFeatureBase(o);
      //     if (this.user && this.user.admin && !TRUST_JSON_FOR_DATA_TYPES.includes(ofb.data_type)) {
      //       ofb.loaded = true;
      //     }
      //     optional_feature_bases.push(ofb);
      //   });
      //   my_response = optional_feature_bases;
      // } else if (data_type === "source_book") {
      //   const source_books: SourceBook[] = [];
      //   body.objects.forEach((o: any) => {
      //     source_books.push(new SourceBook(o));
      //   });
      //   my_response = source_books;
      // } else if (data_type === "feature_user_rating") {
      //   const feature_user_ratings: FeatureUserRating[] = [];
      //   body.objects.forEach((o: any) => {
      //     feature_user_ratings.push(new FeatureUserRating(o));
      //   });
      //   my_response = feature_user_ratings;
      // } else {
      //   my_response = this.handleModelBases(body.objects, data_type, check_hash);
      // }
      // if (my_response.length > 0 && (my_response[0] instanceof FeatureSource || my_response[0] instanceof Creature || my_response[0] instanceof CreatureGift || my_response[0] instanceof Spell)) {
      //   for (let i = 0; i < my_response.length; ++i) {
      //     await this.connectObject(my_response[i]);
      //   }
      // }
      // // if (data_type === "creature") {
      // //   debugger;
      // // }
      // if (this.always_store(data_type)) {
      //   // if (data_type === "condition" && this.smart_hash[data_type]) {
      //   //   console.log([...this.smart_hash[data_type]]);
      //   // }
      //   this.smart_hash[data_type] = my_response;
      //   // if (data_type === "condition") {
      //   //   console.log([...this.smart_hash[data_type]]);
      //   // }
      // } else if (!PLAYER_OBJECT_TYPES.includes(data_type)) {
      //   this.store_to_temp(data_type, response);
      // }
      // if (sessionName !== null && this.always_store(data_type)) {
      //   let expiresAt: string | Date = new Date();
      //   if (noExpiry) 
      //     expiresAt = "never";
      //   else 
      //     expiresAt.setHours(expiresAt.getHours() + 1);
      //   const sessionObj: any = {
      //     expiresAt
      //   };
      //   sessionObj[data_type] = my_response;
      //   sessionStorage.setItem(sessionName, JSON.stringify(sessionObj));
      // }
    } else if (typeof(body) === "object" && body._id) {
      // This is from an upsert
      if (FEATURE_SOURCE_TYPES.includes(data_type) || data_type === "standard_features") {
        if (["item","magic_item_keyword"].includes(data_type)) { // "base_item","magic_item",
          if (!this.smart_hash.weapon_mastery_property) {
            await this.getObjectsOld("weapon_mastery_property");
          }
        }
        if (data_type === "item") {
          if (!this.smart_hash.magic_item_keyword) {
            await this.getObjectsOld("magic_item_keyword");
          }
          // if (!this.smart_hash.tool) {
          //   await this.getObjectsOld("tool");
          // }
        }
        const my_responses = await this.handleModelBases([body], data_type);
        if (my_responses.length === 1) {
          my_response = (my_responses[0] as UpsertableBase) as T;
        } else {
          console.log(body);
          console.log(data_type);
        }
      } else {
        switch(data_type) {
          case "spell":
            my_response = new Spell(body);
          break;
          case "game_card":
            my_response = new GameCard(body);
          break;
          case "game_deck":
            my_response = new GameDeck(body);
          break;
          case "ticket": 
            const ticket = new Ticket(body); 
            if (ticket.user_tickets.length === 0 && ticket.user_ticket_ids.length > 0) {
              const uts = await this.getPlayerObjects({ data_type: "user_ticket", filter: { "_id*ObjectIDs": ticket.user_ticket_ids } }) as UserTicket[];
              ticket.user_tickets = uts;
              for (const ut of uts) {
                ut.ticket = ticket;
              }
            }
            my_response = ticket;
          break;
          case "condition":
            my_response = new Condition(body);
          break;
          case "spell_list": 
            my_response = new SpellList(body);
          break;
          case "background": 
          if (true) {
            const bg = new Background(body);
            bg.feats = (this.smart_hash.feat as Feat[]).filter(o => bg.feat_ids.includes(o._id));
            my_response = bg;
          }
          break;
          case "item":
            switch (body.item_type) {
              case "Armor":
                my_response = new Armor(body);
                const armor = my_response as Armor;
                if (armor.armor_type_name === "") {
                  const obj_finder = ARMOR_TYPES.find(o => o._id === armor.armor_type_id);
                  if (obj_finder) {
                    armor.armor_type = obj_finder;
                  }
                }
              break;
              case "Weapon":
                my_response = new Weapon(body);
              break;
              case "Ammunition":
                my_response = new Ammunition(body);
              break;
              case "Bag":
                my_response = new Container(body);
              break;
              case "Container":
                my_response = new Container(body);
              break;
              case "Deck":
                my_response = new DeckItem(body);
              break;
              case "Gem":
                my_response = new GemItem(body);
              break;
              // case "Tool": case "Gaming Set":
              // case "Musical Instrument":
              // case "Instrument":
              // case "Artisan's Tools":
              //   const tool = new BasicItem(body);
              //   // if (tool.name.includes("Bag")) {
              //   //   debugger;
              //   // }
              //   if (tool.tool_id === "") {
              //     const tool_finder = (this.smart_hash.tool as Tool[]).find(o => o.name.trim() === tool.name.trim());
              //     if (tool_finder) {
              //       tool.tool_id = tool_finder._id;
              //     }
              //   }
              //   my_response = tool;
              // break;
              default:
                my_response = new BasicItem(body);
              break;
            }
          break;
          case "campaign": 
            my_response = new Campaign(body);
          break;
          case "character": 
            my_response = new Character(body);
          break;
          case "user_ticket": 
            const ut = new UserTicket(body);
            my_response = ut;
          break;
          case "creature": 
            const creature = new Creature(body); 
            if (creature.spellcasting_table.entries.length > 0 && 
              creature.spellcasting_table.entries.some(o => o.spells.some(o2 => o2.spell_level === -1))) {
              // Fix the spell levels
              const spells = await this.getObjects<Spell>({ data_type: "spell" });
              for (const entry of creature.spellcasting_table.entries) {
                for (const c_spell of entry.spells) {
                  if (c_spell.spell_level === -1) {
                    const spell = spells.find(o => o._id === c_spell.spell_id);
                    if (spell) {
                      c_spell.spell_level = spell.level;
                    }
                  }
                }
              }
            }
            my_response = creature;
          break;
          case "creature_group": 
            my_response = new CreatureGroup(body);
          break;
          case "creature_gift": 
            my_response = new CreatureGift(body);
          break;
          case "eldritch_invocation": 
            my_response = new EldritchInvocation(body);
          break;
          case "fighting_style": 
            my_response = new FightingStyle(body);
          break;
          case "equipment_pack": 
            my_response = new EquipmentPack(body);
          break;
          case "pact_boon": 
            my_response = new PactBoon(body);
          break;
          case "feat": 
            my_response = new Feat(body);
          break;
          case "game_class": 
            my_response = new GameClass(body);
          break;
          case "organization": 
            my_response = new Organization(body);
          break;
          case "org_instance": 
            my_response = new OrgInstance(body);
          break;
          // case "org_position": 
          //   my_response = new OrgPosition(body);
          // break;
          case "magic_item_keyword": 
            my_response = new MagicItemKeyword(body); 
          break;
          case "weapon_mastery_property": 
            my_response = new WeaponMasteryProperty(body);
          break;
          case "race": 
            my_response = new Race(body);
            if (!this.smart_hash.subrace) {
              await this.getObjectsOld("subrace");
            }
          break;
          case "subrace": 
            if (true) {
              const subrace = new Subrace(body); 
              subrace.races = (this.smart_hash.race as Race[]).filter(o => subrace.race_ids.includes(o._id));
              my_response = subrace;
            }
          break;
          case "gift": 
            my_response = new Gift(body);
          break;
          case "bastion_facility": 
            my_response = new BastionFacility(body);
          break;
          case "transform": 
            my_response = body.creature_potences ? new CreatureTransform(body) : new Transform(body);
          break;
          // case "adaptation": 
          //   my_response = new Adaptation(body);
          // break;
          case "resource":
            my_response = new Resource(body);
          break;
          case "spell_slot_type": 
            my_response = new SpellSlotType(body);
          break;
          case "subclass": 
            if (this.smart_hash.game_class) {
              const subclass = new Subclass(body);
              subclass.game_class = (this.smart_hash.game_class as GameClass[]).find(o => o._id === subclass.game_class_id);
              my_response = subclass;
            }
          break;
          case "table": 
            my_response = new Table(body);
          break;
          case "encounter":
            my_response = new Encounter(body);
          break;
          case "loot_table":
            my_response = new LootTable(body);
          break;
          case "user":
            my_response = new User(body);
          break;
          case "rollable_table":
            my_response = new RollableTable(body);
          break;
          case "template": 
            my_response = new TemplateBase(body);
          break;
          case "floating_feature": 
            my_response = this.new_feature(body);
          break;
          case "creature_feature": 
            my_response = this.new_feature(body);
          break;
          case "feature_user_rating":  
            console.log("I don't think it will ever come here.");
            console.log(body);
            console.log(data_type);
          break;
          case "reusable_string": 
            my_response = new ReusableString(body);
          break;
          case "source_book": 
            my_response = new SourceBook(body);
          break;
          case "sb_section": 
            my_response = new SBSection(body);
          break;
          case "special_feature":
            my_response = new SpecialFeature(body);
          break;
          case "tool": 
            my_response = new Tool(body);
          break;
          case "campaign_deck":
            my_response = new CampaignDeck(body);
          break;
          case "character_receivable": 
            my_response = new CharacterReceivable(body);
          break;
          case "character_drawn_card": 
            my_response = new CharacterDrawnCard(body);
          break;
          case "optional_feature_base":
            my_response = new OptionalFeatureBase(body);
          break;
        }
      }
      if (this.user && this.user.admin && 
        my_response instanceof ModelBase && 
        !(my_response instanceof FeatureSource || 
          my_response instanceof SourceBook || 
          my_response instanceof FloatingFeature)) {
        // if (my_response instanceof CreatureGift) {
        //   debugger;
        // }
        my_response.loaded = true;
      }
      if (data_type === "source_book") {
        this.updateFreeSBIDs();
      }
    }
    return { body, res: my_response as T };
  };
  
  processUserRatingResponse = async (response: any) => {
    const body = await response.json();
    if (response.status !== 200) { 
      throw Error(body.message);
    } else {
      const response: any = body;
      return response;
    }
  };

  connect_floating_features = async () => {
    const ffs = (this.smart_hash.floating_feature as FloatingFeature[]);
    const ability_ffs = ffs.filter(o => o instanceof Ability || o instanceof ExtraEffectsFeature || o instanceof ExtraEffectsOfTypeFeature);
    for (const ff of ability_ffs) {
      if (ff instanceof Ability || ff instanceof ExtraEffectsFeature || ff instanceof ExtraEffectsOfTypeFeature) {
        const potences = ff.effects.filter(o => 
          (o.type === "Summon" || o.type === "Transform") &&
          o.potences.length > 0);
        for (const effect of potences) {
          for (const potence of effect.potences) {
            for (const opt of potence.summon_options.filter(o => o.summon_type === "Custom")) {
              if (opt.custom_stat_block) {
                const summon = opt.custom_stat_block;
                await this.loadFeatureBases(summon.fbs);
              }
            }
          }
        }
      }
    }
  }
  
  new_feature(obj: any) {
    let new_feature = new FloatingFeature(obj);
    if (obj) {
      switch(obj.feature_type) {
        case "Language":
          new_feature = new LanguageFeature(obj);
        break;
        case "Pact Boon":
          new_feature = new PactBoonFeature(obj);
        break;
        case "Proficiency Override":
        case "Ignore Heavy Rule":
          new_feature = new StringFeature(obj);
        break;
        case "Eldritch Invocation":
        case "Eldritch Invocations":
          new_feature = new EldritchInvocationsFeature(obj);
        break;
        case "Creature Type":
          new_feature = new CreatureTypeFeature(obj);
        break;
        case "Fighting Style":
          new_feature = new StringsFeature(obj);
        break;
        case "Reroll":
          new_feature = new Reroll(obj);
        break;
        case "Modifier":
          const mod = new Modifier(obj);
          const mods = mod.modifies as any;
          if (mods === "Unarmed Strikes") {
            const usf = new UnarmedStrikesFeature();
            usf.copyFromModifier(mod);
            new_feature = usf;
          } else if (mods === "Resource Refresh Rule") {
            const ssf = new StringsFeature(obj);
            ssf.feature_type = "Resource Refresh Rule Change";
            ssf.strings.push(mod.modifies_detail_2);
            ssf.strings.push(mod.modifies_detail_3);
            new_feature = ssf;
          } else if (["Attack","Damage"].includes(mod.modifies) && mod.replace_asm) {
            const rasf = new ReplaceAbilityScoreFeature();
            rasf.copyFromModifier(mod);
            new_feature = rasf;
          } else {
            new_feature = mod;
          }
        break;
        case "Minimum Roll":
          new_feature = new MinimumRoll(obj);
        break;
        case "Unarmed Strikes":
          new_feature = new UnarmedStrikesFeature(obj);
        break;
        case "Replace Ability Score for Attacks":
          new_feature = new ReplaceAbilityScoreFeature(obj);
        break;
        case "Attack Upgrade":
          new_feature = new AttackUpgradeFeature(obj);
        break;
        case "Weapon Mastery":
          new_feature = new WeaponMasteryFeature(obj);
        break;
        case "Weapon Mastery Master":
          new_feature = new WeaponMasteryMasterFeature(obj);
        break;
        case "Extra Damage Roll": case "Extra Effects":
          new_feature = new ExtraEffectsFeature(obj);
        break;
        case "Extra Effect Type": case "Extra Effects Type":
          new_feature = new ExtraEffectTypeFeature(obj);
        break;
        case "Extra Effects of Type":
          new_feature = new ExtraEffectsOfTypeFeature(obj);
        break;
        case "Disable Actions":
          new_feature = new DisableActionsFeature(obj);
        break;
        case "Bonus Action Attack":
          new_feature = new BonusActionAttackFeature(obj);
        break;
        case "Light Attack Add Damage Mod":
          new_feature = new LightAttackAddDamageMod(obj);
        break;
        case "Weapon Keyword Upgrade":
          new_feature = new WeaponKeywordUpgradeFeature(obj);
        break;
        case "Weapon Keyword Upgrade 2":
          new_feature = new WeaponKeywordUpgradeFeature2(obj);
        break;
        case "Weapon Damage Upgrade":
          new_feature = new WeaponDamageUpgradeFeature(obj);
        break;
        case "Resource Refresh Rule Change":
          new_feature = new StringsFeature(obj);
        break;
        case "Subclass":
          if (obj.str && obj.subclasses_called === undefined) {
            const str_feature = new StringFeature(obj);
            const subclass_feature = new SubclassFeature();
            subclass_feature.copyStringFeature(str_feature);
            new_feature = subclass_feature;
          } else {
            new_feature = new SubclassFeature(obj);
          }
        break;
        case "Subrace":
          new_feature = new StringFeature(obj);
        break;
        case "Start Equipment":
          new_feature = new StartEquipmentFeature(obj);
        break;
        case "Characteristic":
          new_feature = new CharacteristicFeature(obj);
        break;
        case "Spell Modifier":
          new_feature = new SpellModifier(obj);
        break;
        case "Spell Damage Options":
          new_feature = new SpellDamageOptions(obj);
        break;
        case "Size Options":
          new_feature = new SizeOptions(obj);
        break;
        case "Summon Upgrade":
          new_feature = new SummonUpgradeFeature(obj);
        break;
        case "Condition":
          new_feature = new ConditionFeature(obj);
        break;
        case "Alternate Condition":
          new_feature = new AlternateConditionFeature(obj);
        break;
        case "Allowed Conditions":
          new_feature = new StringsFeature(obj);
        break;
        case "Limit Spoof":
          new_feature = new LimitSpoofFeature(obj);
        break;
        case "Rest Alteration":
          new_feature = new RestAlterationFeature(obj);
        break;
        case "Specific Special Feature":
        case "Special Feature":
        case "Special Feature Choices":
          new_feature = new SFFeature(obj);
        break;
        case "Special Feature List Merge":
          new_feature = new SFMergeFeature(obj);
        break;
        case "Sense":
          new_feature = new SenseFeature(obj);
        break;
        case "Skill Proficiencies":
        case "Skill Proficiency Choices":
        case "Armor Proficiencies":
        case "Weapon Proficiencies":
        case "Special Weapon Proficiencies":
        case "Saving Throw Proficiencies":
        case "Tool Proficiency":
        case "Tool Proficiencies":
        case "Tool Proficiency Choices":
        case "Proficiencies":
          new_feature = new Proficiency(obj);
        break;
        case "Expertise":
          new_feature = new Proficiency(obj);
        break;
        case "Jack of All Trades":
          new_feature = new NumberFeature(obj);
        break;
        case "Feat":
          new_feature = new FeatFeature(obj);
        break;
        case "Transform Upgrade":
          new_feature = new TransformUpgradeFeature(obj);
        break;
        case "Knowledge":
          new_feature = new KnowledgeFeature(obj);
        break;
        case "Learnable":
          new_feature = new LearnableFeature(obj);
        break;
        case "Selectable":
          new_feature = new Selectable(obj);
        break;
        case "Ability":
          new_feature = new Ability(obj);
        break;
        case "Creature Ability":
          new_feature = new CreatureAbility(obj);
        break;
        case "Skill as Ability":
          new_feature = new SkillAsAbility(obj);
        break;
        case "Skill Ability Override":
          new_feature = new SkillAbilityOverrideFeature(obj);
        break;
        case "Extra Attacks":
          new_feature = new NumberFeature(obj);
        break;
        case "Minion Extra Attacks":
          new_feature = new UpgradableNumberFeature(obj);
        break;
        case "Minion Ability":
          new_feature = new MinionAbility(obj);
        break;
        // case "Creature Spell as Ability":
        //   new_feature = new CreatureSpellAsAbility(obj);
        // break;
        case "Advantage":
          new_feature = new Advantage(obj);
        break;
        case "Greater Advantage":
          new_feature = new GreaterAdvantage(obj);
        break;
        case "Damage Multiplier":
          new_feature = new DamageMultiplier(obj);
        break;
        case "Spellcasting":
          new_feature = new SpellcastingFeature(obj);
        break;
        case "Spellcasting Focus":
          new_feature = new StringFeature(obj);
        break;
        case "Bonus Spells":
          new_feature = new BonusSpells(obj);
        break;
        case "Bonus Spells Upgrade":
          new_feature = new BonusSpellsUpgrade(obj);
        break;
        case "Action Type Upgrade": 
          new_feature = new ActionTypeUpgrade(obj);
        break;
        case "Ignore Stealth Disadvantage":
          new_feature = new IgnoreStealthDisadvantage(obj);
        break;
        case "Suggested Spells":
          new_feature = new SuggestedSpells(obj);
        break;
        case "Special Feat Access":
          new_feature = new StringFeature(obj);
        break;
        case "Spell Book":
        case "Spellbook":
          new_feature = new Spellbook(obj);
        break;
        case "Spellbook Modifier":
          new_feature = new SpellbookModifier(obj);
        break;
        case "Spells":
          new_feature = new NumberFeature(obj);
        break;
        case "Cantrips":
          new_feature = new NumberFeature(obj);
        break;
        case "Cantrips from List":
          const cantrips_ishf = new IStringHashFeature(obj);
          cantrips_ishf.feature_type = "Spells from List";
          cantrips_ishf.hash.count_as_class_id = "None";
          cantrips_ishf.hash.max_level = "0"; // max_level === 0 => Cantrips
          cantrips_ishf.hash.spellcasting_ability = "";
          const sfl = new SpellsFromList();
          sfl.copyFromIStringHashFeature(cantrips_ishf);
          new_feature = sfl;
        break;
        case "Spell as Ability": // This got merged into SpellsFromList
        case "Spell As Ability":
        case "Spells from List":
          new_feature = new SpellsFromList(obj);
        break;
        case "Spells from List with Costs":
          new_feature = new SpellsFromListWithCosts(obj);
        break;
        case "Extra Spell Lists":
          new_feature = new ExtraSpellLists(obj);
        break;
        case "Ritual Casting":
          new_feature = new StringFeature(obj);
        break;
        case "Resource":
          new_feature = new ResourceFeature(obj);
        break;
        case "Ability Score Improvement":
          new_feature = new ASIBaseFeature(obj);
        break;
        case "Mystic Arcanum":
          new_feature = new NumberFeature(obj);
        break;
        case "Spell Mastery":
          new_feature = new NumberFeature(obj);
        break;
        // case "Extra IAA Slots":
        //   new_feature = new ExtraIAASlots(obj);
        // break;
      }
    }
    if (new_feature.description.includes("Level") && new_feature.description.length < 15) {
      new_feature.description = "";
    }
    return new_feature;
  }

  new_feature_of_type(type: string) {
    let new_feature = new FloatingFeature();
    switch(type) {
      case "Language":
        new_feature = new LanguageFeature();
      break;
      case "Pact Boon":
        new_feature = new PactBoonFeature();
      break;
      case "Proficiency Override":
      case "Ignore Heavy Rule":
        new_feature = new StringFeature();
      break;
      case "Eldritch Invocation":
      case "Eldritch Invocations":
        new_feature = new EldritchInvocationsFeature();
      break;
      case "Creature Type":
        new_feature = new CreatureTypeFeature();
      break;
      case "Fighting Style":
        new_feature = new StringsFeature();
      break;
      case "Reroll":
        new_feature = new Reroll();
      break;
      case "Modifier":
        new_feature = new Modifier();
      break;
      case "Minimum Roll":
        new_feature = new MinimumRoll();
      break;
      case "Unarmed Strikes":
        new_feature = new UnarmedStrikesFeature();
      break;
      case "Replace Ability Score for Attacks":
        new_feature = new ReplaceAbilityScoreFeature();
      break;
      case "Attack Upgrade":
        new_feature = new AttackUpgradeFeature();
      break;
      case "Weapon Mastery":
        new_feature = new WeaponMasteryFeature();
      break;
      case "Weapon Mastery Master":
        new_feature = new WeaponMasteryMasterFeature();
      break;
      case "Extra Damage Roll": case "Extra Effects":
        new_feature = new ExtraEffectsFeature();
      break;
      case "Extra Effect Type": case "Extra Effects Type":
        new_feature = new ExtraEffectTypeFeature();
      break;
      case "Extra Effects of Type":
        new_feature = new ExtraEffectsOfTypeFeature();
      break;
      case "Disable Actions":
        new_feature = new DisableActionsFeature();
      break;
      case "Bonus Action Attack":
        new_feature = new BonusActionAttackFeature();
      break;
      case "Light Attack Add Damage Mod":
        new_feature = new LightAttackAddDamageMod();
      break;
      case "Weapon Keyword Upgrade":
        new_feature = new WeaponKeywordUpgradeFeature();
      break;
      case "Weapon Keyword Upgrade 2":
        new_feature = new WeaponKeywordUpgradeFeature2();
      break;
      case "Weapon Damage Upgrade":
        new_feature = new WeaponDamageUpgradeFeature();
      break;
      case "Resource Refresh Rule Change":
        new_feature = new StringsFeature();
      break;
      case "Subclass":
        new_feature = new SubclassFeature();
      break;
      case "Subrace":
        new_feature = new StringFeature();
      break;
      case "Start Equipment":
        new_feature = new StartEquipmentFeature();
      break;
      case "Characteristic":
        new_feature = new CharacteristicFeature();
      break;
      case "Spell Modifier":
        new_feature = new SpellModifier();
      break;
      case "Spell Damage Options":
        new_feature = new SpellDamageOptions();
      break;
      case "Size Options":
        new_feature = new SizeOptions();
      break;
      case "Summon Upgrade":
        new_feature = new SummonUpgradeFeature();
      break;
      case "Condition":
        new_feature = new ConditionFeature();
      break;
      case "Alternate Condition":
        new_feature = new AlternateConditionFeature();
      break;
      case "Allowed Conditions":
        new_feature = new StringsFeature();
      break;
      case "Limit Spoof":
        new_feature = new LimitSpoofFeature();
      break;
      case "Rest Alteration":
        new_feature = new RestAlterationFeature();
      break;
      case "Specific Special Feature":
      case "Special Feature":
      case "Special Feature Choices":
        new_feature = new SFFeature();
      break;
      case "Special Feature List Merge":
        new_feature = new SFMergeFeature();
      break;
      case "Sense":
        new_feature = new SenseFeature();
      break;
      case "Skill Proficiencies":
      case "Skill Proficiency Choices":
      case "Armor Proficiencies":
      case "Weapon Proficiencies":
      case "Special Weapon Proficiencies":
      case "Saving Throw Proficiencies":
      case "Tool Proficiency":
      case "Tool Proficiencies":
      case "Tool Proficiency Choices":
      case "Proficiencies":
        new_feature = new Proficiency();
      break;
      case "Expertise":
        new_feature = new Proficiency();
      break;
      case "Jack of All Trades":
        new_feature = new NumberFeature();
      break;
      case "Feat":
        new_feature = new FeatFeature();
      break;
      case "Transform Upgrade":
        new_feature = new TransformUpgradeFeature();
      break;
      case "Knowledge":
        new_feature = new KnowledgeFeature();
      break;
      case "Learnable":
        new_feature = new LearnableFeature();
      break;
      case "Selectable":
        new_feature = new Selectable();
      break;
      case "Ability":
        new_feature = new Ability();
      break;
      case "Creature Ability":
        new_feature = new CreatureAbility();
      break;
      case "Skill as Ability":
        new_feature = new SkillAsAbility();
      break;
      case "Skill Ability Override":
        new_feature = new SkillAbilityOverrideFeature();
      break;
      case "Extra Attacks":
        new_feature = new NumberFeature();
      break;
      case "Minion Extra Attacks":
        new_feature = new UpgradableNumberFeature();
      break;
      case "Minion Ability":
        new_feature = new MinionAbility();
      break;
      // case "Creature Spell as Ability":
      //   new_feature = new CreatureSpellAsAbility();
      // break;
      case "Advantage":
        new_feature = new Advantage();
      break;
      case "Greater Advantage":
        new_feature = new GreaterAdvantage();
      break;
      case "Damage Multiplier":
        new_feature = new DamageMultiplier();
      break;
      case "Spellcasting":
        new_feature = new SpellcastingFeature();
      break;
      case "Spellcasting Focus":
        new_feature = new StringFeature();
      break;
      case "Bonus Spells":
        new_feature = new BonusSpells();
      break;
      case "Bonus Spells Upgrade":
        new_feature = new BonusSpellsUpgrade();
      break;
      case "Action Type Upgrade": 
        new_feature = new ActionTypeUpgrade();
      break;
      case "Ignore Stealth Disadvantage":
        new_feature = new IgnoreStealthDisadvantage();
      break;
      case "Suggested Spells":
        new_feature = new SuggestedSpells();
      break;
      case "Special Feat Access":
        new_feature = new StringFeature();
      break;
      case "Spell Book":
      case "Spellbook":
        new_feature = new Spellbook();
      break;
      case "Spellbook Modifier":
        new_feature = new SpellbookModifier();
      break;
      case "Spells":
        new_feature = new NumberFeature();
      break;
      case "Cantrips":
        new_feature = new NumberFeature();
      break;
      case "Spell as Ability":
      case "Spell As Ability":
      case "Cantrips from List":
      case "Spells from List":
        new_feature = new SpellsFromList();
      break;
      case "Spells from List with Costs":
        new_feature = new SpellsFromListWithCosts();
      break;
      case "Extra Spell Lists":
        new_feature = new ExtraSpellLists();
      break;
      case "Ritual Casting":
        new_feature = new StringFeature();
      break;
      case "Resource":
        new_feature = new ResourceFeature();
      break;
      case "Ability Score Improvement":
        new_feature = new ASIBaseFeature();
      break;
      case "Mystic Arcanum":
        new_feature = new NumberFeature();
      break;
      case "Spell Mastery":
        new_feature = new NumberFeature();
      break;
      // case "Extra IAA Slots":
      //   new_feature = new ExtraIAASlots();
      // break;
    }
    return new_feature;
  }

  to_floating_feature(obj: any) {
    let new_feature = new FloatingFeature(obj);
    if (obj) {
      switch(obj.feature_type) {
        case "Language":
          new_feature = new LanguageFeature(obj);
        break;
        case "Pact Boon":
          new_feature = new PactBoonFeature(obj);
        break;
        case "Proficiency Override":
        case "Ignore Heavy Rule":
          new_feature = new StringFeature(obj);
        break;
        case "Eldritch Invocation":
        case "Eldritch Invocations":
          new_feature = new EldritchInvocationsFeature(obj);
        break;
        case "Creature Type":
          new_feature = new CreatureTypeFeature(obj);
        break;
        case "Fighting Style":
          new_feature = new StringsFeature(obj);
        break;
        case "Reroll":
          new_feature = new Reroll(obj);
        break;
        case "Modifier":
          const mod = new Modifier(obj);
          const mods = mod.modifies as any;
          if (mods === "Unarmed Strikes") {
            const usf = new UnarmedStrikesFeature();
            usf.copyFromModifier(mod);
            new_feature = usf;
          } else if (mods === "Resource Refresh Rule") {
            new_feature = mod;
            const ssf = new StringsFeature(obj);
            ssf.feature_type = "Resource Refresh Rule Change";
            ssf.strings.push(mod.modifies_detail_2);
            ssf.strings.push(mod.modifies_detail_3);
            new_feature = ssf;
          } else if (["Attack","Damage"].includes(mod.modifies) && mod.replace_asm) {
            const rasf = new ReplaceAbilityScoreFeature();
            rasf.copyFromModifier(mod);
            new_feature = rasf;
          } else {
            new_feature = mod;
          }
        break;
        case "Minimum Roll":
          new_feature = new MinimumRoll(obj);
        break;
        case "Unarmed Strikes":
          new_feature = new UnarmedStrikesFeature(obj);
        break;
        case "Replace Ability Score for Attacks":
          new_feature = new ReplaceAbilityScoreFeature(obj);
        break;
        case "Attack Upgrade":
          new_feature = new AttackUpgradeFeature(obj);
        break;
        case "Weapon Mastery":
          new_feature = new WeaponMasteryFeature(obj);
        break;
        case "Weapon Mastery Master":
          new_feature = new WeaponMasteryMasterFeature(obj);
        break;
        case "Extra Damage Roll": case "Extra Effects":
          new_feature = new ExtraEffectsFeature(obj);
        break;
        case "Extra Effect Type": case "Extra Effects Type":
          new_feature = new ExtraEffectTypeFeature(obj);
        break;
        case "Extra Effects of Type":
          new_feature = new ExtraEffectsOfTypeFeature(obj);
        break;
        case "Disable Actions":
          new_feature = new DisableActionsFeature(obj);
        break;
        case "Bonus Action Attack":
          new_feature = new BonusActionAttackFeature(obj);
        break;
        case "Light Attack Add Damage Mod":
          new_feature = new LightAttackAddDamageMod(obj);
        break;
        case "Weapon Keyword Upgrade":
          new_feature = new WeaponKeywordUpgradeFeature(obj);
        break;
        case "Weapon Keyword Upgrade 2":
          new_feature = new WeaponKeywordUpgradeFeature2(obj);
        break;
        case "Weapon Damage Upgrade":
          new_feature = new WeaponDamageUpgradeFeature(obj);
        break;
        case "Resource Refresh Rule Change":
          new_feature = new StringsFeature(obj);
        break;
        case "Subclass":
          if (obj.str && obj.subclasses_called === undefined) {
            const str_feature = new StringFeature(obj);
            const subclass_feature = new SubclassFeature();
            subclass_feature.copyStringFeature(str_feature);
            new_feature = subclass_feature;
          } else {
            new_feature = new SubclassFeature(obj);
          }
        break;
        case "Subrace":
          new_feature = new StringFeature(obj);
        break;
        case "Start Equipment":
          new_feature = new StartEquipmentFeature(obj);
        break;
        case "Characteristic":
          new_feature = new CharacteristicFeature(obj);
        break;
        case "Spell Modifier":
          new_feature = new SpellModifier(obj);
        break;
        case "Spell Damage Options":
          new_feature = new SpellDamageOptions(obj);
        break;
        case "Size Options":
          new_feature = new SizeOptions(obj);
        break;
        case "Summon Upgrade":
          new_feature = new SummonUpgradeFeature(obj);
        break;
        case "Condition":
          new_feature = new ConditionFeature(obj);
        break;
        case "Alternate Condition":
          new_feature = new AlternateConditionFeature(obj);
        break;
        case "Allowed Conditions":
          new_feature = new StringsFeature(obj);
        break;
        case "Limit Spoof":
          new_feature = new LimitSpoofFeature(obj);
        break;
        case "Rest Alteration":
          new_feature = new RestAlterationFeature(obj);
        break;
        case "Specific Special Feature":
        case "Special Feature":
        case "Special Feature Choices":
          new_feature = new SFFeature(obj);
        break;
        case "Special Feature List Merge":
          new_feature = new SFMergeFeature(obj);
        break;
        case "Sense":
          new_feature = new SenseFeature(obj);
        break;
        case "Skill Proficiencies":
        case "Skill Proficiency Choices":
        case "Armor Proficiencies":
        case "Weapon Proficiencies":
        case "Special Weapon Proficiencies":
        case "Saving Throw Proficiencies":
        case "Tool Proficiency":
        case "Tool Proficiencies":
        case "Tool Proficiency Choices":
        case "Proficiencies":
          new_feature = new Proficiency(obj);
        break;
        case "Expertise":
          new_feature = new Proficiency(obj);
        break;
        case "Jack of All Trades":
          new_feature = new NumberFeature(obj);
        break;
        case "Feat":
          new_feature = new FeatFeature(obj);
        break;
        case "Transform Upgrade":
          new_feature = new TransformUpgradeFeature(obj);
        break;
        case "Knowledge":
          new_feature = new KnowledgeFeature(obj);
        break;
        case "Learnable":
          new_feature = new LearnableFeature(obj);
        break;
        case "Selectable":
          new_feature = new Selectable(obj);
        break;
        case "Ability":
          new_feature = new Ability(obj);
        break;
        case "Creature Ability":
          new_feature = new CreatureAbility(obj);
        break;
        case "Skill as Ability":
          new_feature = new SkillAsAbility(obj);
        break;
        case "Skill Ability Override":
          new_feature = new SkillAbilityOverrideFeature(obj);
        break;
        case "Extra Attacks":
          new_feature = new NumberFeature(obj);
        break;
        case "Minion Extra Attacks":
          new_feature = new UpgradableNumberFeature(obj);
        break;
        case "Minion Ability":
          new_feature = new MinionAbility(obj);
        break;
        // case "Creature Spell as Ability":
        //   new_feature = new CreatureSpellAsAbility(obj);
        // break;
        case "Advantage":
          new_feature = new Advantage(obj);
        break;
        case "Greater Advantage":
          new_feature = new GreaterAdvantage(obj);
        break;
        case "Damage Multiplier":
          new_feature = new DamageMultiplier(obj);
        break;
        case "Spellcasting":
          new_feature = new SpellcastingFeature(obj);
        break;
        case "Spellcasting Focus":
          new_feature = new StringFeature(obj);
        break;
        case "Bonus Spells":
          new_feature = new BonusSpells(obj);
        break;
        case "Bonus Spells Upgrade":
          new_feature = new BonusSpellsUpgrade(obj);
        break;
        case "Action Type Upgrade": 
          new_feature = new ActionTypeUpgrade(obj);
        break;
        case "Ignore Stealth Disadvantage":
          new_feature = new IgnoreStealthDisadvantage(obj);
        break;
        case "Suggested Spells":
          new_feature = new SuggestedSpells(obj);
        break;
        case "Special Feat Access":
          new_feature = new StringFeature(obj);
        break;
        case "Spell Book":
        case "Spellbook":
          new_feature = new Spellbook(obj);
        break;
        case "Spellbook Modifier":
          new_feature = new SpellbookModifier(obj);
        break;
        case "Spells":
          new_feature = new NumberFeature(obj);
        break;
        case "Cantrips":
          new_feature = new NumberFeature(obj);
        break;
        case "Cantrips from List":
          const cantrips_ishf = new IStringHashFeature(obj);
          cantrips_ishf.feature_type = "Spells from List";
          cantrips_ishf.hash.count_as_class_id = "None";
          cantrips_ishf.hash.max_level = "0"; // max_level === 0 => Cantrips
          cantrips_ishf.hash.spellcasting_ability = "";
          const sfl = new SpellsFromList();
          sfl.copyFromIStringHashFeature(cantrips_ishf);
          new_feature = sfl;
        break;
        case "Spell as Ability":
        case "Spell As Ability":
        case "Spells from List":
          if (obj.slot_override) {
            new_feature = new SpellsFromList(obj);
          } else {
            const spells_ishf = new IStringHashFeature(obj);
            spells_ishf.feature_type = "Spells from List";
            if (!spells_ishf.hash.spellcasting_ability) {
              spells_ishf.hash.spellcasting_ability = "";
            }
            if (!spells_ishf.hash.count_as_class_id) {
              spells_ishf.hash.count_as_class_id = "None";
            }
            const sfl2 = new SpellsFromList();
            sfl2.copyFromIStringHashFeature(spells_ishf);
            new_feature = sfl2;
          }
        break;
        case "Spells from List with Costs":
          new_feature = new SpellsFromListWithCosts(obj);
        break;
        case "Extra Spell Lists":
          new_feature = new ExtraSpellLists(obj);
        break;
        case "Ritual Casting":
          new_feature = new StringFeature(obj);
        break;
        case "Resource":
          new_feature = new ResourceFeature(obj);
        break;
        case "Ability Score Improvement":
          new_feature = new ASIBaseFeature(obj);
        break;
        case "Mystic Arcanum":
          new_feature = new NumberFeature(obj);
        break;
        case "Spell Mastery":
          new_feature = new NumberFeature(obj);
        break;
        // case "Extra IAA Slots":
        //   new_feature = new ExtraIAASlots(obj);
        // break;
      }
    }
    if (new_feature.description.includes("Level") && new_feature.description.length < 15) {
      new_feature.description = "";
    }
    this.smart_hash.floating_feature.push(new_feature);
    return new_feature;
  }
  
  login = async (auth: AuthContextProps) => {
    // debugger;
    let response: "New User" | "Updating User" | "Error" | "All Good" = "Error";
    if (auth.user) {
      const auth_user = auth.user;
      const auth_id = auth_user.profile.sub;
      const auth_email = auth_user.profile.email;
      const profile_name = auth_user.profile["name"] ? auth_user.profile["name"] : auth_user.profile["cognito:username"];
      let user = await this.knockKnock({ auth_id });
      // debugger;
      if (user) {
        response = "All Good";
        let changed = false;
        if (auth_email && user.email !== auth_email) {
          // They've changed their email.
          user.email = auth_email;
          changed = true;
          response = "Updating User";
        }
        if (typeof profile_name === "string" && user.profile_name !== profile_name) {
          // They've changed their username. 
          user.profile_name = profile_name;
          changed = true;
          response = "Updating User";
        }
        if (changed) {
          // Update our record to match.
          await this.upsertObject("user", user);
        }
      } else {
        // It's possible this is the first time they're logging in with the new system.
        // Check for an existing user with the same email.
        // I can remove this check once everyone is moved over.
        user = await this.knockKnock({ email: auth_email });
        if (user && user.auth_id === "") {
          // They have an existing user. Save their auth_id to it.
          user.auth_id = auth_id;
          if (typeof profile_name === "string" && user.profile_name !== profile_name) {
            // They've changed their username. 
            user.profile_name = profile_name;
          }
          await this.upsertObject("user", user);
          response = "Updating User";
        } else {
          // New user. Create a user record.
          user = new User();
          user.auth_id = auth_user.profile.sub;
          if (auth_email) {
            user.email = auth_email;
          }
          if (typeof profile_name === "string") {
            user.profile_name = profile_name;
          }
          const res = await this.upsertObject("user", user);
          if (!res || !res.res || (res.body.error && res.body.error === "Offline")) {
            this.online = false;
          } else if (res.res instanceof User) {
            user = res.res;
            response = "New User";
          }
        }
      }
      if (user) {
        this.true_user = user;
        if (user.is_admin) {
          const spoofUserObjStr = localStorage.getItem("spoofUser");
          if (spoofUserObjStr) {
            const spoofUserObj = JSON.parse(spoofUserObjStr);
            const spoofUserBody = spoofUserObj.body;
            if (spoofUserBody) {
              const spoof_user = new User(spoofUserBody);
              this.user = spoof_user;
            }
          }
        }
        await this.getObjects({ data_type: "source_book" });
        if (this.user) {
          // Get all the campaigns they're part of to determine their shared_sb_ids
          const campaigns = await this.getPlayerObjects({ data_type: "campaign" }) as Campaign[];
          const shared_sb_ids: string[] = [];
          for (const c of campaigns) {
            shared_sb_ids.push(...c.character_options.source_books);
          }
          this.user.shared_sb_ids = common_util.unique_array(shared_sb_ids);
          this.updateFreeSBIDs();
        }
      } else {
        console.error("Login error");
      }
    }
    return response;
  }
  loginOld = async (userObj: any) => {
    userObj.bypass = true;
    const oldUserObjStr = localStorage.getItem("loginUser");
    let response = await this.postData({ path: "/ingang", data: userObj });
    if (typeof(response) === "string" && response === "Offline") {
      // debugger; 
      this.online = false;
      if (oldUserObjStr) {
        const userObj = JSON.parse(oldUserObjStr);
        response = userObj.body;
      } else {
        return null;
      }
    }
    // debugger;
    const res = await this.processResponse({ 
      response, 
      data_type: "user", 
      sessionName: "user", 
    });
    this.smart_hash = { };
    if (res.res instanceof User) {
      this.true_user = res.res;
      const sessionObj = {
        body: res.res.toDBObj(),
        expiresAt: "never"
      };
      localStorage.setItem("loginUser", JSON.stringify(sessionObj));
      if (this.user) {
        // Get all the campaigns they're part of to determine their shared_sb_ids
        const campaigns = await this.getPlayerObjects({ data_type: "campaign" }) as Campaign[];
        const shared_sb_ids: string[] = [];
        for (const c of campaigns) {
          shared_sb_ids.push(...c.character_options.source_books);
        }
        this.user.shared_sb_ids = common_util.unique_array(shared_sb_ids);
        this.updateFreeSBIDs();
      }
    }
    return res;
  }
  logout = async () => {
    this.true_user = null;
    localStorage.removeItem("loginUser");
    this.subscription_holder.reload();
  }
  spoofUser = (user: User) => {
    if (this.true_user && this.true_user.is_admin) {
      this.user = user;
      const cookieObj = {
        body: user,
        expiresAt: "never"
      };
      localStorage.setItem(`spoofUser`, JSON.stringify(cookieObj));

      this.subscription_holder.reload();
    }
  }
  unSpoofUser = () => {
    if (this.true_user) {
      this.user = this.true_user;
      localStorage.removeItem("spoofUser");
      this.subscription_holder.reload();
    }
  }

  // register = async (user: any) => {
  //   const response = await this.postData({ path: "/register", data: user });
  //   if (typeof(response) === "string" && response === "Offline") {
  //     this.online = false;
  //     return null;
  //   } else {
  //     return this.processResponse({ response, data_type: "user" });
  //   }
  // }
  // requestPasswordReset(data: { email: string }) {
  //   return this.postData({ path: "/requestPasswordReset", data });
  // }
  
  // confirmPasswordReset(data: { email: string; otp: string; newPassword: string }) {
  //   return this.postData({ path: "/confirmPasswordReset", data});
  // }

  // Get Users
  
  getUsers = async (filter: any = {}) => {
    return await this.getPlayerObjects({ data_type: "user", filter });
  };
  getObjectsWithCallbacks = async <T extends UpsertableBase>({
    data_type, 
    filter = {}, 
    refresh = false,
    force_api = false,
    force_json = false,
    onComplete,
    onProgress,
    // onError,
    archives = [],
    // character_options,
    log_it = false,
    load_all
  }: {
    data_type: DataType, 
    filter?: any, 
    refresh?: boolean,
    force_api?: boolean,
    force_json?: boolean,
    onComplete?: (res: T[]) => void,
    onProgress?: (data_type: DataType, percentage: number) => void,
    onError?: Function,
    archives?: string[],
    // character_options?: CharacterOptions,
    log_it?: boolean,
    load_all?: boolean
  }): Promise<T[]> => {
    if (!isDataType(data_type)) {
      debugger;
      return [];
    }
    if (this.user && this.user.admin) { // this.user.is_admin
      console.log(`getObjectsWithCallbacks("${data_type}",${JSON.stringify(filter)}) at ${common_util.get_time_number()}`);
    }
    if (log_it) {
      debugger;
    }
    // if (data_type === "optional_feature_base") {
    //   debugger;
    // }
    let response: UpsertableBase[] = [];
    if (this.alwaysStore(data_type)) {
      if (refresh) {
        delete this.smart_hash[data_type];
      }
      if (!this.smart_hash[data_type]) {
        if (!this.calling.includes(data_type)) {
          this.calling.push(data_type);
          await this.getObjectsFromAPIWithCallbacks({ 
            data_type, 
            force_api, force_json,
            onProgress, log_it
          });
          this.calling = this.calling.filter(o => o !== data_type);
        } else {
          while (this.calling.includes(data_type)) {
            // console.log(`Waiting for ${data_type}`);
            await delay(1000);
          }
          // console.log(this.smart_hash[data_type]);
        }
      }
      if (this.smart_hash[data_type]) {
        response = [...this.smart_hash[data_type]];
      }
    } else if (this.otherStore(data_type)) {
      // For these the filter will always include parent_id.
      if (Object.keys(filter).includes("parent_id")) {
        const parent_id = filter.parent_id;
        if (typeof(parent_id) === "string") {
          const data = this.other_hash.getData(data_type, parent_id);
          response = [...data];
        }
        if (response.length === 0) {
          // It hasn't been loaded into memory yet, so let's get it.
          // We don't want to use the stuff below because that gets all of them, 
          // and we only want to get the ones that match the filter.
          if (this.online) {
            response = await this.getObjectsFromAPIWithCallbacks({ 
              data_type, filter,
              force_api: true, // These are never stored in json. This will bypass unnecessary things
              onProgress, log_it
            });
          }
        }
      }
    } else if (this.use_temp_hash) {
      if (refresh) {
        delete this.temp_hash[data_type];
      }
      if (!this.temp_hash[data_type]) {
        if (!this.calling.includes(data_type)) {
          this.calling.push(data_type);
          await this.getObjectsFromAPIWithCallbacks({ 
            data_type, 
            force_api, force_json,
            onProgress, log_it
          });
          this.calling = this.calling.filter(o => o !== data_type);
        } else {
          while (!this.calling.includes(data_type)) {
            await delay(1000);
          }
        }
      }
      if (this.temp_hash[data_type]) {
        response = [...this.temp_hash[data_type].objects];
      }
    } 
    if (this.online) {
      if (response.length === 0) {
        response = await this.getObjectsFromAPIWithCallbacks({ 
          data_type, 
          force_api, force_json,
          onProgress, log_it
        });
      }

      if (archives.length > 0) {
        const include_archived: ModelBase[] = [];
        if (log_it) {
          debugger;
        }
        for (const archived_sb_id of archives) {
          const response3 = await this.getObjectsFromAPIWithCallbacks({ 
            data_type, 
            force_api, force_json, archived_sb_id,
            onProgress, log_it
          });
          include_archived.push(...response3);
        }
        response.push(...include_archived);
      }
    }
    if (filter && Object.keys(filter).length > 0) {
      response = this.filter(data_type, response, filter) as ModelBase[];
    }
    if (!this.user || !(this.user.view_blocked_source_books || this.user.admin)) {
      response = (response as ModelBase[]).filter(o => !o.blocked);
    }
    if (load_all) {
      for (let i = 0; i < response.length; ++i) {
        const obj = response[i];
        if (obj instanceof ModelBase) {
          if (onProgress) {
            onProgress(data_type, i / response.length);
          }
          this.loadObject(obj);
        }
      }
    }
    if (onComplete) {
      onComplete(response as T[]);
    }
    return response as T[];
  };

  processResponse2 = async ({
    all, 
    data_type,
    merge_hash = false,
    archived_sb_id = "",
    // filtered = false
  }: {
    all: any[], 
    data_type: DataType,
    merge_hash?: boolean,
    archived_sb_id?: string,
    // filtered?: boolean
  }): Promise<PlayerObject[] | ModelBase[] | User[]> => {
    // if (data_type === "adaptation") {
    //   debugger;
    // }
    let response: PlayerObject[] | ModelBase[] | User[] = []; // all;
    // console.log(all);
    if (data_type === "user") {
      const users: User[] = [];
      for (const o of all) {
        users.push(new User(o));
      }
      response = users;
    } else if (PLAYER_OBJECT_TYPES.includes(data_type)) {
      switch(data_type) {
        case "campaign": 
          const campaigns: Campaign[] = [];
          for (const o of all) {
            campaigns.push(new Campaign(o));
          }
          response = campaigns;
        break;
        case "character": 
          const characters: Character[] = [];
          for (const o of all) {
            characters.push(new Character(o));
          }
          response = characters;
        break;
        case "user_ticket": 
          const uts: UserTicket[] = [];
          for (const o of all) {
            const ut = new UserTicket(o);
            uts.push(ut);
          }
          response = uts;
        break;
        case "org_instance": 
          const org_instances: OrgInstance[] = [];
          for (const o of all) {
            org_instances.push(new OrgInstance(o));
          }
          response = org_instances;
        break;
        case "encounter":
          const encounters: Encounter[] = [];
          for (const o of all) {
            encounters.push(new Encounter(o));
          }
          response = encounters;
        break;
        // case "encounter_log":
        //   const encounter_logs: EncounterLog[] = [];
        //   all.forEach((o: any) => {
        //     encounter_logs.push(new EncounterLog(o));
        //   });
        //   response = encounter_logs;
        // break;
        case "campaign_deck": 
          const decks_of_many_things: CampaignDeck[] = [];
          for (const o of all) {
            decks_of_many_things.push(new CampaignDeck(o));
          }
          response = decks_of_many_things;
        break;
        case "character_receivable": 
          const receivables: CharacterReceivable[] = [];
          for (const o of all) {
            receivables.push(new CharacterReceivable(o));
          }
          response = receivables;
        break;
        case "character_drawn_card": 
          const drawn_cards: CharacterDrawnCard[] = [];
          for (const o of all) {
            drawn_cards.push(new CharacterDrawnCard(o));
          }
          response = drawn_cards;
        break;
      }
    } else if (data_type === "floating_feature") {
      this.smart_hash.floating_feature = [];
      for (const o of all) {
        const new_feature = this.new_feature(o);
        this.add_floating_feature(new_feature);
      }
      await this.connect_floating_features();
      response = [...this.smart_hash.floating_feature];
    } else if (data_type === "creature_feature") {
      this.smart_hash.creature_feature = [];
      for (const o of all) {
        const new_feature = this.new_feature(o);
        this.add_creature_feature(new_feature);
      }
      // await this.connect_creature_features();
      response = [...this.smart_hash.creature_feature];
    } else if (data_type === "optional_feature_base") {
      const optional_feature_bases: OptionalFeatureBase[] = [];
      for (const o of all) {
        optional_feature_bases.push(new OptionalFeatureBase(o));
      }
      response = optional_feature_bases;
    } else if (data_type === "source_book") {
      const source_books = all.map(o => new SourceBook(o));
      // Have to filter blocked here because we fetch them differently from others.
      response = this.user && (this.user.view_blocked_source_books || this.user.admin) ? source_books : source_books.filter(o => !o.blocked);
    } else if (data_type === "feature_user_rating") {
      const feature_user_ratings: FeatureUserRating[] = [];
      for (const o of all) {
        feature_user_ratings.push(new FeatureUserRating(o));
      }
      response = feature_user_ratings;
    } else {
      response = await this.handleModelBases(all, data_type, archived_sb_id !== "");
    }
    const user_id = this.user ? this.user._id : "";
    for (const obj of response) {
      if (obj instanceof ModelBase) {
        if (this.user && this.user.admin && 
          !(obj instanceof FeatureSource || 
            obj instanceof SourceBook ||
            obj instanceof FloatingFeature
          ) &&
          TRUST_JSON_FOR_DATA_TYPES.includes(obj.data_type)
        ) {
          // if (obj instanceof CreatureGift) {
          //   debugger;
          // }
          obj.loaded = true;
        }
        if (!(obj instanceof SourceBook) && !(obj instanceof FloatingFeature)) {
          if (!this.smart_hash.source_book) {
            await this.getSourceBooks({});
          }
          const source_books = (this.smart_hash.source_book as SourceBook[]).filter(o => 
            obj.source_books_old.filter(o2 => o2.id === o._id).length > 0);
          // if (obj.name === "Wolfkind") {
          //   debugger;
          // }
          if (this.user && this.user.admin) {
            // debugger;
            const source_books_old = source_books.map(o => {
              return { id: o._id, type: o.source_book_type, third_party: o.third_party };
            });
            if (obj.source_books_old.filter(o => o.id === "Personal" || o.id === user_id).length > 0) {
              source_books_old.push({ id: "Personal", type: "Homebrew", third_party: false });
            }
            obj.source_books_old = source_books_old;
          }
          obj.source_books = source_books;
        }
      }
    }
    // if (response.length > 0 && (response[0] instanceof FeatureSource || response[0] instanceof StandardFeatures || response[0] instanceof Creature || response[0] instanceof CreatureGift || response[0] instanceof OptionalFeatureBase || response[0] instanceof Spell)) {
    //   for (const obj of response) {
    //     if (obj instanceof ModelBase) {
    //       console.log("Should it be coming here at all?");
    //       // await this.loadObject(obj);
    //       await this.connectObject(obj);
    //     }
    //   }
    // }
    // if (data_type === "creature") {
    //   debugger;
    // }
    if (this.alwaysStore(data_type) && data_type !== "floating_feature" && data_type !== "creature_feature") {
      // if (data_type === "condition" && this.smart_hash[data_type]) {
      //   console.log([...this.smart_hash[data_type]]);
      // }
      if (merge_hash) {
        if (archived_sb_id === "") {
          if (this.smart_hash[data_type]) {
            const oldOnes = [...this.smart_hash[data_type]];
            let newOnes = response as ModelBase[];
            newOnes = newOnes.filter(o => oldOnes.filter(o2 => o2._id === o._id).length === 0);
            this.smart_hash[data_type] = [...oldOnes,...newOnes];
            response = this.smart_hash[data_type];
          } else {
            // console.log(response);
            this.smart_hash[data_type] = response as ModelBase[];
          }
        } else {
          if (!this.archives_smart_hash[archived_sb_id]) {
            this.archives_smart_hash[archived_sb_id] = {};
          }
          if (this.archives_smart_hash[archived_sb_id][data_type]) {
            const oldOnes = [...this.archives_smart_hash[archived_sb_id][data_type]];
            let newOnes = response as ModelBase[];
            newOnes = newOnes.filter(o => oldOnes.filter(o2 => o2._id === o._id).length === 0);
            this.archives_smart_hash[archived_sb_id][data_type] = [...oldOnes,...newOnes];
            response = this.archives_smart_hash[archived_sb_id][data_type];
          } else {
            // console.log(response);
            this.archives_smart_hash[archived_sb_id][data_type] = response as ModelBase[];
          }
        }
      } else {
        if (archived_sb_id === "") {
          // console.log(response);
          this.smart_hash[data_type] = response as ModelBase[];
        } else {
          if (!this.archives_smart_hash[archived_sb_id]) {
            this.archives_smart_hash[archived_sb_id] = {};
          }
          this.archives_smart_hash[archived_sb_id][data_type] = response as ModelBase[];
        }
      }
      // if (data_type === "condition") {
      //   console.log([...this.smart_hash[data_type]]);
      // }
    } else if (this.otherStore(data_type)) {
      if (response.length > 0 && response[0] instanceof ParentedBase) {
        this.other_hash.setData(response as ParentedBase[]);
      }
    } else if (!this.alwaysStore(data_type) && !PLAYER_OBJECT_TYPES.includes(data_type)) {
      if (this.use_temp_hash) {
        if (archived_sb_id === "") {
          if (response.length === 0 && this.temp_hash[data_type] && this.temp_hash[data_type].objects.length > 0) {
            response = this.temp_hash[data_type].objects;
          }
          this.store_to_temp(data_type, response as ModelBase[]);
        } else {
          if (!this.archives_temp_hash[archived_sb_id]) {
            this.archives_temp_hash[archived_sb_id] = {};
          }
          if (response.length === 0 && this.archives_temp_hash[archived_sb_id][data_type] && this.archives_temp_hash[archived_sb_id][data_type].objects.length > 0) {
            response = this.archives_temp_hash[archived_sb_id][data_type].objects;
          }
          this.store_to_temp(data_type, response as ModelBase[], archived_sb_id);
        }
      }
    }
    return response;
  };

  getSessionData = (sessionName: string) => {
    const sessionStr = sessionStorage.getItem(sessionName);
    if (sessionStr !== null) {
      const sessionObj = JSON.parse(sessionStr);
      if (sessionObj.expiresAt !== undefined && 
        (sessionObj.expiresAt === "never" || new Date(sessionObj.expiresAt) > new Date())) {
        return sessionObj.response;
      }
    }
    return null;
  };
  
  readJson = async (data_type: DataType) => {
    try {
      if (data_type !== "floating_feature" && 
        data_type !== "creature_feature" && 
        data_type !== "template" && 
        data_type !== "template_base" &&
        this.user && 
        (this.user.view_blocked_source_books || this.user.admin)
      ) {
        const standard = await this.readJsonStandard(data_type);
        const blocked = await this.readJsonBlocked(data_type);
        return [...standard,...blocked];
      } else {
        return await this.readJsonStandard(data_type);
      }
    } catch (ex) {
      debugger;
    }

    return [];
  }
  
  readJsonStandard = async (data_type: DataType) => {
    try {
      const data = await import(`@data/${data_type}.json`);
      return data.default; // Note: need .default for JSON imports
    } catch (ex) {
      console.error('Failed to load JSON:', ex);
      debugger;
    }

    return [];
  }
  
  readJsonBlocked = async (data_type: DataType) => {
    try {
      const data = await import(`@data/${data_type}_blocked.json`);
      return data.default; // Note: need .default for JSON imports
    } catch (ex) {
      console.error('Failed to load JSON:', ex);
      debugger;
    }

    return [];
  }

  getFullObjectForClipboard = async (data_type: DataType, id: string) => {
    const obj = await this.getFullObject(data_type, id);
    if (obj) {
      const fullObj = obj.toDBObj();
      if (fullObj.description) {
        fullObj.description = this.getDisplayObjects(fullObj.description);
      }
      if (obj instanceof Creature) {
        // debugger;
        for (const fb of obj.fbs) {
          const fullObj_fb = fullObj.traits.find((o: any) => o.true_id === fb.true_id);
          if (fullObj_fb) {
            for (const f of fb.features) {
              const fullObj_f = fullObj_fb.features.find((o: any) => o.true_id === f.true_id);
              const ff = this.get_floating_feature(f.floating_feature_id);
              if (ff) {
                fullObj_f.ff = ff.toDBObj();
              }
            }
          }
        }
        if (!this.smart_hash.spell) {
          await this.getObjects({ data_type: "spell" });
        }
        const spells = this.smart_hash.spell as Spell[];
        for (let i = 0; i < fullObj.spellcasting_table.entries.length; ++i) {
          const entry = fullObj.spellcasting_table.entries[i];
          for (let j = 0; j < entry.spells.length; ++j) {
            const spell_obj = entry.spells[j];
            let spell = spells.find(o => o._id === spell_obj.spell_id);
            if (!spell) {
              spell = spells.find(o => o.variant_ids.includes(spell_obj.spell_id) && o.isDominant());
            }
            if (spell) {
              spell_obj.spell_name = spell.name;
            }
          }
        }
        for (let i = 0; i < obj.attributes_table.entries.length; ++i) {
          const entry = obj.attributes_table.entries[i];
          const skill_profs = Object.keys(entry.skill_profs);
          for (const skill_id of skill_profs) {
            const skill_obj = entry.skill_profs[skill_id];
            delete entry.skill_profs[skill_id];
            const skill = SKILLS.find(o => o._id === skill_id);
            if (skill) {
              entry.skill_profs[skill.name] = skill_obj;
            }
          }
        }
      } else if (obj instanceof FeatureSource) {
        if (obj instanceof MagicItemKeyword) {
          // Need to convert weapon_limit's id references to put names instead 
          // so they can be looked up on the other end.
          // console.log(obj);
          if (!this.smart_hash.item || this.smart_hash.item.length === 0) {
            await this.getObjects({ data_type: "item" });
          }
          fullObj.weapon_limit.item_names = obj.weapon_limit.item_ids.map(o => {
            const item = this.smart_hash.item.find(o => o._id);
            if (item) {
              return item.name;
            }
            return "";
          });
          for (const item_id of obj.base_item_ids) {
            const item = this.smart_hash.item.find(o => o._id === item_id);
            if (item) {
              fullObj.weapon_limit.item_names.push(item.name);
            }
          }
          fullObj.weapon_limit.weapon_keyword_names = obj.weapon_limit.weapon_keyword_ids.map(o => {
            const wk = WEAPON_KEYWORDS.find(o => o._id);
            if (wk) {
              return wk.name;
            }
            return "";
          });
        }
        for (const fb of obj.feature_bases) {
          const fullObj_fb = fullObj.feature_bases.find((o: any) => o.true_id === fb.true_id);
          if (fullObj_fb) {
            for (const f of fb.features) {
              const fullObj_f = fullObj_fb.features.find((o: any) => o.true_id === f.true_id);
              const ff = this.get_floating_feature(f.floating_feature_id);
              if (ff) {
                fullObj_f.ff = ff.toDBObj();
              }
            }
          }
        }
      } else if (obj instanceof StandardFeatures) {
        for (const fb of obj.feature_bases) {
          const fullObj_fb = fullObj.feature_bases.find((o: any) => o.true_id === fb.true_id);
          if (fullObj_fb) {
            for (const f of fb.features) {
              const fullObj_f = fullObj_fb.features.find((o: any) => o.true_id === f.true_id);
              const ff = this.get_floating_feature(f.floating_feature_id);
              if (ff) {
                fullObj_f.ff = ff.toDBObj();
              }
            }
          }
        }
      } else if (obj instanceof FloatingFeature) {

      } else if (obj instanceof Spell) {
        // debugger;
        fullObj.spell_lists = this.smart_hash.spell_list.filter(o => obj.spell_list_ids.includes(o._id)).map(o => o.name);
      } else {
        debugger;
      }
      return fullObj;
    } else {
      debugger;
      return { data_type, id, error: "Not Found" };
    }
  }

  getFullObjectsForClipboard = async (data_type: DataType, ids: string[]) => {
    const objs: any[] = [];
    for (const id of ids) {
      const obj = await this.getFullObjectForClipboard(data_type, id);
      objs.push(obj);
    }
    return objs;
  }

  getDisplayObjects = (use_string: string) => {
    const responses: string[] = [];
    while (use_string.includes("[Replacer:")) {
      let index = use_string.indexOf("[Replacer:");
      const before = use_string.substring(0, index);
      if (before.length > 0) {
        responses.push(`<div>${ this.getDisplayObjects(before) }</div><div>&nbsp;</div>`);
      }
      const after = use_string.substring(index + 10);
      index = after.indexOf("]");
      use_string = after.substring(index + 1); // trim use_string down to just the things after this for next go round
      const stuff = after.substring(0, index);
      index = stuff.indexOf(";");
      const data_type = stuff.substring(0, index);
      index = stuff.indexOf("(");
      const name_stuff = stuff.substring(index + 1);
      index = name_stuff.indexOf(")");
      const address_str = name_stuff.substring(0, index);
      const address = address_str.split(",");
      try {
        if (this.smart_hash[data_type]) {
          const obj2 = this.smart_hash[data_type].find(o => address.includes(o._id));
          if (obj2 instanceof ReusableString) {
            if (obj2.label !== "") {
              responses.push(
                `<div>
                  <div className="header">${ obj2.label }</div><div>&nbsp;</div>
                  <div>${ this.getDisplayObjects(obj2.content) }</div><div>&nbsp;</div>
                </div>`
              );
            } else {
              responses.push(this.getDisplayObjects(obj2.content));
            }
          } else if (obj2 instanceof ModelBase) {
            responses.push(
              `<div>
                <div className="header">${ obj2.use_name }</div><div>&nbsp;</div>
                <div>${ this.getDisplayObjects(obj2.description) }</div><div>&nbsp;</div>
              </div>`
            );
          }
        }
      } catch (ex) {}
    }
    if (use_string.length > 0) {
      responses.push(common_util.cleanLineBreaks(this.getStringWithReplacementsSimple({ clean_me: use_string }))); // add anything left over
    }
    return responses.join();
  }

  /**
   * This one replaces things that would draw on something from the 
   * object (character, creature, etc) to get values with a description of what goes there.
   */
  getStringWithReplacementsSimple({
    clean_me, 
    constants,
  }: {
    clean_me: string, 
    constants?: any,
  }): string {
    const clean_it = (cleaning: string) => {
      if (constants) {
        while (cleaning.includes("{constants_map.")) {
          // There's something besides the name that needs replacing.
          let index = cleaning.indexOf("{constants_map.");
          const after = cleaning.substring(index + 15);
          index = after.indexOf("}");
          const stuff = after.substring(0, index);
          index = stuff.indexOf("[");
          const map_name = stuff.substring(0, index);
          const sub = stuff.substring(index + 1);
          index = sub.indexOf("]");
          const key = sub.substring(0, index);
          const field = sub.substring(index + 2);
          let value = "";
          try {
            if (map_name.length > 0 && key.length > 0) {
              if (field.length > 0) {
                value = constants[map_name][key][field];
              } else {
                value = constants[map_name][key];
              }
            }
          } catch (ex) {}
          cleaning = common_util.replaceAll(cleaning, `{constants_map.${stuff}}`, `${value}`);
        }
      }
      if (true) {
        while (cleaning.includes("{ugn=")) {
          // {ugn={formula_key="Bestial Strike Die",min=4}}
          // There's something besides the name that needs replacing.
          let index = cleaning.indexOf("{ugn={");
          const after = cleaning.substring(index + 6);
          index = after.indexOf("}}");
          const ugn_str = after.substring(0, index);
          // debugger;
          const ugn_obj = JSON.parse(`{${ugn_str}}`);
          const ugn = new UpgradableNumber(ugn_obj);
          let value = 0;
          value = ugn.base;
          cleaning = common_util.replaceAll(cleaning, `{ugn={${ugn_str}}}`, `${value}`);
        }
      }
      while (cleaning.includes("{potence_string.")) {
        // {potence_string.x.y.z} => {avg(xdy+z)} (xdy + z), if z === 0 then leave it out
        let index = cleaning.indexOf("{potence_string.");
        const after = cleaning.substring(index + 16);
        index = after.indexOf("}");
        let value = "";
        const str = after.substring(0, index);
        const pieces = str.split(".");
        const count = +pieces[0];
        const size = +pieces[1];
        let flat = pieces[2] === undefined || pieces[2].length === 0 ? 0 : +pieces[2];
        // if (isNaN(flat)) {
        //   if (obj instanceof CreatureInstance || obj instanceof Character) {
        //     switch (pieces[2]) {
        //       case "STR": flat = obj.current_ability_scores.STR; break;
        //       case "DEX": flat = obj.current_ability_scores.DEX; break;
        //       case "CON": flat = obj.current_ability_scores.CON; break;
        //       case "INT": flat = obj.current_ability_scores.INT; break;
        //       case "WIS": flat = obj.current_ability_scores.WIS; break;
        //       case "CHA": flat = obj.current_ability_scores.CHA; break;
        //     }
        //   }
        // }
        let drop_curly = false;
        if (!isNaN(count) && !isNaN(size) && !isNaN(flat)) {
          if (flat === 0) {
            value = `{avg(${count}d${size})} (${count}d${size})`;
          } else if (flat < 0) {
            value = `{avg(${count}d${size}-${flat})} (${count}d${size} - ${flat})`;
          } else {
            value = `{avg(${count}d${size}+${flat})} (${count}d${size} + ${flat})`;
          }
        } else if (!isNaN(count) && !isNaN(size) && pieces[2] === "{cfs" && pieces[4]) {
          // console.log(pieces);
          // console.log(str);
          // pieces[2]-pieces[4] probably was something like {cfs.current_ability_scores.STR
          value = `${count}d${size} + ${pieces[4]}`;
          drop_curly = true;
        }
        cleaning = common_util.replaceAll(cleaning, `{potence_string.${str}${drop_curly ? "}" : ""}}`, `${value}`);
      }
      while (cleaning.includes("{avg(")) {
        // There's something besides the name that needs replacing.
        let index = cleaning.indexOf("{avg(");
        let after = cleaning.substring(index + 5);
        index = after.indexOf(")}");
        if (index < 0) {
          console.error("AVERAGE ERROR!");
          break;
        }
        const roll_str = after.substring(0, index);
        try {
          let value = 0;
          let pieces = roll_str.split("d");
          const before = pieces[0];
          after = pieces[1];
          if (after) {
            const count = +before;
            let size = 0;
            let flat = 0;
            if (after.includes("+")) {
              pieces = after.split("+");
              size = +pieces[0];
              flat = +pieces[1];
            } else if (after.includes("-")) {
              pieces = after.split("-");
              size = +pieces[0];
              flat = +pieces[1] * -1;
            } else {
              size = +after;
            }
            value = Math.floor(count * ((size + 1) / 2) + flat);
            cleaning = common_util.replaceAll(cleaning, `{avg(${roll_str})}`, `${value}`);
          } else {
            cleaning = common_util.replaceAll(cleaning, `{avg(${roll_str})}`, "AVERAGE NOT FOUND!");
          }
        } catch (ex) {
          console.error(ex);
          debugger;
          cleaning = common_util.replaceAll(cleaning, `{avg(${roll_str})}`, "AVERAGE ERROR!");
        }
      }
      while (cleaning.includes("{sum(")) {
        // There's something besides the name that needs replacing.
        let index = cleaning.indexOf("{sum(");
        const after = cleaning.substring(index + 5);
        index = after.indexOf(")}");
        if (index < 0) {
          console.error("SUM ERROR!");
          break;
        }
        const sum_str = after.substring(0, index);
        try {
          let value = 0;
          const pieces = sum_str.split(",");
          for (const piece of pieces) {
            value += +piece;
          }
          cleaning = common_util.replaceAll(cleaning, `{sum(${sum_str})}`, `${value}`);
        } catch (ex) {
          console.error(ex);
          debugger;
          cleaning = common_util.replaceAll(cleaning, `{sum(${sum_str})}`, "SUM ERROR!");
        }
      }
      while (cleaning.includes("{list_of_type:")) {
        // There's something besides the name that needs replacing.
        // {list_of_type:data_type;[ids]}
        // {list_of_type:data_type;display_field;[ids]}
        let index = cleaning.indexOf("{list_of_type:");
        const after = cleaning.substring(index + 14);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        index = stuff.indexOf(";");
        const data_types = stuff.substring(0, index).split(",") as DataType[];
        let ids = stuff.substring(index + 1);
        let display_field = "";
        if (ids.indexOf(";") > -1) {
          // has a display_field specified.
          index = ids.indexOf(";");
          display_field = ids.substring(0, index);
          ids = ids.substring(index + 1);
        } 
        const use_ids = common_util.replaceAll(common_util.replaceAll(ids, "[", ""), "]", "").split(",").map(o => o.trim());
        let unused_ids = [...use_ids];
        
        const values: string[] = [];
        for (const data_type of data_types) {
          try {
            let objects: ModelBase[] = [];
            if (this.smart_hash[data_type]) {
              objects = this.smart_hash[data_type];
            } else if (CONSTANTIZED_DATA_TYPES.includes(data_type)) {
              objects = this.getConstantizedObjects({ data_type });
            }
             
            objects = objects.filter(o => unused_ids.includes(o._id));
            if (display_field === "") {
              values.push(...objects.map(o => o.name));
            } else {
              values.push(...(objects as any[]).map(o => o[display_field]));
            }
            unused_ids = unused_ids.filter(o => !objects.some(o2 => o2._id === o));
          } catch (ex) {}
        }
        let value = common_util.customJoin({ arr: values, separator: ", ", lastSeparator: ", " });

        if (unused_ids.length > 0) {
          value += (value.length > 0 ? "; " : "") + "Not Found: " + common_util.customJoin({ arr: unused_ids, separator: ", ", lastSeparator: ", " });
        }
        cleaning = common_util.replaceAll(cleaning, `{list_of_type:${stuff}}`, `${value}`);
      }
      while (cleaning.includes("{class_table.")) {
        // {class_table.class_id}
        // Constructs a standard table for that class.
        // Columns: Level, Prof Bonus, Features, Columns from table (attached or separate), Spell Slots if caster
        let index = cleaning.indexOf("{class_table.");
        const after = cleaning.substring(index + 1);
        index = after.indexOf("}");
        const id = after.substring(0, index);
        let value = "";
        const game_class = this.smart_hash.game_class.find(o => o._id === id);
        if (game_class instanceof GameClass) {
          // const table = data_util
        }
        cleaning = common_util.replaceAll(cleaning, `{list_of_type:${id}}`, `${value}`);
      }
      if (cleaning.includes("Melee Weapon Attack:") &&
        cleaning.includes("to hit, reach 10 ft., one target. Hit:") &&
        cleaning.includes("piercing damage plus") &&
        cleaning.includes("psychic damage.")) {
        debugger;
      }
      while (cleaning.includes("{attack_string.")) {
        // {attack_string.STAT.z} => +{sum({cfs.proficiency_modifier},{cfs.current_ability_scores.STAT},z)} if z === 0 then leave it out
        let index = cleaning.indexOf("{attack_string.");
        const after = cleaning.substring(index + 15);
        index = after.indexOf("}");
        let value = "";
        const str = after.substring(0, index);
        const pieces = str.split(".");
        const stat = pieces[0];
        const flat = pieces[1] === undefined || pieces[1].length === 0 ? 0 : +pieces[1];
        if (ABILITY_SCORES.includes(stat) && !isNaN(flat)) {
          if (flat === 0) {
            value = `+ ${stat} + Proficiency Bonus`;
          } else {
            value = `+ ${stat} + Proficiency Bonus + ${flat}`;
          }
        }
        cleaning = common_util.replaceAll(cleaning, `{attack_string.${str}}`, `${value}`);
      }
      while (cleaning.includes("{dc_string.")) {
        // {dc_string.STAT.z} => {sum(8,{cfs.proficiency_modifier},{cfs.current_ability_scores.STAT},z)} if z === 0 then leave it out
        let index = cleaning.indexOf("{dc_string.");
        const after = cleaning.substring(index + 11);
        index = after.indexOf("}");
        let value = "";
        const str = after.substring(0, index);
        const pieces = str.split(".");
        // console.log(pieces);
        const stat = pieces[0];
        const flat = pieces[1] === undefined || pieces[1].length === 0 ? 0 : +pieces[1];
        if (ABILITY_SCORES.includes(stat) && !isNaN(flat)) {
          if (flat === 0) {
            value = `8 + ${stat} + Proficiency Bonus`;
          } else {
            value = `8 + ${stat} + Proficiency Bonus + ${flat}`;
          }
        }
        cleaning = common_util.replaceAll(cleaning, `{dc_string.${str}}`, `${value}`);
      }
      while (cleaning.includes("+?")) {
        // Check if the next thing after it is a '-'. If so then leave the plus. Otherwise remove it.
        let index = cleaning.indexOf("+?");
        const before = cleaning.substring(0, index);
        let next = cleaning[index + 2];
        let after = cleaning.substring(index + 2);
        if (next === " ") {
          next = cleaning[index + 3];
          after = cleaning.substring(index + 3);
        }
        if (next === "-") {
          cleaning = before + after;
        } else {
          after = cleaning.substring(index + 2);
          cleaning = before + "+" + after;
        }
      }
      return cleaning;
    }
    const cleaned = clean_it(clean_me); 
    
    // console.log(cleaned);

    return cleaned;
  }
  
  getStringWithReplacementsSimpleAsync = async ({
    clean_me, 
    constants,
  }: {
    clean_me: string, 
    constants?: any,
  }) => {
    const clean_it = async (cleaning: string) => {
      if (constants) {
        while (cleaning.includes("{constants_map.")) {
          // There's something besides the name that needs replacing.
          let index = cleaning.indexOf("{constants_map.");
          const after = cleaning.substring(index + 15);
          index = after.indexOf("}");
          const stuff = after.substring(0, index);
          index = stuff.indexOf("[");
          const map_name = stuff.substring(0, index);
          const sub = stuff.substring(index + 1);
          index = sub.indexOf("]");
          const key = sub.substring(0, index);
          const field = sub.substring(index + 2);
          let value = "";
          try {
            if (map_name.length > 0 && key.length > 0) {
              if (field.length > 0) {
                value = constants[map_name][key][field];
              } else {
                value = constants[map_name][key];
              }
            }
          } catch (ex) {}
          cleaning = common_util.replaceAll(cleaning, `{constants_map.${stuff}}`, `${value}`);
        }
      }
      if (true) {
        while (cleaning.includes("{ugn=")) {
          // {ugn={formula_key="Bestial Strike Die",min=4}}
          // There's something besides the name that needs replacing.
          let index = cleaning.indexOf("{ugn={");
          const after = cleaning.substring(index + 6);
          index = after.indexOf("}}");
          const ugn_str = after.substring(0, index);
          // debugger;
          const ugn_obj = JSON.parse(`{${ugn_str}}`);
          const ugn = new UpgradableNumber(ugn_obj);
          let value = 0;
          value = ugn.base;
          cleaning = common_util.replaceAll(cleaning, `{ugn={${ugn_str}}}`, `${value}`);
        }
      }
      while (cleaning.includes("{potence_string.")) {
        // {potence_string.x.y.z} => {avg(xdy+z)} (xdy + z), if z === 0 then leave it out
        let index = cleaning.indexOf("{potence_string.");
        const after = cleaning.substring(index + 16);
        index = after.indexOf("}");
        let value = "";
        const str = after.substring(0, index);
        const pieces = str.split(".");
        const count = +pieces[0];
        const size = +pieces[1];
        let flat = pieces[2] === undefined || pieces[2].length === 0 ? 0 : +pieces[2];
        // if (isNaN(flat)) {
        //   if (obj instanceof CreatureInstance || obj instanceof Character) {
        //     switch (pieces[2]) {
        //       case "STR": flat = obj.current_ability_scores.STR; break;
        //       case "DEX": flat = obj.current_ability_scores.DEX; break;
        //       case "CON": flat = obj.current_ability_scores.CON; break;
        //       case "INT": flat = obj.current_ability_scores.INT; break;
        //       case "WIS": flat = obj.current_ability_scores.WIS; break;
        //       case "CHA": flat = obj.current_ability_scores.CHA; break;
        //     }
        //   }
        // }
        let drop_curly = false;
        if (!isNaN(count) && !isNaN(size) && !isNaN(flat)) {
          if (flat === 0) {
            value = `{avg(${count}d${size})} (${count}d${size})`;
          } else if (flat < 0) {
            value = `{avg(${count}d${size}-${flat})} (${count}d${size} - ${flat})`;
          } else {
            value = `{avg(${count}d${size}+${flat})} (${count}d${size} + ${flat})`;
          }
        } else if (!isNaN(count) && !isNaN(size) && pieces[2] === "{cfs" && pieces[4]) {
          // console.log(pieces);
          // console.log(str);
          // pieces[2]-pieces[4] probably was something like {cfs.current_ability_scores.STR
          value = `${count}d${size} + ${pieces[4]}`;
          drop_curly = true;
        }
        cleaning = common_util.replaceAll(cleaning, `{potence_string.${str}${drop_curly ? "}" : ""}}`, `${value}`);
      }
      while (cleaning.includes("{avg(")) {
        // There's something besides the name that needs replacing.
        let index = cleaning.indexOf("{avg(");
        let after = cleaning.substring(index + 5);
        index = after.indexOf(")}");
        if (index < 0) {
          console.error("AVERAGE ERROR!");
          break;
        }
        const roll_str = after.substring(0, index);
        try {
          let value = 0;
          let pieces = roll_str.split("d");
          const before = pieces[0];
          after = pieces[1];
          if (after) {
            const count = +before;
            let size = 0;
            let flat = 0;
            if (after.includes("+")) {
              pieces = after.split("+");
              size = +pieces[0];
              flat = +pieces[1];
            } else if (after.includes("-")) {
              pieces = after.split("-");
              size = +pieces[0];
              flat = +pieces[1] * -1;
            } else {
              size = +after;
            }
            value = Math.floor(count * ((size + 1) / 2) + flat);
            cleaning = common_util.replaceAll(cleaning, `{avg(${roll_str})}`, `${value}`);
          } else {
            cleaning = common_util.replaceAll(cleaning, `{avg(${roll_str})}`, "AVERAGE NOT FOUND!");
          }
        } catch (ex) {
          console.error(ex);
          debugger;
          cleaning = common_util.replaceAll(cleaning, `{avg(${roll_str})}`, "AVERAGE ERROR!");
        }
      }
      while (cleaning.includes("{sum(")) {
        // There's something besides the name that needs replacing.
        let index = cleaning.indexOf("{sum(");
        const after = cleaning.substring(index + 5);
        index = after.indexOf(")}");
        if (index < 0) {
          console.error("SUM ERROR!");
          break;
        }
        const sum_str = after.substring(0, index);
        try {
          let value = 0;
          const pieces = sum_str.split(",");
          for (const piece of pieces) {
            value += +piece;
          }
          cleaning = common_util.replaceAll(cleaning, `{sum(${sum_str})}`, `${value}`);
        } catch (ex) {
          console.error(ex);
          debugger;
          cleaning = common_util.replaceAll(cleaning, `{sum(${sum_str})}`, "SUM ERROR!");
        }
      }
      while (cleaning.includes("{list_of_type:")) {
        // There's something besides the name that needs replacing.
        // {list_of_type:data_type;[ids]}
        // {list_of_type:data_type;display_field;[ids]}
        let index = cleaning.indexOf("{list_of_type:");
        const after = cleaning.substring(index + 14);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        index = stuff.indexOf(";");
        const data_types_maybe = stuff.substring(0, index).split(",");
        const data_types = data_types_maybe.filter(o => isDataType(o)) as DataType[];
        if (data_types.length > 0) {
          await this.getSetOfObjects({ data_types }); // Make sure we've got the data
        }
        let ids = stuff.substring(index + 1);
        let value = "";
        let display_field = "";
        if (ids.indexOf(";") > -1) {
          // has a display_field specified.
          index = ids.indexOf(";");
          display_field = ids.substring(0, index);
          ids = ids.substring(index + 1);
        } 
        const use_ids = common_util.replaceAll(common_util.replaceAll(ids, "[", ""), "]", "").split(",").map(o => o.trim());
        let unused_ids = [...use_ids];
        
        for (const data_type of data_types) {
          let value_for_dt = "";
          try {
            let objects: ModelBase[] = [];
            if (this.smart_hash[data_type]) {
              objects = this.smart_hash[data_type];
            } else if (CONSTANTIZED_DATA_TYPES.includes(data_type)) {
              objects = this.getConstantizedObjects({ data_type });
            }
             
            objects = objects.filter(o => unused_ids.includes(o._id));
            if (display_field === "") {
              value_for_dt = objects.map(o => o.name).join(", ");
            } else {
              value_for_dt = (objects as any[]).map(o => o[display_field]).join(", ");
            }
            if (value_for_dt.length > 0) {
              value += (value.length > 0 ? ", " : "") + value_for_dt;
            }
            unused_ids = unused_ids.filter(o => !objects.some(o2 => o2._id === o));
          } catch (ex) {}
        }
        if (unused_ids.length > 0) {
          value += (value.length > 0 ? ", " : "") + unused_ids.join(", ");
        }
        cleaning = common_util.replaceAll(cleaning, `{list_of_type:${stuff}}`, `${value}`);
      }
      while (cleaning.includes("{class_table.")) {
        // {class_table.class_id}
        // Constructs a standard table for that class.
        // Columns: Level, Prof Bonus, Features, Columns from table (attached or separate), Spell Slots if caster
        let index = cleaning.indexOf("{class_table.");
        const after = cleaning.substring(index + 13);
        index = after.indexOf("}");
        const id = after.substring(0, index);
        let value = "";
        if (!this.smart_hash.game_class) {
          await this.getObjects({ data_type: "game_class" });
        }
        if (!this.smart_hash.subclass) {
          await this.getObjects({ data_type: "subclass" });
        }
        const game_class = this.smart_hash.game_class.find(o => o._id === id);
        const subclass = (this.smart_hash.subclass as Subclass[]).find(o => o.game_class_id === id); // We don't need a specific one, this is just to get the levels.
        if (game_class instanceof GameClass && subclass) {
          await this.loadObject(game_class);
          await this.loadObject(subclass);
          let table = await this.getTableAsync(game_class);
          if (!table) { 
            table = new Table(); // If it's not there then just use an empty one
          }
          if (table) {
            const class_table = table;
            let subclass_fb_levels = common_util.unique_array(subclass.feature_bases.filter(o => 
              o.display && o.multiclassing === 0 && o.required_condition_ids.length === 0).map(o => o.level));
            subclass_fb_levels = subclass_fb_levels.filter(o => o > Math.min(...subclass_fb_levels));
            const the_table: string[][] = [];
            const makeCommonTableHeader = () => {
              the_table.push([]);
              the_table[0].push("Level");
              the_table[0].push("Proficiency Bonus");
              the_table[0].push("Features");
              the_table[0].push(...class_table.fields);
            }
            const makeCommonTableRow = (i: number) => {
              the_table.push([]);
              the_table[the_table.length - 1].push(common_util.numberThString(i));
              the_table[the_table.length - 1].push(`+${common_util.proficiencyBonus(i)}`);
              const fbs = game_class.feature_bases.filter(o => 
                o.level === i && o.display && o.multiclassing === 0 && o.required_condition_ids.length === 0);
              let features_string = fbs.map(o => o.fb_name).join(", ");
              if (subclass_fb_levels.includes(i)) {
                if (features_string.length > 0) {
                  features_string += ", ";
                }
                features_string += "Subclass Feature";
              }
              if (features_string.length === 0) {
                features_string = "-";
              }
              the_table[the_table.length - 1].push(features_string);
              const entry = class_table.getEntry(i);
              if (entry) {
                for (const field of class_table.fields) {
                  const value = entry.data[field];
                  if (value === -1) {
                    the_table[the_table.length - 1].push(`Unlimited`); // -1 means it's unlimited.
                  } else {
                    the_table[the_table.length - 1].push(`${value}`);
                  }
                }
              }
            }
            // We've got the class and the table. Now we need to check for spellcasting.
            const { level, spellcasting_feature } = this.getSpellcastingFeatureForFS(game_class);
            let max_spell_level = 0;
            if (spellcasting_feature) {
              await this.getObjects({ data_type: "spell_slot_type" });
              const spellcasting_table = this.smart_hash.spell_slot_type.find(o => o._id === spellcasting_feature.table);
              if (spellcasting_table instanceof SpellSlotType) {
                const spellcasting_level = spellcasting_feature.level; // Full or Half, subclasses can be 1/3, and my code allows for 1/4.
                // We should have everything we need to construct the table, so let's put it together.
                makeCommonTableHeader();
                if (spellcasting_level === 1) {
                  max_spell_level = 9;
                } else if (spellcasting_level === 0.5) {
                  max_spell_level = 5;
                } else if (spellcasting_level === 1/3) {
                  max_spell_level = 4;
                } else if (spellcasting_level === 0.25) {
                  max_spell_level = 3;
                }
                for (let spell_level = 1; spell_level <= max_spell_level; ++spell_level) {
                  the_table[0].push(common_util.numberThString(spell_level));
                }
                for (let char_level = 1; char_level < 21; ++char_level) {
                  makeCommonTableRow(char_level);
                  if (level <= char_level) {
                    const caster_level = Math.ceil(char_level * spellcasting_level);
                    const entry = spellcasting_table.entryForLevel(caster_level);
                    if (entry) {
                      for (let spell_level = 1; spell_level <= max_spell_level; ++spell_level) {
                        const slots = entry.slots_per_level[spell_level];
                        if (slots > 0) {
                          the_table[char_level].push(`${slots}`);
                        } else {
                          the_table[char_level].push("-");
                        }
                      }
                    } else {
                      for (let spell_level = 1; spell_level <= max_spell_level; ++spell_level) {
                        the_table[char_level].push("-");
                      }
                    }
                  } else {
                    for (let spell_level = 1; spell_level <= max_spell_level; ++spell_level) {
                      the_table[char_level].push("-");
                    }
                  }
                }
              }
            } else {
              // Non-spellcaster, so don't need to worry about those columns.
              makeCommonTableHeader();
              for (let char_level = 1; char_level < 21; ++char_level) {
                makeCommonTableRow(char_level);
              }
            }
            if (the_table.length > 0) {
              // All the table data should be in table_hash now, so let's convert it into a string of an html table.
              let table_string = `<b>The ${game_class.name}</b>\n<table class="standard_table">\n`;
              if (max_spell_level > 0) {
                const columns = the_table[0].length;
                table_string += `<tr>\n<th colSpan="${columns - max_spell_level}"></th>\n<th colSpan="${max_spell_level}">-Spell Slots per Spell Level-</th>\n</tr>\n`;
              }
              table_string += "<tr>\n";
              for (let j = 0; j < the_table[0].length; ++j) {
                table_string += `<th>${the_table[0][j]}</th>\n`;
              }
              table_string += "</tr>\n";
              for (let i = 1; i < the_table.length; ++i) {
                table_string += "<tr>\n";
                for (let j = 0; j < the_table[i].length; ++j) {
                  table_string += `<td>${the_table[i][j]}</td>\n`;
                }
                table_string += "</tr>\n";
              }
              table_string += "</table>";
              value = table_string;
            }
          }
        }
        cleaning = common_util.replaceAll(cleaning, `{class_table.${id}}`, `${value}`);
      }
      while (cleaning.includes("{bonus_spells_table.")) {
        // {bonus_spells_table.fs_type.fs_id}
        // Constructs a Bonus Spells table for that selected FeatureSource. 
        // It will search the FS for a Bonus Spells feature, and create the table based on the spells on it.
        // Columns: Level, Prof Bonus, Features, Columns from table (attached or separate), Spell Slots if caster
        let index = cleaning.indexOf("{bonus_spells_table.");
        const after = cleaning.substring(index + 20);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        let value = "";
        const pieces = stuff.split(".");
        if (pieces.length === 2) {
          const data_type = pieces[0];
          if (isDataType(data_type)) {
            const id = pieces[1];
            if (!this.smart_hash[data_type]) {
              await this.getObjects({ data_type });
            }
            if (!this.smart_hash.spell) {
              await this.getObjects({ data_type: "spell" });
            }
            const fs = this.smart_hash[data_type].find(o => o._id === id);
            if (fs instanceof FeatureSource) {
              await this.loadObject(fs);
              const bonus_spells_features = fs.feature_bases
                .filter(o => o.feature_types.includes("Bonus Spells"))
                .flatMap(o => o.features)
                .filter(o => o.floating_feature && o.feature_type === "Bonus Spells")
                .map(o => o.floating_feature) as BonusSpells[];
              if (bonus_spells_features.length === 1) {
                const bonus_spells = bonus_spells_features[0];
                const the_table: string[][] = [];
                the_table.push([]);
                if (fs instanceof Subclass) {
                  the_table[0].push(`${fs.class_name} Level`);
                } else {
                  the_table[0].push(`Level`);
                }
                the_table[0].push("Spells");
                const levels = common_util.unique_array(bonus_spells.spells.map(o => o.level)).sort((a, b) => a - b);
                for (let i = 0; i < levels.length; ++i) {
                  const level = levels[i];
                  const spell_ids = bonus_spells.spells.filter(o => o.level === level).map(o => o.spell_id);
                  const spells = this.smart_hash.spell.filter(o => spell_ids.includes(o._id));
                  the_table.push([]);
                  the_table[i + 1].push(common_util.numberThString(level));
                  the_table[i + 1].push(spells.map(o => o.name).sort().join(", "));
                }
                if (the_table.length > 0) {
                  // All the table data should be in table_hash now, so let's convert it into a string of an html table.
                  let table_string = `<b>${fs.name} Spells</b>\n<table class="standard_table">\n`;
                  table_string += "<tr>\n";
                  for (let j = 0; j < the_table[0].length; ++j) {
                    table_string += `<th>${the_table[0][j]}</th>\n`;
                  }
                  table_string += "</tr>\n";
                  for (let i = 1; i < the_table.length; ++i) {
                    table_string += "<tr>\n";
                    for (let j = 0; j < the_table[i].length; ++j) {
                      table_string += `<td>${the_table[i][j]}</td>\n`;
                    }
                    table_string += "</tr>\n";
                  }
                  table_string += "</table>";
                  value = table_string;
                }
              }
            }
          }
        }
        cleaning = common_util.replaceAll(cleaning, `{bonus_spells_table.${stuff}}`, `${value}`);
      }
      while (cleaning.includes("{spell_list.")) {
        // {spell_list.${spell_list_class_id}${spell_list_source_book_ids.length > 0 ? "." + spell_list_source_book_ids.join(".") : ""}}
        // Displays the full spell list for the class, using only spells from the source books if they're present.
        let index = cleaning.indexOf("{spell_list.");
        const after = cleaning.substring(index + 12);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        let value = "";
        const pieces = stuff.split(".");
        if (pieces.length > 0) {
          const class_id = pieces[0];
          let source_book_ids: string[] = [];
          if (pieces.length > 1) {
            source_book_ids = pieces.filter(o => o !== class_id);
          }
          const gc = await this.getFullObject<GameClass>("game_class", class_id);
          if (gc) {
            value += `<h2>${gc.name} Spells</h2>\n`;
            const spell_list = await this.getSpellList(class_id);
            if (spell_list) {
              value += await this.getSpellListText(spell_list, source_book_ids);
            }
          }
        }
        cleaning = common_util.replaceAll(cleaning, `{spell_list.${stuff}}`, `${value}`);
      }
      // while (cleaning.includes("{spell_descriptions.")) {
      //   // {spell_descriptions.${source_book_id}}
      //   // Displays the spells in a source book in alphabetical order.
      //   let index = cleaning.indexOf("{spell_descriptions.");
      //   const after = cleaning.substring(index + 12);
      //   index = after.indexOf("}");
      //   const stuff = after.substring(0, index);
      //   let value = "";
      //   const pieces = stuff.split(".");
      //   if (pieces.length > 0) {
      //     const class_id = pieces[0];
      //     let source_book_ids: string[] = [];
      //     if (pieces.length > 1) {
      //       source_book_ids = pieces.filter(o => o !== class_id);
      //     }
      //     const gc = await this.getFullObject<GameClass>("game_class", class_id);
      //     if (gc) {
      //       value += `<h2>${gc.name} Spells</h2>\n`;
      //       const spell_list = await this.getSpellList(class_id);
      //       if (spell_list) {
      //         value += await this.getSpellListText(spell_list, source_book_ids);
      //       }
      //     }
      //   }
      //   cleaning = common_util.replaceAll(cleaning, `{spell_list.${stuff}}`, `${value}`);
      // }
      while (cleaning.includes("{creature_stat_block.")) {
        // {creature_stat_block.${creature._id}}
        let index = cleaning.indexOf("{creature_stat_block.");
        const after = cleaning.substring(index + 21);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        const creature = await this.getFullObject<Creature>("creature", stuff);
        const value = creature ? await this.formattedCreatureStatBlock(creature) : "CREATURE NOT FOUND!";
        cleaning = common_util.replaceAll(cleaning, `{creature_stat_block.${stuff}}`, `${value}`);
      }
      while (cleaning.includes("{feature_source_desc.")) {
        // {{feature_source_desc.${fs.data_type}.${fs._id}}
        let index = cleaning.indexOf("{feature_source_desc.");
        const after = cleaning.substring(index + 21);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        const pieces = stuff.split(".");
        let value = "FEATURE SOURCE NOT FOUND!";
        if (pieces.length === 2) {
          const data_type = pieces[0];
          if (isDataType(data_type)) {
            const id = pieces[1];
            const fs = await this.getFullObject<FeatureSource>(data_type, id);
            if (fs) {
              value = await this.formattedFeatureSource(fs);
            }
          }
        }
        cleaning = common_util.replaceAll(cleaning, `{feature_source_desc.${stuff}}`, `${value}`);
      }
      while (cleaning.includes("{spell_link.")) {
        // {spell_link.${spell._id}}
        let index = cleaning.indexOf("{spell_link.");
        const after = cleaning.substring(index + 12);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        const spell = await this.getFullObject<Spell>("spell", stuff);
        const value = spell ? `<a class="tooltip-hover spell-tooltip" href="${spell.linkURL}" onclick="window.linkClicked('spell','${spell._id}'); return false;">${spell.name}</a>` : "SPELL NOT FOUND!";
        cleaning = common_util.replaceAll(cleaning, `{spell_link.${stuff}}`, `${value}`);
      }
      while (cleaning.includes("{deity_table.")) {
        // {deity_table.${sb._id}.${pantheon}}
        let index = cleaning.indexOf("{deity_table.");
        const after = cleaning.substring(index + 13);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        const pieces = stuff.split(".");
        let value = "INVALID DEITY TABLE";
        if (pieces.length === 2) {
          const sb_id = pieces[0];
          const pantheon = pieces[1];
          
          const all_deities = await this.getObjects<Deity>({ data_type: "deity" });
          const these_deities = all_deities.filter(o => 
            o.source_book_ids.includes(sb_id) 
            && o.pantheons.includes(pantheon)
          ).sort((a, b) => a.name.localeCompare(b.name));
          
          value = `<table>/n`;
          value += "<tr><th>Deity</th><th>Alignment</th><th>Suggested Domains</th><th>Symbol</th></tr>\n";
          for (const deity of these_deities) {
            value += `<tr><td>${deity.name}</td><td>${deity.alignment_string}</td><td>${deity.divine_domains_string}</td><th>${deity.symbol}</td></tr>\n`;
          }
          value += "</table>\n";
        }
        cleaning = common_util.replaceAll(cleaning, `{deity_table.${stuff}}`, `${value}`);
      }
      if (cleaning.includes("Melee Weapon Attack:") &&
        cleaning.includes("to hit, reach 10 ft., one target. Hit:") &&
        cleaning.includes("piercing damage plus") &&
        cleaning.includes("psychic damage.")) {
        debugger;
      }
      while (cleaning.includes("{attack_string.")) {
        // {attack_string.STAT.z} => +{sum({cfs.proficiency_modifier},{cfs.current_ability_scores.STAT},z)} if z === 0 then leave it out
        let index = cleaning.indexOf("{attack_string.");
        const after = cleaning.substring(index + 15);
        index = after.indexOf("}");
        let value = "";
        const str = after.substring(0, index);
        const pieces = str.split(".");
        const stat = pieces[0];
        const flat = pieces[1] === undefined || pieces[1].length === 0 ? 0 : +pieces[1];
        if (ABILITY_SCORES.includes(stat) && !isNaN(flat)) {
          if (flat === 0) {
            value = `+ ${stat} + Proficiency Bonus`;
          } else {
            value = `+ ${stat} + Proficiency Bonus + ${flat}`;
          }
        }
        cleaning = common_util.replaceAll(cleaning, `{attack_string.${str}}`, `${value}`);
      }
      while (cleaning.includes("{dc_string.")) {
        // {dc_string.STAT.z} => {sum(8,{cfs.proficiency_modifier},{cfs.current_ability_scores.STAT},z)} if z === 0 then leave it out
        let index = cleaning.indexOf("{dc_string.");
        const after = cleaning.substring(index + 11);
        index = after.indexOf("}");
        let value = "";
        const str = after.substring(0, index);
        const pieces = str.split(".");
        // console.log(pieces);
        const stat = pieces[0];
        const flat = pieces[1] === undefined || pieces[1].length === 0 ? 0 : +pieces[1];
        if (ABILITY_SCORES.includes(stat) && !isNaN(flat)) {
          if (flat === 0) {
            value = `8 + ${stat} + Proficiency Bonus`;
          } else {
            value = `8 + ${stat} + Proficiency Bonus + ${flat}`;
          }
        }
        cleaning = common_util.replaceAll(cleaning, `{dc_string.${str}}`, `${value}`);
      }
      while (cleaning.includes("+?")) {
        // Check if the next thing after it is a '-'. If so then leave the plus. Otherwise remove it.
        let index = cleaning.indexOf("+?");
        const before = cleaning.substring(0, index);
        let next = cleaning[index + 2];
        let after = cleaning.substring(index + 2);
        if (next === " ") {
          next = cleaning[index + 3];
          after = cleaning.substring(index + 3);
        }
        if (next === "-") {
          cleaning = before + after;
        } else {
          after = cleaning.substring(index + 2);
          cleaning = before + "+" + after;
        }
      }
      return cleaning;
    }
    const cleaned = await clean_it(clean_me); 
    
    // console.log(cleaned);

    return cleaned;
  }
  
  getStringAndCFSWithReplacementsSimpleAsync = async ({
    clean_me, 
    constants,
    old_cfs,
  }: {
    clean_me: string, 
    constants?: any,
    old_cfs?: Creature
  }) => {
    let cfs: Creature | null = null;
    const clean_it = async (cleaning: string) => {
      if (constants) {
        while (cleaning.includes("{constants_map.")) {
          // There's something besides the name that needs replacing.
          let index = cleaning.indexOf("{constants_map.");
          const after = cleaning.substring(index + 15);
          index = after.indexOf("}");
          const stuff = after.substring(0, index);
          index = stuff.indexOf("[");
          const map_name = stuff.substring(0, index);
          const sub = stuff.substring(index + 1);
          index = sub.indexOf("]");
          const key = sub.substring(0, index);
          const field = sub.substring(index + 2);
          let value = "";
          try {
            if (map_name.length > 0 && key.length > 0) {
              if (field.length > 0) {
                value = constants[map_name][key][field];
              } else {
                value = constants[map_name][key];
              }
            }
          } catch (ex) {}
          cleaning = common_util.replaceAll(cleaning, `{constants_map.${stuff}}`, `${value}`);
        }
      }
      if (true) {
        while (cleaning.includes("{ugn=")) {
          // {ugn={formula_key="Bestial Strike Die",min=4}}
          // There's something besides the name that needs replacing.
          let index = cleaning.indexOf("{ugn={");
          const after = cleaning.substring(index + 6);
          index = after.indexOf("}}");
          const ugn_str = after.substring(0, index);
          // debugger;
          const ugn_obj = JSON.parse(`{${ugn_str}}`);
          const ugn = new UpgradableNumber(ugn_obj);
          let value = 0;
          value = ugn.base;
          cleaning = common_util.replaceAll(cleaning, `{ugn={${ugn_str}}}`, `${value}`);
        }
      }
      while (cleaning.includes("{potence_string.")) {
        // {potence_string.x.y.z} => {avg(xdy+z)} (xdy + z), if z === 0 then leave it out
        let index = cleaning.indexOf("{potence_string.");
        const after = cleaning.substring(index + 16);
        index = after.indexOf("}");
        let value = "";
        const str = after.substring(0, index);
        const pieces = str.split(".");
        const count = +pieces[0];
        const size = +pieces[1];
        let flat = pieces[2] === undefined || pieces[2].length === 0 ? 0 : +pieces[2];
        if (isNaN(flat)) {
          if (cfs) {
            switch (pieces[2]) {
              case "STR": flat = cfs.current_ability_scores.STR; break;
              case "DEX": flat = cfs.current_ability_scores.DEX; break;
              case "CON": flat = cfs.current_ability_scores.CON; break;
              case "INT": flat = cfs.current_ability_scores.INT; break;
              case "WIS": flat = cfs.current_ability_scores.WIS; break;
              case "CHA": flat = cfs.current_ability_scores.CHA; break;
            }
          }
        }
        let drop_curly = false;
        if (!isNaN(count) && !isNaN(size) && !isNaN(flat)) {
          if (flat === 0) {
            value = `{avg(${count}d${size})} (${count}d${size})`;
          } else if (flat < 0) {
            value = `{avg(${count}d${size}-${flat})} (${count}d${size} - ${flat})`;
          } else {
            value = `{avg(${count}d${size}+${flat})} (${count}d${size} + ${flat})`;
          }
        } else if (!isNaN(count) && !isNaN(size) && pieces[2] === "{cfs" && pieces[4]) {
          // console.log(pieces);
          // console.log(str);
          // pieces[2]-pieces[4] probably was something like {cfs.current_ability_scores.STR
          value = `${count}d${size} + ${pieces[4]}`;
          drop_curly = true;
        }
        cleaning = common_util.replaceAll(cleaning, `{potence_string.${str}${drop_curly ? "}" : ""}}`, `${value}`);
      }
      while (cleaning.includes("{avg(")) {
        // There's something besides the name that needs replacing.
        let index = cleaning.indexOf("{avg(");
        let after = cleaning.substring(index + 5);
        index = after.indexOf(")}");
        if (index < 0) {
          console.error("AVERAGE ERROR!");
          break;
        }
        const roll_str = after.substring(0, index);
        try {
          let value = 0;
          let pieces = roll_str.split("d");
          const before = pieces[0];
          after = pieces[1];
          if (after) {
            const count = +before;
            let size = 0;
            let flat = 0;
            if (after.includes("+")) {
              pieces = after.split("+");
              size = +pieces[0];
              flat = +pieces[1];
            } else if (after.includes("-")) {
              pieces = after.split("-");
              size = +pieces[0];
              flat = +pieces[1] * -1;
            } else {
              size = +after;
            }
            value = Math.floor(count * ((size + 1) / 2) + flat);
            cleaning = common_util.replaceAll(cleaning, `{avg(${roll_str})}`, `${value}`);
          } else {
            cleaning = common_util.replaceAll(cleaning, `{avg(${roll_str})}`, "AVERAGE NOT FOUND!");
          }
        } catch (ex) {
          console.error(ex);
          debugger;
          cleaning = common_util.replaceAll(cleaning, `{avg(${roll_str})}`, "AVERAGE ERROR!");
        }
      }
      while (cleaning.includes("{sum(")) {
        // There's something besides the name that needs replacing.
        let index = cleaning.indexOf("{sum(");
        const after = cleaning.substring(index + 5);
        index = after.indexOf(")}");
        if (index < 0) {
          console.error("SUM ERROR!");
          break;
        }
        const sum_str = after.substring(0, index);
        try {
          let value = 0;
          const pieces = sum_str.split(",");
          for (const piece of pieces) {
            value += +piece;
          }
          cleaning = common_util.replaceAll(cleaning, `{sum(${sum_str})}`, `${value}`);
        } catch (ex) {
          console.error(ex);
          debugger;
          cleaning = common_util.replaceAll(cleaning, `{sum(${sum_str})}`, "SUM ERROR!");
        }
      }
      while (cleaning.includes("{list_of_type:")) {
        // There's something besides the name that needs replacing.
        // {list_of_type:data_type;[ids]}
        // {list_of_type:data_type;display_field;[ids]}
        let index = cleaning.indexOf("{list_of_type:");
        const after = cleaning.substring(index + 14);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        index = stuff.indexOf(";");
        const data_types = stuff.substring(0, index).split(",") as DataType[];
        await this.getSetOfObjects({ data_types }); // Make sure we've got the data
        let ids = stuff.substring(index + 1);
        let value = "";
        let display_field = "";
        if (ids.indexOf(";") > -1) {
          // has a display_field specified.
          index = ids.indexOf(";");
          display_field = ids.substring(0, index);
          ids = ids.substring(index + 1);
        } 
        const use_ids = common_util.replaceAll(common_util.replaceAll(ids, "[", ""), "]", "").split(",").map(o => o.trim());
        let unused_ids = [...use_ids];
        
        for (const data_type of data_types) {
          let value_for_dt = "";
          try {
            let objects: ModelBase[] = [];
            if (this.smart_hash[data_type]) {
              objects = this.smart_hash[data_type];
            } else if (CONSTANTIZED_DATA_TYPES.includes(data_type)) {
              objects = this.getConstantizedObjects({ data_type });
            }
             
            objects = objects.filter(o => unused_ids.includes(o._id));
            if (display_field === "") {
              value_for_dt = objects.map(o => o.name).join(", ");
            } else {
              value_for_dt = (objects as any[]).map(o => o[display_field]).join(", ");
            }
            if (value_for_dt.length > 0) {
              value += (value.length > 0 ? ", " : "") + value_for_dt;
            }
            unused_ids = unused_ids.filter(o => !objects.some(o2 => o2._id === o));
          } catch (ex) {}
        }
        if (unused_ids.length > 0) {
          value += (value.length > 0 ? ", " : "") + unused_ids.join(", ");
        }
        cleaning = common_util.replaceAll(cleaning, `{list_of_type:${stuff}}`, `${value}`);
      }
      while (cleaning.includes("{class_table.")) {
        // {class_table.class_id}
        // Constructs a standard table for that class.
        // Columns: Level, Prof Bonus, Features, Columns from table (attached or separate), Spell Slots if caster
        let index = cleaning.indexOf("{class_table.");
        const after = cleaning.substring(index + 13);
        index = after.indexOf("}");
        const id = after.substring(0, index);
        let value = "";
        if (!this.smart_hash.game_class) {
          await this.getObjects({ data_type: "game_class" });
        }
        if (!this.smart_hash.subclass) {
          await this.getObjects({ data_type: "subclass" });
        }
        const game_class = this.smart_hash.game_class.find(o => o._id === id);
        const subclass = (this.smart_hash.subclass as Subclass[]).find(o => o.game_class_id === id); // We don't need a specific one, this is just to get the levels.
        if (game_class instanceof GameClass && subclass) {
          await this.loadObject(game_class);
          await this.loadObject(subclass);
          let table = await this.getTableAsync(game_class);
          if (!table) { 
            table = new Table(); // If it's not there then just use an empty one
          }
          if (table) {
            const class_table = table;
            let subclass_fb_levels = common_util.unique_array(subclass.feature_bases.filter(o => 
              o.display && o.multiclassing === 0 && o.required_condition_ids.length === 0).map(o => o.level));
            subclass_fb_levels = subclass_fb_levels.filter(o => o > Math.min(...subclass_fb_levels));
            const the_table: string[][] = [];
            const makeCommonTableHeader = () => {
              the_table.push([]);
              the_table[0].push("Level");
              the_table[0].push("Proficiency Bonus");
              the_table[0].push("Features");
              the_table[0].push(...class_table.fields);
            }
            const makeCommonTableRow = (i: number) => {
              the_table.push([]);
              the_table[the_table.length - 1].push(common_util.numberThString(i));
              the_table[the_table.length - 1].push(`+${common_util.proficiencyBonus(i)}`);
              const fbs = game_class.feature_bases.filter(o => 
                o.level === i && o.display && o.multiclassing === 0 && o.required_condition_ids.length === 0);
              let features_string = fbs.map(o => o.fb_name).join(", ");
              if (subclass_fb_levels.includes(i)) {
                if (features_string.length > 0) {
                  features_string += ", ";
                }
                features_string += "Subclass Feature";
              }
              if (features_string.length === 0) {
                features_string = "-";
              }
              the_table[the_table.length - 1].push(features_string);
              const entry = class_table.getEntry(i);
              if (entry) {
                for (const field of class_table.fields) {
                  const value = entry.data[field];
                  if (value === -1) {
                    the_table[the_table.length - 1].push(`Unlimited`); // -1 means it's unlimited.
                  } else {
                    the_table[the_table.length - 1].push(`${value}`);
                  }
                }
              }
            }
            // We've got the class and the table. Now we need to check for spellcasting.
            const { level, spellcasting_feature } = this.getSpellcastingFeatureForFS(game_class);
            let max_spell_level = 0;
            if (spellcasting_feature) {
              await this.getObjects({ data_type: "spell_slot_type" });
              const spellcasting_table = this.smart_hash.spell_slot_type.find(o => o._id === spellcasting_feature.table);
              if (spellcasting_table instanceof SpellSlotType) {
                const spellcasting_level = spellcasting_feature.level; // Full or Half, subclasses can be 1/3, and my code allows for 1/4.
                // We should have everything we need to construct the table, so let's put it together.
                makeCommonTableHeader();
                if (spellcasting_level === 1) {
                  max_spell_level = 9;
                } else if (spellcasting_level === 0.5) {
                  max_spell_level = 5;
                } else if (spellcasting_level === 1/3) {
                  max_spell_level = 4;
                } else if (spellcasting_level === 0.25) {
                  max_spell_level = 3;
                }
                for (let spell_level = 1; spell_level <= max_spell_level; ++spell_level) {
                  the_table[0].push(common_util.numberThString(spell_level));
                }
                for (let char_level = 1; char_level < 21; ++char_level) {
                  makeCommonTableRow(char_level);
                  if (level <= char_level) {
                    const caster_level = Math.ceil(char_level * spellcasting_level);
                    const entry = spellcasting_table.entryForLevel(caster_level);
                    if (entry) {
                      for (let spell_level = 1; spell_level <= max_spell_level; ++spell_level) {
                        const slots = entry.slots_per_level[spell_level];
                        if (slots > 0) {
                          the_table[char_level].push(`${slots}`);
                        } else {
                          the_table[char_level].push("-");
                        }
                      }
                    } else {
                      for (let spell_level = 1; spell_level <= max_spell_level; ++spell_level) {
                        the_table[char_level].push("-");
                      }
                    }
                  } else {
                    for (let spell_level = 1; spell_level <= max_spell_level; ++spell_level) {
                      the_table[char_level].push("-");
                    }
                  }
                }
              }
            } else {
              // Non-spellcaster, so don't need to worry about those columns.
              makeCommonTableHeader();
              for (let char_level = 1; char_level < 21; ++char_level) {
                makeCommonTableRow(char_level);
              }
            }
            if (the_table.length > 0) {
              // All the table data should be in table_hash now, so let's convert it into a string of an html table.
              let table_string = `<b>The ${game_class.name}</b>\n<table class="standard_table">\n`;
              if (max_spell_level > 0) {
                const columns = the_table[0].length;
                table_string += `<tr>\n<th colSpan="${columns - max_spell_level}"></th>\n<th colSpan="${max_spell_level}">-Spell Slots per Spell Level-</th>\n</tr>\n`;
              }
              table_string += "<tr>\n";
              for (let j = 0; j < the_table[0].length; ++j) {
                table_string += `<th>${the_table[0][j]}</th>\n`;
              }
              table_string += "</tr>\n";
              for (let i = 1; i < the_table.length; ++i) {
                table_string += "<tr>\n";
                for (let j = 0; j < the_table[i].length; ++j) {
                  table_string += `<td>${the_table[i][j]}</td>\n`;
                }
                table_string += "</tr>\n";
              }
              table_string += "</table>";
              value = table_string;
            }
          }
        }
        cleaning = common_util.replaceAll(cleaning, `{class_table.${id}}`, `${value}`);
      }
      while (cleaning.includes("{bonus_spells_table.")) {
        // {bonus_spells_table.fs_type.fs_id}
        // Constructs a Bonus Spells table for that selected FeatureSource. 
        // It will search the FS for a Bonus Spells feature, and create the table based on the spells on it.
        // Columns: Level, Prof Bonus, Features, Columns from table (attached or separate), Spell Slots if caster
        let index = cleaning.indexOf("{bonus_spells_table.");
        const after = cleaning.substring(index + 20);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        let value = "";
        const pieces = stuff.split(".");
        if (pieces.length === 2) {
          const data_type = pieces[0];
          if (isDataType(data_type)) {
            const id = pieces[1];
            if (!this.smart_hash[data_type]) {
              await this.getObjects({ data_type });
            }
            if (!this.smart_hash.spell) {
              await this.getObjects({ data_type: "spell" });
            }
            const fs = this.smart_hash[data_type].find(o => o._id === id);
            if (fs instanceof FeatureSource) {
              await this.loadObject(fs);
              const bonus_spells_features = fs.feature_bases
                .filter(o => o.feature_types.includes("Bonus Spells"))
                .flatMap(o => o.features)
                .filter(o => o.floating_feature && o.feature_type === "Bonus Spells")
                .map(o => o.floating_feature) as BonusSpells[];
              if (bonus_spells_features.length === 1) {
                const bonus_spells = bonus_spells_features[0];
                const the_table: string[][] = [];
                the_table.push([]);
                if (fs instanceof Subclass) {
                  the_table[0].push(`${fs.class_name} Level`);
                } else {
                  the_table[0].push(`Level`);
                }
                the_table[0].push("Spells");
                const levels = common_util.unique_array(bonus_spells.spells.map(o => o.level)).sort((a, b) => a - b);
                for (let i = 0; i < levels.length; ++i) {
                  const level = levels[i];
                  const spell_ids = bonus_spells.spells.filter(o => o.level === level).map(o => o.spell_id);
                  const spells = this.smart_hash.spell.filter(o => spell_ids.includes(o._id));
                  the_table.push([]);
                  the_table[i + 1].push(common_util.numberThString(level));
                  the_table[i + 1].push(spells.map(o => o.name).sort().join(", "));
                }
                if (the_table.length > 0) {
                  // All the table data should be in table_hash now, so let's convert it into a string of an html table.
                  let table_string = `<b>${fs.name} Spells</b>\n<table class="standard_table">\n`;
                  table_string += "<tr>\n";
                  for (let j = 0; j < the_table[0].length; ++j) {
                    table_string += `<th>${the_table[0][j]}</th>\n`;
                  }
                  table_string += "</tr>\n";
                  for (let i = 1; i < the_table.length; ++i) {
                    table_string += "<tr>\n";
                    for (let j = 0; j < the_table[i].length; ++j) {
                      table_string += `<td>${the_table[i][j]}</td>\n`;
                    }
                    table_string += "</tr>\n";
                  }
                  table_string += "</table>";
                  value = table_string;
                }
              }
            }
          }
        }
        cleaning = common_util.replaceAll(cleaning, `{bonus_spells_table.${stuff}}`, `${value}`);
      }
      while (cleaning.includes("{spell_list.")) {
        // {spell_list.${spell_list_class_id}${spell_list_source_book_ids.length > 0 ? "." + spell_list_source_book_ids.join(".") : ""}}
        // Displays the full spell list for the class, using only spells from the source books if they're present.
        let index = cleaning.indexOf("{spell_list.");
        const after = cleaning.substring(index + 12);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        let value = "";
        const pieces = stuff.split(".");
        if (pieces.length > 0) {
          const class_id = pieces[0];
          let source_book_ids: string[] = [];
          if (pieces.length > 1) {
            source_book_ids = pieces.filter(o => o !== class_id);
          }
          const gc = await this.getFullObject<GameClass>("game_class", class_id);
          if (gc) {
            value += `<h2>${gc.name} Spells</h2>\n`;
            const spell_list = await this.getSpellList(class_id);
            if (spell_list) {
              value += await this.getSpellListText(spell_list, source_book_ids);
            }
          }
        }
        cleaning = common_util.replaceAll(cleaning, `{spell_list.${stuff}}`, `${value}`);
      }
      while (cleaning.includes("{creature_stat_block.")) {
        // {creature_stat_block.${creature._id}}
        let index = cleaning.indexOf("{creature_stat_block.");
        const after = cleaning.substring(index + 21);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        if (cfs) {
          console.log(cfs.fbs[0].single_ff);
        }
        const creature = await this.getFullObject<Creature>("creature", stuff);
        if (creature) {
          console.log(creature.fbs[0].single_ff);
        }
        cfs = creature;
        const value = creature ? await this.formattedCreatureStatBlock(creature) : "CREATURE NOT FOUND!";
        cleaning = common_util.replaceAll(cleaning, `{creature_stat_block.${stuff}}`, `${value}`);
      }
      while (cleaning.includes("{feature_source_desc.")) {
        // {{feature_source_desc.${fs.data_type}.${fs._id}}
        let index = cleaning.indexOf("{feature_source_desc.");
        const after = cleaning.substring(index + 21);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        const pieces = stuff.split(".");
        let value = "FEATURE SOURCE NOT FOUND!";
        if (pieces.length === 2) {
          const data_type = pieces[0];
          if (isDataType(data_type)) {
            const id = pieces[1];
            const fs = await this.getFullObject<FeatureSource>(data_type, id);
            if (fs) {
              value = await this.formattedFeatureSource(fs);
            }
          }
        }
        cleaning = common_util.replaceAll(cleaning, `{feature_source_desc.${stuff}}`, `${value}`);
      }
      while (cleaning.includes("{spell_link.")) {
        // {spell_link.${spell._id}}
        let index = cleaning.indexOf("{spell_link.");
        const after = cleaning.substring(index + 12);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        const spell = await this.getFullObject<Spell>("spell", stuff);
        const value = spell ? `<a class="tooltip-hover spell-tooltip" href="${spell.linkURL}" onclick="window.linkClicked('spell','${spell._id}'); return false;">${spell.name}</a>` : "SPELL NOT FOUND!";
        cleaning = common_util.replaceAll(cleaning, `{spell_link.${stuff}}`, `${value}`);
      }
      while (cleaning.includes("{deity_table.")) {
        // {deity_table.${sb._id}.${pantheon}}
        let index = cleaning.indexOf("{deity_table.");
        const after = cleaning.substring(index + 13);
        index = after.indexOf("}");
        const stuff = after.substring(0, index);
        const pieces = stuff.split(".");
        let value = "INVALID DEITY TABLE";
        if (pieces.length === 2) {
          const sb_id = pieces[0];
          const pantheon = pieces[1];
          
          const all_deities = await this.getObjects<Deity>({ data_type: "deity" });
          const these_deities = all_deities.filter(o => 
            o.source_book_ids.includes(sb_id) 
            && o.pantheons.includes(pantheon)
          ).sort((a, b) => a.name.localeCompare(b.name));
          
          value = `<table>/n`;
          value += "<tr><th>Deity</th><th>Alignment</th><th>Suggested Domains</th><th>Symbol</th></tr>\n";
          for (const deity of these_deities) {
            value += `<tr><td>${deity.name}</td><td>${deity.alignment_string}</td><td>${deity.divine_domains_string}</td><th>${deity.symbol}</td></tr>\n`;
          }
          value += "</table>\n";
        }
        cleaning = common_util.replaceAll(cleaning, `{deity_table.${stuff}}`, `${value}`);
      }
      if (cleaning.includes("Melee Weapon Attack:") &&
        cleaning.includes("to hit, reach 10 ft., one target. Hit:") &&
        cleaning.includes("piercing damage plus") &&
        cleaning.includes("psychic damage.")) {
        debugger;
      }
      if (!cfs) {
        // If we have a cfs, then it will be processed elsewhere better than we do it here.
        while (cleaning.includes("{attack_string.")) {
          // {attack_string.STAT.z} => +{sum({cfs.proficiency_modifier},{cfs.current_ability_scores.STAT},z)} if z === 0 then leave it out
          let index = cleaning.indexOf("{attack_string.");
          const after = cleaning.substring(index + 15);
          index = after.indexOf("}");
          let value = "";
          const str = after.substring(0, index);
          const pieces = str.split(".");
          const stat = pieces[0];
          const flat = pieces[1] === undefined || pieces[1].length === 0 ? 0 : +pieces[1];
          if (ABILITY_SCORES.includes(stat) && !isNaN(flat)) {
            if (flat === 0) {
              value = `+ ${stat} + Proficiency Bonus`;
            } else {
              value = `+ ${stat} + Proficiency Bonus + ${flat}`;
            }
          }
          cleaning = common_util.replaceAll(cleaning, `{attack_string.${str}}`, `${value}`);
        }
        while (cleaning.includes("{dc_string.")) {
          // {dc_string.STAT.z} => {sum(8,{cfs.proficiency_modifier},{cfs.current_ability_scores.STAT},z)} if z === 0 then leave it out
          let index = cleaning.indexOf("{dc_string.");
          const after = cleaning.substring(index + 11);
          index = after.indexOf("}");
          let value = "";
          const str = after.substring(0, index);
          const pieces = str.split(".");
          // console.log(pieces);
          const stat = pieces[0];
          const flat = pieces[1] === undefined || pieces[1].length === 0 ? 0 : +pieces[1];
          if (ABILITY_SCORES.includes(stat) && !isNaN(flat)) {
            if (flat === 0) {
              value = `8 + ${stat} + Proficiency Bonus`;
            } else {
              value = `8 + ${stat} + Proficiency Bonus + ${flat}`;
            }
          }
          cleaning = common_util.replaceAll(cleaning, `{dc_string.${str}}`, `${value}`);
        }
      }
      while (cleaning.includes("+?")) {
        // Check if the next thing after it is a '-'. If so then leave the plus. Otherwise remove it.
        let index = cleaning.indexOf("+?");
        const before = cleaning.substring(0, index);
        let next = cleaning[index + 2];
        let after = cleaning.substring(index + 2);
        if (next === " ") {
          next = cleaning[index + 3];
          after = cleaning.substring(index + 3);
        }
        if (next === "-") {
          cleaning = before + after;
        } else {
          after = cleaning.substring(index + 2);
          cleaning = before + "+" + after;
        }
      }
      return cleaning;
    }
    const cleaned = await clean_it(clean_me); 
    
    // console.log(cleaned);

    return { cleaned, cfs };
  }

  /**
   * This one works the same as above, 
   * but has the object to use, 
   * so puts the values in instead of a description.
   */
  getStringWithReplacements2({
    clean_me, 
    backup = "{obj.name}", 
    obj,
    constants
  }: {
    clean_me: string, 
    backup?: string, 
    obj: any,
    constants?: any
  }): string {
    // if (obj instanceof Subclass && obj.name.includes("Aberrant")) {
    //   debugger;
    // }
    const obj_as_any = obj as any;
    const getValue = (field: any) => {
      const value = obj_as_any[field];
      if (value !== undefined) {
        return value;
      }
      // console.error("get_string_with_replacements_model_base error");
      // debugger;
      return "VALUE NOT FOUND!";
    }
    const clean_it = (clean_me2: string) => {
      while (clean_me2.includes("{obj.")) {
        // There's something besides the name that needs replacing.
        let index = clean_me2.indexOf("{obj.");
        const after = clean_me2.substring(index + 5);
        index = after.indexOf("}");
        const field = after.substring(0, index);
        const value = getValue(field); 
        clean_me2 = common_util.replaceAll(clean_me2, `{obj.${field}}`, `${value}`);
      }
      clean_me2 = this.getStringWithReplacementsSimple({ clean_me: clean_me2, constants });
      return clean_me2;
    }
    let cleaned = clean_it(clean_me); 
    if (cleaned.includes("VALUE NOT FOUND!") || cleaned === "") {
      if (clean_me === backup && cleaned !== "") {
        cleaned = clean_it("{obj.name}");
      } else {
        cleaned = clean_it(backup); 
      }
    }

    return cleaned;
  }

  getSpellListsForSpell = async (spell: Spell, source_book_ids: string[] = []) => {
    const all_spell_lists = await this.getObjects<SpellList>({ data_type: "spell_list" });
    const spell_lists: SpellList[] = [];
    for (const spell_list of all_spell_lists) {
      if (source_book_ids.length > 0 && 
        (!common_util.anyOverlaps(source_book_ids, spell_list.limit_sbids) 
          || !common_util.anyOverlaps(source_book_ids, spell_list.source_book_ids))) {
        continue;
      }
      const fits = source_book_ids.length > 0 ? 
        source_book_ids.some(sb_id => this.fitSpellList({ spell, sb_id, spell_list, ignore_variance: true })) : 
        this.fitSpellList({ spell, spell_list, ignore_variance: true });
      if (fits) {
        spell_lists.push(spell_list);
      }
    }
    return spell_lists;
  }

  getSpellListText = async (spell_list: SpellList, source_book_ids: string[] = []) => {
    let value = "";
    if (!this.smart_hash.spell) {
      await this.getObjects({ data_type: "spell" });
    }
    let spells = this.getSpellsForLimits({ limits: spell_list.limits });
    if (source_book_ids.length > 0) {
      spells = spells.filter(o => common_util.anyOverlaps(o.source_book_ids, source_book_ids));
    }
    for (const spell_level of SPELL_LEVELS_USING_TYPE) {
      const spells_of_level = spells.filter(o => o.level === spell_level);
      if (spells_of_level.length > 0) {
        if (spell_level === 0) {
          value += "<h3>Cantrips (0 Level)</h3>\n";
        } else {
          value += `<h3>${common_util.numberThString(spell_level)} Level</h3>\n`;
        }
        value += spells_of_level.map(o => {
          return o.name;
        }).sort().join("\n");
      }
    }
    return value;
  }

  getSpellLimitText = async (limit: SpellLimitation, source_book_ids: string[] = []) => {
    let value = "";
    if (!this.smart_hash.spell) {
      await this.getObjects({ data_type: "spell" });
    }
    let spells = this.getSpellsForLimit({ limit, ignore_variance: true });
    if (source_book_ids.length > 0) {
      spells = spells.filter(o => common_util.anyOverlaps(o.source_book_ids, source_book_ids));
    }
    for (const spell_level of SPELL_LEVELS_USING_TYPE) {
      const spells_of_level = spells.filter(o => o.level === spell_level);
      if (spells_of_level.length > 0) {
        if (spell_level === 0) {
          value += "<h3>Cantrips (0 Level)</h3>\n";
        } else {
          value += `<h3>${common_util.numberThString(spell_level)} Level</h3>\n`;
        }
        value += spells_of_level.map(o => {
          return o.name;
        }).sort().join("\n");
      }
    }
    return value;
  }

  getSpellList = async (class_id: string) => {
    const gc = await this.getFullObject<GameClass>("game_class", class_id);
    if (gc) {
      const spellcasting_feature = gc.getSpellcastingFeature();
      if (spellcasting_feature) {
        await this.loadObject(spellcasting_feature);
        if (!this.smart_hash.spell_list) {
          await this.getObjects({ data_type: "spell_list" });
        }
        const spell_lists = this.smart_hash.spell_list.filter(o => 
          spellcasting_feature.spell_list_ids.includes(o._id));
        if (spell_lists.length > 0) {
          if (spell_lists.length > 1) {
            console.error(`Multiple Spell Lists for class: ${gc.name} (${class_id})`);
          }
          if (spell_lists[0] instanceof SpellList) {
            return spell_lists[0];
          }
        }
      }
    }
    return undefined;
  }
  
  formattedCreatureStatBlock = async (creature: Creature) => {
    await this.loadObject(creature);
    let str = `<div class="stat-block">`;
    str += `<h4 class="compendium-hr">${creature.name}</h4>`;
    str += `<p>${creature.size_line}</p>`;
    str += `<p><strong>AC</strong> ${creature.armor_class_string}<strong style="margin-left: 30px">Initiative</strong> ${creature.initiative_string}</p>`;
    const hd_string = creature.hit_dice_string;
    str += `<p><strong>HP</strong> ${hd_string}</p>`;
    str += `<p><strong>Speed</strong> ${creature.speed_string}</p>`;
    str += `<div class="stats">`;
    str += `<table class="physical abilities-saves">\n`;
    str += `<thead>\n`;
    str += `<tr><th>&nbsp;</th><th>&nbsp;</th><th>Mod</th><th>Save</th></tr>\n`;
    str += `</thead>\n`;
    str += `<tbody>\n`;
    str += `<tr><th>STR</th><td>${creature.stat("STR")}</td><td>${common_util.addPlusMaybe(creature.stat_mod("STR"))}</td><td>${common_util.addPlusMaybe(creature.saving_throw("STR"))}</td></tr>\n`;
    str += `<tr><th>DEX</th><td>${creature.stat("DEX")}</td><td>${common_util.addPlusMaybe(creature.stat_mod("DEX"))}</td><td>${common_util.addPlusMaybe(creature.saving_throw("DEX"))}</td></tr>\n`;
    str += `<tr><th>CON</th><td>${creature.stat("CON")}</td><td>${common_util.addPlusMaybe(creature.stat_mod("CON"))}</td><td>${common_util.addPlusMaybe(creature.saving_throw("CON"))}</td></tr>\n`;
    str += `</tbody>\n`;
    str += `</table>`;
    str += `<table class="mental abilities-saves">\n`;
    str += `<thead>\n`;
    str += `<tr><th>&nbsp;</th><th>&nbsp;</th><th>Mod</th><th>Save</th></tr>\n`;
    str += `</thead>\n`;
    str += `<tbody>\n`;
    str += `<tr><th>INT</th><td>${creature.stat("INT")}</td><td>${common_util.addPlusMaybe(creature.stat_mod("INT"))}</td><td>${common_util.addPlusMaybe(creature.saving_throw("INT"))}</td></tr>\n`;
    str += `<tr><th>WIS</th><td>${creature.stat("WIS")}</td><td>${common_util.addPlusMaybe(creature.stat_mod("WIS"))}</td><td>${common_util.addPlusMaybe(creature.saving_throw("WIS"))}</td></tr>\n`;
    str += `<tr><th>CHA</th><td>${creature.stat("CHA")}</td><td>${common_util.addPlusMaybe(creature.stat_mod("CHA"))}</td><td>${common_util.addPlusMaybe(creature.saving_throw("CHA"))}</td></tr>\n`;
    str += `</tbody>\n`;
    str += `</table>`;
    str += `</div>`;
    const entry = creature.attributes_table.getEntry(creature.challenge_rating);
    if (entry) {
      if (creature.skills_string.length > 0) {
        str += `<p><strong>Skills</strong> ${creature.skills_string}</p>`;
      }
      if (entry.vulnerabilities_string.length > 0) {
        str += `<p><strong>Vulnerabilities</strong> ${entry.vulnerabilities_string}</p>`;
      }
      if (entry.resistances_string.length > 0) {
        str += `<p><strong>Resistances</strong> ${entry.resistances_string}</p>`;
      }
      if (entry.immunities_string.length > 0) {
        if (entry.condition_immunities.length > 0) {
          str += `<p><strong>Immunities</strong> ${entry.immunities_string}; ${entry.condition_immunities_string}</p>`;
        } else {
          str += `<p><strong>Immunities</strong> ${entry.immunities_string}</p>`;
        }
      } else if (entry.condition_immunities.length > 0) {
        str += `<p><strong>Immunities</strong> ${entry.condition_immunities_string}</p>`;
      }
      if (entry.senses_string.length > 0) {
        str += `<p><strong>Senses</strong> ${entry.senses_string}; Passive Perception ${creature.passive_perception}</p>`;
      } else {
        str += `<p><strong>Senses</strong> Passive Perception ${creature.passive_perception}</p>`;
      }
      str += `<p><strong>Languages</strong> ${entry.languages}</p>`;
    }
    str += `<p><strong>CR</strong> ${creature.cr_string} (XP ${common_util.commatizeNumber(XP_FOR_CR[`${creature.challenge_rating}`])}; PB +${creature.proficiency_modifier})</p>`;
    const traits = creature.traits.filter(o => o.level <= creature.challenge_rating);
    const legendaries_entry = creature.legendaries_table.getEntry(creature.challenge_rating);
    if (traits.length > 0 || (legendaries_entry && legendaries_entry.resistances > 0)) {
      str += `<p class="monster-header">Traits</p>`;
      for (const fb of traits) {
        const f = fb.features[0];
        str += `<p><strong><em>${f.name}.</em></strong> ${f.description}</p>`;
      }
      if (legendaries_entry && legendaries_entry.resistances > 0) {
        str += `<p><strong><em>Legendary Resistance (${legendaries_entry.resistances}/Day).</em></strong> If the ${creature.small_name} fails a saving throw, it can choose to succeed instead.</p>`;
      }
    }
    // debugger;
    const actions = creature.actions.filter(o => o.level <= creature.challenge_rating);
    const sc_entry = creature.spellcasting_table.getEntry(creature.challenge_rating);
    if (actions.length > 0 || sc_entry) {
      str += `<p class="monster-header">Actions</p>`;
      for (const fb of actions) {
        const f = fb.features[0];
        str += `<p><strong><em>${f.name}.</em></strong> ${f.description}</p>`;
      }
      if (sc_entry) {
        str += `<p><strong><em>Spellcasting.</em></strong> ${common_util.cleanLineBreaks(sc_entry.description)}</p>`;
        // TODO: Make it put the spells here for display, with clickable spells that pull them up in a modal.
        const levels = common_util.unique_array(sc_entry.spells.map(o => o.spell_level)).sort((a,b) => { return +a < +b ? -1 : 1; });
        for (const level of levels) {
          const spells_of_level = sc_entry.spells.filter(o => o.spell_level === level);
          if (level === 0 && spells_of_level.length > 0) {
            str += `<p><strong><em>Cantrips:</em></strong> ` + spells_of_level.map(o => { 
              if (o.extra_text) {
                return `{spell_link.${o.spell_id}} (${o.extra_text})`; 
              } else {
                return `{spell_link.${o.spell_id}}`; 
              }
            }).join(", ") + `</p>`;
          } else {
            // debugger;
            let level_name = "";
            switch(level) {
              case 1: level_name = "1st"; break;
              case 2: level_name = "2nd"; break;
              case 3: level_name = "3rd"; break;
              default: level_name = `${level}th`; break;
            }
            const at_will_spells = spells_of_level.filter(o => o.cast_type === "At Will");
            if (at_will_spells.length > 0) {
              str += `<p><strong><em>At Will (${level_name}):</em></strong> ` + at_will_spells.map(o => { 
                if (o.extra_text) {
                  return `{spell_link.${o.spell_id}} ${o.extra_text}`; 
                } else {
                  return `{spell_link.${o.spell_id}}`; 
                }
              }).join(", ") + `</p>`;
            }
            const slot_spells = spells_of_level.filter(o => o.cast_type === "Slot");
            if (slot_spells.length > 0) {
              const slot_count = creature.use_slots2.find(o => o.resource_id === `Slot: ${level}`);
              if (slot_count && slot_count.total > 0) {
                str += `<p><strong><em>${level_name} level (${slot_count.total} Slots):</em></strong> ` + slot_spells.map(o => { 
                  if (o.extra_text) {
                    return `{spell_link.${o.spell_id}} ${o.extra_text}`; 
                  } else {
                    return `{spell_link.${o.spell_id}}`; 
                  }
                }).join(", ") + `</p>`;
              }
            }
          }
        }
        const spells_by_x = sc_entry.spells_by_x;
        const xs = Object.keys(spells_by_x).map(o => +o).sort();
        for (const x of xs) {
          // 2/Day Each: Fly, Lightning Bolt (level 7 version)
          const spells_by_x2 = spells_by_x[x];
          str += `<p><strong><em>${x}/Day Each:</em></strong> ` + spells_by_x2.map(o => { 
            let extra = "";
            if (o.cast_level) {
              extra = ` (level ${o.cast_level} version)`; 
            } else if (o.extra_text) {
              extra = ` ${o.extra_text}`;
            }
            return `{spell_link.${o.spell_id}}${extra}`; 
          }).join(", ") + `</p>`;
        }
      }
    }
    const bonus_actions = creature.bonus_actions.filter(o => o.level <= creature.challenge_rating);
    if (bonus_actions.length > 0) {
      str += `<p class="monster-header">Bonus Actions</p>`;
      for (const fb of bonus_actions) {
        const f = fb.features[0];
        str += `<p><strong><em>${f.name}.</em></strong> ${f.description}</p>`;
      }
    }
    const reactions = creature.reactions.filter(o => o.level <= creature.challenge_rating);
    if (reactions.length > 0) {
      str += `<p class="monster-header">Reactions</p>`;
      for (const fb of reactions) {
        const f = fb.features[0];
        str += `<p><strong><em>${f.name}.</em></strong> ${f.description}</p>`;
      }
    }
    if (creature.legendary) {
      if (legendaries_entry) {
        const legendary_action_fbs = creature.legendary_action_fbs.filter(o => o.level <= creature.challenge_rating);
        if (legendary_action_fbs.length > 0) {
          str += `<p class="monster-header">Legendary Actions</p>`;
          // Old version: str += `<p>The ${creature.small_name} can	take ${legendaries_entry.actions} legendary actions, choosing from the options below. Only one legendary action option can be used at a time and only at the end of another creature's turn. The ${creature.small_name} regains spent legendary actions at the start of its turn.</p>`;
          str += `<p><strong>Legendary Action Uses:</strong> ${legendaries_entry.actions}${legendaries_entry.act_lair > 0 ? " (" + legendaries_entry.act_lair + " in lair)" : ""}. Immediately after another creature's turn, the ${creature.small_name} can expend a use to take one of the following actions. The ${creature.small_name} regains all expended uses at the start of each of its turns.</p>`;
          
          for (const fb of legendary_action_fbs) {
            const f = fb.features[0];
            str += `<p><strong><em>${f.name}.</em></strong> ${f.description}</p>`;
          }
        }
        const lair_action_fbs = creature.lair_action_fbs.filter(o => o.level <= creature.challenge_rating);
        if (lair_action_fbs.length > 0) {
          str += `<p class="monster-header">Lair Actions</p>`;
          str += `<p>On initiative count 20 (losing initiative ties), the ${creature.small_name} can take a lair action to cause one of the following effects; the ${creature.small_name} can't use the same effect two rounds in a row.</p>`;
          if (legendaries_entry.lair_actions_extra.length > 0) {
            str += `<p>${legendaries_entry.lair_actions_extra}</p>`;
          }
          if (lair_action_fbs.filter(o => o.fb_name.includes("Lair Action")).length === lair_action_fbs.length) {
            str += "<ul>";
            for (const fb of lair_action_fbs) {
              const f = fb.features[0];
              str += `<li>${f.description}</li>`;
            }
            str += "</ul>";
          } else {
            for (const fb of lair_action_fbs) {
              const f = fb.features[0];
              str += `<p><strong><em>${f.name}.</em></strong> ${f.description}</p>`;
            }
          }
        }
      }
    }
    str += `</div>`;
    // if (creature.description.length > 0) {
    //   str += `${creature.description}\n\n`;
    // }
    str += `<hr class="MuiDivider-root MuiDivider-fullWidth css-x148m1-MuiDivider-root" style="margin-top: 8px; margin-bottom: 16px;"></hr>`;
    return str;
  }
  
  formattedFeatureSource = async (fs: FeatureSource) => {
    await this.loadObject(fs);
    let str = `<div class="feature-source">`;
    str += `<h4 class="compendium-hr">${fs.name}</h4>`;
    str += `<p>${this.linkifyURLs(fs.description)}</p>`;
    if (!fs.description.includes("the following benefits")) {
      str += `<p>You gain the following benefits.</p>`;
      
      const fbs = fs.feature_bases_in_order;
      const levels = common_util.unique_array(fbs.map(o => o.level));
      if (levels.length === 1) {
        for (const fb of fbs) {
          str += `<p><strong>${fb.fb_name}.</strong> ${fb.description}</p>`;
        }
      } else {
        for (const fb of fbs) {
          if (fb.level > 1) {
            str += `<p><strong>Level ${fb.level}: ${fb.fb_name}.</strong> ${fb.description}</p>`;
          } else {
            str += `<p><strong>${fb.fb_name}.</strong> ${fb.description}</p>`;
          }
        }
      }
    }
    if (fs.notes.length > 0) {
      str += `<p>${this.linkifyURLs(fs.notes)}</p>`;
    }
    if (fs.tags.length > 0) {
      str += `<p><strong>Tags:</strong> ${fs.tags.join(", ")}<p>`;
    }
    str += `<p><strong>Source Book${fs.source_books.length > 1 ? "s" : ""}:</strong> ${fs.source_books.map(o => o.name).join(", ")}</p>`;
    str += `</div>`;
    str += `<hr class="MuiDivider-root MuiDivider-fullWidth css-x148m1-MuiDivider-root" style="margin-top: 8px; margin-bottom: 16px;"></hr>`;
    return str;
  }
  
  linkifyURLs = (str: string): string => {
    const urlRegex = /https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)/g;
  
    return str.replace(urlRegex, (url, offset) => {
      // Check if this URL is inside an existing <a ...> tag or its text content
      const before = str.slice(0, offset);
      const after = str.slice(offset + url.length);
  
      // If preceded by href=" or href=' — it's already an attribute value
      if (/href=["']$/.test(before)) {
        return url;
      }
  
      // If there's an unclosed <a before it and a </a> after it — it's already link text
      const lastAnchorOpen = before.lastIndexOf('<a ');
      const lastAnchorClose = before.lastIndexOf('</a>');
      if (lastAnchorOpen !== -1 && lastAnchorOpen > lastAnchorClose) {
        // We're inside an anchor tag — check that </a> comes before another 
        const closingTag = after.indexOf('</a>');
        if (closingTag !== -1) {
          return url;
        }
      }
  
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
  };

  getSubsectionsForSpecialSection = async (section: SBSection, sb: SourceBook) => {
    // I'll move this into api later so it can be better accessed in other places.
    const sections: SBSection[] = [];
    switch (section.special_type) {
      case "Classes":
      break;
      case "Feats":
      break;
      case "Items":
        if (true) {
          const special_attributes = section.special_attributes as { item_types?: ItemType[], magic?: boolean };
          const item_types = special_attributes.item_types??[];
          const magic = special_attributes.magic;
          const all_items = await this.getObjects<Item>({ data_type: "item" });
          const all_miks = magic === false ? [] : await this.getObjects<MagicItemKeyword>({ data_type: "magic_item_keyword" });
          let filtered_items = all_items.filter(o => o.source_book_ids.includes(sb._id));
          let filtered_miks = all_miks.filter(o => o.source_book_ids.includes(sb._id));
          if (item_types.length > 0)  {
            filtered_items = filtered_items.filter(o => item_types.includes(o.item_type));
            filtered_miks = filtered_miks.filter(o => item_types.includes(o.item_type));
          }
          if (magic !== undefined) {
            filtered_items = filtered_items.filter(o => o.magical === magic);
          }
          let items_and_miks = [...filtered_items,...filtered_miks];
          items_and_miks = items_and_miks.sort((a, b) => a.name.localeCompare(b.name));

          const formattedItemOrMikDescription = async (item_or_mik: Item | MagicItemKeyword) => {
            if (item_or_mik instanceof Item) {
              // Amulet of Health
              // Wondrous item, rare (requires attunement)

              // Your Constitution score is 19 while you wear this amulet. It has no effect on you if your Constitution is 19 or higher without it.
              const item = item_or_mik;
              
              let item_desc = `<b>${item.name}</b>\n`;
              item_desc += `<i>${item.item_type}, ${item.rarities_string}`;
              if (item.attunement) {
                if (item.limitations.length > 0) {
                  // Figure out who can attune and get description
                  const attunement_desc = await this.getCharLimitsDesc(item.limitations);
                  item_desc += ` (requires attunement by ${attunement_desc})`;
                } else {
                  item_desc += " (requires attunement)";
                }
              }
              item_desc +=  `</i>\n\n`;
              
              item_desc += `${item.description}\n\n`;
              item_desc += `<hr class="MuiDivider-root MuiDivider-fullWidth css-x148m1-MuiDivider-root" style="margin-top: 8px; margin-bottom: 16px;"></hr>`;
              return item_desc;
            } else {
              // Adamantine Armor
              // Armor (medium or heavy, but not hide), uncommon

              // This suit of armor is reinforced with adamantine, one of the hardest substances in existence. While you’re wearing it, any critical hit against you becomes a normal hit.

              // Ammunition, +1, +2, or +3
              // Weapon (any ammunition), uncommon (+1), rare (+2), or very rare (+3)

              // You have a bonus to attack and damage rolls made with this piece of magic ammunition. The bonus is determined by the rarity of the ammunition. Once it hits a target, the ammunition is no longer magical.

              const mik = item_or_mik;
              
              let mik_desc = `<b>${mik.name}</b>\n`;
              // Need to change this for limits
              if (mik.uses_specific && mik.base_item_ids.length > 0) {
                const all_items = await this.getObjects<Item>({ data_type: "item" });
                const items = all_items.filter(o => mik.base_item_ids.includes(o._id));
                const item_types = common_util.unique_array(items.map(o => o.item_type));
                if (item_types.length === 1 && isItemType(item_types[0])) {
                  const item_type = item_types[0];
                  mik_desc += `<i>${item_type} (${common_util.standardJoin(items.map(o => o.name))})`;
                } else {
                  console.error("MIK with multiple Item Types error. This should never happen!");
                }
              } else {
                mik_desc += `<i>${mik.item_type}`;
                if (mik.item_type === "Weapon") {
                  const limit = mik.weapon_limit;
                  console.log(mik);
                  console.log(limit);
                } else if (mik.item_type === "Armor") {
                  const armor_type_ids = mik.base_item_details;
                  const exclusions = mik.exclusions;
                  if (armor_type_ids.length > 0) {
                    const armor_types = ARMOR_TYPES.filter(o => armor_type_ids.includes(o._id));
                    mik_desc += ` (${common_util.standardJoin(armor_types.map(o => o.name))}`;
                    if (exclusions.length > 0) {
                      mik_desc += `, but not ${exclusions}`;
                    } 
                    mik_desc += ")";
                  } else if (exclusions.length > 0) {
                    mik_desc += ` (not ${exclusions})`;
                  }
                }
              }
              mik_desc += `, ${mik.rarities_string}`;
              if (mik.attunement) {
                if (mik.limitations.length > 0) {
                  // Figure out who can attune and get description
                  const attunement_desc = await this.getCharLimitsDesc(mik.limitations);
                  mik_desc += ` (requires attunement by ${attunement_desc})`;
                } else {
                  mik_desc += " (requires attunement)";
                }
              }
              mik_desc +=  `</i>\n\n`;
              
              mik_desc += `${mik.description}\n\n`;
              mik_desc += `<hr class="MuiDivider-root MuiDivider-fullWidth css-x148m1-MuiDivider-root" style="margin-top: 8px; margin-bottom: 16px;"></hr>`;
              return mik_desc;
            }
          }
          let current_section = new SBSection();
          for (const item_or_mik of items_and_miks) {
            const first_letter = item_or_mik.name[0];
            if (current_section.name !== first_letter) {
              if (current_section.name.length > 0) {
                // Add the current section
                sections.push(current_section);
              }
              current_section = new SBSection();
              current_section.name = first_letter;
              current_section.description = "";
            }
            current_section.description += await formattedItemOrMikDescription(item_or_mik);
          }
          if (current_section.name.length > 0) {
            // Add the current section
            sections.push(current_section);
          }
        }
      break;
      case "Races":
      break;
      case "Spell Descriptions":
        if (true) {
          // Load all the spells for this spell book
          const all_spells = await this.getObjects<Spell>({ data_type: "spell" });
          const sb_spells = all_spells.filter(o => o.source_book_ids.includes(sb._id)).sort((a, b) => a.name.localeCompare(b.name));
          // Create pseudo-sections based on starting letter
          // Format properly
          const formattedSpellDescription = async (spell: Spell) => {
            // Acid Arrow
            // Level 2 Evocation (Wizard)
            // Casting Time: Action
            // Range: 90 feet
            // Components: V, S, M (powdered rhubarb leaf)
            // Duration: Instantaneous
            // Description
            const spell_lists = await this.getSpellListsForSpell(spell, [sb._id]);
            let spell_desc = `<b>${spell.name}</b>\n`;
            spell_desc += `<i>${spell.level_string} (${spell_lists.map(o => o.name).join(", ")})</i>\n\n`;
            spell_desc += `<b>Casting Time:</b> ${spell.casting_time !== "Special" ? CASTING_TIME_MAP[spell.casting_time] : spell.special_casting_time}\n`;
            const range = spell.range;
            if (range !== "") {
              spell_desc += `<b>Range:</b> ${spell.range}\n`;
            }
            spell_desc += `<b>Components:</b> ${spell.components_string}\n`;
            spell_desc += `<b>Duration:</b> ${spell.duration}\n\n`;
            // spell_desc += `#### Attack/Save: ${spell.attack_save}\n\r`;
            // spell_desc += `#### Damage/Effect: ${spell.effect_types.join(", ")}\n\r`;
            spell_desc += `${spell.description}\n\n`;
            spell_desc += `<hr class="MuiDivider-root MuiDivider-fullWidth css-x148m1-MuiDivider-root" style="margin-top: 8px; margin-bottom: 16px;"></hr>`;
            return spell_desc;
          }
          let current_section = new SBSection();
          for (const spell of sb_spells) {
            const first_letter = spell.name[0];
            if (current_section.name !== first_letter) {
              if (current_section.name.length > 0) {
                // Add the current section
                sections.push(current_section);
              }
              current_section = new SBSection();
              current_section.name = first_letter;
              current_section.description = "";
            }
            current_section.description += await formattedSpellDescription(spell);
          }
          if (current_section.name.length > 0) {
            // Add the current section
            sections.push(current_section);
          }
        }
      break;
      case "Spell Lists":
      break;
      case "Creatures":
        if (true) {
          // Load all the spells for this spell book
          const all_creatures = await this.getObjects<Creature>({ data_type: "creature" });
          const sb_creatures = all_creatures.filter(o => 
            o.source_book_ids.includes(sb._id) 
            // && o.name === "Adult Black Dragon"
          ).sort((a, b) => a.name.localeCompare(b.name));
          // Create pseudo-sections based on starting letter
          // Format properly
          let current_section = new SBSection();
          for (const creature of sb_creatures) {
            const first_letter = creature.name[0];
            if (current_section.name !== first_letter) {
              if (current_section.name.length > 0) {
                // Add the current section
                sections.push(current_section);
              }
              current_section = new SBSection();
              current_section.name = first_letter;
              current_section.description = "";
            }
            current_section.description += `{creature_stat_block.${creature._id}}`;
          }
          if (current_section.name.length > 0) {
            // Add the current section
            sections.push(current_section);
          }
        }
      break;
      case "Deities":
        if (true) {
          // Load all the spells for this spell book
          const all_deities = await this.getObjects<Deity>({ data_type: "deity" });
          const sb_deities = all_deities.filter(o => 
            o.source_book_ids.includes(sb._id) 
            // && o.name === "Adult Black Dragon"
          );
          // Create pseudo-sections based on starting letter
          // Format properly
          const pantheons = common_util.unique_array(sb_deities.flatMap(o => o.pantheons)).sort((a, b) => a.localeCompare(b));
          for (const pantheon of pantheons) {
            const current_section = new SBSection();
            current_section.name = `${pantheon} Deities`;
            current_section.description += `{deity_table.${sb._id}.${pantheon}}`;
            sections.push(current_section);
          }
        }
      break;
    }
    return sections;
  }

  getSpellByName(spell_name: string, sb_id: string = "") {
    let spells_with_name = this.spells.filter(o => common_util.cleanCompare(o.name, spell_name));
    let spell: Spell | undefined;
    if (spells_with_name.length === 1) {
      spell = spells_with_name[0];
    } else if (spells_with_name.length > 1) {
      if (sb_id !== "") {
        spell = spells_with_name.find(o => o.source_book_ids.includes(sb_id));
      }
      if (!spell) {
        spell = this.getDominant(spells_with_name); // spells_with_name.find(o => o.dominance_level === max_dominance_level);
      }
    }
    return spell;
  }

  getDominant<T extends ModelBase>(objects: T[], log_it: boolean = false) {
    if (log_it) {
      debugger;
    }
    if (objects.length === 0) {
      return undefined;
    } else if (objects.length === 1) {
      return objects[0] as T;
    } else {
      let max = Math.max(...objects.map(o => o.getCoreDominanceLevel()));
      if (max > -1) {
        return objects.find(o => o.getCoreDominanceLevel() === max) as T;
      } else {
        max = Math.max(...objects.map(o => o.getNonCoreDominanceLevel()));
        if (max > -1) {
          return objects.find(o => o.getNonCoreDominanceLevel() === max) as T;
        }
        return objects[0] as T;
      }
    }
  }
    
  parseCreature = async (obj: Creature, elem: HTMLElement, small_name = "") => {
    const new_obj = obj.clone();
    const attributes_entry = new Attributes();
    attributes_entry.level = -1;
    new_obj.name = "";
    new_obj.small_name = small_name;
    new_obj.creature_type = "None";
    new_obj.alignment = "";
    new_obj.armor_class.makeBasic();
    new_obj.armor_class.base = 0;
    new_obj.ac_flavor = "";
    new_obj.attributes_table.entries = [];
    new_obj.attributes_table.entries.push(attributes_entry);
    new_obj.ability_scores = new AbilityScoresUpgradable();

    new_obj.group_id = "";
    new_obj.gear_item_ids = [];

    new_obj.table = new AttachedTable();
    const entry = new TableEntry();
    entry.level = obj.challenge_rating;
    entry.data = {
      "STR": obj.ability_scores.strength.base,
      "DEX": obj.ability_scores.dexterity.base,
      "CON": obj.ability_scores.constitution.base,
      "INT": obj.ability_scores.intelligence.base,
      "WIS": obj.ability_scores.wisdom.base,
      "CHA": obj.ability_scores.charisma.base,
    };
    new_obj.ability_scores.strength.base = 0;
    new_obj.ability_scores.strength.makeBasic();
    new_obj.ability_scores.strength.table_type = "Attached";
    new_obj.ability_scores.strength.table_id = "Attached";
    new_obj.ability_scores.strength.table_field = "STR";
    new_obj.ability_scores.dexterity.base = 0;
    new_obj.ability_scores.dexterity.makeBasic();
    new_obj.ability_scores.dexterity.table_type = "Attached";
    new_obj.ability_scores.dexterity.table_id = "Attached";
    new_obj.ability_scores.dexterity.table_field = "DEX";
    new_obj.ability_scores.constitution.base = 0;
    new_obj.ability_scores.constitution.makeBasic();
    new_obj.ability_scores.constitution.table_type = "Attached";
    new_obj.ability_scores.constitution.table_id = "Attached";
    new_obj.ability_scores.constitution.table_field = "CON";
    new_obj.ability_scores.intelligence.base = 0;
    new_obj.ability_scores.intelligence.makeBasic();
    new_obj.ability_scores.intelligence.table_type = "Attached";
    new_obj.ability_scores.intelligence.table_id = "Attached";
    new_obj.ability_scores.intelligence.table_field = "INT";
    new_obj.ability_scores.wisdom.base = 0;
    new_obj.ability_scores.wisdom.makeBasic();
    new_obj.ability_scores.wisdom.table_type = "Attached";
    new_obj.ability_scores.wisdom.table_id = "Attached";
    new_obj.ability_scores.wisdom.table_field = "WIS";
    new_obj.ability_scores.charisma.base = 0;
    new_obj.ability_scores.charisma.makeBasic();
    new_obj.ability_scores.charisma.table_type = "Attached";
    new_obj.ability_scores.charisma.table_id = "Attached";
    new_obj.ability_scores.charisma.table_field = "CHA";
    entry.data["Hit Dice Count"] = 0;
    const hd = new HitDiceUpgradable();
    hd.count.base = 0;
    hd.count.table_type = "Attached";
    hd.count.table_id = "Attached";
    hd.count.table_field = "Hit Dice Count";
    new_obj.hit_dice = [hd];
    entry.data["Armor Class"] = obj.armor_class.base;
    new_obj.armor_class.base = 0;
    new_obj.armor_class.table_type = "Attached";
    new_obj.armor_class.table_id = "Attached";
    new_obj.armor_class.table_field = "Armor Class";
    entry.data["Size"] = obj.size_ugn.base;
    new_obj.size_ugn.base = 0;
    new_obj.size_ugn.table_type = "Attached";
    new_obj.size_ugn.table_id = "Attached";
    new_obj.size_ugn.table_field = "Size";
    new_obj.table.entries.push(entry);
    new_obj.table.field_order = {
      0: "STR", 1: "DEX", 2: "CON", 3: "INT", 4: "WIS", 5: "CHA", 6: "Hit Dice Count", 7: "Armor Class", 8: "Size"
    };

    new_obj.spellcasting_ability = "None";
    new_obj.dc = new UpgradableNumber();
    new_obj.magic_attack = new UpgradableNumber();
    new_obj.non_magic_attack = new UpgradableNumber();
    new_obj.magic_dmg_bonus = new UpgradableNumber();
    new_obj.non_magic_dmg_bonus = new UpgradableNumber();
    new_obj.environments = [];
    // new_obj.settings = [];
    new_obj.native_planes = [];
    
    new_obj.legendaries_table = new LegendariesTable();
    new_obj.resources_table = new AttachedTable();
    new_obj.spellcasting_table = new SpellcastingTable();
    new_obj.fbs = [];
    const ths = elem.getElementsByTagName("th");
    // console.log(ths);
    let new_format = false;
    for (let i = 0; i < ths.length; ++i) {
      const th = ths[i];
      if (th.textContent && th.textContent === "Mod") {
        new_format = true;
        break;
      }
    }
    if (new_format) {
      if (elem.classList.contains("mon-stat-block-2024")) {
        return await this.parseNewMonStatBlock2024(new_obj, elem);
      } else {
        return await this.parseNewStatBlock(new_obj, elem);
      }
    } else {
      return this.parseOld(new_obj, elem);
    }
  }

  parseCreature5e = async (m_el: Element, sb_id: string, looking_for_creature_name: string, looking_for_fb_name: string) => {
    const all_creatures = await this.getObjects<Creature>({ data_type: "creature" });
    const old_creatures = all_creatures.filter(o => o.source_book_ids.includes(sb_id));

    const c = new Creature();
    c.source_books_old = [{ type: "Source Book", id: sb_id, third_party: false }];
    c.source_books = [];
    const sb = this.source_books.find(o => o._id === sb_id);
    if (sb) {
      c.source_books.push(sb);
    }
    let existing_c: Creature | undefined;
    const p_els = m_el.getElementsByTagName("p");
    const attr_entry = new Attributes();
    const tbl_entry = new TableEntry();
    const resources_entry = new TableEntry();
    c.attributes_table.entries = [attr_entry];
    c.table.entries = [tbl_entry];
    let legendary = false;
    const leg_entry = new Legendaries();
    // await delay(1000);
    const stat_divs = m_el.getElementsByClassName("stat-block-ability-scores-stat");
    for (let j = 0; j < stat_divs.length; ++j) {
      // <div class="stat-block-ability-scores-stat">
      //   <div class="stat-block-ability-scores-heading">STR
      //   </div>
      //   <div class="stat-block-ability-scores-data"><span
      //       class="stat-block-ability-scores-score">21</span><span
      //       class="stat-block-ability-scores-modifier">(+5)</span></div>
      // </div>
      const div = stat_divs[j] as HTMLElement;
      const stat_name_divs = div.getElementsByClassName("stat-block-ability-scores-heading");
      if (stat_name_divs.length === 1 && stat_name_divs[0] instanceof HTMLElement) {
        // console.log(stat_name_divs[0].innerText);
        const stat = stat_name_divs[0].innerText.trim();
        if (isStatType(stat)) {
          const score_spans = div.getElementsByClassName("stat-block-ability-scores-score");
          // console.log(score_spans);
          if (score_spans.length === 1 && score_spans[0] instanceof HTMLElement) {
            const score = +score_spans[0].innerText;
            // console.log(score);
            if (!isNaN(score)) {
              // console.log(`${stat}: ${score}`);
              c.ability_scores.setAbilityScore(stat, score);
            }
          }
        }
      }
    }
    let skills_el: HTMLElement | undefined;
    let fbType: CreatureFBType = "Traits";
    let sc = false;

    const processFB = (p: HTMLElement, extra_ps: HTMLElement[]) => {
      // It's probably a FeatureBase
      // <p class="Stat-Block-Styles_Stat-Block-Body"><span
      //     class="Sans-Serif-Character-Styles_Inline-Subhead-Sans-Serif">Amphibious.</span>&nbsp;The aboleth can breathe air
      //   and water.</p>
      // if (c.name.includes(looking_for_creature_name)) {
      //   const hangings = m_el.getElementsByClassName("Stat-Block-Styles_Stat-Block-Hanging");
      //   for (let k = 0; k < hangings.length; ++k) {
      //     const hanging = hangings[k];
      //     console.log(hanging.outerHTML);
      //   }
      // }
      
      if (p.classList.contains("Stat-Block-Styles_Stat-Block-Hanging") && sc) {
        // This should actually be added to the spellcasting entry.
        const blurg = () => {
          // <p class="Stat-Block-Styles_Stat-Block-Hanging">Cantrips
          //   (at will):&nbsp;<span><a>sacred
          //       flame</a></span>,&nbsp;<span><a>spare the
          //       dying</a></span>,&nbsp;<span><a>thaumaturgy</a></span>
          // </p>
          // <p class="Stat-Block-Styles_Stat-Block-Hanging">1st level
          //   (4 slots):&nbsp;<span><a>command</a></span>,&nbsp;<span><a>detect evil and
          //       good</a></span>,&nbsp;<span><a>detect
          //       magic</a></span></p>
          // <p class="Stat-Block-Styles_Stat-Block-Hanging">2nd level
          //   (3 slots):&nbsp;<span><a>lesser
          //       restoration</a></span>,&nbsp;<span><a>zone of
          //       truth</a></span></p>
          // <p class="Stat-Block-Styles_Stat-Block-Hanging">3rd level
          //   (3 slots):&nbsp;<span><a>dispel
          //       magic</a></span>,&nbsp;<span><a>tongues</a></span>
          // </p>
          // <p class="Stat-Block-Styles_Stat-Block-Hanging">4th level
          //   (3 slots):&nbsp;<span><a>banishment</a></span>,&nbsp;<span><a>freedom of
          //       movement</a></span></p>
          // <p class="Stat-Block-Styles_Stat-Block-Hanging">5th level
          //   (2 slots):&nbsp;<span><a>flame
          //       strike</a></span>,&nbsp;<span><a>greater
          //       restoration</a></span></p>
          // <p class="Stat-Block-Styles_Stat-Block-Hanging">6th level
          //   (1 slot):&nbsp;<span><a>heroes’
          //       feast</a></span></p>
          // if (c.name.includes(looking_for_creature_name)) {
          //   debugger;
          // }
        }
        blurg();
        
        const sc_entry = c.spellcasting_table.entries[0];
        const innerText = common_util.cleanForCompare(p.innerText);
        const start_string = innerText.substring(0, innerText.indexOf(":"));

        let spell_level = -1;
        let cast_type: "Slot" | "At Will" | "X/Day" | "Ritual" = "Slot";
        let x = -1;
        if (start_string.includes("cantrip") || start_string.includes("level")) {
          const level_char = start_string[0];
          if (level_char === "c") {
            spell_level = 0;
          } else {
            spell_level = +level_char;
            if (isNaN(spell_level)) {
              debugger;
            }
          }
          
          const pieces = start_string.split("(");
          if (pieces[1]) {
            const use_string = pieces[1];
            if (use_string.includes("at will")) {
              cast_type = "At Will";
            } else if (use_string.includes("ritual")) {
              cast_type = "Ritual";
            } else if (use_string.includes("/day")) {
              const pieces2 = use_string.split("/");
              x = +pieces2[0];
            } else if (use_string.includes("slot")) {
              const pieces2 = use_string.split(" ");
              const slot_count = +pieces2[0];
              if (!isNaN(slot_count)) {
                resources_entry.data[`Slot: ${spell_level}`] = slot_count;
              }
            } else {
              debugger;
            }
          }
        } else {
          // Different format.
          // <p class="Stat-Block-Styles_Stat-Block-Hanging">At will:&nbsp;
          //   <span><a>detect magic</a></span>,&nbsp;
          //   <span><a>fog cloud</a></span>,&nbsp;
          //   <span><a>light</a></span>
          // </p>
          // <p class="Stat-Block-Styles_Stat-Block-Hanging">3/day each:&nbsp;
          //   <span><a>feather fall</a></span>,&nbsp;
          //   <span><a>fly</a></span>,&nbsp;
          //   <span><a>misty step</a></span>,&nbsp;
          //   <span><a>telekinesis</a></span>
          // </p>
          if (start_string.includes("at will")) {
            cast_type = "At Will";
          } else if (start_string.includes("/day")) {
            cast_type = "X/Day";
            const pieces = start_string.split("/");
            const x_str = pieces[0].trim();
            x = +x_str;
            if (isNaN(x)) {
              debugger;
            }
          } else {
            debugger;
          }
        }

        // sc_entry.description += `\n${innerText}`;
        const spell_anchors = p.getElementsByTagName("a");
        for (let k = 0; k < spell_anchors.length; ++k) {
          const spell_anchor = spell_anchors[k];
          const spell_name = common_util.cleanForCompare(spell_anchor.innerText);
          let spells_with_name = this.spells.filter(o => common_util.cleanCompare(o.name, spell_name));
          let spell: Spell | undefined;
          if (spells_with_name.length === 1) {
            spell = spells_with_name[0];
          } else if (spells_with_name.length > 1) {
            spell = spells_with_name.find(o => o.source_book_ids.includes(sb_id));
            if (!spell) {
              // const max_dominance_level = Math.max(...spells_with_name.map(o => o.dominance_level));
              spell = spells_with_name[0].getDominant() as Spell; // spells_with_name.find(o => o.dominance_level === max_dominance_level);
            }
          }
          if (spell) {
            spell_level = spell.level;
            sc_entry.spells.push({
              spell_level,
              spell_id: spell._id,
              cast_type,
              x
            });
          } else {
            debugger;
          }
        }
      } else {
        sc = false;
        const name_spans = p.getElementsByTagName("span");
        const name_strongs = p.getElementsByTagName("strong");
        if (name_spans.length > 0 || name_strongs.length > 0) {
          const name_span = name_spans.length > 0 ? name_spans[0] : name_strongs[0];
          let name = name_span.innerText.trim();
          if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
            console.log(name);
          }
          if (name.endsWith(".")) {
            name = name.substring(0, name.length - 1);
          }
          name = common_util.normalizeDashes(name);
          name = common_util.replaceAll(name, "’", "'");
          let desc0 = common_util.normalizeSpaces(p.innerText.substring(name.length + 1)).trim();
          if (desc0.startsWith(".")) {
            desc0 = desc0.substring(1).trim();
          }
          for (const pk of extra_ps) {
            desc0 += `<p>${pk.innerText}</p>`;
          }
          let content_str = `${desc0}`;
          if (content_str.includes("cost it extra moment")) {
            content_str = common_util.replaceAll(content_str, "cost it extra moment", "cost it extra movement");
          }

          if (name.includes("Legendary Resistance")) {
            // Not a FeatureBase. We store these in the legendaries table
            legendary = true;
            let legendary_resistances_str = name.split("(")[1];
            if (legendary_resistances_str) {
              let lair_str = "";
              if (legendary_resistances_str.includes(" or ")) {
                lair_str = legendary_resistances_str.split(" or ")[1];
                lair_str = lair_str.substring(0, lair_str.indexOf("/"));
                
                leg_entry.res_lair = +lair_str;
                leg_entry.actions = 0;
              }
              legendary_resistances_str = legendary_resistances_str.substring(0, legendary_resistances_str.indexOf("/"));
              leg_entry.resistances = +legendary_resistances_str;
            }
          } else if (name.includes("Spellcasting")) {
            // Not a FeatureBase. We store this in the spellcasting table.
            // if (c.name.includes(looking_for_creature_name)) {
            //   debugger;
            // }
            sc = true;
            const sc_entry = new SpellcastingEntry();
            sc_entry.level = c.challenge_rating;
            sc_entry.description = content_str;
            if (sc_entry.description.toLocaleLowerCase().includes("spellcasting ability is ")) {
              let spellcasting_ability = common_util.capitalize_first(
                sc_entry.description.substring(sc_entry.description.toLocaleLowerCase().indexOf("spellcasting ability is ") + 24));
              spellcasting_ability = spellcasting_ability.substring(0, 3).toUpperCase();
              c.spellcasting_ability = spellcasting_ability as AbilityScoreType;
            } else if (sc_entry.description.toLocaleLowerCase().includes("as the spellcasting")) {
              // The aarakocra casts one of the following spells, requiring no Material components 
              // and using Wisdom as the spellcasting ability (spell save DC 13):
              let index = sc_entry.description.toLocaleLowerCase().indexOf("as the spellcasting") - 2;
              while (index > 0 && sc_entry.description[index] !== " ") {
                index--;
              }
              index++;
              const spellcasting_ability = sc_entry.description.substring(index, index + 3).toLocaleUpperCase();
              if (ABILITY_SCORES.includes(spellcasting_ability)) {
                c.spellcasting_ability = spellcasting_ability as AbilityScoreType;
              }
            }
            if (sc_entry.description.toLocaleLowerCase().includes(" dc ")) {
              if (c.dc.isBlank()) {
                let dc_str = sc_entry.description.substring(sc_entry.description.toLocaleLowerCase().indexOf(" dc ") + 4).trim();
                if (dc_str.includes(" ")) {
                  dc_str = dc_str.substring(0, dc_str.indexOf(" "));
                }
                dc_str = dc_str.replace(",","").replace(".","").replace(")","").replace(":","").trim();
                const dc_num = +dc_str;
                if (c.spellcasting_ability !== "None" && 
                  dc_num === (8 + c.proficiency_modifier + c.current_ability_scores.getModifier(c.spellcasting_ability))
                ) {
                  c.dc = new UpgradableNumber();
                  c.dc.base = 8;
                  c.dc.add_prof_bonus_mult = 1;
                  // c.dc.add_ability_mod_mult = 1;
                  // c.dc.add_ability_scores = [c.spellcasting_ability];
                  c.dc.add_stats_groups = [{ 
                    true_id: uuidv4().toString(),
                    add_ability_scores: [c.spellcasting_ability],
                    mod_or_score: "Mod",
                    add_mult: 1,
                    multiple_type: "And"
                  }];
                  sc_entry.description = common_util.replaceAll(sc_entry.description, ` dc ${dc_num}`, ` {dc_string.${c.spellcasting_ability}}`);
                  sc_entry.description = common_util.replaceAll(sc_entry.description, ` DC ${dc_num}`, ` {dc_string.${c.spellcasting_ability}}`);
                } else {
                  c.dc = new UpgradableNumber(dc_num);
                }
              }
            }
            if (sc_entry.description.toLocaleLowerCase().includes(" to hit ")) {
              let attack_str = sc_entry.description.substring(0, sc_entry.description.toLocaleLowerCase().indexOf(" to hit ")).trim();
              attack_str = attack_str.substring(attack_str.lastIndexOf("+")+1);
              const attack_num = +attack_str;
              if (c.spellcasting_ability !== "None" && 
                attack_num === (c.proficiency_modifier + c.current_ability_scores.getModifier(c.spellcasting_ability))
              ) {
                c.magic_attack = new UpgradableNumber();
                c.magic_attack.add_prof_bonus_mult = 1;
                // c.magic_attack.add_ability_mod_mult = 1;
                // c.magic_attack.add_ability_scores = [c.spellcasting_ability];
                c.magic_attack.add_stats_groups = [{ 
                  true_id: uuidv4().toString(),
                  add_ability_scores: [c.spellcasting_ability],
                  mod_or_score: "Mod",
                  add_mult: 1,
                  multiple_type: "And"
                }];
                sc_entry.description = common_util.replaceAll(sc_entry.description, `+${attack_num} to hit `, `{attack_string.${c.spellcasting_ability}} to hit `);
              } else {
                c.magic_attack = new UpgradableNumber(attack_num);
              }
            }
            if (c.spellcasting_ability !== "None") {
              const stat = c.getBasicModifier(c.spellcasting_ability);
              const pb = c.proficiency_modifier;
  
              if (c.dc.base > 0) {
                c.dc.base -= stat + pb;
              } else {
                c.dc.base = 8;
              }
              c.dc.add_prof_bonus_mult = 1;
              // c.dc.add_ability_mod_mult = 1;
              // c.dc.add_ability_scores.push(c.spellcasting_ability);
              c.dc.add_stats_groups = [{ 
                true_id: uuidv4().toString(),
                add_ability_scores: [c.spellcasting_ability],
                mod_or_score: "Mod",
                add_mult: 1,
                multiple_type: "And"
              }];
  
              if (c.magic_attack.base > 0) {
                c.magic_attack.base -= stat + pb;
              }
              c.magic_attack.add_prof_bonus_mult = 1;
              // c.magic_attack.add_ability_mod_mult = 1;
              // c.magic_attack.add_ability_scores.push(c.spellcasting_ability);
              c.magic_attack.add_stats_groups = [{ 
                true_id: uuidv4().toString(),
                add_ability_scores: [c.spellcasting_ability],
                mod_or_score: "Mod",
                add_mult: 1,
                multiple_type: "And"
              }];
              c.spellcasting_table.entries = [sc_entry];
            }
          } else {
            const fb = new FeatureBase();
            fb.creature_fb_type = fbType;

            const descVersions: string[] = [desc0];
            content_str = common_util.normalizeDashes(content_str);
            content_str = common_util.replaceAll(content_str, "’", "'");
            if (desc0 !== content_str) {
              descVersions.push(content_str);
            }
            if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
              console.log([...descVersions]);
            }

            let ff: FloatingFeature | undefined = undefined; 
            if (!ff) {
              let desc_helper = common_util.cleanSpaces(common_util.replaceAll(common_util.replaceAll(content_str, ` ${c.name} `, ` {cfs} `), ` ${c.name}'`, ` {cfs}'`));
              if (!descVersions.includes(desc_helper)) {
                descVersions.push(desc_helper);
              }
              if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
                console.log([...descVersions]);
              }
              let new_descs: string[] = [];
              for (const dv of descVersions) {
                const new_desc = common_util.replaceAll(common_util.replaceAll(dv, ` ${c.name} `, ` {cfs} `), ` ${c.name}'`, ` {cfs}'`);
                if (!descVersions.includes(new_desc) && !new_descs.includes(new_desc)) {
                  new_descs.push(new_desc);
                }
              }
              descVersions.push(...new_descs);
              if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
                console.log([...descVersions]);
              }
              if (desc_helper === content_str) {
                const desc_helper2 = common_util.replaceAll(desc_helper, "\n", "");
                if (c.small_name.length === 0) {
                  if (c.name.includes(" ")) {
                    // debugger;
                    const words = common_util.cleanForCompare(c.name).split(" ").map(o => o.toLocaleLowerCase());
                    let small_name = "";
                    for (let i = 0; i < words.length; ++i) {
                      const word = words[i];
                      if (desc_helper2.includes(` ${word} `)) {
                        if (small_name === "") {
                          small_name = word;
                        } else {
                          const prev_word = words[i - 1];
                          if (small_name.includes(prev_word) && desc_helper2.includes(` ${small_name} ${word} `)) {
                            small_name += ` ${word}`;
                          }
                        }
                      }
                    }
                    c.small_name = small_name;
                  } else {
                    // Check for some things that don't fit the above method.
                    if (c.name.toLocaleLowerCase().includes("sphinx")) {
                      c.small_name = "sphinx";
                    } else {
                      c.small_name = c.name.toLocaleLowerCase();
                    }
                  }
                }
                if (c.small_name.length > 0) {
                  if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
                    console.log(content_str);
                  }
                  desc_helper = common_util.replaceAll(content_str, c.small_name, `{cfs}`);
                  
                  new_descs = [];
                  for (const dv of descVersions) {
                    const new_desc = common_util.replaceAll(dv, c.small_name, `{cfs}`);
                    if (!descVersions.includes(new_desc) && !new_descs.includes(new_desc)) {
                      new_descs.push(new_desc);
                    }
                  }
                  descVersions.push(...new_descs);
                  if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
                    console.log([...descVersions]);
                  }
                }
              }
              const makeFF = (desc_str: string) => {
                const ff_obj: any = {
                  name,
                  description: desc_str,
                  feature_type: "Creature Ability"
                };
                if (name === "Multiattack") {
                  ff_obj.feature_type = "Minion Extra Attacks";
                  // Usually says something along the lines of "makes X attacks";
                  const sub = content_str.toLocaleLowerCase();
                  
                  const pieces = common_util.cleanForCompare(sub).split(" ");
                  const num_strings = ["two","three","four","five","six","seven","eight","nine","ten","2","3","4","5","6","7","8","9","10"];
                  let num_string = common_util.findOverlap(num_strings, pieces);
                  if (num_string.length === 0) {
                    // It's more complicated, so just make it 2
                    num_string = "2";
                  }
                  if (num_string.length > 0) {
                    switch(num_string) {
                      case "two": ff_obj.num = 2; break;
                      case "three": ff_obj.num = 3; break;
                      case "four": ff_obj.num = 4; break;
                      case "five": ff_obj.num = 5; break;
                      case "six": ff_obj.num = 6; break;
                      case "seven": ff_obj.num = 7; break;
                      case "eight": ff_obj.num = 8; break;
                      case "nine": ff_obj.num = 9; break;
                      case "ten": ff_obj.num = 10; break;
                      default: ff_obj.num = +num_string; break;
                    }
                    ff_obj.num -= 1;
                    return new UpgradableNumberFeature(ff_obj);
                  }
                } else {
                  const ca = new CreatureAbility(ff_obj);
                  if (ca instanceof CreatureAbility) {
                    if (c.name.includes(looking_for_creature_name) && ca.name.includes(looking_for_fb_name)) {
                      console.log(desc_str);
                    }
                    let using_ability: AbilityScoreType | "" = "";
                    let using_ability_mod = -5;
                    let using_ability_atk: AbilityScoreType | "" = "";
                    let using_ability_atk_mod = -5;
                    
                    if (desc_str.includes("DC") && !desc_str.includes("rapple")) {
                      // New format: Strength Saving Throw: DC 12
                      // Old format: DC 19 Dexterity saving throw
                      // Needs to work with both
                      // debugger;
                      let dc = 0;
                      let save_type = "";
                      const pieces = common_util.cleanForCompare(desc_str).split(" ").map(o => o.replace(",",""));
                      let dc_index = -1;
                      for (let i = 0; i < pieces.length; ++i) {
                        const piece = pieces[i];
                        if (piece === "dc") {
                          dc_index = i;
                          break;
                        }
                      }
                      const dc_str = pieces[dc_index + 1].replace(",", "").replace(".","").replace(")","").replace(":","");
                      dc = +dc_str;
                      if (isNaN(dc)) {
                        console.error("DC Parse Error");
                        // debugger;
                      } else {
                        // Check New Method first
                        let type_maybe = pieces[dc_index - 3];
                        if (type_maybe.length > 3) {
                          type_maybe = type_maybe.substring(0, 3).toLocaleUpperCase();
                          if (ABILITY_SCORES.includes(type_maybe)) {
                            save_type = type_maybe;
                          }
                        }
                        if (save_type === "") {
                          // Didn't find it, so Check Old Method
                          type_maybe = pieces[dc_index + 2];
                          if (type_maybe) {
                            if (type_maybe.length > 3) {
                              type_maybe = type_maybe.substring(0, 3).toLocaleUpperCase();
                              if (ABILITY_SCORES.includes(type_maybe)) {
                                save_type = type_maybe;
                              }
                            }
                          } else {
                            let spell_index = pieces.indexOf("spell");
                            if (spell_index < 0) {
                              spell_index = pieces.indexOf("Spell");
                            }
                            let spell: ModelBase | undefined = undefined;
                            if (spell_index > -1) {
                              // Check the words before and after spell_index
                              let spell_name = "";
                              let use_index = spell_index - 1;
                              while (use_index > -1) {
                                spell_name = (`${pieces[use_index].toLocaleLowerCase()} ${spell_name}`).trim();
                                // console.log(spell_name);
                                if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
                                  debugger;
                                }
                                
                                spell = this.getSpellByName(spell_name);
                                if (spell) {
                                  break;
                                } else {
                                  use_index--;
                                }
                              }
                              if (!spell) {
                                spell_name = "";
                                use_index = spell_index + 1;
                                while (use_index < pieces.length) {
                                  spell_name = (`${spell_name} ${pieces[use_index].toLocaleLowerCase()}`).trim();
                                  if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
                                    debugger;
                                  }
                                  
                                  spell = this.getSpellByName(spell_name);
                                  if (spell) {
                                    break;
                                  } else {
                                    use_index++;
                                  }
                                }
                              }
                            }
                            if (spell instanceof Spell) {
                              const save_effect = spell.effects.find(o => o.attack_type === "Save");
                              if (save_effect) {
                                save_type = save_effect.saving_throw_ability_score;
                              } else {
                                console.error(pieces);
                                console.error(dc_index);
                                console.error(dc);
                                debugger;
                              }
                            } else {
                              spell_index = pieces.indexOf("casts");
                              if (spell_index < 0) {
                                spell_index = pieces.indexOf("cast");
                              }
                              if (spell_index > -1) {
                                // Check the words before and after spell_index
                                let spell_name = "";
                                let use_index = spell_index - 1;
                                while (use_index > -1) {
                                  spell_name = (`${pieces[use_index].toLocaleLowerCase()} ${spell_name}`).trim();
                                  // console.log(spell_name);
                                  if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
                                    debugger;
                                  }
                                  
                                  spell = this.getSpellByName(spell_name);
                                  if (spell) {
                                    break;
                                  } else {
                                    use_index--;
                                  }
                                }
                                if (!spell) {
                                  spell_name = "";
                                  use_index = spell_index + 1;
                                  while (use_index < pieces.length) {
                                    spell_name = (`${spell_name} ${pieces[use_index].toLocaleLowerCase()}`).trim();
                                    if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
                                      debugger;
                                    }
                                    
                                    spell = this.getSpellByName(spell_name);
                                    if (spell) {
                                      break;
                                    } else {
                                      use_index++;
                                    }
                                  }
                                }
                              }
                              if (spell instanceof Spell) {
                                const save_effect = spell.effects.find(o => o.attack_type === "Save");
                                if (save_effect) {
                                  save_type = save_effect.saving_throw_ability_score;
                                } else {
                                  console.error(pieces);
                                  console.error(dc_index);
                                  console.error(dc);
                                  // debugger;
                                }
                              } else {
                                console.error(pieces);
                                console.error(dc_index);
                                console.error(dc);
                                debugger;
                              }
                            }
                          }
                        }
                        
                        const ability_mod = dc - 8 - c.proficiency_modifier;
                        
                        if (ca.name.includes("Breath")) {
                          // Breath Weapons the DC is generally based on CON.
                          if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                            using_ability = "CON";
                          }
                        }
                        if (using_ability === "") {
                          // This order is the most likely.
                          if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
                            using_ability = "CHA";
                          } else if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
                            using_ability = "STR";
                          } else if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
                            using_ability = "DEX";
                          } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
                            using_ability = "WIS";
                          } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
                            using_ability = "INT";
                          } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                            using_ability = "CON";
                          }
                        }
                        if (using_ability === "") {
                          // Could be anything.  Just leave it as a base.
                          ca.dc.base = dc;
                        } else {
                          ca.dc.base = 8;
                          ca.dc.add_prof_bonus_mult = 1;
                          // ca.dc.add_ability_mod_mult = 1;
                          // ca.dc.add_ability_scores.push(using_ability);
                          ca.dc.add_stats_groups = [{ 
                            true_id: uuidv4().toString(),
                            add_ability_scores: [using_ability],
                            mod_or_score: "Mod",
                            add_mult: 1,
                            multiple_type: "And"
                          }];
                          ca.description = ca.description.replace(`DC ${dc}`, `{dc_string.${using_ability}}`);
                          using_ability_mod = ability_mod;
                        }
                        ca.saving_throw_ability_score = ABILITY_SCORE_FULL_TO_ABBREV_MAP[save_type];
                      }
                    }
                    if (desc_str.includes("Attack Roll:") || desc_str.includes("Attack:")) {
                      // New stuff:
                      // <p><strong><em>Fist.</em></strong> <em>Melee Attack
                      //   Roll:</em> +5, reach 5 ft. <em>Hit:</em> 5 (1d4 + 3) Bludgeoning damage.</p>
                      // Old stuff:
                      // <p><span>Tentacle.&nbsp;</span><span>Melee Weapon Attack:</span>&nbsp;+9 to hit, reach
                      //   10 ft., one target.&nbsp;<span>Hit:</span>&nbsp;12 (2d6 + 5) bludgeoning damage.
                      //   If the target is a creature, it must succeed on a DC 14 Constitution saving throw or become diseased. The
                      //   disease has no effect for 1 minute and can be removed by any magic that cures disease. After 1 minute, the
                      //   diseased creature’s skin becomes translucent and slimy, the creature can’t regain hit points unless it is
                      //   underwater, and the disease can be removed only by <a>heal</a> or
                      //   another disease-curing spell of 6th level or higher. When the creature is outside a body of water, it takes 6
                      //   (1d12) acid damage every 10 minutes unless moisture is applied to the skin before 10 minutes have passed.</p>

                      if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
                        debugger;
                      }
                      let attack_num = 0;
                      let index = desc_str.indexOf("Attack Roll:");
                      if (index > -1) {
                        let attack_str = desc_str.substring(index + 12).trim();
                        index = attack_str.indexOf(",");
                        attack_str = attack_str.substring(0, index);
                        attack_str = attack_str.replace("+", "");
                        attack_num = +attack_str;
                      } else {
                        index = desc_str.indexOf("Attack:");
                        if (index > -1) {
                          let attack_str = desc_str.substring(index + 7).trim();
                          index = attack_str.indexOf(",");
                          attack_str = attack_str.substring(0, index);
                          attack_str = attack_str.replace("+", "");
                          attack_str = attack_str.replace("to hit", "").trim();
                          attack_num = +attack_str;
                        }
                      }
                      if (isNaN(attack_num)) {
                        console.error("Attack Parse Error");
                        debugger;
                      } else {
                        // Check New Method first
                        const ability_mod = attack_num - c.proficiency_modifier;
                        
                        if (using_ability_atk === "") {
                          if (desc_str.includes("Spell")) {
                            // This order is the most likely.
                            if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
                              using_ability_atk = "CHA";
                            } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
                              using_ability_atk = "WIS";
                            } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
                              using_ability_atk = "INT";
                            } else if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
                              using_ability_atk = "STR";
                            } else if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
                              using_ability_atk = "DEX";
                            } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                              using_ability_atk = "CON";
                            }
                          } else if (desc_str.includes("Ranged")) {
                            // This order is the most likely.
                            if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
                              using_ability_atk = "DEX";
                            } else if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
                              using_ability_atk = "STR";
                            } else if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
                              using_ability_atk = "CHA";
                            } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
                              using_ability_atk = "WIS";
                            } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
                              using_ability_atk = "INT";
                            } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                              using_ability_atk = "CON";
                            }
                          } else {
                            // This order is the most likely.
                            if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
                              using_ability_atk = "STR";
                            } else if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
                              using_ability_atk = "DEX";
                            } else if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
                              using_ability_atk = "CHA";
                            } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
                              using_ability_atk = "WIS";
                            } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
                              using_ability_atk = "INT";
                            } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                              using_ability_atk = "CON";
                            }
                          }
                        }
                        if (using_ability_atk === "") {
                          // Could be anything.  Just leave it as a base.
                          ca.attack_bonus.base = attack_num;
                        } else {
                          ca.attack_bonus.add_prof_bonus_mult = 1;
                          // ca.attack_bonus.add_ability_mod_mult = 1;
                          // ca.attack_bonus.add_ability_scores.push(using_ability_atk);
                          ca.attack_bonus.add_stats_groups = [{ 
                            true_id: uuidv4().toString(),
                            add_ability_scores: [using_ability_atk],
                            mod_or_score: "Mod",
                            add_mult: 1,
                            multiple_type: "And"
                          }];
                          if (ca.description.includes(`Attack Roll: ${common_util.addPlusMaybe(attack_num)}`)) {
                            ca.description = ca.description.replace(`Attack Roll: ${common_util.addPlusMaybe(attack_num)}`, `Attack Roll: {attack_string.${using_ability_atk}}`);
                          } else if (ca.description.includes(`Attack: ${common_util.addPlusMaybe(attack_num)}`)) {
                            ca.description = ca.description.replace(`Attack: ${common_util.addPlusMaybe(attack_num)}`, `Attack: {attack_string.${using_ability_atk}}`);
                          }
                          using_ability_atk_mod = ability_mod;
                        }
                      }
                    }
                    if (desc_str.includes("reach")) {
                      let sub = desc_str.substring(desc_str.indexOf("reach") + 5).trim();
                      sub = sub.substring(0, sub.indexOf(" ")).trim();
                      ca.range = sub;
                    } else if (desc_str.includes("range")) {
                      let sub = desc_str.substring(desc_str.indexOf("range") + 5).trim();
                      sub = sub.substring(0, sub.indexOf(" ")).trim();
                      ca.range = sub;
                    }
                    let damage_str = desc_str.toLocaleLowerCase();
                    const lower_case = ca.description.toLowerCase();
                    // if (name.includes(looking_for_fb_name)) {
                    //   console.log(damage_str);
                    // }
                    while (damage_str.includes("damage")) {
                      let damage_sub = damage_str.substring(0, damage_str.indexOf("damage") - 1).trim();
                      damage_str = damage_str.substring(damage_str.indexOf("damage") + 6);
                      const words = damage_sub.split(" ");
                      // console.log(words);
                      const damage_type = common_util.capitalize_first(words[words.length - 1]);
                      if (damage_sub.includes("(") && damage_sub.includes(")")) {
                        let start_index = damage_sub.indexOf("(");
                        start_index -= 2;
                        while (start_index >= 0 && damage_sub[start_index] !== " ") {
                          start_index--;
                        }
                        let replace_me = damage_sub.substring(start_index);
                        const end_index = replace_me.indexOf(")") + 1;
                        replace_me = replace_me.substring(0, end_index).trim();
                        damage_sub = damage_sub.substring(damage_sub.indexOf("("));
                        damage_sub = damage_sub.substring(0, damage_sub.indexOf(")"));
                        damage_sub = damage_sub.replace("(","").replace(")","");
                        damage_sub = common_util.normalizeDashes(damage_sub);
                        let count = 0;
                        let size = 0;
                        let flat = 0;
                        if (damage_sub.includes("+")) {
                          const flat_str = damage_sub.substring(damage_sub.indexOf("+")).replace("+","");
                          damage_sub = damage_sub.substring(0, damage_sub.indexOf("+")).replace("+","");
                          flat = +flat_str;
                        } else if (damage_sub.includes("-")) {
                          const flat_str = damage_sub.substring(damage_sub.indexOf("-")).replace("-","");
                          damage_sub = damage_sub.substring(0, damage_sub.indexOf("-")).replace("-","");
                          flat = -1 * +flat_str;
                        } 
                        if (damage_sub.includes("d")) {
                          const pieces = damage_sub.split("d");
                          count = +pieces[0];
                          size = +pieces[1];
                        }
                        const effect = new AbilityEffect();
                        effect.type = "Damage";
                        effect.damage_types.push(isDamageType(damage_type) ? damage_type as DamageType : "Typeless");
                        if (ca.effects.length === 0) {
                          const first_part = lower_case.substring(0, 20);
                          if (first_part.includes("melee")) {
                            if (first_part.includes("spell")) {
                              effect.attack_type = "Melee Spell";
                            } else {
                              effect.attack_type = "Melee";
                            }
                          } else if (first_part.includes("ranged")) {
                            if (first_part.includes("spell")) {
                              effect.attack_type = "Ranged Spell";
                            } else {
                              effect.attack_type = "Ranged";
                            }
                          } else if (ca.saving_throw_ability_score !== "") {
                            effect.attack_type = "Save";
                            effect.saving_throw_ability_score = ca.saving_throw_ability_score;
                          }
                          if (ca.range !== "") {
                            try {
                              const range = +ca.range;
                              if (!isNaN(range)) {
                                effect.target_int.range.base = range;
                              }
                            } catch (ex) { }
                          }
                          // X-foot cone
                          if (ca.description.includes("-foot")) {
                            const foot_index = ca.description.indexOf("-foot");
                            let area_str = "";
                            let i = 1;
                            while (ca.description[foot_index - i] !== " ") {
                              area_str = ca.description[foot_index - i] + area_str;
                              ++i;
                            }
                            // debugger;
                            i = 6;
                            let shape_str = "";
                            while (ca.description[foot_index + i] !== " ") {
                              shape_str += ca.description[foot_index + i];
                              ++i;
                            }
                            if (shape_str.endsWith(".")) {
                              shape_str = shape_str.substring(0, shape_str.length - 1);
                            }
                            effect.target_int.target_type = "Area";
                            try {
                              const area = +area_str;
                              if (!isNaN(area)) {
                                effect.target_int.area.base = area;
                              }
                            } catch (ex) { }
                            shape_str = common_util.capitalize_first(shape_str);
                            if (AREA_TYPES.includes(shape_str)) {
                              effect.target_int.area_type = shape_str as AreaType;
                            } else {
                              effect.target_int.area_type = "Sphere";
                            }
                          }
                        }
                        const potence = new Potence();
                        potence.level = 1;
                        potence.rolls.count.base = count;
                        potence.rolls.size.base = size;
                        // console.log(damage_str);
                        // console.log(damage_sub);
                        if (flat !== 0) {
                          if (using_ability !== "" && using_ability_atk === "") {
                            if (using_ability_mod <= flat) {
                              potence.rolls.flat.base = flat - using_ability_mod;
                              // potence.rolls.flat.add_ability_mod_mult = 1;
                              if (using_ability === c.spellcasting_ability) {
                                // potence.rolls.flat.add_ability_scores.push("Spellcasting");
                                potence.rolls.flat.add_stats_groups = [{ 
                                  true_id: uuidv4().toString(),
                                  add_ability_scores: ["Spellcasting"],
                                  mod_or_score: "Mod",
                                  add_mult: 1,
                                  multiple_type: "And"
                                }];
                              } else {
                                // potence.rolls.flat.add_ability_scores.push(using_ability);
                                potence.rolls.flat.add_stats_groups = [{ 
                                  true_id: uuidv4().toString(),
                                  add_ability_scores: [using_ability],
                                  mod_or_score: "Mod",
                                  add_mult: 1,
                                  multiple_type: "And"
                                }];
                              }
                            } else {
                              potence.rolls.flat.base = flat;
                            }
                          } else if (using_ability === "" && using_ability_atk !== "") {
                            if (using_ability_atk_mod <= flat) {
                              potence.rolls.flat.base = flat - using_ability_atk_mod;
                              // potence.rolls.flat.add_ability_mod_mult = 1;
                              if (using_ability_atk === c.spellcasting_ability) {
                                // potence.rolls.flat.add_ability_scores.push("Spellcasting");
                                potence.rolls.flat.add_stats_groups = [{ 
                                  true_id: uuidv4().toString(),
                                  add_ability_scores: ["Spellcasting"],
                                  mod_or_score: "Mod",
                                  add_mult: 1,
                                  multiple_type: "And"
                                }];
                              } else {
                                // potence.rolls.flat.add_ability_scores.push(using_ability_atk);
                                potence.rolls.flat.add_stats_groups = [{ 
                                  true_id: uuidv4().toString(),
                                  add_ability_scores: [using_ability_atk],
                                  mod_or_score: "Mod",
                                  add_mult: 1,
                                  multiple_type: "And"
                                }];
                              }
                            } else {
                              potence.rolls.flat.base = flat;
                            }
                          } else if (using_ability !== "" && using_ability_atk !== "") {
                            if (using_ability_atk_mod === flat) {
                              potence.rolls.flat.base = 0;
                              // potence.rolls.flat.add_ability_mod_mult = 1;
                              if (using_ability_atk === c.spellcasting_ability) {
                                // potence.rolls.flat.add_ability_scores.push("Spellcasting");
                                potence.rolls.flat.add_stats_groups = [{ 
                                  true_id: uuidv4().toString(),
                                  add_ability_scores: ["Spellcasting"],
                                  mod_or_score: "Mod",
                                  add_mult: 1,
                                  multiple_type: "And"
                                }];
                              } else {
                                // potence.rolls.flat.add_ability_scores.push(using_ability_atk);
                                potence.rolls.flat.add_stats_groups = [{ 
                                  true_id: uuidv4().toString(),
                                  add_ability_scores: [using_ability_atk],
                                  mod_or_score: "Mod",
                                  add_mult: 1,
                                  multiple_type: "And"
                                }];
                              }
                            } else if (using_ability_mod === flat) {
                              potence.rolls.flat.base = 0;
                              // potence.rolls.flat.add_ability_mod_mult = 1;
                              if (using_ability === c.spellcasting_ability) {
                                // potence.rolls.flat.add_ability_scores.push("Spellcasting");
                                potence.rolls.flat.add_stats_groups = [{ 
                                  true_id: uuidv4().toString(),
                                  add_ability_scores: ["Spellcasting"],
                                  mod_or_score: "Mod",
                                  add_mult: 1,
                                  multiple_type: "And"
                                }];
                              } else {
                                // potence.rolls.flat.add_ability_scores.push(using_ability);
                                potence.rolls.flat.add_stats_groups = [{ 
                                  true_id: uuidv4().toString(),
                                  add_ability_scores: [using_ability],
                                  mod_or_score: "Mod",
                                  add_mult: 1,
                                  multiple_type: "And"
                                }];
                              }
                            } else if (using_ability_mod < flat) {
                              potence.rolls.flat.base = flat - using_ability_mod;
                              // potence.rolls.flat.add_ability_mod_mult = 1;
                              if (using_ability === c.spellcasting_ability) {
                                // potence.rolls.flat.add_ability_scores.push("Spellcasting");
                                potence.rolls.flat.add_stats_groups = [{ 
                                  true_id: uuidv4().toString(),
                                  add_ability_scores: ["Spellcasting"],
                                  mod_or_score: "Mod",
                                  add_mult: 1,
                                  multiple_type: "And"
                                }];
                              } else {
                                // potence.rolls.flat.add_ability_scores.push(using_ability);
                                potence.rolls.flat.add_stats_groups = [{ 
                                  true_id: uuidv4().toString(),
                                  add_ability_scores: [using_ability],
                                  mod_or_score: "Mod",
                                  add_mult: 1,
                                  multiple_type: "And"
                                }];
                              }
                            } else if (using_ability_atk_mod < flat) {
                              potence.rolls.flat.base = flat - using_ability_atk_mod;
                              // potence.rolls.flat.add_ability_mod_mult = 1;
                              if (using_ability_atk === c.spellcasting_ability) {
                                // potence.rolls.flat.add_ability_scores.push("Spellcasting");
                                potence.rolls.flat.add_stats_groups = [{ 
                                  true_id: uuidv4().toString(),
                                  add_ability_scores: ["Spellcasting"],
                                  mod_or_score: "Mod",
                                  add_mult: 1,
                                  multiple_type: "And"
                                }];
                              } else {
                                // potence.rolls.flat.add_ability_scores.push(using_ability_atk);
                                potence.rolls.flat.add_stats_groups = [{ 
                                  true_id: uuidv4().toString(),
                                  add_ability_scores: [using_ability_atk],
                                  mod_or_score: "Mod",
                                  add_mult: 1,
                                  multiple_type: "And"
                                }];
                              }
                            } else {
                              potence.rolls.flat.base = flat;
                            }
                          }
                        }
                        ca.description = ca.description.replace(replace_me, potence.potence_string);
                        effect.potences.push(potence);
                        ca.effects.push(effect);
                      }
                    }
                    if (ca.effects.length === 0) {
                      const effect = new AbilityEffect();
                      if (ca.dc.isBlank()) {
                        effect.type = "Utility";
                      } else {
                        effect.type = "Control";
                        effect.attack_type = "Save";
                        effect.saving_throw_ability_score = ca.saving_throw_ability_score;
                        const conditions = this.conditions;
                        for (const condition of conditions.filter(o => o.common)) {
                          const condition_name = condition.name;
                          if (ca.description.includes(condition_name) || ca.description.includes(condition_name.toLowerCase())) {
                            effect.utility_detail = condition_name;
                          }
                        }
                      }
                      ca.effects.push(effect);
                    }
                    if (fbType === "Legendary Actions") {
                      // debugger;
                      ca.consumed_resource.resource_consumed = "Legendary Action";
                      if (ca.name.includes("(Costs") && ca.name.includes("Actions)")) {
                        try {
                          // (Costs X Actions)
                          const actions_str = ca.name.substring(ca.name.indexOf("(Costs"));
                          const pieces = common_util.cleanForCompare(actions_str).split(" ");
                          const action_num = +pieces[1];
                          if (!isNaN(action_num)) {
                            ca.consumed_resource.amount_consumed = action_num;
                          }
                        } catch (ex) { }
                      } else {
                        ca.consumed_resource.amount_consumed = 1;
                      }
                    } else {
                      if (ca.name.includes("/Day)")) {
                        ca.consumed_resource.amount_consumed = 1;
                        
                        const times_index = ca.name.indexOf("/Day");
                        
                        let times_str = "";
                        let i = 1;
                        while (ca.name[times_index - i] !== " ") {
                          times_str = ca.name[times_index - i] + times_str;
                          ++i;
                        }
                        
                        try {
                          const times = +times_str;
                          if (!isNaN(times)) {
                            ca.consumed_resource.special_resource_amount = new UpgradableNumber(times);
                          }
                        } catch (ex) { }
                      }
                      if (fbType === "Bonus Actions") {
                        ca.casting_time = "BA";
                      } else if (fbType === "Reactions") {
                        ca.casting_time = "RA";
                      }
                    }
                    // Bite. Melee Weapon Attack: +12 to hit, reach 10 ft., one target. 
                    // Hit: 18 (2d10 + 7) piercing damage plus 5 (1d10) lightning damage.

                    // Frightful Presence. Each creature of the dragon's choice that is 
                    // within 120 feet of the dragon and aware of it must succeed on a 
                    // DC 17 Wisdom saving throw or become frightened for 1 minute. 
                    // A creature can repeat the saving throw at the end of each of its turns, 
                    // ending the effect on itself on a success. If a creature's saving throw 
                    // is successful or the effect ends for it, the creature is immune to the 
                    // dragon's Frightful Presence for the next 24 hours.

                    if (ca.range.length > 0 && ca.effects.length > 0) {
                      const pieces = ca.range.split("/");
                      const range1 = +pieces[0];
                      const range2 = pieces.length > 1 ? +pieces[1] : 0;
                      if (!isNaN(range1) && !isNaN(range2)) {
                        ca.effects[0].target_int.range.base = range1;
                        ca.effects[0].target_int.range2.base = range2;
                      }
                    }
                    // Lightning Breath (Recharge 5–6)
                    // Ink Cloud (1/Day)
                    if (ca.name.includes("(Recharge 5-6)")) {
                      ca.consumed_resource.recharge_die = 6;
                      ca.consumed_resource.recharge_on = [5,6];
                      ca.consumed_resource.resource_consumed = "Recharge";
                    } else if (ca.name.includes("(Recharge 4-6)")) {
                      ca.consumed_resource.recharge_die = 6;
                      ca.consumed_resource.recharge_on = [4,5,6];
                      ca.consumed_resource.resource_consumed = "Recharge";
                    } else if (ca.name.includes("(Recharge 6)")) {
                      ca.consumed_resource.recharge_die = 6;
                      ca.consumed_resource.recharge_on = [6];
                      ca.consumed_resource.resource_consumed = "Recharge";
                    } else if (ca.name.includes("/Day)")) {
                      ca.consumed_resource.resource_consumed = "Special";
                      ca.consumed_resource.amount_consumed = 1;
                      ca.consumed_resource.special_resource_refresh_rule = "Long Rest";
                      try {
                        let pieces = ca.name.split("(");
                        pieces = pieces[1].split("/");
                        const x_day = +pieces[0];
                        if (!isNaN(x_day)) {
                          ca.consumed_resource.special_resource_amount = new UpgradableNumber(x_day);
                        }
                      } catch (ex) {
                        ca.consumed_resource.special_resource_amount = new UpgradableNumber(1);
                      }
                    }
                  }
                  return ca;
                }
              }
              new_descs = [];
              for (const dv of descVersions) {
                const temp_ff = makeFF(dv);
                
                if (temp_ff && !descVersions.includes(temp_ff.description)) {
                  new_descs.push(temp_ff.description);
                }
              }
              descVersions.push(...new_descs);
              ff = makeFF(desc_helper);
              if (ff && !descVersions.includes(ff.description)) {
                descVersions.push(ff.description);
              }
              
              const desc = common_util.cleanSpaces(ff ? `${ff.description}` : `${content_str}`);
              if (!descVersions.includes(desc)) {
                descVersions.push(desc);
              }
              if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
                console.log([...descVersions]);
              }
              new_descs = [];
              for (const dv of descVersions) {
                const new_desc = common_util.cleanSpaces(dv);
                if (!descVersions.includes(new_desc) && !new_descs.includes(new_desc)) {
                  new_descs.push(new_desc);
                }
              }
              descVersions.push(...new_descs);
              let ffsWithName: FloatingFeature[] = [];
              if (ff) {
                if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
                  console.log([...descVersions]);
                }
                const feature_type = ff.feature_type;
                ffsWithName = ff && ff.feature_type === "Creature Ability" ? 
                  this.all_creature_features.filter(o => o.name === name) :
                  this.all_floating_features.filter(o => o.feature_type === feature_type && o.name === name);
              }
              if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
                console.log([...descVersions]);
                console.log(ffsWithName.map(o => o.description));
              }
              content_str = desc; // Update the fb's description to be the best cleaned one, so we can update the ff to use it as well.
              let old_ff = ffsWithName.find(o => 
                common_util.cleanCompare(o.description, desc) //, name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name))
              );
              if (!old_ff) {
                for (const dv of descVersions) {
                  old_ff = ffsWithName.find(o => 
                    common_util.cleanCompare(o.description, dv) //, name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name))
                  );
                  if (old_ff) {
                    break;
                  }
                }
              }
              if (old_ff) {
                ff = old_ff;
              }
            }
            if (ff) {
              // debugger;
              if (fbType === "Actions" || fbType === "Bonus Actions" || fbType === "Reactions") {
                const feature = new Feature();
                feature.floating_feature = ff;
                feature.floating_feature_id = ff._id;
                fb.creature_fb_type = "Actions";
                fb.fb_name = ff.name;
                fb.level = c.min_challenge_rating;
                fb.features.push(feature);
              } else if (fbType === "Legendary Actions") {
                const feature = new Feature();
                feature.floating_feature = ff;
                feature.floating_feature_id = ff._id;
                fb.creature_fb_type = "Legendary Actions";
                fb.fb_name = ff.name;
                fb.level = c.min_challenge_rating;
                fb.features.push(feature);
              } else if (fbType === "Traits") {
                const feature = new Feature();
                feature.floating_feature = ff;
                feature.floating_feature_id = ff._id;
                fb.creature_fb_type = "Traits";
                fb.fb_name = ff.name;
                fb.level = c.min_challenge_rating;
                fb.features.push(feature);
              } else {
                // console.log(action_type);
                // console.log(content_str);
                debugger;
              }
            } else {
              // console.log(action_type);
              // console.log(content_str);
              debugger;
            }

            fb.fb_name = name;
            fb.description = content_str;
            c.fbs.push(fb);
          }
        } 
      }
    }

    for (let j = 0; j < p_els.length; ++j) {
      const p = p_els[j] as HTMLElement;
      if (p.classList.contains("Stat-Block-Styles_Stat-Block-Title")) {
        // <p class="Stat-Block-Styles_Stat-Block-Title">
        //   <a class="tooltip-hover monster-tooltip">Aboleth</a>
        // </p>
        c.name = p.innerText.trim();
        existing_c = old_creatures.find(o => o.name === c.name);
        if (existing_c) {
          await this.loadObject(existing_c);
        }
      } else if (p.classList.contains("Stat-Block-Styles_Stat-Block-Metadata")) {
        if (c.creature_type === "None") {
          // <p class="Stat-Block-Styles_Stat-Block-Metadata">Large aberration, lawful evil</p>
          // <p class="Stat-Block-Styles_Stat-Block-Metadata">Gargantuan Dragon (Gem), Typically Neutral</p>
          if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Metadata") {
            debugger;
          }
          const pieces = p.innerText.split(",");
          const pieces2 = common_util.cleanForCompare(pieces[0]).split(" ");
          c.size_ugn.base = SIZE_TO_NUM_MAP[common_util.capitalize_first(pieces2[0])];
          if (pieces2.length === 2) {
            const ct_str = common_util.capitalize_first(pieces2[1]);
            if (isCreatureType(ct_str)) {
              c.creature_type = ct_str;
            }
          } else if (pieces2.length > 2) {
            // <p class="Stat-Block-Styles_Stat-Block-Metadata">Huge fiend (demon), chaotic evil</p>
            // debugger;
            const ct_str = common_util.capitalize_first(pieces2[1]);
            if (isCreatureType(ct_str)) {
              c.creature_type = ct_str;
            }
            let subtype_str = pieces[0].substring(common_util.cleanForCompare(pieces[0]).indexOf(pieces2[1]) + pieces2[1].length).trim();
            subtype_str = common_util.capitalize_firsts(subtype_str.replace("(", "").replace(")", ""));
            c.subtypes = [subtype_str];
          } else {
            debugger;
          }
          if (common_util.cleanForCompare(c.name).includes(c.creature_type.toLocaleLowerCase())) {
            c.small_name = c.creature_type.toLocaleLowerCase();
          }
          const alignment_string = pieces[1].trim();
          c.alignment = alignment_string;
        }
      } else if (p.classList.contains("Stat-Block-Styles_Stat-Block-Data")) {
        if (c.armor_class.isBlank() && p.innerText.includes("Armor Class")) {
          // <p class="Stat-Block-Styles_Stat-Block-Data">
          //   <span class="Sans-Serif-Character-Styles_Bold-Sans-Serif">Armor Class</span>&nbsp;17 (natural armor)</p>
          let pieces = common_util.cleanForCompare(p.innerText).split(" ");
          if (pieces.length > 2) {
            c.armor_class.base = +pieces[2];
            pieces = pieces.slice(3);
            c.ac_flavor = pieces.join(" ").replace("(","").replace(")","");
          }
        } else if (c.hit_dice.length === 0 && p.innerText.includes("Hit Points")) {
          // <p class="Stat-Block-Styles_Stat-Block-Data">
          //   <span class="Sans-Serif-Character-Styles_Bold-Sans-Serif">Hit Points</span>&nbsp;135 (18d10 + 36)</p>
          let index = p.innerText.indexOf("(");
          let hd_str = p.innerText.substring(index + 1).replace(")", "");
          index = hd_str.indexOf(" ");
          if (index > 0) {
            hd_str = hd_str.substring(0, index);
          }
          const pieces = hd_str.split("d");
          if (pieces.length === 2) {
            const hd = new HitDiceUpgradable();
            hd.count.base = +pieces[0];
            c.hit_dice.push(hd);
          }
        } else if (Object.keys(attr_entry.speeds).length === 0 && p.innerText.includes("Speed")) {
          // <p class="Stat-Block-Styles_Stat-Block-Data">
          //   <span class="Sans-Serif-Character-Styles_Bold-Sans-Serif">Speed</span>&nbsp;10 ft., swim 40 ft.</p>
          if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Speed") {
            debugger;
          }
          let spd_str = p.innerText;
          const pieces = common_util.cleanForCompare(spd_str).split(" ");
          let spd_type: SpeedType = "walk";
          for (const piece of pieces) {
            if (isSpeedType(piece)) {
              spd_type = piece;
            } else if (spd_type === "fly" && piece.includes("hover")) {
              attr_entry.hover = true;
            } else if (!piece.includes("ft.")) {
              attr_entry.speeds[spd_type] = +piece;
            }
          }
        } else if (p.innerText.includes("Saving Throws")) {
          // <p class="Stat-Block-Styles_Stat-Block-Data">
          //  <span class="Sans-Serif-Character-Styles_Bold-Sans-Serif">Saving Throws</span>&nbsp;Con +6, Int +8, Wis +6
          // </p>
          const pieces = common_util.cleanForCompare(p.innerText).split(" ");
          for (const piece of pieces) {
            const stat = piece.replace(",", "").toLocaleUpperCase();
            if (isStatType(stat)) {
              attr_entry.save_profs.push(stat);
            }
          }
        } else if (p.innerText.includes("Skills")) {
          skills_el = p; // It needs to be processed after we know the challenge rating.
        } else if (p.innerText.includes("Senses")) {
          // <p class="Stat-Block-Styles_Stat-Block-Data"><span class="Sans-Serif-Character-Styles_Bold-Sans-Serif">Senses</span>&nbsp;<a
          //   class="tooltip-hover sense-tooltip">darkvision</a> 120
          // ft., passive Perception 20</p>
          if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Senses") {
            debugger;
          }
          const pieces = common_util.cleanForCompare(p.innerText).split(" ");
          let sense: Sense | undefined;
          for (const piece of pieces) {
            const sense_str = piece.toLocaleLowerCase().replace(",", "").replace(" ", "");
            if (sense) {
              const num = +sense_str;
              if (!isNaN(num)) {
                attr_entry.senses[sense.name] = num;
                continue;
              } else {
                sense = undefined;
              }
            }
            const sense_maybe = SENSES.find(o => o.name.toLocaleLowerCase().replace(" ", "").includes(sense_str));
            if (sense_maybe) {
              sense = sense_maybe;
            }
          }
        } else if (p.innerText.includes("Languages")) {
          // <p class="Stat-Block-Styles_Stat-Block-Data"><span
          //     class="Sans-Serif-Character-Styles_Bold-Sans-Serif">Languages</span>&nbsp;Deep Speech, telepathy 120
          //   ft.</p>
          const language_str = p.innerText.replace("Languages", "").trim();
          attr_entry.languages = language_str;
        } 
      } else if (p.classList.contains("Stat-Block-Styles_Stat-Block-Data-Last")) {
        if (p.innerText.includes("Challenge")) {
          // <p class="Stat-Block-Styles_Stat-Block-Data-Last"><span
          //   class="Sans-Serif-Character-Styles_Bold-Sans-Serif">Challenge</span>&nbsp;10 (5,900 XP)</p>
          const pieces = p.innerText.replace("Challenge", "").trim().split(" ");
          if (pieces.length > 0) {
            const cr_str = pieces[0];
            if (cr_str.includes("/")) { // 1/4 or 1/2
              const pieces2 = cr_str.split("/");
              const num = +pieces2[0];
              const den = +pieces2[1];
              if (!isNaN(num) && !isNaN(den)) {
                c.challenge_rating = num / den;
              } else {
                debugger;
              }
            } else {
              const num = +cr_str;
              if (!isNaN(num)) {
                c.challenge_rating = num;
              } else {
                debugger;
              }
            }
          } else {
            debugger;
          }
        } 
      } else if (p.classList.contains("Stat-Block-Styles_Stat-Block-Body") || 
        p.classList.contains("Stat-Block-Styles_Stat-Block-Hanging") ||
        p.classList.contains("Stat-Block-Styles_Stat-Block-Body-Last--apply-before-heading-")
      ) {
        if (p.innerText.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
          console.log(p);
        }
        if (fbType === "Legendary Actions" && p.innerText.includes("Only one legendary action option")) {
          // <p class="Stat-Block-Styles_Stat-Block-Body">The
          //   aboleth can take 3 legendary actions, choosing from the options below. Only one legendary action option can be
          //   used at a time and only at the end of another creature’s turn. The aboleth regains spent legendary actions at the
          //   start of its turn.</p>
          let desc = p.innerText;
          let index = desc.indexOf("can take");
          desc = desc.substring(index + 8);
          index = desc.indexOf("legendary actions");
          desc = desc.substring(0, index).trim();
          const pieces = common_util.cleanForCompare(desc).split(" ");
          const num = pieces.length > 0 ? +pieces[0] : 0;
          if (pieces.length === 1 && !isNaN(num)) {
            leg_entry.actions = num;
          } else {
            debugger;
          }
        } else {
          // Look for additional ps that should be part of it.
          const extra_ps: HTMLElement[] = [];
          for (let k = (j + 1); k < p_els.length; k++) {
            const pk = p_els[k];
            if (pk.classList.contains("Stat-Block-Styles_Stat-Block-Body") || 
              pk.classList.contains("Stat-Block-Styles_Stat-Block-Hanging") ||
              pk.classList.contains("Stat-Block-Styles_Stat-Block-Body-Last--apply-before-heading-")
            ) {
              const name_spans = pk.getElementsByTagName("span");
              const name_strongs = p.getElementsByTagName("strong");
              if (name_spans.length === 0 && name_strongs.length === 0) {
                extra_ps.push(pk);
              } else {
                break;
              }
            } else {
              break;
            }
          }
          processFB(p, extra_ps);
        }
      } else if (p.classList.contains("Stat-Block-Styles_Stat-Block-Heading")) {
        // It's a FeatureBase type
        // <p class="Stat-Block-Styles_Stat-Block-Heading">Actions</p>
        const str = p.innerText.trim();
        if (isCreatureFBType(str)) {
          if (str === "Legendary Actions") {
            legendary = true;
          }
          fbType = str;
        }
        sc = false;
      } 
    }
    if (skills_el) {
      // <p class="Stat-Block-Styles_Stat-Block-Data">
      //  <span class="Sans-Serif-Character-Styles_Bold-Sans-Serif">Skills</span>&nbsp;<a
      //     class="tooltip-hover skill-tooltip">History</a> +12, <a
      //     class="tooltip-hover skill-tooltip">Perception</a> +10
      // </p>
      const pieces = common_util.cleanForCompare(skills_el.innerText).split(" ");
      const pb = c.proficiency_modifier;
      let skill: Skill | undefined;
      for (let k = 0; k < pieces.length; ++k) {
        const piece = pieces[k].replace(",", "").replace("+", "");
        let skill_mod = +piece;
        if (isNaN(skill_mod)) {
          skill = SKILLS.find(o => o.name === common_util.capitalize_first(piece));
          if (!skill) {
            // It might be animal handling.
            if (piece === "animal") {
              skill = SKILLS.find(o => o.name === "Animal Handling");
              ++k;
            } else if (piece === "sleight") {
              skill = SKILLS.find(o => o.name === "Sleight of Hand");
              ++k;
              ++k;
            }
          }
        } else if (skill) {
          const stat_score = c.ability_scores.getBasicScore(skill.use_ability_score);
          const stat_mod = common_util.modFromScore(stat_score);
          skill_mod -= stat_mod;
          attr_entry.skill_profs[skill._id] = skill_mod / pb;
        }
      }
    }
    attr_entry.level = c.challenge_rating;
    tbl_entry.level = c.challenge_rating;
    if (leg_entry.resistances > 0 || leg_entry.actions > 0) {
      leg_entry.level = c.challenge_rating;
      c.legendaries_table.entries = [leg_entry];
    }
    if (Object.keys(resources_entry.data).length > 0) {
      resources_entry.level = c.challenge_rating;
      c.resources_table.entries = [resources_entry];
    }
    if (existing_c) {
      if (existing_c.name.includes(looking_for_creature_name)) {
        // debugger;
        console.log(existing_c.name);
      }
      existing_c.size_ugn.copy(c.size_ugn);
      existing_c.armor_class.copy(c.armor_class);
      existing_c.ac_flavor = c.ac_flavor;
      existing_c.hit_dice = c.hit_dice;
      existing_c.ability_scores = c.ability_scores;
      existing_c.fbs = c.fbs;
      existing_c.attributes_table.entries = c.attributes_table.entries;
      existing_c.table.entries = c.table.entries;
      existing_c.legendaries_table.entries = c.legendaries_table.entries;
      existing_c.spellcasting_table.entries = c.spellcasting_table.entries;
      existing_c.spellcasting_ability = c.spellcasting_ability;
      existing_c.resources_table.entries = c.resources_table.entries;
      existing_c.small_name = c.small_name;
      existing_c.creature_type = c.creature_type;
      existing_c.subtypes = c.subtypes;
      return existing_c;
    } else {
      const c_name = c.name;
      const old_dominant = all_creatures.find(o => o.name === c_name && o.isDominant());
      if (old_dominant) {
        // c.dominance_level = old_dominant.dominance_level + 1;
        c.variant_of_id = old_dominant.variant_group_id;
      }
      return c;
    }
  }

  parseCreature55e = async (m_el: Element, sb_id: string, looking_for_creature_name: string, looking_for_fb_name: string) => {
    const all_creatures = await this.getObjects<Creature>({ data_type: "creature" });
    const old_creatures = all_creatures.filter(o => o.source_book_ids.includes(sb_id));

    const c = new Creature();
    c.source_books_old = [{ type: "Source Book", id: sb_id, third_party: false }];
    c.source_books = [];
    const sb = this.source_books.find(o => o._id === sb_id);
    if (sb) {
      c.source_books.push(sb);
    }
    let existing_c: Creature | undefined;
    const name_els = m_el.getElementsByClassName("monster-tooltip");
    if (name_els[0] instanceof HTMLElement) {
      // <a class="tooltip-hover monster-tooltip">Aboleth</a>
      c.name = common_util.capitalize_firsts(name_els[0].innerText.trim().toLocaleLowerCase());
      // if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Name") {
      //   debugger;
      // }
      existing_c = old_creatures.find(o => o.name === c.name);
      if (existing_c) {
        await this.loadObject(existing_c);
      }
    }
    const p_els = m_el.getElementsByTagName("p");
    const attr_entry = new Attributes();
    const tbl_entry = new TableEntry();
    const resources_entry = new TableEntry();
    c.attributes_table.entries = [attr_entry];
    c.table.entries = [tbl_entry];
    let legendary = false;
    const leg_entry = new Legendaries();
    const stat_divs = m_el.getElementsByClassName("stats");
    if (stat_divs.length === 1) {
      // <div class="stats" data-content-chunk-id="7dea6640-cf25-4585-b2db-662dcc79e2c9">
      //   <div class="table-overflow-wrapper">
      //     <table class="physical abilities-saves" data-content-chunk-id="1e0999ef-488e-4bb9-b17b-9c69b785413f">
      //       <thead>
      //         <tr>
      //           <th>Ability</th>
      //           <th>Score</th>
      //           <th>Mod</th>
      //           <th>Save</th>
      //         </tr>
      //       </thead>
      //       <tbody>
      //         <tr>
      //           <th>Str</th>
      //           <td>21</td>
      //           <td>+5</td>
      //           <td>+5</td>
      //         </tr>
      //         <tr>
      //           <th>Dex</th>
      //           <td>9</td>
      //           <td>−1</td>
      //           <td>+3</td>
      //         </tr>
      //         <tr>
      //           <th>Con</th>
      //           <td>15</td>
      //           <td>+2</td>
      //           <td>+6</td>
      //         </tr>
      //       </tbody>
      //     </table>
      //   </div>
      //   <div class="table-overflow-wrapper">
      //     <table class="mental abilities-saves" data-content-chunk-id="c94341a1-6d75-495b-9f61-fb5672610989">
      //       <thead>
      //         <tr>
      //           <th>Ability</th>
      //           <th>Score</th>
      //           <th>Mod</th>
      //           <th>Save</th>
      //         </tr>
      //       </thead>
      //       <tbody>
      //         <tr>
      //           <th>Int</th>
      //           <td>18</td>
      //           <td>+4</td>
      //           <td>+8</td>
      //         </tr>
      //         <tr>
      //           <th>Wis</th>
      //           <td>15</td>
      //           <td>+2</td>
      //           <td>+6</td>
      //         </tr>
      //         <tr>
      //           <th>Cha</th>
      //           <td>18</td>
      //           <td>+4</td>
      //           <td>+4</td>
      //         </tr>
      //       </tbody>
      //     </table>
      //   </div>
      // </div>
      const stat_rows = stat_divs[0].getElementsByTagName("tr");
      for (let j = 0; j < stat_rows.length; ++j) {
        // <tr>
        //   <th>Str</th>
        //   <td>21</td>
        //   <td>+5</td>
        //   <td>+5</td>
        // </tr>
        const row = stat_rows[j];
        if (row.cells.length === 4) {
          const stat_name_cell = row.cells[0];
          const stat_score_cell = row.cells[1];
          const stat_mod_cell = row.cells[3];
          const stat_save_cell = row.cells[3];
          
          const stat = stat_name_cell.innerText.toLocaleUpperCase();
          if (isStatType(stat)) {
            const score = +stat_score_cell.innerText;
            if (!isNaN(score)) {
              c.ability_scores.setAbilityScore(stat, score);
            } else {
              debugger;
            }
            if (stat_mod_cell.innerText !== stat_save_cell.innerText) {
              attr_entry.save_profs.push(stat);
            }
          } else if (stat.length > 0 && stat !== "ABILITY") {
            debugger;
          }
        } else {
          debugger;
        }
      }
    }
    let skills_el: HTMLElement | undefined;
    let fbType: CreatureFBType | "Main Details" = "Main Details";
    let sc = false;

    const processFB = (p: HTMLElement, extra_ps: HTMLElement[]) => {
      // It's probably a FeatureBase
      
      const name_strings = p.getElementsByTagName("strong");
      if (name_strings.length > 0) {
        const name_strong = name_strings[0];
        let name = name_strong.innerText.trim();
        if (name.endsWith(".")) {
          name = name.substring(0, name.length - 1);
        }
        name = common_util.normalizeDashes(name);
        name = common_util.replaceAll(name, "’", "'");
        if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
          console.log(name);
        }
        if ((name.includes("At Will:") || name.includes("/Day")) && sc) {
          // This should actually be added to the spellcasting entry.
          const blurg = () => {
            // <p><strong>At Will:</strong> <a
            //     class="tooltip-hover spell-tooltip">Detect
            //     Magic</a>, <a class="tooltip-hover spell-tooltip">Mind Spike</a>
            //   (level 4 version), <a class="tooltip-hover spell-tooltip">Minor
            //     Illusion</a>, <a class="tooltip-hover spell-tooltip">Shapechange</a>
            //   (Beast or Humanoid form only, no Temporary Hit Points gained from the spell, and no <a
            //     class="tooltip-hover rule-tooltip">Concentration</a>
            //   or <a class="tooltip-hover rule-tooltip">Temporary
            //     Hit Points</a> required to maintain the spell)</p>
            // <p data-content-chunk-id="0fa7dd01-cdc8-470f-986c-ffdc51f06343"><strong>1/Day Each:</strong> <a
            //     class="tooltip-hover spell-tooltip">Greater
            //     Restoration</a>, <a class="tooltip-hover spell-tooltip">Major Image</a>
            // </p>
          }
          blurg();
          
          let sc_entry = c.spellcasting_table.entries[0];
          if (!sc_entry) {
            sc_entry = new SpellcastingEntry();
            c.spellcasting_table.entries.push(sc_entry);
          }
          const innerText = common_util.cleanForCompare(p.innerText);
          const start_string = innerText.substring(0, innerText.indexOf(":"));

          let spell_level = -1;
          let cast_type: "Slot" | "At Will" | "X/Day" | "Ritual" = "Slot";
          let x = -1;
          if (start_string.includes("cantrip") || start_string.includes("level")) {
            const level_char = start_string[0];
            if (level_char === "c") {
              spell_level = 0;
            } else {
              spell_level = +level_char;
              if (isNaN(spell_level)) {
                debugger;
              }
            }
            
            const pieces = start_string.split("(");
            if (pieces[1]) {
              const use_string = pieces[1];
              if (use_string.includes("at will")) {
                cast_type = "At Will";
              } else if (use_string.includes("ritual")) {
                cast_type = "Ritual";
              } else if (use_string.includes("/day")) {
                const pieces2 = use_string.split("/");
                x = +pieces2[0];
              } else if (use_string.includes("slot")) {
                const pieces2 = use_string.split(" ");
                const slot_count = +pieces2[0];
                if (!isNaN(slot_count)) {
                  resources_entry.data[`Slot: ${spell_level}`] = slot_count;
                }
              } else {
                debugger;
              }
            }
          } else {
            // Different format.
            // <p class="Stat-Block-Styles_Stat-Block-Hanging">At will:&nbsp;
            //   <span><a>detect magic</a></span>,&nbsp;
            //   <span><a>fog cloud</a></span>,&nbsp;
            //   <span><a>light</a></span>
            // </p>
            // <p class="Stat-Block-Styles_Stat-Block-Hanging">3/day each:&nbsp;
            //   <span><a>feather fall</a></span>,&nbsp;
            //   <span><a>fly</a></span>,&nbsp;
            //   <span><a>misty step</a></span>,&nbsp;
            //   <span><a>telekinesis</a></span>
            // </p>
            if (start_string.includes("at will")) {
              cast_type = "At Will";
            } else if (start_string.includes("/day")) {
              cast_type = "X/Day";
              const pieces = start_string.split("/");
              const x_str = pieces[0].trim();
              x = +x_str;
              if (isNaN(x)) {
                debugger;
              }
            } else {
              debugger;
            }
          }

          // sc_entry.description += `\n${innerText}`;
          const p_children = p.childNodes;
          let last_spell: iCreatureSpell | undefined;
          for (let k = 0; k < p_children.length; ++k) {
            const node = p_children[k];
            if (node instanceof HTMLAnchorElement && node.classList.contains("spell-tooltip")) {
              const spell_anchor = node;
              const spell_name = common_util.cleanForCompare(spell_anchor.innerText);
              const spell = this.getSpellByName(spell_name);
              if (spell) {
                if (last_spell && last_spell.extra_text) {
                  // Clean the extra text.
                  // debugger;
                  last_spell.extra_text = last_spell.extra_text.trim();
                  if (last_spell.extra_text.startsWith(",")) {
                    last_spell.extra_text = last_spell.extra_text.substring(1);
                  }
                  if (last_spell.extra_text.endsWith(",")) {
                    last_spell.extra_text = last_spell.extra_text.substring(0, last_spell.extra_text.length - 1);
                  }
                  last_spell.extra_text = last_spell.extra_text;
                  last_spell.extra_text = last_spell.extra_text.replace("(","").replace(")", "").trim();
                  if (last_spell.extra_text.length === 0) {
                    delete last_spell.extra_text;
                  }
                }
                spell_level = spell.level;
                last_spell = {
                  spell_level,
                  spell_id: spell._id,
                  cast_type,
                  x
                };
                sc_entry.spells.push(last_spell);
              } else {
                debugger;
              }
            } else if (last_spell) {
              // debugger;
              let text = "";
              if (node.nodeName === "#text" && node.textContent) {
                text = node.textContent;
              } else if (node instanceof HTMLElement) {
                text = node.innerText;
              }
              if (text.length > 2) {
                // less than 3 generally means it's something like ', '
                if (!last_spell.extra_text) {
                  last_spell.extra_text = "";
                }
                last_spell.extra_text += text;
              }
            }
          }
        } else {
          // <p><strong><em>Amphibious.</em></strong> The aboleth
          //     can breathe air and water.</p>
          // <p><strong><em>Fist.</em></strong> <em>Melee Attack
          //   Roll:</em> +5, reach 5 ft. <em>Hit:</em> 5 (1d4 + 3) Bludgeoning damage.</p>
          sc = false;
          let desc0 = p.innerText.substring(name.length + 1).trim();
          if (desc0.startsWith(".")) {
            desc0 = desc0.substring(1).trim();
          }
          for (const pk of extra_ps) {
            desc0 += `<p>${pk.innerText}</p>`;
          }
          let content_str = `${desc0}`;
          if (content_str.includes("cost it extra moment")) {
            content_str = common_util.replaceAll(content_str, "cost it extra moment", "cost it extra movement");
          }

          if (name.includes("Legendary Resistance")) {
            // <p><strong><em>Legendary Resistance (3/Day, or 4/Day in
            //   Lair).</em></strong> If the aboleth fails a saving throw, it can choose to succeed instead.</p>
            // Not a FeatureBase. We store these in the legendaries table
            legendary = true;
            let legendary_resistances_str = name.split("(")[1];
            if (legendary_resistances_str) {
              let lair_str = "";
              if (legendary_resistances_str.includes(" or ")) {
                lair_str = legendary_resistances_str.split(" or ")[1];
                lair_str = lair_str.substring(0, lair_str.indexOf("/"));
                
                leg_entry.res_lair = +lair_str;
                leg_entry.actions = 0;
              }
              legendary_resistances_str = legendary_resistances_str.substring(0, legendary_resistances_str.indexOf("/"));
              leg_entry.resistances = +legendary_resistances_str;
            }
          } else if (name.includes("Spellcasting")) {
            // Not a FeatureBase. We store this in the spellcasting table.
            // <p><strong><em>Spellcasting.</em></strong> The dragon
            //   casts one of the following spells, requiring no Material components and using Charisma as the spellcasting ability
            //   (spell save DC 17, +9 to hit with spell attacks):</p>
            // <p><strong>At Will:</strong> <a>Detect
            //     Magic</a>, <a>Fear</a>, <a>Melf’s Acid
            //     Arrow</a> (level 3 version)</p>
            // <p><strong>1/Day Each:</strong> <a>Speak with
            //     Dead</a>, <a>Vitriolic
            //     Sphere</a></p>
            if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Spellcasting") {
              debugger;
            }
            sc = true;
            const sc_entry = new SpellcastingEntry();
            sc_entry.level = c.challenge_rating;
            sc_entry.description = content_str;
            let desc = common_util.cleanForCompare(sc_entry.description);
            if (desc.includes(" as the spellcasting ability")) {
              let spellcasting_ability = common_util.capitalize_first(
                desc.substring(desc.indexOf("spellcasting ability is ") + 24));
              spellcasting_ability = spellcasting_ability.substring(0, 3).toUpperCase();
              c.spellcasting_ability = spellcasting_ability as AbilityScoreType;
            } else if (desc.includes("as the spellcasting")) {
              // The aarakocra casts one of the following spells, requiring no Material components 
              // and using Wisdom as the spellcasting ability (spell save DC 13):
              let index = desc.indexOf("as the spellcasting") - 2;
              while (index > 0 && desc[index] !== " ") {
                index--;
              }
              index++;
              const spellcasting_ability = desc.substring(index, index + 3).toLocaleUpperCase();
              if (ABILITY_SCORES.includes(spellcasting_ability)) {
                c.spellcasting_ability = spellcasting_ability as AbilityScoreType;
              }
            } else if (desc.includes("as spellcasting")) {
              // <p><strong><em>Spellcasting.</em></strong> The planetar
              //   casts one of the following spells, requiring no Material components and using Charisma as spellcasting ability
              //   (spell save DC 20):</p>
              let index = desc.indexOf("as spellcasting") - 2;
              while (index > 0 && desc[index] !== " ") {
                index--;
              }
              index++;
              const spellcasting_ability = desc.substring(index, index + 3).toLocaleUpperCase();
              if (ABILITY_SCORES.includes(spellcasting_ability)) {
                c.spellcasting_ability = spellcasting_ability as AbilityScoreType;
              }
            }
            if (desc.includes(" dc ")) {
              if (c.dc.isBlank()) {
                let dc_str = desc.substring(desc.indexOf(" dc ") + 4).trim();
                if (dc_str.includes(" ")) {
                  dc_str = dc_str.substring(0, dc_str.indexOf(" "));
                }
                dc_str = dc_str.replace(",","").replace(".","").replace(")","").replace(":","").trim();
                const dc_num = +dc_str;
                if (c.spellcasting_ability !== "None" && 
                  dc_num === (8 + c.proficiency_modifier + c.current_ability_scores.getModifier(c.spellcasting_ability))
                ) {
                  c.dc = new UpgradableNumber();
                  c.dc.base = 8;
                  c.dc.add_prof_bonus_mult = 1;
                  // c.dc.add_ability_mod_mult = 1;
                  // c.dc.add_ability_scores = [c.spellcasting_ability];
                  c.dc.add_stats_groups = [{ 
                    true_id: uuidv4().toString(),
                    add_ability_scores: [c.spellcasting_ability],
                    mod_or_score: "Mod",
                    add_mult: 1,
                    multiple_type: "And"
                  }];
                  sc_entry.description = common_util.replaceAll(sc_entry.description, ` dc ${dc_num}`, ` {dc_string.${c.spellcasting_ability}}`);
                  sc_entry.description = common_util.replaceAll(sc_entry.description, ` DC ${dc_num}`, ` {dc_string.${c.spellcasting_ability}}`);
                } else {
                  c.dc = new UpgradableNumber(dc_num);
                }
              }
            }
            if (desc.includes(" to hit ")) {
              let attack_str = desc.substring(0, desc.indexOf(" to hit ")).trim();
              attack_str = attack_str.substring(attack_str.lastIndexOf("+")+1);
              const attack_num = +attack_str;
              if (c.spellcasting_ability !== "None" && 
                attack_num === (c.proficiency_modifier + c.current_ability_scores.getModifier(c.spellcasting_ability))
              ) {
                c.magic_attack = new UpgradableNumber();
                c.magic_attack.add_prof_bonus_mult = 1;
                // c.magic_attack.add_ability_mod_mult = 1;
                // c.magic_attack.add_ability_scores = [c.spellcasting_ability];
                c.magic_attack.add_stats_groups = [{ 
                  true_id: uuidv4().toString(),
                  add_ability_scores: [c.spellcasting_ability],
                  mod_or_score: "Mod",
                  add_mult: 1,
                  multiple_type: "And"
                }];
                sc_entry.description = common_util.replaceAll(sc_entry.description, `+${attack_num} to hit `, `{attack_string.${c.spellcasting_ability}} to hit `);
              } else {
                c.magic_attack = new UpgradableNumber(attack_num);
              }
            }
            if (c.spellcasting_ability !== "None") {
              const stat = c.getBasicModifier(c.spellcasting_ability);
              const pb = c.proficiency_modifier;
  
              if (c.dc.base > 0) {
                c.dc.base -= stat + pb;
              } else {
                c.dc.base = 8;
              }
              c.dc.add_prof_bonus_mult = 1;
              // c.dc.add_ability_mod_mult = 1;
              // c.dc.add_ability_scores.push(c.spellcasting_ability);
              c.dc.add_stats_groups = [{ 
                true_id: uuidv4().toString(),
                add_ability_scores: [c.spellcasting_ability],
                mod_or_score: "Mod",
                add_mult: 1,
                multiple_type: "And"
              }];
  
              if (c.magic_attack.base > 0) {
                c.magic_attack.base -= stat + pb;
              }
              c.magic_attack.add_prof_bonus_mult = 1;
              // c.magic_attack.add_ability_mod_mult = 1;
              // c.magic_attack.add_ability_scores.push(c.spellcasting_ability);
              c.magic_attack.add_stats_groups = [{ 
                true_id: uuidv4().toString(),
                add_ability_scores: [c.spellcasting_ability],
                mod_or_score: "Mod",
                add_mult: 1,
                multiple_type: "And"
              }];
              c.spellcasting_table.entries = [sc_entry];
            }
          } else if (fbType !== "Main Details") {
            const fb = new FeatureBase();
            fb.creature_fb_type = fbType;

            const descVersions: string[] = [desc0];
            content_str = common_util.normalizeDashes(content_str);
            content_str = common_util.replaceAll(content_str, "’", "'");
            if (desc0 !== content_str) {
              descVersions.push(content_str);
            }
            if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
              console.log([...descVersions]);
            }

            let ff: FloatingFeature | undefined = undefined; 
            if (!ff) {
              let desc_helper = common_util.replaceAll(common_util.replaceAll(content_str, ` ${c.name} `, ` {cfs} `), ` ${c.name}'`, ` {cfs}'`);
              if (!descVersions.includes(desc_helper)) {
                descVersions.push(desc_helper);
              }
              if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
                console.log([...descVersions]);
              }
              let new_descs: string[] = [];
              for (const dv of descVersions) {
                const new_desc = common_util.replaceAll(common_util.replaceAll(dv, ` ${c.name} `, ` {cfs} `), ` ${c.name}'`, ` {cfs}'`);
                if (!descVersions.includes(new_desc) && !new_descs.includes(new_desc)) {
                  new_descs.push(new_desc);
                }
              }
              descVersions.push(...new_descs);
              if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
                console.log([...descVersions]);
              }
              if (desc_helper === content_str) {
                const desc_helper2 = common_util.replaceAll(desc_helper, "\n", "");
                if (c.small_name.length === 0) {
                  if (c.name.includes(" ")) {
                    // debugger;
                    const words = common_util.cleanForCompare(c.name).split(" ").map(o => o.toLocaleLowerCase());
                    let small_name = "";
                    for (let i = 0; i < words.length; ++i) {
                      const word = words[i];
                      if (desc_helper2.includes(` ${word} `)) {
                        if (small_name === "") {
                          small_name = word;
                        } else {
                          const prev_word = words[i - 1];
                          if (small_name.includes(prev_word) && desc_helper2.includes(` ${small_name} ${word} `)) {
                            small_name += ` ${word}`;
                          }
                        }
                      }
                    }
                    c.small_name = small_name;
                  } else {
                    // Check for some things that don't fit the above method.
                    if (c.name.toLocaleLowerCase().includes("sphinx")) {
                      c.small_name = "sphinx";
                    } else {
                      c.small_name = c.name.toLocaleLowerCase();
                    }
                  }
                }
                if (c.small_name.length > 0) {
                  desc_helper = common_util.replaceAll(content_str, c.small_name, `{cfs}`);
                  
                  new_descs = [];
                  for (const dv of descVersions) {
                    const new_desc = common_util.replaceAll(dv, c.small_name, `{cfs}`);
                    if (!descVersions.includes(new_desc) && !new_descs.includes(new_desc)) {
                      new_descs.push(new_desc);
                    }
                  }
                  descVersions.push(...new_descs);
                  if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
                    console.log([...descVersions]);
                  }
                }
              }
              const makeFF = (desc_str: string) => {
                const ff_obj: any = {
                  name,
                  description: desc_str,
                  feature_type: "Creature Ability"
                };
                if (name === "Multiattack") {
                  ff_obj.feature_type = "Minion Extra Attacks";
                  // Usually says something along the lines of "makes X attacks";
                  const sub = content_str.toLocaleLowerCase();
                  
                  const pieces = common_util.cleanForCompare(sub).split(" ");
                  const num_strings = ["two","three","four","five","six","seven","eight","nine","ten","2","3","4","5","6","7","8","9","10"];
                  let num_string = common_util.findOverlap(num_strings, pieces);
                  if (num_string.length === 0) {
                    // It's more complicated, so just make it 2
                    num_string = "2";
                  }
                  if (num_string.length > 0) {
                    switch(num_string) {
                      case "two": ff_obj.num = 2; break;
                      case "three": ff_obj.num = 3; break;
                      case "four": ff_obj.num = 4; break;
                      case "five": ff_obj.num = 5; break;
                      case "six": ff_obj.num = 6; break;
                      case "seven": ff_obj.num = 7; break;
                      case "eight": ff_obj.num = 8; break;
                      case "nine": ff_obj.num = 9; break;
                      case "ten": ff_obj.num = 10; break;
                      default: ff_obj.num = +num_string; break;
                    }
                    ff_obj.num -= 1;
                    return new UpgradableNumberFeature(ff_obj);
                  }
                } else {
                  const ca = new CreatureAbility(ff_obj);
                  if (ca instanceof CreatureAbility) {
                    if (c.name.includes(looking_for_creature_name) && ca.name.includes(looking_for_fb_name)) {
                      debugger;
                    }
                    let using_ability: AbilityScoreType | "" = "";
                    let using_ability_mod = -5;
                    
                    if (desc_str.includes("DC") && !desc_str.includes("rapple") && !desc_str.includes("(DC")) {
                      // New format: Strength Saving Throw: DC 12
                      // Old format: DC 19 Dexterity saving throw
                      // Needs to work with both
                      // debugger;
                      let dc = 0;
                      let save_type = "";
                      const pieces = common_util.cleanSpaces(desc_str).split(" ").map(o => o.replace(",",""));
                      let dc_index = -1;
                      for (let i = 0; i < pieces.length; ++i) {
                        const piece = pieces[i];
                        if (piece === "DC") {
                          dc_index = i;
                          break;
                        }
                      }
                      const dc_str = pieces[dc_index + 1].replace(",", "").replace(".","").replace(")","").replace(":","");
                      dc = +dc_str;
                      if (isNaN(dc)) {
                        console.error("DC Parse Error");
                        debugger;
                      } else {
                        // Check New Method first
                        let type_maybe = pieces[dc_index - 3];
                        if (type_maybe.length > 3) {
                          type_maybe = type_maybe.substring(0, 3).toLocaleUpperCase();
                          if (ABILITY_SCORES.includes(type_maybe)) {
                            save_type = type_maybe;
                          }
                        }
                        if (save_type === "") {
                          // Didn't find it, so Check Old Method
                          type_maybe = pieces[dc_index + 2];
                          if (type_maybe) {
                            if (type_maybe.length > 3) {
                              type_maybe = type_maybe.substring(0, 3).toLocaleUpperCase();
                              if (ABILITY_SCORES.includes(type_maybe)) {
                                save_type = type_maybe;
                              }
                            }
                          } else {
                            let spell_index = pieces.indexOf("spell");
                            if (spell_index < 0) {
                              spell_index = pieces.indexOf("Spell");
                            }
                            let spell: ModelBase | undefined = undefined;
                            if (spell_index > -1) {
                              // Check the words before and after spell_index
                              let spell_name = "";
                              let use_index = spell_index - 1;
                              while (use_index > -1) {
                                spell_name = (`${pieces[use_index].toLocaleLowerCase()} ${spell_name}`).trim();
                                // console.log(spell_name);
                                if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
                                  debugger;
                                }
                                
                                spell = this.getSpellByName(spell_name);
                                if (spell) {
                                  break;
                                } else {
                                  use_index--;
                                }
                              }
                              if (!spell) {
                                spell_name = "";
                                use_index = spell_index + 1;
                                while (use_index < pieces.length) {
                                  spell_name = (`${spell_name} ${pieces[use_index].toLocaleLowerCase()}`).trim();
                                  if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
                                    debugger;
                                  }
                                  
                                  spell = this.getSpellByName(spell_name);
                                  if (spell) {
                                    break;
                                  } else {
                                    use_index++;
                                  }
                                }
                              }
                            }
                            if (spell instanceof Spell) {
                              const save_effect = spell.effects.find(o => o.attack_type === "Save");
                              if (save_effect) {
                                save_type = save_effect.saving_throw_ability_score;
                              } else {
                                console.error(pieces);
                                console.error(dc_index);
                                console.error(dc);
                                debugger;
                              }
                            } else {
                              spell_index = pieces.indexOf("casts");
                              if (spell_index < 0) {
                                spell_index = pieces.indexOf("cast");
                              }
                              if (spell_index > -1) {
                                // Check the words before and after spell_index
                                let spell_name = "";
                                let use_index = spell_index - 1;
                                while (use_index > -1) {
                                  spell_name = (`${pieces[use_index].toLocaleLowerCase()} ${spell_name}`).trim();
                                  // console.log(spell_name);
                                  if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
                                    debugger;
                                  }
                                  
                                  spell = this.getSpellByName(spell_name);
                                  if (spell) {
                                    break;
                                  } else {
                                    use_index--;
                                  }
                                }
                                if (!spell) {
                                  spell_name = "";
                                  use_index = spell_index + 1;
                                  while (use_index < pieces.length) {
                                    spell_name = (`${spell_name} ${pieces[use_index].toLocaleLowerCase()}`).trim();
                                    if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
                                      debugger;
                                    }
                                    
                                    spell = this.getSpellByName(spell_name);
                                    if (spell) {
                                      break;
                                    } else {
                                      use_index++;
                                    }
                                  }
                                }
                              }
                              if (spell instanceof Spell) {
                                const save_effect = spell.effects.find(o => o.attack_type === "Save");
                                if (save_effect) {
                                  save_type = save_effect.saving_throw_ability_score;
                                } else {
                                  console.error(pieces);
                                  console.error(dc_index);
                                  console.error(dc);
                                  // debugger;
                                }
                              } else {
                                console.error(pieces);
                                console.error(dc_index);
                                console.error(dc);
                                debugger;
                              }
                            }
                          }
                        }
                        
                        const ability_mod = dc - 8 - c.proficiency_modifier;
                        
                        if (ca.name.includes("Breath")) {
                          // Breath Weapons the DC is generally based on CON.
                          if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                            using_ability = "CON";
                          }
                        }
                        if (using_ability === "") {
                          // This order is the most likely.
                          if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
                            using_ability = "CHA";
                          } else if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
                            using_ability = "STR";
                          } else if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
                            using_ability = "DEX";
                          } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
                            using_ability = "WIS";
                          } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
                            using_ability = "INT";
                          } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                            using_ability = "CON";
                          }
                        }
                        if (using_ability === "") {
                          // Could be anything.  Just leave it as a base.
                          ca.dc.base = dc;
                        } else {
                          ca.dc.base = 8;
                          ca.dc.add_prof_bonus_mult = 1;
                          // ca.dc.add_ability_mod_mult = 1;
                          // ca.dc.add_ability_scores.push(using_ability);
                          ca.dc.add_stats_groups = [{ 
                            true_id: uuidv4().toString(),
                            add_ability_scores: [using_ability],
                            mod_or_score: "Mod",
                            add_mult: 1,
                            multiple_type: "And"
                          }];
                          ca.description = ca.description.replace(`DC ${dc}`, `{dc_string.${using_ability}}`);
                          using_ability_mod = ability_mod;
                        }
                        ca.saving_throw_ability_score = ABILITY_SCORE_FULL_TO_ABBREV_MAP[save_type];
                      }
                    } else if (desc_str.includes("Attack Roll:")) {
                      // <p><strong><em>Fist.</em></strong> <em>Melee Attack
                      //   Roll:</em> +5, reach 5 ft. <em>Hit:</em> 5 (1d4 + 3) Bludgeoning damage.</p>

                      let attack_num = 0;
                      let index = desc_str.indexOf("Attack Roll:");
                      let attack_str = desc_str.substring(index + 12).trim();
                      index = attack_str.indexOf(",");
                      attack_str = attack_str.substring(0, index);
                      attack_str = attack_str.replace("+", "");
                      attack_num = +attack_str;
                      if (isNaN(attack_num)) {
                        // <p><strong><em>Bite.</em></strong> <em>Melee Attack
                        //     Roll:</em> +5 (with Advantage if the target is <a>Grappled</a> by
                        //   the ankheg), reach 5 ft. <em>Hit:</em> 10 (2d6 + 3) Slashing damage plus 3 (1d6) Acid damage. If the target is a
                        //   Large or smaller creature, it has the <a>Grappled</a>
                        //   condition (escape DC 13).</p>
                        index = attack_str.indexOf("(");
                        attack_str = attack_str.substring(0, index);
                        attack_str = attack_str.replace("+", "");
                        attack_num = +attack_str;
                      }
                      if (isNaN(attack_num)) {
                        console.error("Attack Parse Error");
                        debugger;
                      } else {
                        // Check New Method first
                        const ability_mod = attack_num - c.proficiency_modifier;
                        
                        if (using_ability === "") {
                          if (desc_str.includes("Spell")) {
                            // This order is the most likely.
                            if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
                              using_ability = "CHA";
                            } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
                              using_ability = "WIS";
                            } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
                              using_ability = "INT";
                            } else if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
                              using_ability = "STR";
                            } else if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
                              using_ability = "DEX";
                            } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                              using_ability = "CON";
                            }
                          } else if (desc_str.includes("Ranged")) {
                            // This order is the most likely.
                            if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
                              using_ability = "DEX";
                            } else if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
                              using_ability = "STR";
                            } else if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
                              using_ability = "CHA";
                            } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
                              using_ability = "WIS";
                            } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
                              using_ability = "INT";
                            } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                              using_ability = "CON";
                            }
                          } else {
                            // This order is the most likely.
                            if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
                              using_ability = "STR";
                            } else if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
                              using_ability = "DEX";
                            } else if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
                              using_ability = "CHA";
                            } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
                              using_ability = "WIS";
                            } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
                              using_ability = "INT";
                            } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                              using_ability = "CON";
                            }
                          }
                        }
                        if (using_ability === "") {
                          // Could be anything.  Just leave it as a base.
                          ca.attack_bonus.base = attack_num;
                        } else {
                          ca.attack_bonus.add_prof_bonus_mult = 1;
                          // ca.attack_bonus.add_ability_mod_mult = 1;
                          // ca.attack_bonus.add_ability_scores.push(using_ability);
                          ca.attack_bonus.add_stats_groups = [{ 
                            true_id: uuidv4().toString(),
                            add_ability_scores: [using_ability],
                            mod_or_score: "Mod",
                            add_mult: 1,
                            multiple_type: "And"
                          }];
                          ca.description = ca.description.replace(`Attack Roll: ${common_util.addPlusMaybe(attack_num)}`, `Attack Roll: {attack_string.${using_ability}}`);
                          using_ability_mod = ability_mod;
                        }
                      }
                    }
                    if (desc_str.includes("reach")) {
                      let sub = desc_str.substring(desc_str.indexOf("reach") + 5).trim();
                      sub = sub.substring(0, sub.indexOf(" ")).trim();
                      ca.range = sub;
                    } else if (desc_str.includes("range")) {
                      let sub = desc_str.substring(desc_str.indexOf("range") + 5).trim();
                      sub = sub.substring(0, sub.indexOf(" ")).trim();
                      ca.range = sub;
                    }
                    let damage_str = desc_str.toLocaleLowerCase();
                    const lower_case = ca.description.toLowerCase();
                    // if (name.includes(looking_for_fb_name)) {
                    //   console.log(damage_str);
                    // }
                    while (damage_str.includes("damage")) {
                      let damage_sub = damage_str.substring(0, damage_str.indexOf("damage") - 1).trim();
                      damage_str = damage_str.substring(damage_str.indexOf("damage") + 6);
                      const words = damage_sub.split(" ");
                      // console.log(words);
                      const damage_type = common_util.capitalize_first(words[words.length - 1]);
                      if (damage_sub.includes("(") && damage_sub.includes(")")) {
                        let start_index = damage_sub.indexOf("(");
                        start_index -= 2;
                        while (start_index >= 0 && damage_sub[start_index] !== " ") {
                          start_index--;
                        }
                        let replace_me = damage_sub.substring(start_index);
                        const end_index = replace_me.indexOf(")") + 1;
                        replace_me = replace_me.substring(0, end_index).trim();
                        damage_sub = damage_sub.substring(damage_sub.indexOf("("));
                        damage_sub = damage_sub.substring(0, damage_sub.indexOf(")"));
                        damage_sub = damage_sub.replace("(","").replace(")","");
                        damage_sub = common_util.normalizeDashes(damage_sub);
                        let count = 0;
                        let size = 0;
                        let flat = 0;
                        if (damage_sub.includes("+")) {
                          const flat_str = damage_sub.substring(damage_sub.indexOf("+")).replace("+","");
                          damage_sub = damage_sub.substring(0, damage_sub.indexOf("+")).replace("+","");
                          flat = +flat_str;
                        } else if (damage_sub.includes("-")) {
                          const flat_str = damage_sub.substring(damage_sub.indexOf("-")).replace("-","");
                          damage_sub = damage_sub.substring(0, damage_sub.indexOf("-")).replace("-","");
                          flat = -1 * +flat_str;
                        } 
                        if (damage_sub.includes("d")) {
                          const pieces = damage_sub.split("d");
                          count = +pieces[0];
                          size = +pieces[1];
                        }
                        const effect = new AbilityEffect();
                        effect.type = "Damage";
                        effect.damage_types.push(isDamageType(damage_type) ? damage_type as DamageType : "Typeless");
                        if (ca.effects.length === 0) {
                          const first_part = lower_case.substring(0, 20);
                          if (first_part.includes("melee")) {
                            if (first_part.includes("spell")) {
                              effect.attack_type = "Melee Spell";
                            } else {
                              effect.attack_type = "Melee";
                            }
                          } else if (first_part.includes("ranged")) {
                            if (first_part.includes("spell")) {
                              effect.attack_type = "Ranged Spell";
                            } else {
                              effect.attack_type = "Ranged";
                            }
                          } else if (ca.saving_throw_ability_score !== "") {
                            effect.attack_type = "Save";
                            effect.saving_throw_ability_score = ca.saving_throw_ability_score;
                          }
                          if (ca.range !== "") {
                            try {
                              const range = +ca.range;
                              if (!isNaN(range)) {
                                effect.target_int.range.base = range;
                              }
                            } catch (ex) { }
                          }
                          // X-foot cone
                          if (ca.description.includes("-foot")) {
                            const foot_index = ca.description.indexOf("-foot");
                            let area_str = "";
                            let i = 1;
                            while (ca.description[foot_index - i] !== " ") {
                              area_str = ca.description[foot_index - i] + area_str;
                              ++i;
                            }
                            // debugger;
                            i = 6;
                            let shape_str = "";
                            while (ca.description[foot_index + i] !== " ") {
                              shape_str += ca.description[foot_index + i];
                              ++i;
                            }
                            if (shape_str.endsWith(".")) {
                              shape_str = shape_str.substring(0, shape_str.length - 1);
                            }
                            effect.target_int.target_type = "Area";
                            try {
                              const area = +area_str;
                              if (!isNaN(area)) {
                                effect.target_int.area.base = area;
                              }
                            } catch (ex) { }
                            shape_str = common_util.capitalize_first(shape_str);
                            if (AREA_TYPES.includes(shape_str)) {
                              effect.target_int.area_type = shape_str as AreaType;
                            } else {
                              effect.target_int.area_type = "Sphere";
                            }
                          }
                        }
                        const potence = new Potence();
                        potence.level = 1;
                        potence.rolls.count.base = count;
                        potence.rolls.size.base = size;
                        // console.log(damage_str);
                        // console.log(damage_sub);
                        if (flat !== 0) {
                          if (using_ability !== "" && using_ability_mod <= flat) {
                            potence.rolls.flat.base = flat - using_ability_mod;
                            // potence.rolls.flat.add_ability_mod_mult = 1;
                            if (using_ability === c.spellcasting_ability) {
                              // potence.rolls.flat.add_ability_scores.push("Spellcasting");
                              potence.rolls.flat.add_stats_groups = [{ 
                                true_id: uuidv4().toString(),
                                add_ability_scores: ["Spellcasting"],
                                mod_or_score: "Mod",
                                add_mult: 1,
                                multiple_type: "And"
                              }];
                            } else {
                              // potence.rolls.flat.add_ability_scores.push(using_ability);
                              potence.rolls.flat.add_stats_groups = [{ 
                                true_id: uuidv4().toString(),
                                add_ability_scores: [using_ability],
                                mod_or_score: "Mod",
                                add_mult: 1,
                                multiple_type: "And"
                              }];
                            }
                          } else {
                            potence.rolls.flat.base = flat;
                          }
                        }
                        ca.description = ca.description.replace(replace_me, potence.potence_string);
                        effect.potences.push(potence);
                        ca.effects.push(effect);
                      }
                    }
                    if (ca.effects.length === 0) {
                      const effect = new AbilityEffect();
                      if (ca.dc.isBlank()) {
                        effect.type = "Utility";
                      } else {
                        effect.type = "Control";
                        effect.attack_type = "Save";
                        effect.saving_throw_ability_score = ca.saving_throw_ability_score;
                        const conditions = this.conditions;
                        for (const condition of conditions.filter(o => o.common)) {
                          const condition_name = condition.name;
                          if (ca.description.includes(condition_name) || ca.description.includes(condition_name.toLowerCase())) {
                            effect.utility_detail = condition_name;
                          }
                        }
                      }
                      ca.effects.push(effect);
                    }
                    if (fbType === "Legendary Actions") {
                      // debugger;
                      ca.consumed_resource.resource_consumed = "Legendary Action";
                      if (ca.name.includes("(Costs") && ca.name.includes("Actions)")) {
                        try {
                          // (Costs X Actions)
                          const actions_str = ca.name.substring(ca.name.indexOf("(Costs"));
                          const pieces = common_util.cleanForCompare(actions_str).split(" ");
                          const action_num = +pieces[1];
                          if (!isNaN(action_num)) {
                            ca.consumed_resource.amount_consumed = action_num;
                          }
                        } catch (ex) { }
                      } else {
                        ca.consumed_resource.amount_consumed = 1;
                      }
                    } else {
                      if (ca.name.includes("/Day)")) {
                        ca.consumed_resource.amount_consumed = 1;
                        
                        const times_index = ca.name.indexOf("/Day");
                        
                        let times_str = "";
                        let i = 1;
                        while (ca.name[times_index - i] !== " ") {
                          times_str = ca.name[times_index - i] + times_str;
                          ++i;
                        }
                        
                        try {
                          const times = +times_str;
                          if (!isNaN(times)) {
                            ca.consumed_resource.special_resource_amount = new UpgradableNumber(times);
                          }
                        } catch (ex) { }
                      }
                      if (fbType === "Bonus Actions") {
                        ca.casting_time = "BA";
                      } else if (fbType === "Reactions") {
                        ca.casting_time = "RA";
                      }
                    }
                    // Bite. Melee Weapon Attack: +12 to hit, reach 10 ft., one target. 
                    // Hit: 18 (2d10 + 7) piercing damage plus 5 (1d10) lightning damage.

                    // Frightful Presence. Each creature of the dragon's choice that is 
                    // within 120 feet of the dragon and aware of it must succeed on a 
                    // DC 17 Wisdom saving throw or become frightened for 1 minute. 
                    // A creature can repeat the saving throw at the end of each of its turns, 
                    // ending the effect on itself on a success. If a creature's saving throw 
                    // is successful or the effect ends for it, the creature is immune to the 
                    // dragon's Frightful Presence for the next 24 hours.

                    if (ca.range.length > 0 && ca.effects.length > 0) {
                      const pieces = ca.range.split("/");
                      const range1 = +pieces[0];
                      const range2 = pieces.length > 1 ? +pieces[1] : 0;
                      if (!isNaN(range1) && !isNaN(range2)) {
                        ca.effects[0].target_int.range.base = range1;
                        ca.effects[0].target_int.range2.base = range2;
                      }
                    }
                    // Lightning Breath (Recharge 5–6)
                    // Ink Cloud (1/Day)
                    if (ca.name.includes("(Recharge 5-6)")) {
                      ca.consumed_resource.recharge_die = 6;
                      ca.consumed_resource.recharge_on = [5,6];
                      ca.consumed_resource.resource_consumed = "Recharge";
                    } else if (ca.name.includes("(Recharge 4-6)")) {
                      ca.consumed_resource.recharge_die = 6;
                      ca.consumed_resource.recharge_on = [4,5,6];
                      ca.consumed_resource.resource_consumed = "Recharge";
                    } else if (ca.name.includes("(Recharge 6)")) {
                      ca.consumed_resource.recharge_die = 6;
                      ca.consumed_resource.recharge_on = [6];
                      ca.consumed_resource.resource_consumed = "Recharge";
                    } else if (ca.name.includes("/Day)")) {
                      ca.consumed_resource.resource_consumed = "Special";
                      ca.consumed_resource.amount_consumed = 1;
                      ca.consumed_resource.special_resource_refresh_rule = "Long Rest";
                      try {
                        let pieces = ca.name.split("(");
                        pieces = pieces[1].split("/");
                        const x_day = +pieces[0];
                        if (!isNaN(x_day)) {
                          ca.consumed_resource.special_resource_amount = new UpgradableNumber(x_day);
                        }
                      } catch (ex) {
                        ca.consumed_resource.special_resource_amount = new UpgradableNumber(1);
                      }
                    }
                  }
                  return ca;
                }
              }
              new_descs = [];
              for (const dv of descVersions) {
                const temp_ff = makeFF(dv);
                
                if (temp_ff && !descVersions.includes(temp_ff.description)) {
                  new_descs.push(temp_ff.description);
                }
              }
              descVersions.push(...new_descs);
              ff = makeFF(desc_helper);
              if (ff && !descVersions.includes(ff.description)) {
                descVersions.push(ff.description);
              }
              
              const desc = common_util.cleanSpaces(ff ? `${ff.description}` : `${content_str}`);
              if (!descVersions.includes(desc)) {
                descVersions.push(desc);
              }
              if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
                console.log([...descVersions]);
              }
              new_descs = [];
              for (const dv of descVersions) {
                const new_desc = common_util.cleanSpaces(dv);
                if (!descVersions.includes(new_desc) && !new_descs.includes(new_desc)) {
                  new_descs.push(new_desc);
                }
              }
              descVersions.push(...new_descs);
              let ffsWithName: FloatingFeature[] = [];
              if (ff) {
                const desc = ff.description;
                const feature_type = ff.feature_type;
                ffsWithName = ff && ff.feature_type === "Creature Ability" ? 
                  this.all_creature_features.filter(o => o.name === name) :
                  this.all_floating_features.filter(o => o.feature_type === feature_type && o.name === name);
              }
              if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
                console.log([...descVersions]);
                console.log(ffsWithName.map(o => o.description));
              }
              content_str = desc; // Update the fb's description to be the best cleaned one, so we can update the ff to use it as well.
              let old_ff = ffsWithName.find(o => 
                common_util.cleanCompare(o.description, desc) //, name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name))
              );
              if (!old_ff) {
                for (const dv of descVersions) {
                  old_ff = ffsWithName.find(o => 
                    common_util.cleanCompare(o.description, dv) //, name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name))
                  );
                  if (old_ff) {
                    break;
                  }
                }
              }
              if (old_ff) {
                ff = old_ff;
              }
            }
            if (ff) {
              // debugger;
              if (fbType === "Actions" || fbType === "Bonus Actions" || fbType === "Reactions") {
                const feature = new Feature();
                feature.floating_feature = ff;
                feature.floating_feature_id = ff._id;
                fb.creature_fb_type = "Actions";
                fb.fb_name = ff.name;
                fb.level = c.min_challenge_rating;
                fb.features.push(feature);
              } else if (fbType === "Legendary Actions") {
                const feature = new Feature();
                feature.floating_feature = ff;
                feature.floating_feature_id = ff._id;
                fb.creature_fb_type = "Legendary Actions";
                fb.fb_name = ff.name;
                fb.level = c.min_challenge_rating;
                fb.features.push(feature);
              } else if (fbType === "Traits") {
                const feature = new Feature();
                feature.floating_feature = ff;
                feature.floating_feature_id = ff._id;
                fb.creature_fb_type = "Traits";
                fb.fb_name = ff.name;
                fb.level = c.min_challenge_rating;
                fb.features.push(feature);
              } else {
                // console.log(action_type);
                // console.log(content_str);
                debugger;
              }
            } else {
              // console.log(action_type);
              // console.log(content_str);
              debugger;
            }

            fb.fb_name = name;
            fb.description = content_str;
            c.fbs.push(fb);
          }
        } 
      }
    }

    for (let j = 0; j < p_els.length; ++j) {
      // Unfortunately, in the 2024 Basic Rules, they weren't as kind with the classes, etc, 
      // so we have to figure out what's going on based on the content and on previous ps.
      const p = p_els[j] as HTMLElement;
      if (j === 0) {
        // <p>Large Aberration, Lawful Evil</p>
        // They always put this line right after the name, so it will always be the first one.
        // <p>Medium Swarm of Tiny Beasts, Unaligned</p> 
        // These aren't handled properly, but there's not very many, so I'll just handle them manually.
        // <p>Medium Humanoid (Human, Wizard), Chaotic Evil</p>
        if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Creature Type") {
          console.log(p);
          debugger;
        }
        const pieces = p.innerText.split(",");
        let piece0 = pieces[0];
        for (let i = 1; i < pieces.length - 1; ++i) {
          piece0 += "," + pieces[i];
        }
        let pieces2 = common_util.cleanForCompare(piece0).split(" ");
        c.size_ugn.base = SIZE_TO_NUM_MAP[common_util.capitalize_first(pieces2[0])];
        if (pieces2[1] === "or" && pieces2.length > 2) {
          pieces2.splice(0, 2);
          const alt = new UpgradableNumber();
          alt.base = SIZE_TO_NUM_MAP[common_util.capitalize_first(pieces2[0])];
          c.alternate_size_ugns.push(alt);
        }
        if (pieces2.length === 2) {
          const ct_str = common_util.capitalize_first(pieces2[1]);
          if (isCreatureType(ct_str)) {
            c.creature_type = ct_str;
          }
        } else if (pieces2.length > 2) {
          // <p class="Stat-Block-Styles_Stat-Block-Metadata">Huge fiend (demon), chaotic evil</p>
          // debugger;
          const ct_str = common_util.capitalize_first(pieces2[1]);
          if (isCreatureType(ct_str)) {
            c.creature_type = ct_str;
          }
          let subtype_str = piece0.substring(piece0.indexOf(pieces2[1]) + pieces2[1].length).trim();
          subtype_str = subtype_str.substring(subtype_str.indexOf("(") + 1);
          subtype_str = subtype_str.substring(0, subtype_str.indexOf(")"));
          subtype_str = common_util.capitalize_firsts(subtype_str);
          c.subtypes = subtype_str.split(",").map(o => o.trim());
        } else {
          debugger;
        }
        const alignment_string = pieces[pieces.length - 1].trim();
        c.alignment = alignment_string;
      } else if (j === 1) {
        if (p.innerText.includes("AC")) {
          // <p><strong>AC</strong> 17 <strong>Initiative</strong>
          // +7 (17)</p>
          if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "AC") {
            console.log(p);
            debugger;
          }
          const pieces = common_util.cleanSpacesForElement(p).split(" ");
          
          let working_on = pieces[0];
          for (let k = 1; k < pieces.length; ++k) {
            let piece = common_util.normalizeDashes(pieces[k]);
            if (piece === "Initiative") {
              working_on = piece;
            } else if (working_on === "AC") {
              if (c.armor_class.base === 0) {
                c.armor_class.base = +piece;
                if (isNaN(c.armor_class.base)) {
                  debugger;
                }
                c.ac_flavor = "";
              } else {
                c.ac_flavor += piece + " ";
              }
            } else if (working_on === "Initiative") {
              if (c.initiative_modifier.isBlank()) {
                let init = +piece;
                if (isNaN(init)) {
                  // It may be a negative initiative.
                  if (piece.startsWith("-")) {
                    piece = piece.substring(1);
                    init = +piece;
                    if (!isNaN(init)) {
                      init *= -1;
                    }
                  }
                }
                if (isNaN(init)) {
                  debugger;
                } else {
                  c.initiative_modifier.base = init - c.current_ability_scores.DEX;
                  // c.initiative_modifier.add_ability_mod_mult = 1;
                  // c.initiative_modifier.add_ability_scores.push("DEX");
                  c.initiative_modifier.add_stats_groups = [{ 
                    true_id: uuidv4().toString(),
                    add_ability_scores: ["DEX"],
                    mod_or_score: "Mod",
                    add_mult: 1,
                    multiple_type: "And"
                  }];
                }
              }
            }
          }
          c.ac_flavor = c.ac_flavor.trim();
        } else {
          debugger;
        }
      } else if (j === 2) {
        if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "HP") {
          console.log(p);
        }
        if (p.innerText.includes("HP")) {
          // <p><strong>HP</strong> 150 (20d10 + 40)</p>
          let index = p.innerText.indexOf("(");
          let hd_str = p.innerText.substring(index + 1).replace(")", "");
          index = hd_str.indexOf(" ");
          if (index > 0) {
            hd_str = hd_str.substring(0, index);
          }
          const pieces = hd_str.split("d");
          if (pieces.length === 2) {
            const hd = new HitDiceUpgradable();
            hd.count.base = +pieces[0];
            c.hit_dice.push(hd);
          }
        } else {
          debugger;
        }
      } else if (j === 3) {
        if (p.innerText.includes("Speed")) {
          // <p><strong>Speed</strong> 10 ft., Swim 40 ft.</p>
          if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Speed") {
            console.log(p);
            debugger;
          }
          let spd_str = p.innerText;
          const pieces = common_util.cleanForCompare(spd_str).split(" ");
          let spd_type: SpeedType = "walk";
          for (const piece of pieces) {
            if (isSpeedType(piece)) {
              spd_type = piece;
            } else if (!piece.includes("ft.") && piece !== "speed") {
              attr_entry.speeds[spd_type] = +piece;
            }
          }
        } else {
          debugger;
        }
      } else {
        // At this point it's harder to predict because some get skipped.
        if (p.classList.contains("monster-header")) {
          // It's a FeatureBase type
          // <p class="monster-header">Traits</p>
          const str = p.innerText.trim();
          if (isCreatureFBType(str)) {
            if (str === "Legendary Actions") {
              legendary = true;
            }
            fbType = str;
          }
          sc = false;
        } else if (p.classList.contains("legendary-actions")) {
          // <p class="legendary-actions">Legendary Action Uses: 3
          //   (4 in Lair). Immediately after another creature’s turn, the aboleth can expend a use to take one of the
          //   following
          //   actions. The aboleth regains all expended uses at the start of each of its turns.</p>
          if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Legendary") {
            console.log(p);
            debugger;
          }
          let desc = p.innerText;
          let index = desc.indexOf("Legendary Action Uses: ");
          desc = desc.substring(index + 23);
          index = desc.indexOf(".");
          desc = desc.substring(0, index).trim();
          desc = common_util.cleanSpaces(desc);
          if (desc.includes("in Lair)")) {
            index = desc.indexOf("(");
            const lair = desc.substring(index + 1);
            const pieces2 = lair.split(" ");
            const lair_num = +pieces2[0];
            if (isNaN(lair_num)) {
              debugger;
            } else {
              leg_entry.act_lair = lair_num;
            }
          }
          const pieces = desc.split(" ");
          const num = pieces.length > 0 ? +pieces[0] : 0;
          if (!isNaN(num) && num > 0) {
            leg_entry.actions = num;
          } else {
            debugger;
          }
        } else if (fbType === "Main Details") {
          if (p.innerText.includes("Skills")) {
            skills_el = p; // It needs to be processed after we know the challenge rating.
          } else if (p.innerText.includes("Senses")) {
            // <p><strong>Senses</strong> <a>Darkvision</a>
            //     120
            //     ft.; Passive Perception 20</p>
            if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Senses") {
              debugger;
            }
            const pieces = common_util.cleanForCompare(p.innerText).split(" ");
            let sense: Sense | undefined;
            for (const piece of pieces) {
              const sense_str = piece.toLocaleLowerCase().replace(",", "").replace(" ", "");
              if (sense) {
                const num = +sense_str;
                if (!isNaN(num)) {
                  attr_entry.senses[sense.name] = num;
                  continue;
                } else {
                  sense = undefined;
                }
              }
              const sense_maybe = SENSES.find(o => o.name.toLocaleLowerCase().replace(" ", "").includes(sense_str));
              if (sense_maybe) {
                sense = sense_maybe;
              }
            }
          } else if (p.innerText.includes("Vulnerabilities")) {
            // <p data-content-chunk-id="d899e2ed-4505-4338-a9f4-70ff7ba276cc"><strong>Vulnerabilities</strong> Poison</p>
            if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Vulnerabilities") {
              debugger;
            }
            const str = p.innerText.substring(p.innerText.indexOf("Vulnerabilities") + 15).trim();
            const pieces = common_util.cleanForCompare(str).split(" ");
            for (const piece of pieces) {
              const dt_str = piece.toLocaleLowerCase().replace(",", "").replace(" ", "");
              const dt_maybe = DAMAGE_TYPES.find(o => o.toLocaleLowerCase().replace(" ", "").includes(dt_str));
              if (dt_maybe) {
                attr_entry.damage_vulnerabilities.push(dt_maybe);
              } else {
                if (attr_entry.extra_damage_vulnerabilities.length > 0) {
                  attr_entry.extra_damage_vulnerabilities += ", ";
                }
                attr_entry.extra_damage_vulnerabilities += piece;
              }
            }
          } else if (p.innerText.includes("Resistances")) {
            // <p data-content-chunk-id="d899e2ed-4505-4338-a9f4-70ff7ba276cc"><strong>Resistances</strong> Poison</p>
            if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Resistances") {
              debugger;
            }
            const str = p.innerText.substring(p.innerText.indexOf("Resistances") + 11).trim();
            const pieces = common_util.cleanForCompare(str).split(" ");
            for (const piece of pieces) {
              const dt_str = piece.toLocaleLowerCase().replace(",", "").replace(" ", "");
              const dt_maybe = DAMAGE_TYPES.find(o => o.toLocaleLowerCase().replace(" ", "").includes(dt_str));
              if (dt_maybe) {
                attr_entry.damage_resistances.push(dt_maybe);
              } else {
                if (attr_entry.extra_damage_resistances.length > 0) {
                  attr_entry.extra_damage_resistances += ", ";
                }
                attr_entry.extra_damage_resistances += piece;
              }
            }
          } else if (p.innerText.includes("Immunities")) {
            // <p data-content-chunk-id="88026590-4a67-4d4f-a8cc-b7a6529ca134"><strong>Immunities</strong> Poison; <a
            //         class="tooltip-hover condition-tooltip"
            //         href="/sources/dnd/free-rules/rules-glossary#ExhaustionCondition"
            //         data-tooltip-href="//www.dndbeyond.com/conditions/4-tooltip?disable-webm=1&amp;disable-webm=1">Exhaustion</a>,
            //     <a class="tooltip-hover condition-tooltip" href="/sources/dnd/free-rules/rules-glossary#PoisonedCondition"
            //         data-tooltip-href="//www.dndbeyond.com/conditions/11-tooltip?disable-webm=1&amp;disable-webm=1">Poisoned</a>
            // </p>
            if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Immunities") {
              debugger;
            }
            const str = p.innerText.substring(p.innerText.indexOf("Immunities") + 10).trim();
            const main_pieces = str.split(";");
            if (main_pieces.length === 1) {
              // It might only be conditions
              let pieces = main_pieces[0].split(",").map(o => o.trim());
              let is_conditions = false;
              if (!isDamageType(pieces[0])) {
                const cond = this.conditions.find(o => o.name === pieces[0]);
                if (cond) {
                  is_conditions = true;
                }
              }
              if (is_conditions) {
                for (const piece of pieces) {
                  const cond = this.conditions.find(o => o.name === piece);
                  if (cond instanceof Condition) {
                    attr_entry.condition_immunities.push(piece);
                  } else {
                    console.error("Something weird is happening with its immunities");
                    debugger;
                  }
                }
              } else {
                for (const piece of pieces) {
                  if (isDamageType(piece)) {
                    attr_entry.damage_immunities.push(piece);
                  } else {
                    attr_entry.extra_damage_immunities += piece;
                  }
                }
              }
            } else {
              // Damage Immunities
              let pieces = main_pieces[0].split(",").map(o => o.trim());
              for (const piece of pieces) {
                if (isDamageType(piece)) {
                  attr_entry.damage_immunities.push(piece);
                } else {
                  attr_entry.extra_damage_immunities += piece;
                }
              }
              // Condition Immunities
              pieces = main_pieces[1].split(",").map(o => o.trim());
              for (const piece of pieces) {
                const cond = this.conditions.find(o => o.name === piece);
                if (cond instanceof Condition) {
                  attr_entry.condition_immunities.push(piece);
                } else {
                  console.error("Something weird is happening with its immunities");
                  debugger;
                }
              }
            }
          } else if (p.innerText.includes("Languages")) {
            // <p><strong>Languages</strong> Deep Speech; telepathy
            //     120 ft.</p>
            const language_str = common_util.cleanSpaces(p.innerText.replace("Languages", "")).trim();
            attr_entry.languages = language_str;
          } else if (p.innerText.includes("CR")) {
            // <p><strong>CR</strong> 10 (XP 5,900, or 7,200 in lair;
            //     PB +4)</p>
            const pieces = common_util.cleanSpaces(p.innerText.replace("CR", "").trim()).split(" ");
            if (pieces.length > 0) {
              const cr_str = pieces[0];
              if (cr_str.includes("/")) { // 1/4 or 1/2
                const pieces2 = cr_str.split("/");
                const num = +pieces2[0];
                const den = +pieces2[1];
                if (!isNaN(num) && !isNaN(den)) {
                  c.challenge_rating = num / den;
                } else {
                  debugger;
                }
              } else {
                const num = +cr_str;
                if (!isNaN(num)) {
                  c.challenge_rating = num;
                } else {
                  debugger;
                }
              }
            } else {
              debugger;
            }
          } else if (p.innerText.includes("Challenge")) {
            // <p><strong>Challenge</strong> 10 (XP 5,900, or 7,200 in lair;
            //     PB +4)</p>
            const pieces = common_util.cleanSpaces(p.innerText.replace("Challenge", "").trim()).split(" ");
            if (pieces.length > 0) {
              const cr_str = pieces[0];
              if (cr_str.includes("/")) { // 1/4 or 1/2
                const pieces2 = cr_str.split("/");
                const num = +pieces2[0];
                const den = +pieces2[1];
                if (!isNaN(num) && !isNaN(den)) {
                  c.challenge_rating = num / den;
                } else {
                  debugger;
                }
              } else {
                const num = +cr_str;
                if (!isNaN(num)) {
                  c.challenge_rating = num;
                } else {
                  debugger;
                }
              }
            } else {
              debugger;
            }
          } 
        } else {
          if (p.innerText.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
            console.log(p);
          }
          // Look for additional ps that should be part of it.
          // <p><strong><em>Dominate Mind (2/Day).</em></strong>
          //     <em>Wisdom Saving Throw:</em> DC 16, one creature the aboleth can see within 30 feet. <em>Failure:</em> The
          //     target
          //     has the <a>Charmed</a>
          //     condition until the aboleth dies or is on a different plane of existence from the target. While Charmed, the
          //     target acts as an ally to the aboleth and is under its control while within 60 feet of it. In addition, the
          //     aboleth and the target can communicate telepathically with each other over any distance.
          // </p>
          // <p>The target repeats the save whenever it takes damage
          //     as well as after every 24 hours it spends at least 1 mile away from the aboleth, ending the effect on itself on
          //     a
          //     success.</p>
          const extra_ps: HTMLElement[] = [];
          for (let k = (j + 1); k < p_els.length; k++) {
            const pk = p_els[k];
            if (pk.classList.contains("monster-header")) {
              break;
            }
            const name_strongs = pk.getElementsByTagName("strong");
            if (name_strongs.length === 0) {
              extra_ps.push(pk);
            } else {
              break;
            }
          }
          processFB(p, extra_ps);
        } 
      }
    }
    if (c.initiative_modifier.base > 0 && (c.initiative_modifier.base % c.proficiency_modifier) === 0) {
      c.initiative_modifier.add_prof_bonus_mult = c.initiative_modifier.base / c.proficiency_modifier;
      c.initiative_modifier.base = 0;
    }
    if (skills_el) {
      // <p><strong>Skills</strong> <a>History</a> +12,
      //     <a>Perception</a>
      //     +10
      // </p>
      if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Skills") {
        debugger;
      }
      const pieces = common_util.cleanForCompare(skills_el.innerText).split(" ");
      const pb = c.proficiency_modifier;
      let skill: Skill | undefined;
      for (let k = 0; k < pieces.length; ++k) {
        const piece = pieces[k].replace(",", "").replace("+", "");
        let skill_mod = +piece;
        if (isNaN(skill_mod)) {
          skill = SKILLS.find(o => o.name.toLocaleLowerCase() === piece.toLocaleLowerCase());
          if (!skill) {
            // It might be animal handling.
            if (piece === "animal") {
              skill = SKILLS.find(o => o.name === "Animal Handling");
              ++k;
            } else if (piece === "sleight") {
              skill = SKILLS.find(o => o.name === "Sleight of Hand");
              ++k;
              ++k;
            }
          }
        } else if (skill) {
          const stat_score = c.ability_scores.getBasicScore(skill.use_ability_score);
          const stat_mod = common_util.modFromScore(stat_score);
          skill_mod -= stat_mod;
          attr_entry.skill_profs[skill._id] = skill_mod / pb;
        }
      }
    }
    attr_entry.level = c.challenge_rating;
    tbl_entry.level = c.challenge_rating;
    if (leg_entry.resistances > 0 || leg_entry.actions > 0) {
      leg_entry.level = c.challenge_rating;
      c.legendaries_table.entries = [leg_entry];
    }
    if (Object.keys(resources_entry.data).length > 0) {
      resources_entry.level = c.challenge_rating;
      c.resources_table.entries = [resources_entry];
    }
    if (existing_c) {
      if (existing_c.name.includes(looking_for_creature_name)) {
        // debugger;
        console.log(existing_c.name);
      }
      existing_c.size_ugn.copy(c.size_ugn);
      existing_c.alternate_size_ugns = c.alternate_size_ugns.map(o => o.clone());
      existing_c.armor_class.copy(c.armor_class);
      existing_c.ac_flavor = c.ac_flavor;
      existing_c.hit_dice = c.hit_dice;
      existing_c.ability_scores = c.ability_scores;
      existing_c.fbs = c.fbs;
      existing_c.attributes_table.entries = c.attributes_table.entries;
      existing_c.table.entries = c.table.entries;
      existing_c.legendaries_table.entries = c.legendaries_table.entries;
      existing_c.spellcasting_table.entries = c.spellcasting_table.entries;
      existing_c.spellcasting_ability = c.spellcasting_ability;
      existing_c.resources_table.entries = c.resources_table.entries;
      existing_c.small_name = c.small_name;
      existing_c.creature_type = c.creature_type;
      existing_c.subtypes = c.subtypes;
      const variant_group_id = existing_c.variant_group_id;
      const c_id = existing_c._id;
      // const old_dominant = all_creatures.find(o => o._id !== c_id && o.variant_group_id === variant_group_id && o.is_true_dominant);
      // if (old_dominant) {
      //   existing_c.dominance_level = old_dominant.dominance_level + 1;
      // }
      return existing_c;
    } else {
      const c_name = c.name;
      const old_dominant = all_creatures.find(o => o.name === c_name && o.isDominant());
      if (old_dominant) {
        // c.dominance_level = old_dominant.dominance_level + 1;
        c.variant_of_id = old_dominant.variant_group_id;
      }
      return c;
    }
  }

  parseNewMonStatBlock2024 = async (new_obj: Creature, elem: HTMLElement | null) => {
    const attr_entry = new_obj.attributes_table.entries[0];
    const table_entry = new_obj.table.entries[0];
    let section: "Traits" | "Actions" | "Bonus Actions" | "Reactions" | "Spellcasting" | "" = ""; // There will probably be more of these later
    let initiative_modifier = 0;
    const parseElement = (elem: HTMLElement) => {
      if (elem.nodeName === "H2" && new_obj.name.length === 0 && elem.textContent) {
        new_obj.name = elem.textContent.replace("\n","").trim();
      } else if (elem.nodeName === "P" && elem.textContent) {
        if (section === "") {
          const str = elem.textContent;
          const pieces = str.split(" ");
          if (new_obj.creature_type === "None") {
            // Medium Beast, Unaligned
            const size_str = pieces[0].replace(",","").trim().toLocaleLowerCase();
            const creature_type_str = pieces[1].replace(",","").trim();
            const alignment_str = pieces.length > 2 ? pieces[2].replace(",","").trim() : "";
            new_obj.size_ugn.base = SIZE_TO_NUM_MAP[common_util.capitalize_first(size_str)];
            if (CREATURE_TYPES.includes(creature_type_str)) {
              new_obj.creature_type = creature_type_str as CreatureType;
            }
            new_obj.alignment = alignment_str;
          } else if (str.includes("Initiative ")) {
            // AC 12 Initiative +2 (12)
            if (pieces.length === 5) {
              const ac_str = pieces[1];
              const init_str = pieces[3].replace("–","-");
              const ac = +ac_str;
              if (!isNaN(ac)) {
                // For now we're just setting the ac.base to it.  
                // Then at the end we can convert it to be based on DEX.
                new_obj.armor_class.makeBasic();
                new_obj.armor_class.base = ac;
              }
              const init = +init_str;
              if (!isNaN(init)) {
                // For now we're just setting the init.base to it.  
                // Then at the end we can convert it to be based on DEX.
                new_obj.initiative_modifier.makeBasic();
                new_obj.initiative_modifier.base = init;
              }
            } else {
              // No example of this yet, but it should have natural armor, etc, in here somewhere.
              debugger;
            }
          } else if (str.includes("HP ")) {
            // HP 19 (3d8 + 6)
            // const max_str = pieces[1];
            const dice_str = pieces[2].replace("(","").replace(")","");
            // const mod_str = pieces.length > 4 ? pieces[4].replace(")","") : "0";
            // const max = +max_str;
            const dice = common_util.parseDiceRoll(dice_str); // + " + " + mod_str);
            const hd_upg = new HitDiceUpgradable();
            hd_upg.count.base = dice.count;
            hd_upg.size = dice.size;
            new_obj.hit_dice = [];
            new_obj.hit_dice.push(hd_upg);
            new_obj.hit_dice_bonus.makeBasic();
            new_obj.hit_dice_bonus.base = 0;
            // new_obj.hit_dice_bonus.add_ability_mod_mult = 1;
            // new_obj.hit_dice_bonus.add_ability_scores.push("CON");
            new_obj.hit_dice_bonus.add_stats_groups = [{ 
              true_id: uuidv4().toString(),
              add_ability_scores: ["CON"],
              mod_or_score: "Mod",
              add_mult: 1,
              multiple_type: "And"
            }];
          } else if (str.includes("Speed ")) {
            // Speed 30 ft., Climb 30 ft.
            let current_speed_type: "Speed" | "Climb" | "Burrow" | "Fly" | "Swim" | "" = "";
            for (let i = 0; i < pieces.length; ++i) {
              const piece = pieces[i];
              switch (piece) {
                case "Speed": current_speed_type = "Speed"; break;
                case "Climb": current_speed_type = "Climb"; break;
                case "Burrow": current_speed_type = "Burrow"; break;
                case "Fly": current_speed_type = "Fly"; break;
                case "Swim": current_speed_type = "Swim"; break;
                default: 
                  const val = +piece;
                  switch (current_speed_type) {
                    case "Speed": 
                      if ((attr_entry.speeds["walk"] === undefined || 
                        attr_entry.speeds["walk"] === 0) && !isNaN(val)) {
                        attr_entry.speeds["walk"] = val;
                      }
                    break;
                    default: 
                      if ((attr_entry.speeds[current_speed_type.toLocaleLowerCase()] === undefined || 
                        attr_entry.speeds[current_speed_type.toLocaleLowerCase()] === 0) && !isNaN(val)) {
                        attr_entry.speeds[current_speed_type.toLocaleLowerCase()] = val;
                      }
                    break;
                  }
                break;
              }
            }
          } else if (str.includes("Skills ")) {
            // Skills Athletics +5, Perception +3
            let skill_name = "";
            for (let i = 1; i < pieces.length; ++i) {
              const piece = pieces[i].replace(",","");
              const val = +piece;
              if (!isNaN(val)) { 
                const skill = SKILLS.find(o => o.name === skill_name);
                if (skill) {
                  // This part is intentionally wrong.  It's just storing it here.
                  // Later we'll fix it once we know the PB and the stats.
                  attr_entry.skill_profs[skill._id] = val;
                }
              } else {
                skill_name = piece;
              }
            }
          } else if (str.includes("Senses ")) {
            // Senses Darkvision 60 ft., Passive Perception 15
            // Passive Perception will automatically get ignored by this, which is fine.
            // I automatically build it appropriately at the end.
            // console.log(str);
            // console.log(pieces);
            let sense_name = "";
            for (let i = 1; i < pieces.length; ++i) {
              const piece = pieces[i].replace(",","");
              const val = +piece;
              if (!isNaN(val)) { 
                const sense = SENSES.find(o => o.name.replace(" ","").toLocaleLowerCase() === sense_name);
                if (sense) {
                  attr_entry.senses[sense.name] = val;
                }
              } else {
                sense_name = piece.toLocaleLowerCase();
              }
            }
          } else if (str.includes("Resistances ")) {
            // Resistances Cold
            for (let i = 1; i < pieces.length; ++i) {
              const piece = common_util.capitalize_first(pieces[i]).replace(",","").replace(".","").replace(";","");
              if (isDamageType(piece)) {
                attr_entry.damage_resistances.push(piece);
              } else {
                if (attr_entry.extra_damage_resistances.length > 0) {
                  attr_entry.extra_damage_resistances += ", ";
                }
                attr_entry.extra_damage_resistances += piece;
              }
            }
          } else if (str.includes("Immunities ")) {
            // Immunities Fire, Poison; Poisoned
            for (let i = 1; i < pieces.length; ++i) {
              const piece = common_util.capitalize_first(pieces[i]).replace(",","").replace(".","").replace(";","");
              if (isDamageType(piece)) {
                attr_entry.damage_immunities.push(piece);
              } else {
                const condition = this.conditions.find(o => o.common && o.name === piece);
                if (condition) {
                  attr_entry.condition_immunities.push(condition.name);
                } else {
                  if (attr_entry.extra_damage_immunities.length > 0) {
                    attr_entry.extra_damage_immunities += ", ";
                  }
                  attr_entry.extra_damage_immunities += piece;
                }
              }
            }
          } else if (str.includes("Vulnerabilities ")) {
            // Vulnerabilities Bludgeoning
            for (let i = 1; i < pieces.length; ++i) {
              const piece = common_util.capitalize_first(pieces[i]).replace(",","").replace(".","").replace(";","");
              if (isDamageType(piece)) {
                attr_entry.damage_vulnerabilities.push(piece);
              } else {
                if (attr_entry.extra_damage_vulnerabilities.length > 0) {
                  attr_entry.extra_damage_vulnerabilities += ", ";
                }
                attr_entry.extra_damage_vulnerabilities += piece;
              }
            }
          } else if (str.includes("Gear ")) {
            // Gear Shortbow, Shortsword
            // let sense_name = "";
            // for (let i = 1; i < pieces.length; ++i) {
            //   const piece = pieces[i].replace(",","");
            //   const val = +piece;
            //   if (!isNaN(val)) {// eslint-disable-next-line
            //     const sense = SENSES.find(o => o.name === sense_name);
            //     if (sense) {
            //       attr_entry.senses[sense.name] = val;
            //     }
            //   } else {
            //     sense_name = piece;
            //   }
            // }
            debugger;
          } else if (str.includes("Tools ")) {
            console.error("Tool Parser not implemented");
            debugger;
          } else if (str.includes("Languages ")) {
            // Languages None
            attr_entry.languages = str.substring(10);
          } else if (str.includes("CR ")) {
            // CR 1/2 (XP 100; PB +2)
            const cr_str = pieces[1];
            let cr = +cr_str;
            if (isNaN(cr) && cr_str.includes("/")) {
              const cr_pieces = cr_str.split("/");
              if (cr_pieces.length === 2) {
                const numerator = +cr_pieces[0];
                const denominator = +cr_pieces[1];
                if (!isNaN(numerator) && !isNaN(denominator)) {
                  cr = numerator / denominator;
                }
              }
            }
            if (!isNaN(cr)) {
              new_obj.challenge_rating = cr;
              new_obj.min_challenge_rating = cr;
              new_obj.max_challenge_rating = cr;
            }
          } else if (str.trim() === "Traits") {
            section = "Traits";
          } else if (str.trim() === "Actions") {
            section = "Actions";
          } else if (str.trim() === "Bonus Actions") {
            section = "Bonus Actions";
          } else if (str.trim() === "Reactions") {
            section = "Reactions";
          }
        } else {
          const str = elem.textContent;
          if (str.trim() === "Traits") {
            section = "Traits";
          } else if (str.trim() === "Actions") {
            section = "Actions";
          } else if (str.trim() === "Bonus Actions") {
            section = "Bonus Actions";
          } else if (str.trim() === "Reactions") {
            section = "Reactions";
          } else {
            if (table_entry.level === 0 && new_obj.challenge_rating > 0) {
              table_entry.level = new_obj.challenge_rating;
            }
            const spellcasting = this.parseStatBlockAction(new_obj, elem, section);
            if (spellcasting === 1) {
              section = "Spellcasting";
            } else if (spellcasting === -1) {
              section = "Actions";
              this.parseStatBlockAction(new_obj, elem, section);
            }
          }
        }
      } else if (elem.nodeName === "DIV") {
        if (elem.classList.contains("mon-stat-block-2024__header")) {
          // debugger;
          for (let i = 0; i < elem.children.length; ++i) {
            const child = elem.children[i];
            if (child instanceof HTMLElement && child.textContent) {
              if (child.classList.contains("mon-stat-block-2024__name")) {
                new_obj.name = child.textContent.replace("\n","").trim();
                if (new_obj.name.includes("Dragon")) {
                  if (new_obj.name.includes("Wyrmling")) {
                    new_obj.age_category = "Wyrmling";
                  } else if (new_obj.name.includes("Young")) {
                    new_obj.age_category = "Young";
                  } else if (new_obj.name.includes("Adult")) {
                    new_obj.age_category = "Adult";
                  } else if (new_obj.name.includes("Ancient")) {
                    new_obj.age_category = "Ancient";
                  } else if (new_obj.name.toLocaleLowerCase().includes("greatwyrm")) {
                    new_obj.age_category = "Greatwyrm";
                  }
                }
              } else if (child.classList.contains("mon-stat-block-2024__meta")) {
                // Medium Fiend (Devil), Lawful Evil
                const str = child.textContent.replace("\n","").trim();
                const pieces = str.split(",").map(o => o.trim());
                const main_pieces = pieces[0].split(" ");
                const size_str = main_pieces[0].toLocaleLowerCase();
                const creature_type_str = main_pieces[1];
                table_entry.data["Size"] = SIZE_TO_NUM_MAP[common_util.capitalize_first(size_str)];
                if (CREATURE_TYPES.includes(creature_type_str)) {
                  new_obj.creature_type = creature_type_str as CreatureType;
                }
                new_obj.subtypes = [];
                if (main_pieces.length > 2) {
                  const subtype = main_pieces[2].replace("(","").replace(")","");
                  if (new_obj.creature_type === "Dragon") {
                    if (METALLIC_DRAGONS.includes(subtype)) {
                      new_obj.subtypes.push("Metallic");
                    } else if (CHROMATIC_DRAGONS.includes(subtype)) {
                      new_obj.subtypes.push("Chromatic");
                    } else if (GEM_DRAGONS.includes(subtype)) {
                      new_obj.subtypes.push("Gem");
                    }
                  }
                  new_obj.subtypes.push(subtype);
                }
                if (CREATURE_ALIGNMENTS.includes(pieces[1])) {
                  new_obj.alignment = pieces[1].trim();
                }
              }
            }
          }
        } else if (elem.classList.contains("mon-stat-block-2024__attributes")) {
          for (let i = 0; i < elem.children.length; ++i) {
            const child = elem.children[i];
            if (child instanceof HTMLElement && child.textContent) {
              const label_elems = child.getElementsByClassName("mon-stat-block-2024__attribute-label");
              const value_elems = child.getElementsByClassName("mon-stat-block-2024__attribute-data-value");
              let label_obj = label_elems[0];
              let value_obj = value_elems[0];
              if (label_obj && value_obj) {
                if ((label_obj.textContent === "AC" || label_obj.textContent === "Armor Class") && value_obj.textContent) {
                  // debugger;
                  table_entry.data["Armor Class"] = +value_obj.textContent;
                  label_obj = label_elems[1];
                  value_obj = value_elems[1];
                  
                  if (label_obj && value_obj) {
                    if (label_obj.textContent === "Initiative" && value_obj.textContent) {
                      const pieces = value_obj.textContent.replace("\n","").trim().split(" ");
                      initiative_modifier = +pieces[0].replace("+","");
                    }
                  }
                } else if (label_obj.textContent === "HP" || label_obj.textContent === "Hit Points") {
                  // debugger;
                  const extra = child.getElementsByClassName("mon-stat-block-2024__attribute-data-extra")[0];
                  if (extra && extra.textContent) {
                    // (21d8 + 84)
                    let pieces = extra.textContent.replace("\n","").trim().split(" ");
                    pieces = pieces[0].replace("(", "").split("d");
                    const count = +pieces[0];
                    // const size = +pieces[1];
                    table_entry.data["Hit Dice Count"] = count;
                  }
                } else if (label_obj.textContent === "Speed" && value_obj.textContent) {
                  // 30 ft., fly 60 ft.
                  const pieces = value_obj.textContent.split(",");
                  for (let i = 0; i < pieces.length; ++i) {
                    const sub_pieces = pieces[i].replace("\n","").trim().split(" ");
                    if (sub_pieces.includes("fly")) {
                      attr_entry.speeds.fly = +sub_pieces[1];
                    } else if (sub_pieces.includes("swim")) {
                      attr_entry.speeds.swim = +sub_pieces[1];
                    } else if (sub_pieces.includes("climb")) {
                      attr_entry.speeds.climb = +sub_pieces[1];
                    } else if (sub_pieces.includes("burrow")) {
                      attr_entry.speeds.burrow = +sub_pieces[1];
                    } else {
                      attr_entry.speeds.walk = +sub_pieces[0];
                    }
                  }
                }
              }
            }
          }
        } else if (elem.classList.contains("mon-stat-block-2024__stats")) {
          // <div class="mon-stat-block-2024__stats">
          //     <table class="stat-table physical">
          //         <thead>
          //             <tr>
          //                 <th></th>
          //                 <th></th>
          //                 <th>Mod</th>
          //                 <th>Save</th>
          //             </tr>
          //         </thead>
          //         <tbody>
          //             <tr>
          //                 <th>STR</th>
          //                 <td>18</td>
          //                 <td class="modifier">+4</td>
          //                 <td class="modifier">+4</td>
          //             </tr>
          //             <tr>
          //                 <th>DEX</th>
          //                 <td>16</td>
          //                 <td class="modifier">+3</td>
          //                 <td class="modifier">+7</td>
          //             </tr>
          //             <tr>
          //                 <th>CON</th>
          //                 <td>18</td>
          //                 <td class="modifier">+4</td>
          //                 <td class="modifier">+8</td>
          //             </tr>
          //         </tbody>
          //     </table>
          //     <table class="stat-table mental">
          //         <thead>
          //             <tr>
          //                 <th></th>
          //                 <th></th>
          //                 <th>Mod</th>
          //                 <th>Save</th>
          //             </tr>
          //         </thead>
          //         <tbody>
          //             <tr>
          //                 <th>INT</th>
          //                 <td>14</td>
          //                 <td class="modifier">+2</td>
          //                 <td class="modifier">+2</td>
          //             </tr>
          //             <tr>
          //                 <th>WIS</th>
          //                 <td>14</td>
          //                 <td class="modifier">+2</td>
          //                 <td class="modifier">+2</td>
          //             </tr>
          //             <tr>
          //                 <th>CHA</th>
          //                 <td>18</td>
          //                 <td class="modifier">+4</td>
          //                 <td class="modifier">+8</td>
          //             </tr>
          //         </tbody>
          //     </table>
          // </div>
          const tables = elem.getElementsByTagName("table");
          for (let table_num = 0; table_num < tables.length; ++table_num) {
            const table = tables[table_num];
            // console.log(table);
            if (table instanceof HTMLTableElement) {
              for (let row_num = 0; row_num < table.rows.length; ++row_num) {
                const row = table.rows[row_num];
                // console.log(row);
                if (row.cells.length === 4) {
                  if (row.cells[0].textContent && row.cells[1].textContent && row.cells[2].textContent && row.cells[3].textContent) {
                    if (row.cells[2].textContent === "Mod") {
                      // Ignore this row
                    } else {
                      const stat_str = row.cells[0].textContent.toLocaleUpperCase();
                      const score_str = row.cells[1].textContent;
                      // const mod_str = row.cells[2].textContent.replace("–","-");
                      const save_str = row.cells[3].textContent.replace("–","-").replace("−","-");
                      const score = +score_str;
                      const save = +save_str;
                      if (ABILITY_SCORES.includes(stat_str) && !isNaN(score) && !isNaN(save)) {
                        table_entry.data[stat_str] = score;
                        const mod = new_obj.getBasicModifier(stat_str);
                        if (save > mod) {
                          attr_entry.save_profs.push(stat_str);
                        }
                      } else {
                        debugger;
                        console.error("Ability Scores Parse Error");
                      }
                    }
                  }
                }
              }
            }
          }
        } else if (elem.classList.contains("mon-stat-block-2024__tidbits")) {
          // <div class="mon-stat-block-2024__tidbits">
          //         <div class="mon-stat-block-2024__tidbit">
          //             <span class="mon-stat-block-2024__tidbit-label">Skills</span>
          //             <span class="mon-stat-block-2024__tidbit-data">
          //                 <a class="tooltip-hover skill-tooltip" href="/sources/dnd/free-rules/playing-the-game#Skills" data-tooltip-href="//www.dndbeyond.com/skills/14-tooltip?disable-webm=1&amp;disable-webm=1">Perception</a> +6, <a class="tooltip-hover skill-tooltip" href="/sources/dnd/free-rules/playing-the-game#Skills" data-tooltip-href="//www.dndbeyond.com/skills/19-tooltip?disable-webm=1&amp;disable-webm=1">Persuasion</a> +8
          //             </span>
          //         </div>
          //         <div class="mon-stat-block-2024__tidbit">
          //             <span class="mon-stat-block-2024__tidbit-label">Resistances</span>
          //             <span class="mon-stat-block-2024__tidbit-data">
          //                 Cold
          //             </span>
          //         </div>
          //         <div class="mon-stat-block-2024__tidbit">
          //             <span class="mon-stat-block-2024__tidbit-label">Immunities</span>
          //             <span class="mon-stat-block-2024__tidbit-data">
          //                 Fire, Poison;&nbsp;<a class="tooltip-hover condition-tooltip" href="/sources/dnd/free-rules/rules-glossary#PoisonedCondition" data-tooltip-href="//www.dndbeyond.com/conditions/11-tooltip?disable-webm=1&amp;disable-webm=1">Poisoned</a>
          //             </span>
          //         </div>
          //         <div class="mon-stat-block-2024__tidbit">
          //             <span class="mon-stat-block-2024__tidbit-label">Senses</span>
          //             <span class="mon-stat-block-2024__tidbit-data">
          //                 <a class="tooltip-hover sense-tooltip" href="/sources/dnd/free-rules/rules-glossary#Truesight" data-tooltip-href="//www.dndbeyond.com/senses/4-tooltip?disable-webm=1&amp;disable-webm=1">Truesight</a> 120 ft.,  Passive Perception 16
          //             </span>
          //         </div>
          //         <div class="mon-stat-block-2024__tidbit">
          //             <span class="mon-stat-block-2024__tidbit-label">Languages</span>
          //             <span class="mon-stat-block-2024__tidbit-data">
          //                 Infernal, Telepathy 120 ft.
          //             </span>
          //         </div>
          //     <div class="mon-stat-block-2024__tidbit-container">
          //         <div class="mon-stat-block-2024__tidbit">
          //             <span class="mon-stat-block-2024__tidbit-label">CR</span>
          //             <span class="mon-stat-block-2024__tidbit-data">
          //                 12 (XP 8,400; PB +4)
          //             </span>
          //         </div>
          //     </div>
          // </div>
          let skills_str = "";
          for (let i = 0; i < elem.children.length; ++i) {
            const child = elem.children[i];
            if (child instanceof HTMLElement && child.textContent) {
              const label_elems = child.getElementsByClassName("mon-stat-block-2024__tidbit-label");
              const value_elems = child.getElementsByClassName("mon-stat-block-2024__tidbit-data");
              const label_obj = label_elems[0];
              const value_obj = value_elems[0];
              if (label_obj && value_obj) {
                if (label_obj.textContent === "Skills" && value_obj.textContent) {
                  skills_str = value_obj.textContent; // Save for later because we need to get the PB first, which is based on CR, which is at the end of tidbits
                } else if (label_obj.textContent === "Resistances" && value_obj.textContent) {
                  const pieces = value_obj.textContent.split(",").map(o => o.trim());
                  for (const piece of pieces) {
                    if (isDamageType(piece)) {
                      attr_entry.damage_resistances.push(piece);
                    } else {
                      console.error("Something weird is happening with its resistances");
                      debugger;
                    }
                  }
                } else if (label_obj.textContent === "Immunities" && value_obj.textContent) {
                  // Immunities is complicated because it has both the damage immunities and the condition immunities
                  if (value_obj.textContent.includes(";")) {
                    const main_pieces = value_obj.textContent.split(";");
                    // Damage Immunities
                    let pieces = main_pieces[0].split(",").map(o => o.trim());
                    for (const piece of pieces) {
                      if (isDamageType(piece)) {
                        attr_entry.damage_immunities.push(piece);
                      } else {
                        console.error("Something weird is happening with its immunities");
                        debugger;
                      }
                    }
                    // Condition Immunities
                    pieces = main_pieces[1].split(",").map(o => o.trim());
                    for (const piece of pieces) {
                      const cond = this.conditions.find(o => o.name === piece);
                      if (cond instanceof Condition) {
                        attr_entry.condition_immunities.push(piece);
                      } else {
                        console.error("Something weird is happening with its immunities");
                        debugger;
                      }
                    }
                  } else {
                    // It could be that it only has one or the other.
                    const pieces = value_obj.textContent.split(",").map(o => o.trim());
                    for (const piece of pieces) {
                      if (isDamageType(piece)) {
                        attr_entry.damage_immunities.push(piece);
                      } else {
                        const cond = this.conditions.find(o => o.name === piece);
                        if (cond instanceof Condition) {
                          attr_entry.condition_immunities.push(piece);
                        } else {
                          console.error("Something weird is happening with its immunities");
                          debugger;
                        }
                      }
                    }
                  }
                } else if (label_obj.textContent === "Vulnerabilities" && value_obj.textContent) {
                  const pieces = value_obj.textContent.split(",").map(o => o.trim());
                  for (const piece of pieces) {
                    if (isDamageType(piece)) {
                      attr_entry.damage_vulnerabilities.push(piece);
                    } else {
                      console.error("Something weird is happening with its vulnerabilities");
                      debugger;
                    }
                  }
                } else if (label_obj.textContent === "Senses" && value_obj.textContent) {
                  const pieces = value_obj.textContent.split(",").map(o => o.trim());
                  // debugger;
                  for (const piece of pieces) {
                    if (!piece.includes("Passive")) {
                      const sense_pieces = piece.replace("\n","").trim().split(" ");
                      const sense_name = (sense_pieces.length === 4 ? `${sense_pieces[0]} ${sense_pieces[1]}` : sense_pieces[0]).toLocaleLowerCase();
                      let sense = SENSES.find(o => o.name.toLocaleLowerCase() === sense_name);
                      if (!sense) {
                        sense = SENSES.find(o => o.name.toLocaleLowerCase().replace(" ","") === sense_name.replace(" ",""));
                      }
                      if (sense) {
                        attr_entry.senses[sense.name] = +sense_pieces[1]; // It uses sense_name because of an error that made it when I switched to id it screwed something up. Haven't had time to figure it out.
                      } else {
                        console.error("Something weird is happening with its senses");
                        debugger;
                      }
                    }
                  }
                } else if (label_obj.textContent === "Languages" && value_obj.textContent) {
                  attr_entry.languages = value_obj.textContent.replace("\n","").trim();
                } else if ((label_obj.textContent === "CR" || label_obj.textContent === "Challenge") && value_obj.textContent) {
                  // debugger;
                  const pieces = value_obj.textContent.replace("\n","").trim().split(" ");
                  let cr = +pieces[0];
                  if (isNaN(cr) && pieces[0].includes("/")) {
                    const pieces2 = pieces[0].split("/");
                    const numerator = +pieces2[0];
                    const denominator = +pieces2[1];
                    if (!isNaN(numerator) && !isNaN(denominator)) {
                      cr = numerator / denominator;
                    }
                  } 
                  attr_entry.level = new_obj.challenge_rating = new_obj.min_challenge_rating = new_obj.max_challenge_rating = cr;
                } else {
                  console.error("Something weird is happening with its tidbits");
                  debugger;
                }
              }
            }
          }
          if (skills_str.length > 0) {
            // debugger;
            // Now we have the PB, so we can figure out proficiency level
            const pb = new_obj.proficiency_modifier;
            const pieces = skills_str.split(",").map(o => o.trim());
            for (const piece of pieces) {
              const skill_pieces = piece.replace("\n","").trim().split(" ");
              const skill_name = skill_pieces[0];
              const skill = SKILLS.find(o => o.name === skill_name);
              if (skill) {
                const stat = table_entry.data[skill.use_ability_score];
                if (stat !== undefined) {
                  let mod = Math.floor((stat - 10) / 2);
                  let bonus = +skill_pieces[1];
                  bonus -= mod;
                  let prof_level = bonus / pb; // Usually 1, occasionally 2, rarely 0.5
                  if (![1,2,0.5].includes(prof_level) && mod < 0) {
                    // It could be that they're using 0 for the mod.
                    // That's cheating, but whatevs.
                    mod = 0;
                    bonus = +skill_pieces[1];
                    bonus -= mod;
                    prof_level = bonus / pb;
                  }
                  if (![1,2,0.5].includes(prof_level)) {
                    console.error("Something weird is happening with its skills");
                    debugger;
                  }
                  attr_entry.skill_profs[skill._id] = prof_level;
                }
              }
            }
          }
        } else if (elem.classList.contains("mon-stat-block-2024__description-blocks")) {
          // <div class="mon-stat-block-2024__description-blocks">
          //         <div class="mon-stat-block-2024__description-block"> 
          //             <div class="mon-stat-block-2024__description-block-heading">Traits</div>
          //             <div class="mon-stat-block-2024__description-block-content">
          //                 <p><em><strong>Diabolical Restoration.</strong></em> If the erinyes dies outside the Nine Hells, its body disappears in sulfurous smoke, and it gains a new body instantly, reviving with all its Hit Points somewhere in the Nine Hells.</p>
          //                 <p><em><strong>Magic Resistance.</strong></em> The erinyes has <a class="tooltip-hover rule-tooltip" href="/sources/dnd/free-rules/rules-glossary#Advantage" data-tooltip-href="//www.dndbeyond.com/rules-glossary/4-tooltip?disable-webm=1&amp;disable-webm=1">Advantage</a> on saving throws against spells and other magical effects.</p>
          //                 <p><em><strong>Magic Rope.</strong></em> The erinyes has a magic rope. While bearing it, the erinyes can use the Entangling Rope action. The rope has AC 20, HP 90, and Immunity to Poison and Psychic damage. The rope turns to dust if reduced to 0 Hit Points, if it is 5+ feet away from the erinyes for 1 hour or more, or if the erinyes dies. If the rope is damaged or destroyed, the erinyes can fully restore it when finishing a Short or Long Rest.</p>                
          //             </div>
          //         </div>
          //         <div class="mon-stat-block-2024__description-block">
          //             <div class="mon-stat-block-2024__description-block-heading">Actions</div>
          //             <div class="mon-stat-block-2024__description-block-content">
          //                 <p><em><strong>Multiattack.</strong></em> The erinyes makes three Withering Sword attacks and can use Entangling Rope.</p>
          //                 <p><em><strong>Withering Sword.</strong> Melee Attack Roll:</em> <span data-dicenotation="1d20+8" data-rolltype="to hit" data-rollaction="Withering Sword">+8</span>, reach 5 ft. <em>Hit:</em> 13 <span data-dicenotation="2d8+4" data-rolltype="damage" data-rollaction="Withering Sword" data-rolldamagetype="Slashing">(2d8 + 4)</span> Slashing damage plus 11 <span data-dicenotation="2d10" data-rolltype="damage" data-rollaction="Withering Sword" data-rolldamagetype="Necrotic">(2d10)</span> Necrotic damage.</p>
          //                 <p><em><strong>Entangling Rope (Requires Magic Rope).</strong> Strength Saving Throw:</em> DC 16, one creature the erinyes can see within 120 feet. <em>Failure:</em> 14 <span data-dicenotation="4d6" data-rolltype="damage" data-rollaction="Entangling Rope" data-rolldamagetype="Force">(4d6)</span> Force damage, and the target has the <a class="tooltip-hover condition-tooltip" href="/sources/dnd/free-rules/rules-glossary#RestrainedCondition" data-tooltip-href="//www.dndbeyond.com/conditions/13-tooltip?disable-webm=1&amp;disable-webm=1">Restrained</a> condition until the rope is destroyed, the erinyes uses a Bonus Action to release the target, or the erinyes uses Entangling Rope again.</p>                            
          //             </div>
          //         </div>
          //         <div class="mon-stat-block-2024__description-block">
          //             <div class="mon-stat-block-2024__description-block-heading">Reactions</div>
          //             <div class="mon-stat-block-2024__description-block-content">
          //                 <p><em><strong>Parry.</strong> Trigger:</em> The erinyes is hit by a melee attack roll while holding a weapon. <em>Response:</em> The erinyes adds 4 to its AC against that attack, possibly causing it to miss.</p>
          //             </div>
          //         </div>
          // </div>
          // debugger;
          for (let i = 0; i < elem.children.length; ++i) {
            const child = elem.children[i];
            if (child instanceof HTMLElement && child.textContent) {
              const heading = child.children[0];
              const content = child.children[1];
              if (heading instanceof HTMLElement && 
                heading.classList.contains("mon-stat-block-2024__description-block-heading") &&
                heading.textContent &&
                content instanceof HTMLElement && 
                content.classList.contains("mon-stat-block-2024__description-block-content")
              ) {
                const action_type = heading.textContent;
                let creature_fb_type: "Actions" | "Legendary Actions" | "Traits" | "Lair Actions" | "Spellcasting" | "" = "";
                if (action_type === "Actions" || action_type === "Bonus Actions" || action_type === "Reactions") {
                  creature_fb_type = "Actions";
                } else if (action_type === "Traits" || action_type === "Legendary Actions") {
                  creature_fb_type = action_type;
                }
                if (creature_fb_type !== "") {
                  if (table_entry.level === 0 && new_obj.challenge_rating > 0) {
                    table_entry.level = new_obj.challenge_rating;
                  }
                  // <p><em><strong>Diabolical Restoration.</strong></em> If the erinyes dies outside the Nine Hells, its body disappears in sulfurous smoke, and it gains a new body instantly, reviving with all its Hit Points somewhere in the Nine Hells.</p>
                  // <p><em><strong>Magic Resistance.</strong></em> The erinyes has <a class="tooltip-hover rule-tooltip" href="/sources/dnd/free-rules/rules-glossary#Advantage" data-tooltip-href="//www.dndbeyond.com/rules-glossary/4-tooltip?disable-webm=1&amp;disable-webm=1">Advantage</a> on saving throws against spells and other magical effects.</p>
                  // <p><em><strong>Magic Rope.</strong></em> The erinyes has a magic rope. While bearing it, the erinyes can use the Entangling Rope action. The rope has AC 20, HP 90, and Immunity to Poison and Psychic damage. The rope turns to dust if reduced to 0 Hit Points, if it is 5+ feet away from the erinyes for 1 hour or more, or if the erinyes dies. If the rope is damaged or destroyed, the erinyes can fully restore it when finishing a Short or Long Rest.</p>   
                  for (let j = 0; j < content.children.length; ++j) {
                    const child = content.children[j];
                    if (child instanceof HTMLElement) {
                      // Hopefully the old parseStatBlockAction will still work
                      const spellcasting = this.parseStatBlockAction(new_obj, child, creature_fb_type);
                      if (spellcasting === 1) {
                        creature_fb_type = "Spellcasting";
                      } else if (spellcasting === -1) {
                        creature_fb_type = "Actions";
                        this.parseStatBlockAction(new_obj, child, section);
                      }
                    }
                  }
                } else {
                  console.error("Something weird is happening with its blocks");
                  debugger;
                }
              }
            }
          }
        } else {
        }
      } 
    }

    if (elem) {
      for (let i = 0; i < elem.children.length; ++i) {
        parseElement(elem.children[i] as HTMLElement);
      }
    }
    // It's all parsed.  Now let's go through Init, AC, etc and convert to be based on stats.
    const DEX = new_obj.getBasicModifier("DEX");
    const PB = new_obj.get_proficiency_modifier(new_obj.challenge_rating);
    
    if (true) {
      // debugger;
      // init is almost always equal to DEX.  When it's not it's usually DEX + PB
      new_obj.initiative_modifier = new UpgradableNumber();
      // new_obj.initiative_modifier.add_ability_mod_mult = 1;
      // new_obj.initiative_modifier.add_ability_scores.push("DEX");
      new_obj.initiative_modifier.add_stats_groups = [{ 
        true_id: uuidv4().toString(),
        add_ability_scores: ["DEX"],
        mod_or_score: "Mod",
        add_mult: 1,
        multiple_type: "And"
      }];
      if (initiative_modifier === (DEX + PB)) {
        new_obj.initiative_modifier.add_prof_bonus_mult = 1;
      }
    }

    await this.parseWithNames(new_obj);

    return new_obj;
  }
  
  parseNewStatBlock = async (new_obj: Creature, elem: HTMLElement | null) => {
    const attr_entry = new_obj.attributes_table.entries[0];
    const table_entry = new_obj.table.entries[0];
    let section: "Traits" | "Actions" | "Bonus Actions" | "Reactions" | "Legendary Actions" | "Spellcasting" | "" = ""; // There will probably be more of these later
    let initiative_modifier = 0;
    let hp_for_check = 0;
    let skills_str = "";
    
    const parseElement = (child: HTMLElement) => {
      // console.log(new_obj.name);
      if (child.classList.contains("heading-anchor") && new_obj.name.length === 0 && child.textContent) {
        new_obj.name = child.textContent.replace("\n","").trim();
      } else if (child.nodeName === "P" && child.textContent) {
        const parseOther = (content: string) => {
          // Have to figure out what it is from the content.
          if (["Traits","Actions","Bonus Actions","Reactions","Legendary Actions"].includes(content)) {
            section = content as ("Traits" | "Actions" | "Bonus Actions" | "Reactions" | "Legendary Actions");
          } else if (section === "") {
            // <p data-content-chunk-id="721c51bd-323c-474a-bd2d-de85b4f1714f">Medium or Small Humanoid (Cleric), Neutral</p>
            // if (content.includes("Medium or Small Humanoid")) {
            //   debugger;
            // }
            const pieces = content.split(",");
            const p1s = pieces[0].split(" ");
            if (p1s[1] === "or") {
              const size1 = SIZE_OPTIONS.find(o => o.text === p1s[0]);
              const size2 = SIZE_OPTIONS.find(o => o.text === p1s[2]);
              if (size1 && size2) {
                table_entry.data["Size"] = +size1.id;
                table_entry.data["Size2"] = +size2.id; // This stores it, but it's otherwise not implemented yet.
                if (p1s.length > 3) {
                  const creature_type = p1s[3];
                  if (CREATURE_TYPES.includes(creature_type)) {
                    new_obj.creature_type = creature_type as CreatureType;
                    if (p1s.length > 4) {
                      for (let i = 4; i < p1s.length; ++i) {
                        const subtype = p1s[i].replace("(","").replace(")","");
                        new_obj.subtypes.push(subtype);
                      }
                    }
                  } else {
                    console.error(content);
                  }
                } else {
                  console.error(content);
                }
              } else {
                console.error(content);
              }
            } else {
              const size = SIZE_OPTIONS.find(o => o.text === p1s[0]);
              if (size) {
                table_entry.data["Size"] = +size.id;
                if (p1s.length > 1) {
                  const creature_type = p1s[1];
                  if (CREATURE_TYPES.includes(creature_type)) {
                    new_obj.creature_type = creature_type as CreatureType;
                    if (p1s.length > 2) {
                      for (let i = 2; i < p1s.length; ++i) {
                        const subtype = p1s[i].replace("(","").replace(")","");
                        new_obj.subtypes.push(subtype); // subtypes may be names of classes, 
                      }
                    }
                  } else {
                    for (let k = 2; k < p1s.length; ++k) {
                      let p = p1s[k];
                      if (new_obj.creature_type !== "None") {
                        if (p.startsWith("(")) {
                          const subtype = p.replace("(","").replace(")","");
                          new_obj.subtypes.push(subtype);
                        }
                      } else {
                        if (CREATURE_TYPES.includes(p)) {
                          new_obj.creature_type = p as CreatureType;
                        } else if (p.endsWith("s")) {
                          p = p.substring(0, p.length - 1);
                          if (CREATURE_TYPES.includes(p)) {
                            new_obj.creature_type = p as CreatureType;
                          } else if (p === "Monstrositie") {
                            new_obj.creature_type = "Monstrosity";
                          }
                        }
                      }
                    }
                    if (new_obj.creature_type === "None") {
                      console.error(new_obj);
                      console.error(child);
                      console.error(content);
                    }
                  }
                } else {
                  console.error(new_obj);
                  console.error(child);
                  console.error(content);
                }
              } else {
                console.error(new_obj);
                console.error(child);
                console.error(content);
              }
            }
            if (pieces.length > 1) {
              new_obj.alignment = pieces[1].trim();
            }
          } else {
            if (table_entry.level === 0 && new_obj.challenge_rating > 0) {
              table_entry.level = new_obj.challenge_rating;
            }
            // Hopefully the old parseStatBlockAction will still work
            const spellcasting = this.parseStatBlockAction(new_obj, child, section);
            if (spellcasting === 1) {
              section = "Spellcasting";
            } else if (spellcasting === -1) {
              section = "Actions";
              this.parseStatBlockAction(new_obj, child, section);
            }
          }
        }

        if (section === "") {
          const strongs = child.getElementsByTagName("strong");
          if (strongs.length > 0) {
            for (let i = 0; i < strongs.length; ++i) {
              const strong = strongs[i];
              const label = strong.textContent?.replace("\n","").trim()??"";
              if (label === "Skills") {
                skills_str = child.textContent.replace("Skills","").replace("\n","").trim(); // Save for later because we need to get the PB first, which is based on CR, which is at the end of tidbits
              } else {
                const content = strong.nextSibling?.textContent??null;
                if (label !== "" && content) {
                  const pieces = content.trim().split(" ");
                  switch (label) {
                    case "AC": 
                      const ac = +content.trim();
                      if (!isNaN(ac)) {
                        table_entry.data["Armor Class"] = ac;
                      }
                    break;
                    case "Initiative": 
                      // " +3 (13)" We can ignore the parentheses because it's always just 10 + the mod
                      initiative_modifier = +pieces[0].replace("+","");
                    break;
                    case "HP": 
                      // " 66 (12d8 + 12)" For this we just want the parentheses, and really just the first part.
                      hp_for_check = +pieces[0]; // Save this so we can double check that it matches at the end.
                      const dice_str = pieces[1].replace("(","").replace(")","").trim();
                      const dice_pieces = dice_str.split("d");
                      table_entry.data["Hit Dice Count"] = +dice_pieces[0];
                    break;
                    case "Speed": 
                      // " 20 ft., Fly 50 ft."
                      const speeds_pieces = content.trim().split(",").map(o => o.trim());
                      for (const speeds_piece of speeds_pieces) {
                        const speed_pieces = speeds_piece.split(" ");
                        const piece1 = speed_pieces[0].toLocaleLowerCase();
                        if (SPEED_OPTIONS.filter(o => o.id === piece1).length > 0) {
                          attr_entry.speeds[piece1] = +speed_pieces[1];
                        } else {
                          attr_entry.speeds.walk = +piece1;
                        }
                      }
                    break;
                    case "Senses": 
                      if (content.trim().length > 0) {
                        // " Passive Perception 17"
                        const sense_pieces = content.split(",").map(o => o.trim());
                        // debugger;
                        for (const piece of sense_pieces) {
                          if (!piece.includes("Passive")) {
                            const sense_pieces = piece.replace("\n","").trim().split(" ");
                            const sense_name = (sense_pieces.length === 4 ? `${sense_pieces[0]} ${sense_pieces[1]}` : sense_pieces[0]).toLocaleLowerCase();
                            let sense = SENSES.find(o => o.name.toLocaleLowerCase() === sense_name);
                            if (!sense) {
                              sense = SENSES.find(o => o.name.toLocaleLowerCase().replace(" ","") === sense_name.replace(" ",""));
                            }
                            if (sense) {
                              attr_entry.senses[sense.name] = +sense_pieces[1]; // It uses sense_name because of an error that made it when I switched to id it screwed something up. Haven't had time to figure it out.
                            } else {
                              console.log(child);
                              console.log(sense_name);
                              console.log(sense_pieces);
                              console.log(content);
                              console.error("Something weird is happening with its senses");
                              // debugger;
                            }
                          }
                        }
                      } else {
                        // <p data-content-chunk-id="0c1be70e-df2d-43e8-8b68-6530c6bfae46"><strong>Senses</strong> <a class="tooltip-hover sense-tooltip" href="/sources/dnd/free-rules/rules-glossary#Darkvision" data-tooltip-href="//www.dndbeyond.com/senses/2-tooltip?disable-webm=1&amp;disable-webm=1">Darkvision</a> 120 ft.; Passive Perception 20</p>
                        const sense_as = child.getElementsByTagName("a");
                        for (let j = 0; j < sense_as.length; ++j) {
                          const sense_a = sense_as[j];
                          if (sense_a.classList.contains("sense-tooltip")) {
                            const sense_name = sense_a.textContent?.toLocaleLowerCase()??"";
                            let sense = SENSES.find(o => o.name.toLocaleLowerCase() === sense_name);
                            if (!sense) {
                              sense = SENSES.find(o => o.name.toLocaleLowerCase().replace(" ","") === sense_name.replace(" ",""));
                            }
                            if (sense && sense_a.nextSibling && sense_a.nextSibling.textContent) {
                              let amt_string = sense_a.nextSibling.textContent.trim();
                              amt_string = amt_string.split(" ")[0];
                              if (sense.name.includes("Darkvision") && 
                                child.textContent && child.textContent.includes("unimpeded by magical")) {
                                const sense2 = SENSES.find(o => o.name === "Devil's Sight");
                                if (sense2) {
                                  sense = sense2;
                                }
                              }
                              attr_entry.senses[sense.name] = +amt_string; // It uses sense_name because of an error that made it when I switched to id it screwed something up. Haven't had time to figure it out.
                            } else {
                              console.log(child);
                              console.log(sense_name);
                              console.log(content);
                              console.error("Something weird is happening with its senses");
                              // debugger;
                            }
                          }
                        }
                      }
                    break;
                    case "Languages": 
                      // " Aarakocra, Primordial (Auran)"
                      attr_entry.languages = content.replace("\n","").trim();
                    break;
                    case "CR": 
                      // " 4 (XP 1,100; PB +2)"
                      // if (new_obj.name.includes("Axe Beak")) {
                      //   debugger;
                      // }
                      const cr_str = pieces[0];
                      let cr = +cr_str;
                      if (isNaN(cr) && cr_str.includes("/")) {
                        const cr_pieces = cr_str.split("/");
                        if (cr_pieces.length === 2) {
                          const numerator = +cr_pieces[0];
                          const denominator = +cr_pieces[1];
                          if (!isNaN(numerator) && !isNaN(denominator)) {
                            cr = numerator / denominator;
                          }
                        }
                      }
                      if (!isNaN(cr)) {
                        attr_entry.level = new_obj.challenge_rating = new_obj.min_challenge_rating = new_obj.max_challenge_rating = cr;
                      }
                    break;
                    case "Vulnerabilities": 
                      // " Fire, Poison"
                      if (true) {
                        const sub_pieces = pieces[0].split(",").map(o => o.trim()).filter(o => isDamageType(o)) as DamageType[];
                        attr_entry.damage_vulnerabilities = sub_pieces;
                      }
                    break;
                    case "Resistances": 
                      // " Fire, Poison"
                      if (true) {
                        const sub_pieces = pieces[0].split(",").map(o => o.trim()).filter(o => isDamageType(o)) as DamageType[];
                        attr_entry.damage_resistances = sub_pieces;
                      }
                    break;
                    case "Immunities": 
                      // " Fire, Poison; Prone"
                      if (true) {
                        const sub_pieces = pieces[0].split(";");
                        const dmg_pieces = sub_pieces[0].split(",").map(o => o.trim()).filter(o => isDamageType(o)) as DamageType[];
                        attr_entry.damage_immunities = dmg_pieces;
                        if (sub_pieces.length > 1) {
                          const cond_pieces = sub_pieces[1].split(",").map(o => o.trim());
                          attr_entry.condition_immunities = cond_pieces;
                        }
                      }
                    break;
                    case "Gear": 
                      // <p data-content-chunk-id="6a0f285b-6544-4249-95f8-658b6b596ff0"><strong>Gear</strong> <a
                      //       class="tooltip-hover item-tooltip" href="/equipment/15-crossbow-light"
                      //       data-tooltip-href="//www.dndbeyond.com/weapons/15-tooltip?disable-webm=1&amp;disable-webm=1">Light
                      //       Crossbow</a>, <a class="tooltip-hover item-tooltip" href="/equipment/30-shortsword"
                      //       data-tooltip-href="//www.dndbeyond.com/weapons/30-tooltip?disable-webm=1&amp;disable-webm=1">Shortsword</a>,
                      //   <a class="tooltip-hover item-tooltip" href="/equipment/3-studded-leather"
                      //       data-tooltip-href="//www.dndbeyond.com/armor/3-tooltip?disable-webm=1&amp;disable-webm=1">Studded
                      //       Leather Armor</a></p>
                      if (true) {
                        const gear_as = child.getElementsByTagName("a");
                        for (let j = 0; j < gear_as.length; ++j) {
                          const gear_a = gear_as[j];
                          const next_child = gear_a.nextSibling;
                          let count = 1;
                          let item_name = gear_a.textContent?.toLocaleLowerCase()??"";
                          item_name = item_name.replace("’","'");
                          let item = this.smart_hash.item.find(o => o.name.toLocaleLowerCase() === item_name && o.isDominant());
                          if (!item) {
                            // If it has the word Armor, try taking that out
                            if (item_name.includes("armor")) {
                              item_name = item_name.replace("armor","").trim();
                              item = this.smart_hash.item.find(o => o.name.toLocaleLowerCase() === item_name && o.isDominant());
                            }
                            if (item_name.includes("-")) {
                              item_name = item_name.replace("-"," ").trim();
                              item = this.smart_hash.item.find(o => o.name.toLocaleLowerCase() === item_name && o.isDominant());
                            }
                            if (next_child && next_child.textContent && next_child.textContent.includes("(") && next_child.textContent.includes(")")) {
                              let count_str = next_child.textContent.trim();
                              if (count_str.startsWith("(")) {
                                count_str = count_str.substring(1);
                                const index = count_str.indexOf(")");
                                count_str = count_str.substring(0, index - 1);
                              }
                              
                              count = +count_str;
                            }
                            if (isNaN(count)) {
                              count = 1;
                            } else {
                              if (item_name.endsWith("s")) {
                                item_name = item_name.substring(0, item_name.length - 1);
                                item = this.smart_hash.item.find(o => o.name.toLocaleLowerCase() === item_name && o.isDominant());
                              }
                            }
                            if (!item) {
                              // It could also be that the name is rearranged, like Light Crossbow vs Crossbow, Light or Crossbow (Light)
                              item_name = item_name.replace(",","").replace("(","").replace(")","").trim();
                              const name_pieces = item_name.split(" ");
                              let items = this.smart_hash.item.filter(o => o.isDominant());
                              for (const name_piece of name_pieces) {
                                items = items.filter(o => o.name.toLocaleLowerCase().includes(name_piece));
                              }
                              if (items.length === 1) {
                                item = items[0];
                              }
                            }
                          }
                          if (item instanceof Item) {
                            for (let k = 0; k < count; ++k) {
                              new_obj.gear_item_ids.push(item._id);
                            }
                          } else {
                            console.log(child);
                            console.log(item_name);
                            console.log(content);
                            console.error("Something weird is happening with its gear");
                            // debugger;
                          }
                        }
                        const sub_pieces = pieces[0].split(";");
                        const dmg_pieces = sub_pieces[0].split(",").map(o => o.trim()).filter(o => isDamageType(o)) as DamageType[];
                        attr_entry.damage_immunities = dmg_pieces;
                        if (sub_pieces.length > 1) {
                          const cond_pieces = sub_pieces[1].split(",").map(o => o.trim());
                          attr_entry.condition_immunities = cond_pieces;
                        }
                      }
                    break;
                    default: 
                      console.log(label);
                      console.log(content);
                    break;
                  }
                } else {
                  console.error(strong);
                  debugger;
                }
              }
            }
          } else {
            const content = child.textContent.replace("\n","").trim();
            if (content) {
              parseOther(content);
            }
          }
        } else {
          // debugger;
          const content = child.textContent.replace("\n","").trim();
          if (content) {
            parseOther(content);
          }
        }
      } else if (child.nodeName === "DIV") {
        if (child.classList.contains("stats")) {

          // <div class="stats" data-content-chunk-id="a3b7cbb5-387b-4df9-9107-e2929810580a">
          //     <div class="table-overflow-wrapper">
          //         <table class="physical abilities-saves" data-content-chunk-id="bc2f5b96-2b8a-4db7-9f1f-0e4b9975f275">
          //             <thead>
          //                 <tr>
          //                     <th></th>
          //                     <th></th>
          //                     <th>Mod</th>
          //                     <th>Save</th>
          //                 </tr>
          //             </thead>
          //             <tbody>
          //                 <tr>
          //                     <th>Str</th>
          //                     <td>10</td>
          //                     <td>+0</td>
          //                     <td>+0</td>
          //                 </tr>
          //                 <tr>
          //                     <th>Dex</th>
          //                     <td>16</td>
          //                     <td>+3</td>
          //                     <td>+5</td>
          //                 </tr>
          //                 <tr>
          //                     <th>Con</th>
          //                     <td>12</td>
          //                     <td>+1</td>
          //                     <td>+1</td>
          //                 </tr>
          //             </tbody>
          //         </table>
          //     </div>
          //     <div class="table-overflow-wrapper">
          //         <table class="mental abilities-saves" data-content-chunk-id="c68ae731-393e-44b4-af63-ad9d49a2edf6">
          //             <thead>
          //                 <tr>
          //                     <th></th>
          //                     <th></th>
          //                     <th>Mod</th>
          //                     <th>Save</th>
          //                 </tr>
          //             </thead>
          //             <tbody>
          //                 <tr>
          //                     <th>Int</th>
          //                     <td>13</td>
          //                     <td>+1</td>
          //                     <td>+1</td>
          //                 </tr>
          //                 <tr>
          //                     <th>Wis</th>
          //                     <td>17</td>
          //                     <td>+3</td>
          //                     <td>+5</td>
          //                 </tr>
          //                 <tr>
          //                     <th>Cha</th>
          //                     <td>12</td>
          //                     <td>+1</td>
          //                     <td>+1</td>
          //                 </tr>
          //             </tbody>
          //         </table>
          //     </div>
          // </div>
          const tables = child.getElementsByTagName("table");
          for (let table_num = 0; table_num < tables.length; ++table_num) {
            const table = tables[table_num];
            // console.log(table);
            if (table instanceof HTMLTableElement) {
              for (let row_num = 0; row_num < table.rows.length; ++row_num) {
                const row = table.rows[row_num];
                // console.log(row);
                if (row.cells.length === 4) {
                  if (row.cells[0].textContent && row.cells[1].textContent && row.cells[2].textContent && row.cells[3].textContent) {
                    if (row.cells[2].textContent === "Mod") {
                      // Ignore this row
                    } else {
                      const stat_str = row.cells[0].textContent.toLocaleUpperCase();
                      const score_str = row.cells[1].textContent;
                      // const mod_str = row.cells[2].textContent.replace("–","-");
                      const save_str = row.cells[3].textContent.replace("–","-").replace("−","-");
                      const score = +score_str;
                      const save = +save_str;
                      if (ABILITY_SCORES.includes(stat_str) && !isNaN(score) && !isNaN(save)) {
                        table_entry.data[stat_str] = score;
                        const mod = new_obj.getBasicModifier(stat_str);
                        if (save > mod) {
                          attr_entry.save_profs.push(stat_str);
                        }
                      } else {
                        debugger;
                        console.error("Ability Scores Parse Error");
                      }
                    }
                  }
                }
              }
            }
          }
        }
      } 
    }

    if (elem) {
      for (let i = 0; i < elem.children.length; ++i) {
        parseElement(elem.children[i] as HTMLElement);
      }
    }

    await this.parseWithNames(new_obj);
    
    if (skills_str.length > 0) {
      // debugger;
      // Now we have the PB, so we can figure out proficiency level
      const pb = new_obj.proficiency_modifier;
      const pieces = skills_str.split(",").map(o => o.trim());
      for (const piece of pieces) {
        const skill_pieces = piece.replace("\n","").trim().split(" ");
        const skill_name = skill_pieces[0];
        const skill = SKILLS.find(o => o.name === skill_name);
        if (skill) {
          const stat = table_entry.data[skill.use_ability_score];
          if (stat !== undefined) {
            let mod = Math.floor((stat - 10) / 2);
            let bonus = +skill_pieces[1];
            bonus -= mod;
            let prof_level = bonus / pb; // Usually 1, occasionally 2, rarely 0.5
            if (![1,2,0.5].includes(prof_level) && mod < 0) {
              // It could be that they're using 0 for the mod.
              // That's cheating, but whatevs.
              mod = 0;
              bonus = +skill_pieces[1];
              bonus -= mod;
              prof_level = bonus / pb;
            }
            if (![1,2,0.5].includes(prof_level)) {
              console.error("Something weird is happening with its skills");
              debugger;
            }
            attr_entry.skill_profs[skill._id] = prof_level;
          }
        }
      }
    }
    // It's all parsed.  Now let's go through Init, AC, etc and convert to be based on stats.
    const DEX = new_obj.getBasicModifier("DEX");
    const PB = new_obj.get_proficiency_modifier(new_obj.challenge_rating);
    
    if (true) {
      // debugger;
      // init is almost always equal to DEX.  When it's not it's usually DEX + PB
      new_obj.initiative_modifier = new UpgradableNumber();
      // new_obj.initiative_modifier.add_ability_mod_mult = 1;
      // new_obj.initiative_modifier.add_ability_scores.push("DEX");
      new_obj.initiative_modifier.add_stats_groups = [{ 
        true_id: uuidv4().toString(),
        add_ability_scores: ["DEX"],
        mod_or_score: "Mod",
        add_mult: 1,
        multiple_type: "And"
      }];
      if (initiative_modifier === (DEX + PB)) {
        new_obj.initiative_modifier.add_prof_bonus_mult = 1;
      }
    }
    if (hp_for_check !== new_obj.max_hp_for_cr(new_obj.challenge_rating)) {
      console.log(new_obj.challenge_rating);
      console.log(elem?.outerHTML??"");
      console.error(new_obj.name);
      console.error(table_entry);
      console.error(new_obj.max_hp_for_cr(new_obj.challenge_rating, true));
      console.error("HP Calc Error");
      // debugger;
    }
    return new_obj;
  }

  parseWithNames = async (new_obj: Creature) => {
    if (new_obj.small_name.length > 0) {
      switch (new_obj.small_name) {
        case "aarakocra": new_obj.subtypes.push("Air"); break;
        case "mind flayer": case "elder brain": new_obj.subtypes.push("Illithid"); break;
      }
      const capped = common_util.capitalize_firsts(new_obj.small_name);
      new_obj.subtypes.push(capped);
    }

    if (new_obj.creature_type !== "Beast" && new_obj.creature_type !== "Monstrosity" && new_obj.creature_type !== "Ooze") {
      const game_classes = this.smart_hash.game_class as GameClass[];
      const subclasses = this.smart_hash.subclass as Subclass[];
      const lc_name = new_obj.name.toLocaleLowerCase();
      const name_words = lc_name.split(" ");
      for (let i = 0; i < name_words.length; ++i) {
        const word = name_words[i];
        const gc = game_classes.find(o => o.name.toLocaleLowerCase() === word);
        if (gc) {
          new_obj.class_ids.push(gc._id);
        } else {
          let subclass = subclasses.find(o => o.name.toLocaleLowerCase() === word);
          if (!subclass && i > 0) {
            // It may be a subclass whose name is multiple words. 
            const scs = subclasses.filter(o => o.name.toLocaleLowerCase().includes(word));
            if (scs.length > 0) {
              // Try getting previous words. If we add them and end up getting 1 where the name is the same then we're good
              let j = i - 1;
              let use_name = `${name_words[j]} ${word}`;
              while (j >= 0) {  
                subclass = subclasses.find(o => o.name.toLocaleLowerCase() === use_name);
                if (subclass) {
                  break;
                }
                --j;
                use_name = `${name_words[j]} ${use_name}`;
              }
            }
          }
          if (subclass) {
            new_obj.class_ids.push(subclass._id);
          }
        }
      }
      if (lc_name.includes("bandit")) {
        const gc = game_classes.find(o => o.name === "Rogue" && o.isDominant());
        if (gc) {
          new_obj.class_ids.push(gc._id);
        }
      }
      if (lc_name.includes("mancer") && !lc_name.includes("necromancer")) {
        const gc = game_classes.find(o => o.name === "Sorcerer" && o.isDominant());
        if (gc) {
          new_obj.class_ids.push(gc._id);
        }
      }
      if (lc_name.includes("mage") || lc_name.includes("lich") || lc_name.includes("necromancer")) {
        const gc = game_classes.find(o => o.name === "Wizard" && o.isDominant());
        if (gc) {
          new_obj.class_ids.push(gc._id);
        }
      }
    }
    if (new_obj.creature_type === "Aberration") {
      if (new_obj.name.includes("Beholder") || new_obj.name.includes("Spectator") || new_obj.name.includes("Gazer") || new_obj.name.includes("Tyrant")) {
        new_obj.subtypes.push("Beholderkin");
      }
    }
    if (new_obj.creature_type !== "Beast" && new_obj.creature_type !== "Monstrosity" && new_obj.creature_type !== "Ooze") {
      const game_classes = this.smart_hash.game_class as GameClass[];
      const subclasses = this.smart_hash.subclass as Subclass[];
      for (const subtype of new_obj.subtypes) {
        let gc = game_classes.find(o => o.name === subtype && o.isDominant());
        if (gc) {
          new_obj.class_ids.push(gc._id);
        } else {
          const sc = subclasses.find(o => o.name === subtype && o.isDominant());
          if (sc) {
            gc = game_classes.find(o => o.variant_ids.includes(sc.game_class_id) && o.isDominant());
            if (gc) {
              new_obj.class_ids.push(gc._id);
            }
          }
        }
      }
    }

    new_obj.class_ids = common_util.unique_array(new_obj.class_ids);
    new_obj.subtypes = common_util.unique_array(new_obj.subtypes);
  }

  parseOld = (new_obj: Creature, elem: HTMLElement) => {
    // debugger;
    const attr_entry = new_obj.attributes_table.entries[0];
    let section: "Traits" | "Actions" | "Bonus Actions" | "Reactions" | "Spellcasting" | "" = ""; // There will probably be more of these later
    let initiative_modifier = 0;
    const parseElement = (elem: HTMLElement) => {
      if (elem.nodeName === "P" && elem.textContent) {
        if (section === "") {
          const str = elem.textContent.replace("\n","").trim();
          const pieces = str.split(" ");
          if (new_obj.creature_type === "None") {
            // Medium Beast, Unaligned
            const size_str = pieces[0].replace(",","").trim().toLocaleLowerCase();
            const creature_type_str = pieces[1].replace(",","").trim();
            const alignment_str = pieces.length > 2 ? pieces[2].replace(",","").trim() : "";
            new_obj.size_ugn.base = SIZE_TO_NUM_MAP[common_util.capitalize_first(size_str)];
            if (CREATURE_TYPES.includes(creature_type_str)) {
              new_obj.creature_type = creature_type_str as CreatureType;
            }
            new_obj.alignment = alignment_str;
          } else if (str.includes("Initiative ")) {
            // AC 12 Initiative +2 (12)
            if (pieces.length === 5) {
              const ac_str = pieces[1];
              const init_str = pieces[3].replace("–","-");
              const ac = +ac_str;
              if (!isNaN(ac)) {
                // For now we're just setting the ac.base to it.  
                // Then at the end we can convert it to be based on DEX.
                new_obj.armor_class.makeBasic();
                new_obj.armor_class.base = ac;
              }
              const init = +init_str;
              if (!isNaN(init)) {
                // For now we're just setting the init.base to it.  
                // Then at the end we can convert it to be based on DEX.
                new_obj.initiative_modifier.makeBasic();
                new_obj.initiative_modifier.base = init;
              }
            } else {
              // No example of this yet, but it should have natural armor, etc, in here somewhere.
              debugger;
            }
          } else if (str.includes("HP ")) {
            // HP 19 (3d8 + 6)
            // const max_str = pieces[1];
            const dice_str = pieces[2].replace("(","").replace(")","");
            // const mod_str = pieces.length > 4 ? pieces[4].replace(")","") : "0";
            // const max = +max_str;
            const dice = common_util.parseDiceRoll(dice_str); // + " + " + mod_str);
            const hd_upg = new HitDiceUpgradable();
            hd_upg.count.base = dice.count;
            hd_upg.size = dice.size;
            new_obj.hit_dice = [];
            new_obj.hit_dice.push(hd_upg);
            new_obj.hit_dice_bonus.makeBasic();
            new_obj.hit_dice_bonus.base = 0;
            // new_obj.hit_dice_bonus.add_ability_mod_mult = 1;
            // new_obj.hit_dice_bonus.add_ability_scores.push("CON");
            new_obj.hit_dice_bonus.add_stats_groups = [{ 
              true_id: uuidv4().toString(),
              add_ability_scores: ["CON"],
              mod_or_score: "Mod",
              add_mult: 1,
              multiple_type: "And"
            }];
          } else if (str.includes("Speed ")) {
            // Speed 30 ft., Climb 30 ft.
            let current_speed_type: "Speed" | "Climb" | "Burrow" | "Fly" | "Swim" | "" = "";
            for (let i = 0; i < pieces.length; ++i) {
              const piece = pieces[i];
              switch (piece) {
                case "Speed": current_speed_type = "Speed"; break;
                case "Climb": current_speed_type = "Climb"; break;
                case "Burrow": current_speed_type = "Burrow"; break;
                case "Fly": current_speed_type = "Fly"; break;
                case "Swim": current_speed_type = "Swim"; break;
                default: 
                  const val = +piece;
                  switch (current_speed_type) {
                    case "Speed": 
                      if ((attr_entry.speeds["walk"] === undefined || 
                        attr_entry.speeds["walk"] === 0) && !isNaN(val)) {
                        attr_entry.speeds["walk"] = val;
                      }
                    break;
                    default: 
                      if ((attr_entry.speeds[current_speed_type.toLocaleLowerCase()] === undefined || 
                        attr_entry.speeds[current_speed_type.toLocaleLowerCase()] === 0) && !isNaN(val)) {
                        attr_entry.speeds[current_speed_type.toLocaleLowerCase()] = val;
                      }
                    break;
                  }
                break;
              }
            }
          } else if (str.includes("Skills ")) {
            // Skills Athletics +5, Perception +3
            let skill_name = "";
            for (let i = 1; i < pieces.length; ++i) {
              const piece = pieces[i].replace(",","");
              const val = +piece;
              if (!isNaN(val)) { 
                const skill = SKILLS.find(o => o.name === skill_name);
                if (skill) {
                  // This part is intentionally wrong.  It's just storing it here.
                  // Later we'll fix it once we know the PB and the stats.
                  attr_entry.skill_profs[skill._id] = val;
                }
              } else {
                skill_name = piece;
              }
            }
          } else if (str.includes("Senses ")) {
            // Senses Darkvision 60 ft., Passive Perception 15
            // Passive Perception will automatically get ignored by this, which is fine.
            // I automatically build it appropriately at the end.
            // console.log(str);
            // console.log(pieces);
            let sense_name = "";
            for (let i = 1; i < pieces.length; ++i) {
              const piece = pieces[i].replace(",","");
              const val = +piece;
              if (!isNaN(val)) { 
                const sense = SENSES.find(o => o.name.replace(" ","").toLocaleLowerCase() === sense_name);
                if (sense) {
                  attr_entry.senses[sense.name] = val;
                }
              } else {
                sense_name = piece.toLocaleLowerCase();
              }
            }
          } else if (str.includes("Resistances ")) {
            // Resistances Cold
            for (let i = 1; i < pieces.length; ++i) {
              const piece = common_util.capitalize_first(pieces[i]).replace(",","").replace(".","").replace(";","");
              if (isDamageType(piece)) {
                attr_entry.damage_resistances.push(piece);
              } else {
                if (attr_entry.extra_damage_resistances.length > 0) {
                  attr_entry.extra_damage_resistances += ", ";
                }
                attr_entry.extra_damage_resistances += piece;
              }
            }
          } else if (str.includes("Immunities ")) {
            // Immunities Fire, Poison; Poisoned
            for (let i = 1; i < pieces.length; ++i) {
              const piece = common_util.capitalize_first(pieces[i]).replace(",","").replace(".","").replace(";","");
              if (isDamageType(piece)) {
                attr_entry.damage_immunities.push(piece);
              } else {
                const condition = this.conditions.find(o => o.common && o.name === piece);
                if (condition) {
                  attr_entry.condition_immunities.push(condition.name);
                } else {
                  if (attr_entry.extra_damage_immunities.length > 0) {
                    attr_entry.extra_damage_immunities += ", ";
                  }
                  attr_entry.extra_damage_immunities += piece;
                }
              }
            }
          } else if (str.includes("Vulnerabilities ")) {
            // Vulnerabilities Bludgeoning
            for (let i = 1; i < pieces.length; ++i) {
              const piece = common_util.capitalize_first(pieces[i]).replace(",","").replace(".","").replace(";","");
              if (isDamageType(piece)) {
                attr_entry.damage_vulnerabilities.push(piece);
              } else {
                if (attr_entry.extra_damage_vulnerabilities.length > 0) {
                  attr_entry.extra_damage_vulnerabilities += ", ";
                }
                attr_entry.extra_damage_vulnerabilities += piece;
              }
            }
          } else if (str.includes("Gear ")) {
            // Gear Shortbow, Shortsword
            // let sense_name = "";
            // for (let i = 1; i < pieces.length; ++i) {
            //   const piece = pieces[i].replace(",","");
            //   const val = +piece;
            //   if (!isNaN(val)) {// eslint-disable-next-line
            //     const sense = SENSES.find(o => o.name === sense_name);
            //     if (sense) {
            //       attr_entry.senses[sense.name] = val;
            //     }
            //   } else {
            //     sense_name = piece;
            //   }
            // }
            debugger;
          } else if (str.includes("Tools ")) {
            console.error("Tool Parser not implemented");
            debugger;
          } else if (str.includes("Languages ")) {
            // Languages None
            attr_entry.languages = str.substring(10);
          } else if (str.includes("CR ")) {
            // CR 1/2 (XP 100; PB +2)
            const cr_str = pieces[1];
            let cr = +cr_str;
            if (isNaN(cr) && cr_str.includes("/")) {
              const cr_pieces = cr_str.split("/");
              if (cr_pieces.length === 2) {
                const numerator = +cr_pieces[0];
                const denominator = +cr_pieces[1];
                if (!isNaN(numerator) && !isNaN(denominator)) {
                  cr = numerator / denominator;
                }
              }
            }
            if (!isNaN(cr)) {
              new_obj.challenge_rating = cr;
              new_obj.min_challenge_rating = cr;
              new_obj.max_challenge_rating = cr;
            }
          } else if (str.trim() === "Traits") {
            section = "Traits";
          } else if (str.trim() === "Actions") {
            section = "Actions";
          } else if (str.trim() === "Bonus Actions") {
            section = "Bonus Actions";
          } else if (str.trim() === "Reactions") {
            section = "Reactions";
          }
        } else {
          const str = elem.textContent.replace("\n","").trim();
          if (str.trim() === "Traits") {
            section = "Traits";
          } else if (str.trim() === "Actions") {
            section = "Actions";
          } else if (str.trim() === "Bonus Actions") {
            section = "Bonus Actions";
          } else if (str.trim() === "Reactions") {
            section = "Reactions";
          }else {
            const spellcasting = this.parseStatBlockAction(new_obj, elem, section);
            if (spellcasting === 1) {
              section = "Spellcasting";
            } else if (spellcasting === -1) {
              section = "Actions";
              this.parseStatBlockAction(new_obj, elem, section);
            }
          }
        }
      } else if (elem.nodeName === "DIV") {
        if (elem.classList.contains("mon-stat-block__header")) {
          // debugger;
          for (let i = 0; i < elem.children.length; ++i) {
            const child = elem.children[i];
            if (child instanceof HTMLElement && child.textContent) {
              if (child.classList.contains("mon-stat-block__name")) {
                new_obj.name = child.textContent.replace("\n","").trim();
                if (new_obj.name.includes("Dragon")) {
                  if (new_obj.name.includes("Wyrmling")) {
                    new_obj.age_category = "Wyrmling";
                  } else if (new_obj.name.includes("Young")) {
                    new_obj.age_category = "Young";
                  } else if (new_obj.name.includes("Adult")) {
                    new_obj.age_category = "Adult";
                  } else if (new_obj.name.includes("Ancient")) {
                    new_obj.age_category = "Ancient";
                  } else if (new_obj.name.toLocaleLowerCase().includes("greatwyrm")) {
                    new_obj.age_category = "Greatwyrm";
                  }
                }
              } else if (child.classList.contains("mon-stat-block__meta")) {
                // Medium Fiend (Devil), Lawful Evil
                const str = child.textContent.replace("\n","").trim();
                const pieces = str.split(",").map(o => o.trim());
                const main_pieces = pieces[0].split(" ");
                const size_str = main_pieces[0].toLocaleLowerCase();
                const creature_type_str = main_pieces[1];
                const entry = new_obj.table.entries[0];
                entry.data["Size"] = SIZE_TO_NUM_MAP[common_util.capitalize_first(size_str)];
                if (CREATURE_TYPES.includes(creature_type_str)) {
                  new_obj.creature_type = creature_type_str as CreatureType;
                }
                new_obj.subtypes = [];
                if (main_pieces.length > 2) {
                  const subtype = main_pieces[2].replace("(","").replace(")","");
                  if (new_obj.creature_type === "Dragon") {
                    if (METALLIC_DRAGONS.includes(subtype)) {
                      new_obj.subtypes.push("Metallic");
                    } else if (CHROMATIC_DRAGONS.includes(subtype)) {
                      new_obj.subtypes.push("Chromatic");
                    } else if (GEM_DRAGONS.includes(subtype)) {
                      new_obj.subtypes.push("Gem");
                    }
                  }
                  new_obj.subtypes.push(subtype);
                }
                if (CREATURE_ALIGNMENTS.includes(pieces[1])) {
                  new_obj.alignment = pieces[1].trim();
                }
              }
            }
          }
        } else if (elem.classList.contains("mon-stat-block__attributes")) {
          const entry = new_obj.table.entries[0];
          for (let i = 0; i < elem.children.length; ++i) {
            const child = elem.children[i];
            if (child instanceof HTMLElement && child.textContent) {
              const label_elems = child.getElementsByClassName("mon-stat-block__attribute-label");
              const value_elems = child.getElementsByClassName("mon-stat-block__attribute-data-value");
              let label_obj = label_elems[0];
              let value_obj = value_elems[0];
              if (label_obj && value_obj) {
                if ((label_obj.textContent === "AC" || label_obj.textContent === "Armor Class") && value_obj.textContent) {
                  // <div class="mon-stat-block__attribute">
                  //     <span class="mon-stat-block__attribute-label">Armor Class</span>
                  //     <span class="mon-stat-block__attribute-value">
                  //         <span class="mon-stat-block__attribute-data-value">
                  //             17
                  //         </span>
                          
                  //             <span class="mon-stat-block__attribute-data-extra">
                  //                 (natural armor)
                  //             </span> 
                                  
                  //     </span>
                  // </div>
                  entry.data["Armor Class"] = +value_obj.textContent;
                  label_obj = label_elems[1];
                  value_obj = value_elems[1];
                  
                  if (label_obj && value_obj) {
                    if (label_obj.textContent === "Initiative" && value_obj.textContent) {
                      const pieces = value_obj.textContent.replace("\n","").trim().split(" ");
                      initiative_modifier = +pieces[0].replace("+","");
                    }
                  }
                } else if (label_obj.textContent === "HP" || label_obj.textContent === "Hit Points") {
                  // <div class="mon-stat-block__attribute">
                  //     <span class="mon-stat-block__attribute-label">Hit Points</span>
                  //     <span class="mon-stat-block__attribute-data">
                  //         <span class="mon-stat-block__attribute-data-value">
                  //             114
                  //         </span>
                  //         <span class="mon-stat-block__attribute-data-extra">
                  //             (12d8 + 60)
                  //         </span>      
                  //     </span>
                  // </div>
                  const extra = child.getElementsByClassName("mon-stat-block__attribute-data-extra")[0];
                  if (extra && extra.textContent) {
                    // (21d8 + 84)
                    let pieces = extra.textContent.replace("\n","").trim().split(" ");
                    pieces = pieces[0].replace("(", "").split("d");
                    const count = +pieces[0];
                    // const size = +pieces[1];
                    entry.data["Hit Dice Count"] = count;
                  }
                } else if (label_obj.textContent === "Speed" && value_obj.textContent) {
                  // 30 ft., fly 60 ft.
                  const pieces = value_obj.textContent.split(",");
                  for (let i = 0; i < pieces.length; ++i) {
                    const sub_pieces = pieces[i].replace("\n","").trim().split(" ");
                    if (sub_pieces.includes("fly")) {
                      attr_entry.speeds.fly = +sub_pieces[1];
                    } else if (sub_pieces.includes("swim")) {
                      attr_entry.speeds.swim = +sub_pieces[1];
                    } else if (sub_pieces.includes("climb")) {
                      attr_entry.speeds.climb = +sub_pieces[1];
                    } else if (sub_pieces.includes("burrow")) {
                      attr_entry.speeds.burrow = +sub_pieces[1];
                    } else {
                      attr_entry.speeds.walk = +sub_pieces[0];
                    }
                  }
                }
              }
            }
          }
        } else if (elem.classList.contains("mon-stat-block__stat-block")) {
          // <div class="mon-stat-block__stat-block">
          //     <div class="mon-stat-block__separator">
          //         <img class="mon-stat-block__separator-img" alt="" src="https://www.dndbeyond.com/file-attachments/0/579/stat-block-header-bar.svg">
          //     </div>
          //     <div class="ability-block">
          //         <div class="ability-block__stat ability-block__stat--str">
          //             <div class="ability-block__heading">STR</div>
          //             <div class="ability-block__data">
          //                 <span class="ability-block__score">20</span>
          //                 <span class="ability-block__modifier">(+5)</span>
          //             </div>
          //         </div>
          //         <div class="ability-block__stat ability-block__stat--dex">
          //             <div class="ability-block__heading">DEX</div>
          //             <div class="ability-block__data">
          //                 <span class="ability-block__score">13</span>
          //                 <span class="ability-block__modifier">(+1)</span>
          //             </div>
          //         </div>
          //         <div class="ability-block__stat ability-block__stat--con">
          //             <div class="ability-block__heading">CON</div>
          //             <div class="ability-block__data">
          //                 <span class="ability-block__score">20</span>
          //                 <span class="ability-block__modifier">(+5)</span>
          //             </div>
          //         </div>
          //         <div class="ability-block__stat ability-block__stat--int">
          //             <div class="ability-block__heading">INT</div>
          //             <div class="ability-block__data">
          //                 <span class="ability-block__score">13</span>
          //                 <span class="ability-block__modifier">(+1)</span>
          //             </div>
          //         </div>
          //         <div class="ability-block__stat ability-block__stat--wis">
          //             <div class="ability-block__heading">WIS</div>
          //             <div class="ability-block__data">
          //                 <span class="ability-block__score">10</span>
          //                 <span class="ability-block__modifier">(+0)</span>
          //             </div>
          //         </div>
          //         <div class="ability-block__stat ability-block__stat--cha">
          //             <div class="ability-block__heading">CHA</div>
          //             <div class="ability-block__data">
          //                 <span class="ability-block__score">17</span>
          //                 <span class="ability-block__modifier">(+3)</span>
          //             </div>
          //         </div>
          //     </div>
          //     <div class="mon-stat-block__separator">
          //         <img class="mon-stat-block__separator-img" alt="" src="https://www.dndbeyond.com/file-attachments/0/579/stat-block-header-bar.svg">
          //     </div>
          // </div>
          const stat_divs = elem.getElementsByClassName("ability-block__stat");
          const entry = new_obj.table.entries[0];
          for (let i = 0; i < stat_divs.length; ++i) {
            const child = stat_divs[i];
            // console.log(table);
            if (child instanceof HTMLDivElement) {
              const label_elems = child.getElementsByClassName("ability-block__heading");
              const value_elems = child.getElementsByClassName("ability-block__score");
              const label_obj = label_elems[0];
              const value_obj = value_elems[0];
              if (label_obj instanceof HTMLElement && value_obj instanceof HTMLElement && 
                label_obj.textContent && value_obj.textContent) {
                const stat_name = label_obj.textContent.replace("\n","").trim();
                const stat_val = value_obj.textContent.replace("\n","").trim();
                if (ABILITY_SCORES.includes(stat_name)) {
                  entry.data[stat_name] = +stat_val;
                }
              }
            }
          }
        } else if (elem.classList.contains("mon-stat-block__tidbits")) {
          // <div class="mon-stat-block__tidbits">
          //         <div class="mon-stat-block__tidbit">
          //             <span class="mon-stat-block__tidbit-label">Skills</span>
          //             <span class="mon-stat-block__tidbit-data">
          //                 <a class="tooltip-hover skill-tooltip" href="/sources/dnd/free-rules/playing-the-game#Skills" data-tooltip-href="//www.dndbeyond.com/skills/14-tooltip?disable-webm=1&amp;disable-webm=1">Perception</a> +6, <a class="tooltip-hover skill-tooltip" href="/sources/dnd/free-rules/playing-the-game#Skills" data-tooltip-href="//www.dndbeyond.com/skills/19-tooltip?disable-webm=1&amp;disable-webm=1">Persuasion</a> +8
          //             </span>
          //         </div>
          //         <div class="mon-stat-block__tidbit">
          //             <span class="mon-stat-block__tidbit-label">Resistances</span>
          //             <span class="mon-stat-block__tidbit-data">
          //                 Cold
          //             </span>
          //         </div>
          //         <div class="mon-stat-block__tidbit">
          //             <span class="mon-stat-block__tidbit-label">Immunities</span>
          //             <span class="mon-stat-block__tidbit-data">
          //                 Fire, Poison;&nbsp;<a class="tooltip-hover condition-tooltip" href="/sources/dnd/free-rules/rules-glossary#PoisonedCondition" data-tooltip-href="//www.dndbeyond.com/conditions/11-tooltip?disable-webm=1&amp;disable-webm=1">Poisoned</a>
          //             </span>
          //         </div>
          //         <div class="mon-stat-block__tidbit">
          //             <span class="mon-stat-block__tidbit-label">Senses</span>
          //             <span class="mon-stat-block__tidbit-data">
          //                 <a class="tooltip-hover sense-tooltip" href="/sources/dnd/free-rules/rules-glossary#Truesight" data-tooltip-href="//www.dndbeyond.com/senses/4-tooltip?disable-webm=1&amp;disable-webm=1">Truesight</a> 120 ft.,  Passive Perception 16
          //             </span>
          //         </div>
          //         <div class="mon-stat-block__tidbit">
          //             <span class="mon-stat-block__tidbit-label">Languages</span>
          //             <span class="mon-stat-block__tidbit-data">
          //                 Infernal, Telepathy 120 ft.
          //             </span>
          //         </div>
          //     <div class="mon-stat-block__tidbit-container">
          //         <div class="mon-stat-block__tidbit">
          //             <span class="mon-stat-block__tidbit-label">CR</span>
          //             <span class="mon-stat-block__tidbit-data">
          //                 12 (XP 8,400; PB +4)
          //             </span>
          //         </div>
          //     </div>
          // </div>
          let skills_str = "";
          for (let i = 0; i < elem.children.length; ++i) {
            const child = elem.children[i];
            if (child instanceof HTMLElement && child.textContent) {
              const label_elems = child.getElementsByClassName("mon-stat-block__tidbit-label");
              const value_elems = child.getElementsByClassName("mon-stat-block__tidbit-data");
              const label_obj = label_elems[0];
              const value_obj = value_elems[0];
              if (label_obj && value_obj) {
                if (label_obj.textContent === "Saving Throws" && value_obj.textContent) {
                  const pieces = value_obj.textContent.split(",").map(o => o.trim());
                  for (const piece of pieces) {
                    const save_pieces = piece.replace("\n","").trim().split(" ");
                    const stat_name = save_pieces[0];
                    attr_entry.save_profs.push(stat_name);
                  }
                } else if (label_obj.textContent === "Skills" && value_obj.textContent) {
                  skills_str = value_obj.textContent; // Save for later because we need to get the PB first, which is based on CR, which is at the end of tidbits
                } else if (label_obj.textContent === "Resistances" && value_obj.textContent) {
                  const pieces = value_obj.textContent.split(",").map(o => o.trim());
                  for (const piece of pieces) {
                    if (isDamageType(piece)) {
                      attr_entry.damage_resistances.push(piece);
                    } else {
                      console.error("Something weird is happening with its resistances");
                      debugger;
                    }
                  }
                } else if (label_obj.textContent === "Immunities" && value_obj.textContent) {
                  // Immunities is complicated because it has both the damage immunities and the condition immunities
                  if (value_obj.textContent.includes(";")) {
                    const main_pieces = value_obj.textContent.split(";");
                    // Damage Immunities
                    let pieces = main_pieces[0].split(",").map(o => o.trim());
                    for (const piece of pieces) {
                      if (isDamageType(piece)) {
                        attr_entry.damage_immunities.push(piece);
                      } else {
                        console.error("Something weird is happening with its immunities");
                        debugger;
                      }
                    }
                    // Condition Immunities
                    pieces = main_pieces[1].split(",").map(o => o.trim());
                    for (const piece of pieces) {
                      const cond = this.conditions.find(o => o.name === piece);
                      if (cond instanceof Condition) {
                        attr_entry.condition_immunities.push(piece);
                      } else {
                        console.error("Something weird is happening with its immunities");
                        debugger;
                      }
                    }
                  } else {
                    // It could be that it only has one or the other.
                    const pieces = value_obj.textContent.split(",").map(o => o.trim());
                    for (const piece of pieces) {
                      if (isDamageType(piece)) {
                        attr_entry.damage_immunities.push(piece);
                      } else {
                        const cond = this.conditions.find(o => o.name === piece);
                        if (cond instanceof Condition) {
                          attr_entry.condition_immunities.push(piece);
                        } else {
                          console.error("Something weird is happening with its immunities");
                          debugger;
                        }
                      }
                    }
                  }
                } else if (label_obj.textContent === "Vulnerabilities" && value_obj.textContent) {
                  const pieces = value_obj.textContent.split(",").map(o => o.trim());
                  for (const piece of pieces) {
                    if (isDamageType(piece)) {
                      attr_entry.damage_vulnerabilities.push(piece);
                    } else {
                      console.error("Something weird is happening with its vulnerabilities");
                      debugger;
                    }
                  }
                } else if (label_obj.textContent === "Senses" && value_obj.textContent) {
                  const pieces = value_obj.textContent.split(",").map(o => o.trim());
                  // debugger;
                  for (const piece of pieces) {
                    if (!piece.includes("Passive")) {
                      const sense_pieces = piece.replace("\n","").trim().split(" ");
                      const sense_name = (sense_pieces.length === 4 ? `${sense_pieces[0]} ${sense_pieces[1]}` : sense_pieces[0]).toLocaleLowerCase();
                      let sense = SENSES.find(o => o.name.toLocaleLowerCase() === sense_name);
                      if (!sense) {
                        sense = SENSES.find(o => o.name.toLocaleLowerCase().replace(" ","") === sense_name.replace(" ",""));
                      }
                      if (sense) {
                        attr_entry.senses[sense.name] = +sense_pieces[1]; // It uses sense_name because of an error that made it when I switched to id it screwed something up. Haven't had time to figure it out.
                      } else {
                        console.error("Something weird is happening with its senses");
                        debugger;
                      }
                    }
                  }
                } else if (label_obj.textContent === "Languages" && value_obj.textContent) {
                  attr_entry.languages = value_obj.textContent.replace("\n","").trim();
                } else if ((label_obj.textContent === "CR" || label_obj.textContent === "Challenge") && value_obj.textContent) {
                  debugger;
                  const pieces = value_obj.textContent.replace("\n","").trim().split(" ");
                  let cr = +pieces[0];
                  if (isNaN(cr) && pieces[0].includes("/")) {
                    const pieces2 = pieces[0].split("/");
                    const numerator = +pieces2[0];
                    const denominator = +pieces2[1];
                    if (!isNaN(numerator) && !isNaN(denominator)) {
                      cr = numerator / denominator;
                    }
                  } 
                  attr_entry.level = new_obj.challenge_rating = new_obj.min_challenge_rating = new_obj.max_challenge_rating = cr;
                } else if (label_obj.textContent === "Condition Immunities" && value_obj.textContent) {
                  // debugger;
                  const pieces = value_obj.textContent.replace("\n","").split(",").map(o => o.trim());
                  for (const piece of pieces) {
                    const cond = this.conditions.find(o => o.name === piece);
                    if (cond instanceof Condition) {
                      attr_entry.condition_immunities.push(piece);
                    } else {
                      console.error("Something weird is happening with its immunities");
                      debugger;
                    }
                  }
                } else {
                  console.error("Something weird is happening with its tidbits");
                  debugger;
                }
              }
            }
          }
          if (skills_str.length > 0) {
            // debugger;
            // Now we have the PB, so we can figure out proficiency level
            const entry = new_obj.table.entries[0];
            const pb = new_obj.proficiency_modifier;
            const pieces = skills_str.split(",").map(o => o.trim());
            for (const piece of pieces) {
              const skill_pieces = piece.replace("\n","").trim().split(" ");
              const skill_name = skill_pieces[0];
              const skill = SKILLS.find(o => o.name === skill_name);
              if (skill) {
                const stat = entry.data[skill.use_ability_score];
                if (stat !== undefined) {
                  let mod = Math.floor((stat - 10) / 2);
                  let bonus = +skill_pieces[1];
                  bonus -= mod;
                  let prof_level = bonus / pb; // Usually 1, occasionally 2, rarely 0.5
                  if (![1,2,0.5].includes(prof_level) && mod < 0) {
                    // It could be that they're using 0 for the mod.
                    // That's cheating, but whatevs.
                    mod = 0;
                    bonus = +skill_pieces[1];
                    bonus -= mod;
                    prof_level = bonus / pb;
                  }
                  if (![1,2,0.5].includes(prof_level)) {
                    console.error("Something weird is happening with its skills");
                    debugger;
                  }
                  attr_entry.skill_profs[skill._id] = prof_level;
                }
              }
            }
          }
        } else if (elem.classList.contains("mon-stat-block__description-blocks")) {
          // <div class="mon-stat-block__description-block">
          //     <div class="mon-stat-block__description-block-heading">Actions</div>
          //     <div class="mon-stat-block__description-block-content">
          //         <p><em><strong>Multiattack.</strong></em> Endelyn makes two Puppeteer's Lash attacks</p>
          //         <p><em><strong>Puppeteer's Lash. </strong>Melee or Ranged Spell Attack:</em> <span data-dicenotation="1d20+6"
          //                 data-rolltype="to hit" data-rollaction="Puppeteer's Lash">+6</span> to hit, reach 5 ft. or range 60 ft.,
          //             one creature. <em>Hit:</em> 17 <span data-dicenotation="4d6+3" data-rolltype="damage"
          //                 data-rollaction="Puppeteer's Lash" data-rolldamagetype="psychic">(4d6 + 3)</span> psychic damage, and if
          //             the target is Large or smaller, Endelyn telekinetically moves it up to 10 feet in any direction
          //             horizontally.</p>
          //         <p><em><strong>Spellcasting.</strong></em> Endelyn casts one of the following spells, requiring no material
          //             components and using Charisma as the spellcasting ability (spell save DC 14):</p>
          //         <p>At will: <a class="tooltip-hover spell-tooltip" href="/spells/2065-detect-magic"
          //                 data-tooltip-href="//www.dndbeyond.com/spells/2065-tooltip?disable-webm=1&amp;disable-webm=1">detect
          //                 magic</a>, <a class="tooltip-hover spell-tooltip" href="/spells/2173-mage-hand"
          //                 data-tooltip-href="//www.dndbeyond.com/spells/2173-tooltip?disable-webm=1&amp;disable-webm=1">mage
          //                 hand</a></p>
          //         <p>2/day each: <a class="tooltip-hover spell-tooltip" href="/spells/2007-augury"
          //                 data-tooltip-href="//www.dndbeyond.com/spells/2007-tooltip?disable-webm=1&amp;disable-webm=1">augury</a>,
          //             <a class="tooltip-hover spell-tooltip" href="/spells/2209-polymorph"
          //                 data-tooltip-href="//www.dndbeyond.com/spells/2209-tooltip?disable-webm=1&amp;disable-webm=1">polymorph</a>
          //         </p>
          //         <p>1/day: <a class="tooltip-hover spell-tooltip" href="/spells/2206-plane-shift"
          //                 data-tooltip-href="//www.dndbeyond.com/spells/2206-tooltip?disable-webm=1&amp;disable-webm=1">plane
          //                 shift</a> (self only)</p>
          //     </div>
          // </div>
          // debugger;
          for (let i = 0; i < elem.children.length; ++i) {
            const child = elem.children[i];
            if (child instanceof HTMLElement && child.textContent) {
              const heading = child.children[0];
              const content = child.children[1];
              if (heading instanceof HTMLElement && 
                heading.classList.contains("mon-stat-block__description-block-heading") &&
                heading.textContent &&
                content instanceof HTMLElement && 
                content.classList.contains("mon-stat-block__description-block-content")
              ) {
                const action_type = heading.textContent;
                let creature_fb_type: "Actions" | "Legendary Actions" | "Traits" | "Lair Actions" | "Spellcasting" | "" = "";
                if (action_type === "Actions" || action_type === "Bonus Actions" || action_type === "Reactions") {
                  creature_fb_type = "Actions";
                } else if (action_type === "Traits" || action_type === "Legendary Actions") {
                  creature_fb_type = action_type;
                }
                if (creature_fb_type !== "") {
                  for (let j = 0; j < content.children.length; ++j) {
                    const child = content.children[j];
                    if (child instanceof HTMLElement) {
                      const spellcasting = this.parseStatBlockAction(new_obj, child, creature_fb_type);
                      if (spellcasting === 1) {
                        creature_fb_type = "Spellcasting";
                      } else if (spellcasting === -1) {
                        creature_fb_type = "Actions";
                        this.parseStatBlockAction(new_obj, child, section);
                      }
                    }
                  }
                } else {
                  console.error("Something weird is happening with its blocks");
                  debugger;
                }
              }
            }
          }
        } else {
          // // Ability Scores and Save Profs
          // // <div class="stats" data-content-chunk-id="8047d831-0a39-4542-a725-609af500ab45">
          // //   <div class="table-overflow-wrapper">
          // //     <table class="physical abilities-saves" data-content-chunk-id="ede93d4e-46de-4a2f-a37d-274e628327aa">
          // //       <thead><tr><th></th><th></th><th>Mod</th><th>Save</th></tr></thead>
          // //       <tbody>
          // //         <tr><th>STR</th><td>15</td><td>+2</td><td>+2</td></tr>
          // //         <tr><th>DEX</th><td>12</td><td>+1</td><td>+1</td></tr>
          // //         <tr><th>CON</th><td>14</td><td>+2</td><td>+2</td></tr>
          // //       </tbody>
          // //     </table>
          // //   </div>
          // //   <div class="table-overflow-wrapper">
          // //     <table class="mental abilities-saves" data-content-chunk-id="fe51ce5e-044e-4423-bc98-ecbf82a573f3">
          // //       <thead><tr><th></th><th></th><th>Mod</th><th>Save</th></tr></thead>
          // //       <tbody>
          // //         <tr><th>INT</th><td>2</td><td>–4</td><td>–4</td></tr>
          // //         <tr><th>WIS</th><td>12</td><td>+1</td><td>+1</td></tr>
          // //         <tr><th>CHA</th><td>7</td><td>–2</td><td>−2</td></tr>
          // //       </tbody>
          // //     </table>
          // //   </div>
          // // </div>
          // const tables = elem.getElementsByTagName("table");
          // for (let table_num = 0; table_num < tables.length; ++table_num) {
          //   const table = tables[table_num];
          //   // console.log(table);
          //   if (table instanceof HTMLTableElement) {
          //     for (let row_num = 0; row_num < table.rows.length; ++row_num) {
          //       const row = table.rows[row_num];
          //       // console.log(row);
          //       if (row.cells.length === 4) {
          //         if (row.cells[0].textContent && row.cells[1].textContent && row.cells[2].textContent && row.cells[3].textContent) {
          //           if (row.cells[2].textContent === "Mod") {
          //             // Ignore this row
          //           } else {
          //             const stat_str = row.cells[0].textContent.toLocaleUpperCase();
          //             const score_str = row.cells[1].textContent;
          //             // const mod_str = row.cells[2].textContent.replace("–","-");
          //             const save_str = row.cells[3].textContent.replace("–","-").replace("−","-");
          //             const score = +score_str;
          //             const save = +save_str;
          //             if (ABILITY_SCORES.includes(stat_str) && !isNaN(score) && !isNaN(save)) {
                        // table_entry.data[stat_str] = score;
          //               const mod = new_obj.getBasicModifier(stat_str);
          //               if (save > mod) {
          //                 new_obj.attributes_table.entries[0].save_profs.push(stat_str);
          //               }
          //             } else {
          //               debugger;
          //               console.error("Ability Scores Parse Error");
          //             }
          //           }
          //         }
          //       }
          //     }
          //   }
          // }
        }
      } 
    }

    // while (elem && !elem.classList.contains("stat-block") && !elem.classList.contains("mon-stat-block") && !elem.classList.contains("mon-stat-block")) {
    //   elem = elem.firstElementChild as (HTMLElement | null);
    // }
    if (elem) {
      for (let i = 0; i < elem.children.length; ++i) {
        parseElement(elem.children[i] as HTMLElement);
      }
    }
    new_obj.table.entries[0].level = new_obj.challenge_rating;
    // It's all parsed.  Now let's go through Init, AC, etc and convert to be based on stats.
    const DEX = new_obj.getBasicModifier("DEX");
    const PB = new_obj.get_proficiency_modifier(new_obj.challenge_rating);
    
    // console.log(new_obj.armor_class);
    if (true) {
      // debugger;
      // init is almost always equal to DEX.  When it's not it's usually DEX + PB
      new_obj.initiative_modifier = new UpgradableNumber();
      // new_obj.initiative_modifier.add_ability_mod_mult = 1;
      // new_obj.initiative_modifier.add_ability_scores.push("DEX");
      new_obj.initiative_modifier.add_stats_groups = [{ 
        true_id: uuidv4().toString(),
        add_ability_scores: ["DEX"],
        mod_or_score: "Mod",
        add_mult: 1,
        multiple_type: "And"
      }];
      if (initiative_modifier === (DEX + PB)) {
        new_obj.initiative_modifier.add_prof_bonus_mult = 1;
      }
    }
    return new_obj;
  }
  parseStatBlockAction = (new_obj: Creature, elem: HTMLElement, action_type: string) => {
    const label_obj = elem.children[0];
    // if (action_type === "Legendary Actions") {
    //   debugger;
    // }
    // debugger;
    // const content_obj = elem.children[1];
    if (label_obj && label_obj.textContent) {
      let heading_str = label_obj.textContent.trim();
      let name = heading_str;
      let sub_name = "";
      if (name.includes(".")) {
        const pieces = name.split(".");
        name = pieces[0].trim();
        sub_name = pieces[1].trim();
      }
      if (action_type === "Spellcasting") {
        if (elem.textContent) {
          // Form 1:
          // <p>Cantrips (at will): <a class="tooltip-hover spell-tooltip" href="/spells/mage-hand" data-tooltip-href="//www.dndbeyond.com/spells/2173-tooltip?disable-webm=1">mage hand</a>, <a class="tooltip-hover spell-tooltip" href="/spells/ray-of-frost" data-tooltip-href="//www.dndbeyond.com/spells/2226-tooltip?disable-webm=1">ray of frost</a>, <a class="tooltip-hover spell-tooltip" href="/spells/shocking-grasp" data-tooltip-href="//www.dndbeyond.com/spells/2250-tooltip?disable-webm=1">shocking grasp</a></p>
          // <p>4th level (3 slots): <a class="tooltip-hover spell-tooltip" href="/spells/blight" data-tooltip-href="//www.dndbeyond.com/spells/2017-tooltip?disable-webm=1">blight</a>, <a class="tooltip-hover spell-tooltip" href="/spells/ice-storm" data-tooltip-href="//www.dndbeyond.com/spells/2151-tooltip?disable-webm=1">ice storm</a>, <a class="tooltip-hover spell-tooltip" href="/spells/phantasmal-killer" data-tooltip-href="//www.dndbeyond.com/spells/2202-tooltip?disable-webm=1">phantasmal killer</a></p>
          // Form 2:
          // <p>At will: <a class="tooltip-hover spell-tooltip" href="/spells/2065-detect-magic" data-tooltip-href="//www.dndbeyond.com/spells/2065-tooltip?disable-webm=1&amp;disable-webm=1">detect magic</a>, <a class="tooltip-hover spell-tooltip" href="/spells/2173-mage-hand" data-tooltip-href="//www.dndbeyond.com/spells/2173-tooltip?disable-webm=1&amp;disable-webm=1">mage hand</a></p>
          // <p>2/day each: <a class="tooltip-hover spell-tooltip" href="/spells/2007-augury" data-tooltip-href="//www.dndbeyond.com/spells/2007-tooltip?disable-webm=1&amp;disable-webm=1">augury</a>, <a class="tooltip-hover spell-tooltip" href="/spells/2209-polymorph" data-tooltip-href="//www.dndbeyond.com/spells/2209-tooltip?disable-webm=1&amp;disable-webm=1">polymorph</a></p>
          // <p>1/day: <a class="tooltip-hover spell-tooltip" href="/spells/2206-plane-shift" data-tooltip-href="//www.dndbeyond.com/spells/2206-tooltip?disable-webm=1&amp;disable-webm=1">plane shift</a> (self only)</p>
          // Identify the level, the slots, and then the spells
          try {
            // debugger;
            let entry = new_obj.spellcasting_table.getEntry(new_obj.min_challenge_rating);
            if (!entry) {
              entry = new SpellcastingEntry();
              entry.level = new_obj.min_challenge_rating;
              new_obj.spellcasting_table.entries.push(entry);
            }
            let pieces = elem.textContent.split(":");
            if (pieces.length > 0 && (pieces[0].toLocaleLowerCase().includes("cantrip") || pieces[0].toLocaleLowerCase().includes(" level"))) {
              // Form 1
              pieces = elem.textContent.split("(");
              const level_str = pieces[0].trim();
              pieces = pieces[1].split(")");
              const slots_str = pieces[0].trim();
              let spells_str = pieces[1].trim();
              if (spells_str.startsWith(":")) {
                spells_str = spells_str.substring(1).trim();
              }
              pieces = spells_str.split(",");
              const spell_ids: string[] = [];
              for (const piece of pieces) {
                const spell = this.smart_hash.spells.filter(o => o.name.trim().toLocaleLowerCase() === piece.trim().toLocaleLowerCase())[0];
                if (spell) {
                  spell_ids.push(spell._id);
                } else {
                  console.error("Spell Find Error");
                  console.error(piece);
                  console.error(this.smart_hash.spells);
                  debugger;
                }
              }
              let level = -1;
              if (level_str.toLocaleLowerCase().includes("cantrip")) {
                level = 0;
              } else {
                level = +level_str[0];
              }
              if (level > -1) {
                entry.spells = spell_ids.map(spell_id => { 
                  const spell = this.smart_hash.spells.find(o => o._id === spell_id);
                  let return_me: { spell_level: number, spell_id: string, cast_type: "Slot" | "At Will" | "X/Day" | "Ritual", x: number } = { spell_level: -1, spell_id, cast_type: "Slot", x: 0 };
                  if (spell instanceof Spell) {
                    return_me = { spell_level: spell.level, spell_id, cast_type: "Slot", x: 0 };
                  } else {
                    console.error("Should never come here.");
                  }
                  return return_me;
                }).filter(o => o.spell_level > -1);
                if (slots_str.toLocaleLowerCase().includes("at will")) {
                  // new_obj.slots[level] = -1;
                } else {
                  pieces = slots_str.split(" ");
                  const slots = +pieces[0];
                  console.log(slots);
                  // TODO: I haven't implemented this since changing creatures to use resources_table instead.
                  // const new_entry = new TableEntry();
                  // new_entry.level = level;
                  // new_entry.data
                  // new_obj.slots[level] = slots;
                  // if (new_obj.old_resources[new_obj.challenge_rating] === undefined) {
                  //   new_obj.old_resources[new_obj.challenge_rating] = [];
                  // }
                  // new_obj.old_resources[new_obj.challenge_rating].push({ 
                  //   resource_id: `Slot: ${level}`, 
                  //   count: slots,
                  //   size: 1
                  // });
                }
              }
            } else {
              // Form 2
              let cast_type: "Slot" | "At Will" | "X/Day" | "Ritual" = "At Will";
              let x = 0;
              if (pieces[0].toLocaleLowerCase() === "at will") {

              } else if (pieces[0].toLocaleLowerCase().includes("/day")) {
                cast_type = "X/Day";
                const sub_pieces = pieces[0].split("/day");
                x = +sub_pieces[0];
                if (isNaN(x)) {
                  x = 1;
                }
              } else {
                return -1; // It was spellcasting, but now it's not.
              }
              const spell_tags = elem.getElementsByClassName("spell-tooltip");
              const spells = this.smart_hash.spell as Spell[];
              for (let i = 0; i < spell_tags.length; ++i) {
                const spell_tag = spell_tags[i];
                if (spell_tag instanceof HTMLAnchorElement && spell_tag.textContent) {
                  const spell_name = spell_tag.textContent.replace("\n","").trim();
                  const spell = spells.find(o => o.name.toLocaleLowerCase() === spell_name.toLocaleLowerCase() && o.isDominant());
                  if (spell) {
                    const new_spell: iCreatureSpell = {
                      cast_type,
                      x, 
                      spell_id: spell._id,
                      spell_level: spell.level
                    };
                    entry.spells.push(new_spell);
                  }
                }
              }
            }
            entry.description += "\n" + elem.textContent.replace("\n","").trim();
          } catch (ex) {
            console.error("Spellcasting Parse Error");
            console.error(ex);
            debugger;
          }
        }
      } else if (name.includes("Legendary Resistance")) {
        let legendary_resistances_str = name.split("(")[1];
        if (legendary_resistances_str) {
          let lair_str = "";
          if (legendary_resistances_str.includes(" or ")) {
            lair_str = legendary_resistances_str.split(" or ")[1];
            lair_str = lair_str.substring(0, lair_str.indexOf("/"));
            if (new_obj.legendaries_table.entries.length === 0) {
              const new_entry = new Legendaries();
              new_entry.level = new_obj.min_challenge_rating;
              new_entry.res_lair = +lair_str;
              new_entry.actions = 0;
              new_obj.legendaries_table.entries.push(new_entry);
            } else {
              const entry = new_obj.legendaries_table.entries[0];
              entry.res_lair = +lair_str;
            }
          }
          legendary_resistances_str = legendary_resistances_str.substring(0, legendary_resistances_str.indexOf("/"));
          if (new_obj.legendaries_table.entries.length === 0) {
            const new_entry = new Legendaries();
            new_entry.level = new_obj.min_challenge_rating;
            new_entry.resistances = +legendary_resistances_str;
            new_entry.actions = 0;
            new_obj.legendaries_table.entries.push(new_entry);
          } else {
            const entry = new_obj.legendaries_table.entries[0];
            entry.resistances = +legendary_resistances_str;
          }
        }
      } else if (name.includes("Spellcasting")) {
        // debugger;
        // Spellcasting. Acererak is a 20th-level spellcaster. 
        // His spellcasting ability is Intelligence 
        // (spell save DC 23, +15 to hit with spell attacks). 
        // Acererak has the following wizard spells prepared:

        // Innate Spellcasting (1/Day). The mephit can innately cast dancing lights, 
        // requiring no material components. Its innate spellcasting ability is Charisma.
        if (elem.textContent) {
          let entry = new_obj.spellcasting_table.getEntry(new_obj.min_challenge_rating);
          if (!entry) {
            entry = new SpellcastingEntry();
            entry.level = new_obj.min_challenge_rating;
            new_obj.spellcasting_table.entries.push(entry);
          }
          entry.description = common_util.replaceAll(elem.textContent.replace(name, "").trim(), "\n", "");
          if (entry.description.startsWith(".")) {
            entry.description = entry.description.substring(1).trim();
          }
          if (entry.description.toLocaleLowerCase().includes("spellcasting ability is ")) {
            let spellcasting_ability = common_util.capitalize_first(
              entry.description.substring(entry.description.toLocaleLowerCase().indexOf("spellcasting ability is ") + 24));
            spellcasting_ability = spellcasting_ability.substring(0, 3).toUpperCase();
            new_obj.spellcasting_ability = spellcasting_ability as AbilityScoreType;
          } else if (entry.description.toLocaleLowerCase().includes("as the spellcasting")) {
            // The aarakocra casts one of the following spells, requiring no Material components 
            // and using Wisdom as the spellcasting ability (spell save DC 13):
            let index = entry.description.toLocaleLowerCase().indexOf("as the spellcasting") - 2;
            while (index > 0 && entry.description[index] !== " ") {
              index--;
            }
            index++;
            const spellcasting_ability = entry.description.substring(index, index + 3).toLocaleUpperCase();
            if (ABILITY_SCORES.includes(spellcasting_ability)) {
              new_obj.spellcasting_ability = spellcasting_ability as AbilityScoreType;
            }
          }
          if (entry.description.toLocaleLowerCase().includes(" dc ")) {
            if (new_obj.dc.isBlank()) {
              let dc_str = entry.description.substring(entry.description.toLocaleLowerCase().indexOf(" dc ") + 4).trim();
              if (dc_str.includes(" ")) {
                dc_str = dc_str.substring(0, dc_str.indexOf(" "));
              }
              dc_str = dc_str.replace(",","").replace(".","").replace(")","").replace(":","").trim();
              const dc_num = +dc_str;
              new_obj.dc = new UpgradableNumber(dc_num);
            }
          }
          if (entry.description.toLocaleLowerCase().includes(" to hit ")) {
            let attack_str = entry.description.substring(0, entry.description.toLocaleLowerCase().indexOf(" to hit ")).trim();
            attack_str = attack_str.substring(attack_str.lastIndexOf("+")+1);
            new_obj.magic_attack = new UpgradableNumber(+attack_str);
          }
          if (new_obj.spellcasting_ability !== "None") {
            const stat = new_obj.getBasicModifier(new_obj.spellcasting_ability);
            const pb = new_obj.proficiency_modifier;

            if (new_obj.dc.base > 0) {
              new_obj.dc.base -= stat + pb;
            } else {
              new_obj.dc.base = 8;
            }
            new_obj.dc.add_prof_bonus_mult = 1;
            // new_obj.dc.add_ability_mod_mult = 1;
            // new_obj.dc.add_ability_scores.push(new_obj.spellcasting_ability);
            new_obj.dc.add_stats_groups = [{ 
              true_id: uuidv4().toString(),
              add_ability_scores: [new_obj.spellcasting_ability],
              mod_or_score: "Mod",
              add_mult: 1,
              multiple_type: "And"
            }];

            if (new_obj.magic_attack.base > 0) {
              new_obj.magic_attack.base -= stat + pb;
            }
            new_obj.magic_attack.add_prof_bonus_mult = 1;
            // new_obj.magic_attack.add_ability_mod_mult = 1;
            // new_obj.magic_attack.add_ability_scores.push(new_obj.spellcasting_ability);
            new_obj.magic_attack.add_stats_groups = [{ 
              true_id: uuidv4().toString(),
              add_ability_scores: [new_obj.spellcasting_ability],
              mod_or_score: "Mod",
              add_mult: 1,
              multiple_type: "And"
            }];
          }
        }
        return 1;
      } else {
        let content_str = common_util.cleanSpaces(elem.textContent ? elem.textContent.substring(name.length) : "");
        if (content_str.startsWith(".")) {
          content_str = content_str.substring(1);
        }
        content_str = content_str.replace(name, "").trim();
        content_str = common_util.replaceAll(content_str, "−", "-");
        content_str = common_util.replaceAll(content_str, "’", "'");
        // Going to need to look it up in floating_features.
        // If it doesn't exist then add it in
        // Look for matcing names and descriptions

        let ff: FloatingFeature | undefined = undefined; 
        if (!ff) {
          let desc_helper = common_util.replaceAll(common_util.replaceAll(content_str, ` ${new_obj.name} `, ` {cfs} `), ` ${new_obj.name}'`, ` {cfs}'`);
          if (desc_helper === content_str) {
            const desc_helper2 = common_util.replaceAll(desc_helper, "\n", "");
            if (new_obj.small_name.length === 0 && new_obj.name.includes(" ")) {
              // debugger;
              const words = new_obj.name.split(" ").map(o => o.toLocaleLowerCase());
              let small_name = "";
              for (let i = 0; i < words.length; ++i) {
                const word = words[i];
                if (desc_helper2.includes(` ${word} `)) {
                  if (small_name === "") {
                    small_name = word;
                  } else {
                    const prev_word = words[i - 1];
                    if (small_name.includes(prev_word) && desc_helper2.includes(` ${small_name} ${word} `)) {
                      small_name += ` ${word}`;
                    }
                  }
                }
              }
              new_obj.small_name = small_name;
            }
            if (new_obj.small_name.length > 0) {
              desc_helper = common_util.replaceAll(content_str, new_obj.small_name, `{cfs}`);
            }
          }
          const ff_obj: any = {
            name,
            description: desc_helper,
            feature_type: "Creature Ability"
          };
          if (heading_str.endsWith(".")) {
            heading_str = heading_str.substring(0, heading_str.length - 1);
          }
          if (heading_str === "Multiattack") {
            ff_obj.feature_type = "Minion Extra Attacks";
            // Usually says something along the lines of "makes X attacks";
            const sub = content_str.toLocaleLowerCase();
            
            const pieces = sub.split(" ");
            const num_strings = ["two","three","four","five","six","seven","eight","nine","ten","2","3","4","5","6","7","8","9","10"];
            let num_string = common_util.findOverlap(num_strings, pieces);
            if (num_string.length === 0) {
              // It's more complicated, so just make it 2
              num_string = "2";
            }
            if (num_string.length > 0) {
              switch(num_string) {
                case "two": ff_obj.num = 2; break;
                case "three": ff_obj.num = 3; break;
                case "four": ff_obj.num = 4; break;
                case "five": ff_obj.num = 5; break;
                case "six": ff_obj.num = 6; break;
                case "seven": ff_obj.num = 7; break;
                case "eight": ff_obj.num = 8; break;
                case "nine": ff_obj.num = 9; break;
                case "ten": ff_obj.num = 10; break;
                default: ff_obj.num = +num_string; break;
              }
              ff_obj.num -= 1;
              ff = new UpgradableNumberFeature(ff_obj);
            }
          } else {
            const ca = new CreatureAbility(ff_obj); // this.get_or_create_floating_feature(ff_obj);
            if (ca instanceof CreatureAbility) {
              // if (ca.name.includes("Trampling Charge")) {
              //   debugger;
              // }
              let using_ability: AbilityScoreType | "" = "";
              let using_ability_mod = -5;
              if (sub_name === "") {
                if (content_str.includes("Attack") && !content_str.includes("Opportunity Attacks")) {
                  sub_name = content_str.substring(0, content_str.indexOf("Attack") + 6);
                }
              }
              if (sub_name.includes("Attack")) { // Melee Weapon Attack:
                let sub = content_str.substring(content_str.indexOf("+")); // +12 to hit
                sub = sub.substring(0, sub.indexOf(" ")).replace("+","").replace(",","").trim();
                const sub_num = +sub;
                if (!isNaN(sub_num)) {
                  const ability_mod = sub_num - new_obj.proficiency_modifier;
                  if (sub_name.includes("Spell")) {
                    // It should be the new_obj.spellcasting_ability
                    if (new_obj.spellcasting_ability !== "None") {
                      const mod = new_obj.getBasicModifier(new_obj.spellcasting_ability);
                      if (mod === ability_mod) {
                        using_ability = new_obj.spellcasting_ability;
                        using_ability_mod = mod;
                      } else {
                        console.error("It should never come here.");
                      }
                    } else {
                      // This means that they have Actions that use Spell Attacks, but no actual spells.
                      // In that situation they probably use the same ability for all such actions, 
                      // so once it's identified, we will set it as their spellcasting ability.
                      // Monsters usually use CHA, so check that first. Next INT, then WIS
                      let mod = new_obj.getBasicModifier("CHA");
                      if (mod === ability_mod) {
                        using_ability = "CHA";
                      } else {
                        mod = new_obj.getBasicModifier("INT");
                        if (mod === ability_mod) {
                          using_ability = "INT";
                        } else {
                          mod = new_obj.getBasicModifier("WIS");
                          if (mod === ability_mod) {
                            using_ability = "WIS";
                          }
                        }
                      }
                      if (using_ability !== "") {
                        using_ability_mod = mod;
                        new_obj.spellcasting_ability = using_ability;
                        new_obj.dc.makeBasic();
                        new_obj.dc.base = 8;
                        new_obj.dc.add_prof_bonus_mult = 1;
                        // new_obj.dc.add_ability_mod_mult = 1;
                        // new_obj.dc.add_ability_scores.push(new_obj.spellcasting_ability);
                        new_obj.dc.add_stats_groups = [{ 
                          true_id: uuidv4().toString(),
                          add_ability_scores: [using_ability],
                          mod_or_score: "Mod",
                          add_mult: 1,
                          multiple_type: "And"
                        }];
                        new_obj.magic_attack.makeBasic();
                        new_obj.magic_attack.add_prof_bonus_mult = 1;
                        // new_obj.magic_attack.add_ability_mod_mult = 1;
                        // new_obj.magic_attack.add_ability_scores.push(new_obj.spellcasting_ability);
                        new_obj.magic_attack.add_stats_groups = [{ 
                          true_id: uuidv4().toString(),
                          add_ability_scores: [using_ability],
                          mod_or_score: "Mod",
                          add_mult: 1,
                          multiple_type: "And"
                        }];
                      }
                    }
                  } else if (sub_name.includes("Melee")) {
                    // Check STR first
                    let mod = new_obj.getBasicModifier("STR");
                    if (mod === ability_mod) {
                      using_ability = "STR";
                    } else {
                      mod = new_obj.getBasicModifier("DEX");
                      if (mod === ability_mod) {
                        using_ability = "DEX";
                      }
                    }
                    if (using_ability === "") {
                      if (new_obj.spellcasting_ability !== "None") {
                        mod = new_obj.getBasicModifier(new_obj.spellcasting_ability);
                        if (mod === ability_mod) {
                          using_ability = new_obj.spellcasting_ability;
                        } else {
                          console.error("It should never come here.");
                          console.error(mod);
                          console.error(ability_mod);
                          console.error(content_str);
                          console.error(new_obj.table.entries[0].clone());
                        }
                      }
                    }
                    if (using_ability === "") {
                      mod = new_obj.getBasicModifier("INT");
                      if (mod === ability_mod) {
                        using_ability = "INT";
                      } else {
                        mod = new_obj.getBasicModifier("WIS");
                        if (mod === ability_mod) {
                          using_ability = "WIS";
                        } else {
                          mod = new_obj.getBasicModifier("CHA");
                          if (mod === ability_mod) {
                            using_ability = "CHA";
                          }
                        }
                      }
                    }
                    if (using_ability !== "") {
                      using_ability_mod = mod;
                    }
                  } else {
                    // Check DEX first
                    let mod = new_obj.getBasicModifier("DEX");
                    if (mod === ability_mod) {
                      using_ability = "DEX";
                    } else {
                      mod = new_obj.getBasicModifier("STR");
                      if (mod === ability_mod) {
                        using_ability = "STR";
                      }
                    }
                    if (using_ability === "") {
                      if (new_obj.spellcasting_ability !== "None") {
                        mod = new_obj.getBasicModifier(new_obj.spellcasting_ability);
                        if (mod === ability_mod) {
                          using_ability = new_obj.spellcasting_ability;
                        }
                      }
                    }
                    if (using_ability === "") {
                      mod = new_obj.getBasicModifier("INT");
                      if (mod === ability_mod) {
                        using_ability = "INT";
                      } else {
                        mod = new_obj.getBasicModifier("WIS");
                        if (mod === ability_mod) {
                          using_ability = "WIS";
                        } else {
                          mod = new_obj.getBasicModifier("CHA");
                          if (mod === ability_mod) {
                            using_ability = "CHA";
                          } else {
                            mod = new_obj.getBasicModifier("CON");
                            if (mod === ability_mod) {
                              using_ability = "CON";
                            }
                          }
                        }
                      }
                    }
                    if (using_ability !== "") {
                      using_ability_mod = mod;
                    }
                  }
                  if (using_ability !== "") {
                    if (using_ability === new_obj.spellcasting_ability) {
                      ca.attack_bonus.add_spell_attack_mult = 1;
                      ca.description = ca.description.replace(`Attack: +${sub}`, "Attack: +{cfs.spell_attack}");
                      ca.description = ca.description.replace(`Attack Roll: +${sub}`, "Attack Roll: +{cfs.spell_attack}");
                    } else {
                      ca.attack_bonus.add_prof_bonus_mult = 1;
                      // ca.attack_bonus.add_ability_mod_mult = 1;
                      // ca.attack_bonus.add_ability_scores.push(using_ability);
                      ca.attack_bonus.add_stats_groups = [{ 
                        true_id: uuidv4().toString(),
                        add_ability_scores: [using_ability],
                        mod_or_score: "Mod",
                        add_mult: 1,
                        multiple_type: "And"
                      }];
                      ca.description = ca.description.replace(`Attack: +${sub}`, `Attack: {attack_string.${using_ability}}`);
                      ca.description = ca.description.replace(`Attack Roll: +${sub}`, `Attack Roll: {attack_string.${using_ability}}`);
                    }
                  } else {
                    console.error("It should never come here.");
                    console.error(using_ability_mod);
                    console.error(ability_mod);
                    console.error(content_str);
                    console.error(new_obj.table.entries[0].clone());
                    console.error(new_obj.name);
                  }
                  // ca.attack_bonus.base = +sub;
                }
              }
              if (content_str.includes("DC") && !content_str.includes("rapple")) {
                // New format: Strength Saving Throw: DC 12
                // Old format: DC 19 Dexterity saving throw
                // Needs to work with both
                // debugger;
                let dc = 0;
                let save_type = "";
                const pieces = content_str.split(" ").map(o => o.replace(",",""));
                let dc_index = -1;
                for (let i = 0; i < pieces.length; ++i) {
                  const piece = pieces[i];
                  if (piece === "DC") {
                    dc_index = i;
                    break;
                  }
                }
                const dc_str = pieces[dc_index + 1].replace(",", "").replace(".","").replace(")","").replace(":","");
                dc = +dc_str;
                if (isNaN(dc)) {
                  console.error("DC Parse Error");
                  // debugger;
                } else {
                  // Check New Method first
                  let type_maybe = pieces[dc_index - 3];
                  if (type_maybe.length > 3) {
                    type_maybe = type_maybe.substring(0, 3).toLocaleUpperCase();
                    if (ABILITY_SCORES.includes(type_maybe)) {
                      save_type = type_maybe;
                    }
                  }
                  if (save_type === "") {
                    // Didn't find it, so Check Old Method
                    type_maybe = pieces[dc_index + 2];
                    if (type_maybe) {
                      if (type_maybe.length > 3) {
                        type_maybe = type_maybe.substring(0, 3).toLocaleUpperCase();
                        if (ABILITY_SCORES.includes(type_maybe)) {
                          save_type = type_maybe;
                        }
                      }
                    } else {
                      let spell_index = pieces.indexOf("spell");
                      if (spell_index < 0) {
                        spell_index = pieces.indexOf("Spell");
                      }
                      let spell: ModelBase | undefined = undefined;
                      if (spell_index > -1) {
                        // Check the words before and after spell_index
                        let spell_name = "";
                        let use_index = spell_index - 1;
                        while (use_index > -1) {
                          spell_name = (`${pieces[use_index].toLocaleLowerCase()} ${spell_name}`).trim();
                          // console.log(spell_name);
                           
                          spell = this.smart_hash.spell.find(o => o.name.toLocaleLowerCase() === spell_name);
                          if (spell) {
                            break;
                          } else {
                            use_index--;
                          }
                        }
                        if (!spell) {
                          spell_name = "";
                          use_index = spell_index + 1;
                          while (use_index < pieces.length) {
                            spell_name = (`${spell_name} ${pieces[use_index].toLocaleLowerCase()}`).trim();
                             
                            spell = this.smart_hash.spell.find(o => o.name.toLocaleLowerCase() === spell_name);
                            if (spell) {
                              break;
                            } else {
                              use_index++;
                            }
                          }
                        }
                      }
                      if (spell instanceof Spell) {
                        const save_effect = spell.effects.find(o => o.attack_type === "Save");
                        if (save_effect) {
                          save_type = save_effect.saving_throw_ability_score;
                        } else {
                          console.error(pieces);
                          console.error(dc_index);
                          console.error(dc);
                          debugger;
                        }
                      } else {
                        spell_index = pieces.indexOf("casts");
                        if (spell_index < 0) {
                          spell_index = pieces.indexOf("cast");
                        }
                        if (spell_index > -1) {
                          // Check the words before and after spell_index
                          let spell_name = "";
                          let use_index = spell_index - 1;
                          while (use_index > -1) {
                            spell_name = (`${pieces[use_index].toLocaleLowerCase()} ${spell_name}`).trim();
                            // console.log(spell_name);
                             
                            spell = this.smart_hash.spell.find(o => o.name.toLocaleLowerCase() === spell_name);
                            if (spell) {
                              break;
                            } else {
                              use_index--;
                            }
                          }
                          if (!spell) {
                            spell_name = "";
                            use_index = spell_index + 1;
                            while (use_index < pieces.length) {
                              spell_name = (`${spell_name} ${pieces[use_index].toLocaleLowerCase()}`).trim();
                               
                              spell = this.smart_hash.spell.find(o => o.name.toLocaleLowerCase() === spell_name);
                              if (spell) {
                                break;
                              } else {
                                use_index++;
                              }
                            }
                          }
                        }
                        if (spell instanceof Spell) {
                          const save_effect = spell.effects.find(o => o.attack_type === "Save");
                          if (save_effect) {
                            save_type = save_effect.saving_throw_ability_score;
                          } else {
                            console.error(pieces);
                            console.error(dc_index);
                            console.error(dc);
                            // debugger;
                          }
                        } else {
                          console.error(pieces);
                          console.error(dc_index);
                          console.error(dc);
                          debugger;
                        }
                      }
                    }
                  }
                  // if (content_str.includes("Saving Throw: DC")) {

                  // } else {
                  //   let sub = content_str.substring(content_str.indexOf("DC") + 2);
                  //   sub = sub.substring(0, sub.indexOf("sav") - 1).trim(); // 19 Dexterity
                  //   const pieces = sub.split(" ");
                  //   dc = +pieces[0];
                  //   save_type = pieces[1];
                  // }
                  const ability_mod = dc - 8 - new_obj.proficiency_modifier;
                  // if (new_obj.spellcasting_ability !== "None") {
                  //   if (ability_mod === new_obj.getBasicModifier(new_obj.spellcasting_ability)) {
                  //     ca.dc.add_spell_save_dc_mult = 1;
                  //     using_ability = new_obj.spellcasting_ability;
                  //     using_ability_mod = ability_mod;
                  //     ca.description = ca.description.replace(`DC ${dc}`, "DC {cfs.spell_dc}");
                  //   }
                  // }
                  if (ca.name.includes("Breath")) {
                    // Breath Weapons the DC is generally based on CON.
                    if (common_util.modFromScore(new_obj.ability_scores.CON.base) === ability_mod) {
                      using_ability = "CON";
                    }
                  }
                  if (using_ability === "") {
                    // This order is the most likely.
                    if (common_util.modFromScore(new_obj.ability_scores.CHA.base) === ability_mod) {
                      using_ability = "CHA";
                    } else if (common_util.modFromScore(new_obj.ability_scores.STR.base) === ability_mod) {
                      using_ability = "STR";
                    } else if (common_util.modFromScore(new_obj.ability_scores.DEX.base) === ability_mod) {
                      using_ability = "DEX";
                    } else if (common_util.modFromScore(new_obj.ability_scores.WIS.base) === ability_mod) {
                      using_ability = "WIS";
                    } else if (common_util.modFromScore(new_obj.ability_scores.INT.base) === ability_mod) {
                      using_ability = "INT";
                    } else if (common_util.modFromScore(new_obj.ability_scores.CON.base) === ability_mod) {
                      using_ability = "CON";
                    }
                  }
                  if (using_ability === "") {
                    // Could be anything.  Just leave it as a base.
                    ca.dc.base = dc;
                  } else {
                    ca.dc.base = 8;
                    ca.dc.add_prof_bonus_mult = 1;
                    // ca.dc.add_ability_mod_mult = 1;
                    // ca.dc.add_ability_scores.push(using_ability);
                    ca.dc.add_stats_groups = [{ 
                      true_id: uuidv4().toString(),
                      add_ability_scores: [using_ability],
                      mod_or_score: "Mod",
                      add_mult: 1,
                      multiple_type: "And"
                    }];
                    ca.description = ca.description.replace(`DC ${dc}`, `{dc_string.${using_ability}}`);
                    using_ability_mod = ability_mod;
                  }
                  ca.saving_throw_ability_score = ABILITY_SCORE_FULL_TO_ABBREV_MAP[save_type];
                }
              }
              if (content_str.includes("reach")) {
                let sub = content_str.substring(content_str.indexOf("reach") + 5).trim();
                sub = sub.substring(0, sub.indexOf(" ")).trim();
                ca.range = sub;
              } else if (content_str.includes("range")) {
                let sub = content_str.substring(content_str.indexOf("range") + 5).trim();
                sub = sub.substring(0, sub.indexOf(" ")).trim();
                ca.range = sub;
              }
              let damage_str = content_str.toLocaleLowerCase();
              const lower_case = ca.description.toLowerCase();
              if (name.includes("Wing Attack")) {
                console.log(damage_str);
              }
              while (damage_str.includes("damage")) {
                let damage_sub = damage_str.substring(0, damage_str.indexOf("damage") - 1).trim();
                damage_str = damage_str.substring(damage_str.indexOf("damage") + 6);
                const words = damage_sub.split(" ");
                // console.log(words);
                const damage_type = common_util.capitalize_first(words[words.length - 1]);
                if (damage_sub.includes("(") && damage_sub.includes(")")) {
                  let start_index = damage_sub.indexOf("(");
                  start_index -= 2;
                  while (start_index >= 0 && damage_sub[start_index] !== " ") {
                    start_index--;
                  }
                  let replace_me = damage_sub.substring(start_index);
                  const end_index = replace_me.indexOf(")") + 1;
                  replace_me = replace_me.substring(0, end_index).trim();
                  damage_sub = damage_sub.substring(damage_sub.indexOf("("));
                  damage_sub = damage_sub.substring(0, damage_sub.indexOf(")"));
                  damage_sub = damage_sub.replace("(","").replace(")","");
                  let count = 0;
                  let size = 0;
                  let flat = 0;
                  if (damage_sub.includes("+")) {
                    const flat_str = damage_sub.substring(damage_sub.indexOf("+")).replace("+","");
                    damage_sub = damage_sub.substring(0, damage_sub.indexOf("+")).replace("+","");
                    flat = +flat_str;
                  } else if (damage_sub.includes("-")) {
                    const flat_str = damage_sub.substring(damage_sub.indexOf("-")).replace("-","");
                    damage_sub = damage_sub.substring(0, damage_sub.indexOf("-")).replace("-","");
                    flat = -1 * +flat_str;
                  } else if (damage_sub.includes("−")) {
                    const flat_str = damage_sub.substring(damage_sub.indexOf("−")).replace("−","");
                    damage_sub = damage_sub.substring(0, damage_sub.indexOf("−")).replace("−","");
                    flat = -1 * +flat_str;
                  }
                  if (damage_sub.includes("d")) {
                    const pieces = damage_sub.split("d");
                    count = +pieces[0];
                    size = +pieces[1];
                  }
                  const effect = new AbilityEffect();
                  effect.type = "Damage";
                  effect.damage_types.push(isDamageType(damage_type) ? damage_type as DamageType : "Typeless");
                  if (ca.effects.length === 0) {
                    const first_part = lower_case.substring(0, 20);
                    if (first_part.includes("melee")) {
                      if (first_part.includes("spell")) {
                        effect.attack_type = "Melee Spell";
                      } else {
                        effect.attack_type = "Melee";
                      }
                    } else if (first_part.includes("ranged")) {
                      if (first_part.includes("spell")) {
                        effect.attack_type = "Ranged Spell";
                      } else {
                        effect.attack_type = "Ranged";
                      }
                    } else if (ca.saving_throw_ability_score !== "") {
                      effect.attack_type = "Save";
                      effect.saving_throw_ability_score = ca.saving_throw_ability_score;
                    }
                    if (ca.range !== "") {
                      try {
                        const range = +ca.range;
                        if (!isNaN(range)) {
                          effect.target_int.range.base = range;
                        }
                      } catch (ex) { }
                    }
                    // X-foot cone
                    if (ca.description.includes("-foot")) {
                      const foot_index = ca.description.indexOf("-foot");
                      let area_str = "";
                      let i = 1;
                      while (ca.description[foot_index - i] !== " ") {
                        area_str = ca.description[foot_index - i] + area_str;
                        ++i;
                      }
                      // debugger;
                      i = 6;
                      let shape_str = "";
                      while (ca.description[foot_index + i] !== " ") {
                        shape_str += ca.description[foot_index + i];
                        ++i;
                      }
                      if (shape_str.endsWith(".")) {
                        shape_str = shape_str.substring(0, shape_str.length - 1);
                      }
                      effect.target_int.target_type = "Area";
                      try {
                        const area = +area_str;
                        if (!isNaN(area)) {
                          effect.target_int.area.base = area;
                        }
                      } catch (ex) { }
                      shape_str = common_util.capitalize_first(shape_str);
                      if (AREA_TYPES.includes(shape_str)) {
                        effect.target_int.area_type = shape_str as AreaType;
                      } else {
                        effect.target_int.area_type = "Sphere";
                      }
                    }
                  }
                  const potence = new Potence();
                  potence.level = 1;
                  potence.rolls.count.base = count;
                  potence.rolls.size.base = size;
                  // console.log(damage_str);
                  // console.log(damage_sub);
                  if (flat !== 0) {
                    if (using_ability !== "" && using_ability_mod <= flat) {
                      potence.rolls.flat.base = flat - using_ability_mod;
                      // potence.rolls.flat.add_ability_mod_mult = 1;
                      if (using_ability === new_obj.spellcasting_ability) {
                        // potence.rolls.flat.add_ability_scores.push("Spellcasting");
                        potence.rolls.flat.add_stats_groups = [{ 
                          true_id: uuidv4().toString(),
                          add_ability_scores: ["Spellcasting"],
                          mod_or_score: "Mod",
                          add_mult: 1,
                          multiple_type: "And"
                        }];
                      } else {
                        // potence.rolls.flat.add_ability_scores.push(using_ability);
                        potence.rolls.flat.add_stats_groups = [{ 
                          true_id: uuidv4().toString(),
                          add_ability_scores: [using_ability],
                          mod_or_score: "Mod",
                          add_mult: 1,
                          multiple_type: "And"
                        }];
                      }
                    } else {
                      potence.rolls.flat.base = flat;
                    }
                  }
                  ca.description = ca.description.replace(replace_me, potence.potence_string);
                  effect.potences.push(potence);
                  ca.effects.push(effect);
                }
              }
              if (ca.effects.length === 0) {
                const effect = new AbilityEffect();
                if (ca.dc.isBlank()) {
                  effect.type = "Utility";
                } else {
                  effect.type = "Control";
                  effect.attack_type = "Save";
                  effect.saving_throw_ability_score = ca.saving_throw_ability_score;
                  const conditions = this.conditions;
                  for (const condition of conditions.filter(o => o.common)) {
                    const condition_name = condition.name;
                    if (ca.description.includes(condition_name) || ca.description.includes(condition_name.toLowerCase())) {
                      effect.utility_detail = condition_name;
                    }
                  }
                }
                ca.effects.push(effect);
              }
              if (action_type === "Legendary Actions") {
                // debugger;
                ca.consumed_resource.resource_consumed = "Legendary Action";
                if (ca.name.includes("(Costs") && ca.name.includes("Actions)")) {
                  try {
                    // (Costs X Actions)
                    const actions_str = ca.name.substring(ca.name.indexOf("(Costs"));
                    const pieces = actions_str.split(" ");
                    const action_num = +pieces[1];
                    if (!isNaN(action_num)) {
                      ca.consumed_resource.amount_consumed = action_num;
                    }
                  } catch (ex) { }
                } else {
                  ca.consumed_resource.amount_consumed = 1;
                }
              } else {
                if (ca.name.includes("/Day)")) {
                  ca.consumed_resource.amount_consumed = 1;
                  
                  const times_index = ca.name.indexOf("/Day");
                  
                  let times_str = "";
                  let i = 1;
                  while (ca.name[times_index - i] !== " ") {
                    times_str = ca.name[times_index - i] + times_str;
                    ++i;
                  }
                  
                  try {
                    const times = +times_str;
                    if (!isNaN(times)) {
                      ca.consumed_resource.special_resource_amount = new UpgradableNumber(times);
                    }
                  } catch (ex) { }
                }
                if (action_type === "Bonus Actions") {
                  ca.casting_time = "BA";
                } else if (action_type === "Reactions") {
                  ca.casting_time = "RA";
                }
              }
              // Bite. Melee Weapon Attack: +12 to hit, reach 10 ft., one target. 
              // Hit: 18 (2d10 + 7) piercing damage plus 5 (1d10) lightning damage.

              // Frightful Presence. Each creature of the dragon's choice that is 
              // within 120 feet of the dragon and aware of it must succeed on a 
              // DC 17 Wisdom saving throw or become frightened for 1 minute. 
              // A creature can repeat the saving throw at the end of each of its turns, 
              // ending the effect on itself on a success. If a creature's saving throw 
              // is successful or the effect ends for it, the creature is immune to the 
              // dragon's Frightful Presence for the next 24 hours.

              if (ca.range.length > 0 && ca.effects.length > 0) {
                const pieces = ca.range.split("/");
                const range1 = +pieces[0];
                const range2 = pieces.length > 1 ? +pieces[1] : 0;
                if (!isNaN(range1) && !isNaN(range2)) {
                  ca.effects[0].target_int.range.base = range1;
                  ca.effects[0].target_int.range2.base = range2;
                }
              }
              // Lightning Breath (Recharge 5–6)
              // Ink Cloud (1/Day)
              if (ca.name.includes("(Recharge 5-6)")) {
                ca.consumed_resource.recharge_die = 6;
                ca.consumed_resource.recharge_on = [5,6];
                ca.consumed_resource.resource_consumed = "Recharge";
              } else if (ca.name.includes("(Recharge 4-6)")) {
                ca.consumed_resource.recharge_die = 6;
                ca.consumed_resource.recharge_on = [4,5,6];
                ca.consumed_resource.resource_consumed = "Recharge";
              } else if (ca.name.includes("(Recharge 6)")) {
                ca.consumed_resource.recharge_die = 6;
                ca.consumed_resource.recharge_on = [6];
                ca.consumed_resource.resource_consumed = "Recharge";
              } else if (ca.name.includes("/Day)")) {
                ca.consumed_resource.resource_consumed = "Special";
                ca.consumed_resource.amount_consumed = 1;
                ca.consumed_resource.special_resource_refresh_rule = "Long Rest";
                try {
                  let pieces = ca.name.split("(");
                  pieces = pieces[1].split("/");
                  const x_day = +pieces[0];
                  if (!isNaN(x_day)) {
                    ca.consumed_resource.special_resource_amount = new UpgradableNumber(x_day);
                  }
                } catch (ex) {
                  ca.consumed_resource.special_resource_amount = new UpgradableNumber(1);
                }
              }
            }
            ff = ca;
          }
          const desc = common_util.cleanSpaces(ff ? ff.description : content_str);
          if (name.includes("Wing Attack")) {
            console.log(desc);
          }
          const old_ff = this.all_floating_features.find(o => 
            o.name === name &&
            common_util.cleanSpaces(o.description).toLocaleLowerCase() === desc.toLocaleLowerCase());
          if (old_ff) {
            ff = old_ff;
          }
        }
        if (ff) {
          // debugger;
          if (action_type === "Actions" || action_type === "Bonus Actions" || action_type === "Reactions") {
            const feature = new Feature();
            feature.floating_feature = ff;
            feature.floating_feature_id = ff._id;
            const fb = new FeatureBase();
            fb.creature_fb_type = "Actions";
            fb.fb_name = ff.name;
            fb.level = new_obj.min_challenge_rating;
            fb.features.push(feature);
            new_obj.fbs.push(fb);
          } else if (action_type === "Legendary Actions") {
            const feature = new Feature();
            feature.floating_feature = ff;
            feature.floating_feature_id = ff._id;
            const fb = new FeatureBase();
            fb.creature_fb_type = "Legendary Actions";
            fb.fb_name = ff.name;
            fb.level = new_obj.min_challenge_rating;
            fb.features.push(feature);
            new_obj.fbs.push(fb);
          } else if (action_type === "Special Abilities" || action_type === "Traits") {
            const feature = new Feature();
            feature.floating_feature = ff;
            feature.floating_feature_id = ff._id;
            const fb = new FeatureBase();
            fb.creature_fb_type = "Traits";
            fb.fb_name = ff.name;
            fb.level = new_obj.min_challenge_rating;
            fb.features.push(feature);
            new_obj.fbs.push(fb);
          } else {
            // console.log(action_type);
            // console.log(content_str);
            debugger;
          }
        } else {
          // console.log(action_type);
          // console.log(content_str);
          debugger;
        }
      }
    } else if (elem.classList.contains("legendary-actions")) {
      // <p class="legendary-actions" data-content-chunk-id="5a612322-1836-4fac-b5bb-401165fd2ab4">
      // Legendary Action Uses: 3 (4 in Lair). Immediately after another creature’s turn, the aboleth can expend a use to take one of the following actions. The aboleth regains all expended uses at the start of each of its turns.
      // </p>
      let actions_str = elem.textContent;
      if (actions_str && actions_str.startsWith("Legendary Action Uses: ")) {
        const legendaries_entry = new_obj.legendaries_table.entries[0];
        actions_str = actions_str.substring(23).trim();
        if (actions_str.includes("in Lair)")) {
          let in_lair_index = actions_str.indexOf("in Lair)");
          let lair_str = actions_str.substring(0, in_lair_index);
          in_lair_index = lair_str.lastIndexOf("(");
          lair_str = lair_str.substring(in_lair_index + 1).trim();
          const lair_actions = +lair_str;
          if (!isNaN(lair_actions)) {
            legendaries_entry.act_lair = lair_actions;
          }
        }
        actions_str = actions_str.split(" ")[0];
        const leg_actions = +actions_str;
        if (!isNaN(leg_actions)) {
          legendaries_entry.actions = leg_actions;
        }
      }
    }
    return 0;
  }

  get conditions() {
    if (this.smart_hash.condition) {
      return this.smart_hash.condition as Condition[];
    }
    return [];
  }
  get source_books(): SourceBook[] {
    if (this.smart_hash.source_book) {
      return this.smart_hash.source_book as SourceBook[];
    }
    return [];
  }
  get spells() {
    if (this.smart_hash.spell) {
      return this.smart_hash.spell as Spell[];
    }
    return [];
  }
}